import { preorderBadgeKey } from './components/preorderNavigation';
import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, Suspense } from 'react';
import axios from 'axios';
import {
  AppProvider,
  Page,
  Layout,
  Card,
  TextField,
  Button,
  Banner,
  Badge,
  Text,
  Box,
  InlineStack,
  BlockStack,
  Modal,
  DataTable,
  EmptyState,
  ButtonGroup,
  Icon,
  Checkbox,
  Select,
  Tooltip
} from '@shopify/polaris';
import {
  SearchIcon,
  LocationIcon
} from '@shopify/polaris-icons';
import enTranslations from '@shopify/polaris/locales/en.json';
import { searchProductDirect, refreshProductDirect, isDirectAPIAvailable } from './services/directAPI';
import { debounce } from './utils/debounce';
import {
  isIncomingStockTargetRefreshing,
  resolvePlannerCostBackfillAsyncTarget,
  resolvePlannerSupplierSkuSyncAsyncTarget,
  resolvePlannerAsyncRefreshTarget,
  shouldRestoreRefreshPickerRun,
  buildRefreshStoreSelection
} from './utils/cacheRefreshStatus';
import SaveQueueStatus from './components/SaveQueueStatus';
import CarrierMark, { carrierForLink, hasCarrierMark } from './components/CarrierMark';
import POTaskChecklist from './components/POTaskChecklist';
import UnmatchedArrivalsPanel from './components/UnmatchedArrivalsPanel';
import { fetchSecurityIncidentSummary } from './components/securityIncidentSummary';
import MountAfterFirstOpen from './components/MountAfterFirstOpen';
import UserProfileBadge from './components/UserProfileBadge';
import './App.css';
import './components/RefreshDatabaseModal.css';
import { retryWithBackoff, isRetryableError } from './utils/retry';
import {
  nextSaveQueueItemId,
  buildProductSaveData,
  summarizeStoreResults,
  clearSavedFieldsFromEditable
} from './utils/productSaveQueue';
import { isPlaceholderBarcode } from './utils/barcodePlaceholders';
import { fetchLiveStockBatch, getLiveStockUpdate, applyLiveStockUpdates } from './utils/liveStockBatch';
import { isLocationQuery, normalizeLocation, getLocationType } from './utils/locationDetector';
import { getDevModeHeaderStyles, DevModePill, isDevMode, canViewInternalWarnings } from './utils/devMode';
import ModuleNavBar from './components/ModuleNavBar';
import { isMobileViewport, isPickingTouchViewport, isTabletViewport } from './utils/viewport';
import { getDisplayVariantTitle } from './utils/variantTitle';
import { buildA4ProductListHtml } from './utils/productListPrint';
import { resolveLabelQuantity } from './utils/labelQuantity';
import BackendDownBanner from './components/BackendDownBanner';
import { IconPin, IconWeight, IconBox, IconCalendar, ACCENT } from './components/fieldIcons';
import { expiryApi } from './components/expiry/expiryApi';
import { rowExpiryFromEntry, linkedExpiryStores } from './components/expiry/expiryDisplay';
import { buildKitSplitIndex, fetchKitSplits, kitIdFor, kitKindInfo, kitLinkFor } from './components/kitSplits/kitSplitsApi';
import { productBarcodeList, replacePrimaryBarcode } from './utils/productBarcodes';
import { buildProductLinkIndex, fetchProductLinks, mergeAffected, productLinkFor } from './components/productLinks/productLinksApi';
import { resolvePoItemDisplayTitle } from './utils/poItemDisplay';
import { buildExtraPoItems } from './utils/poReceiptSummary';
import { subtractMovedItemsFromPoItems } from './utils/poPreArrivalSplit';
import { MODULE_COLORS, DEFAULT_HEADER_COLOR, NAV_KEYS, NAV_JOB_HUB_KEYS, HEADER_TOOL_KEYS, HEADER_HEIGHT_PX, getNavLabel, getHeaderContainerStyle, getModuleFocusRingColor, getModuleBaseColor, getModuleAccentBadge, getSectionAccentColor, getDocumentTitle } from './utils/moduleConfig';
import { getDbStatusLabel, getDbStatusTier } from './utils/navPresentation';
import { resolveSelfStaffId } from './utils/jobSelfStaff';
import { isWarehousePath, normalizeModuleUrl } from './utils/appUrl';
import { normalizeProductLocationFields } from './utils/productLocationFields';
import { applyStockEditToRow, lightspeedLevelAfterWrite, lightspeedReadBackLags, stockSystemFor } from './utils/productStockEdit';
import { collectPoAuditItems, mapTubClashSlots } from './utils/poTubClashAudit';
import {
  buildHighQuantityPoLabelTaskOverride,
  HIGH_PO_LABEL_QUANTITY_THRESHOLD,
} from './utils/poLabelGuard';
import {
  getPoLineOrderedQuantity,
  getPoLineArrivedQuantity,
  poLineArrivalDiffersFromOrder,
  poLineDidNotArrive,
  getArrivedPoLines
} from './utils/poLineQuantity';
import {
  ensureCanonicalUrl,
  incomingStockPath,
  isIncomingStockPath,
  isPlannerPath,
  isPricemolePath,
  isProductsPath,
  isJobs3dPath,
  isRepairsPath,
  isPickupKioskPath,
  isShipPath,
  isStocktakePath,
  parseStocktakeLocation,
  STOCKTAKE_PATH,
  parseShipRoute,
  parsePickupKioskPane,
  pickupKioskPanePath,
  isLocalDeliveriesPath,
  parsePlannerChannelFromPath,
  parsePricemoleChannelFromPath,
  plannerChannelPath,
  pricemoleChannelPath,
  productsHomePath,
  JOBS_3D_PATH,
  REPAIRS_PATH,
  PICKUP_KIOSK_PATH,
  SHIP_PATH,
  LOCAL_DELIVERIES_PATH,
  EXPIRY_PATH,
  isExpiryPath,
  CEO_PATH,
  isCeoPath,
  normalizePathname,
  navHrefForModule,
} from './utils/moduleRoutes';
import { computeRemoteRestricted, isTailnetHost, tailnetRootLandsOnDeliveries } from './utils/remoteAccess';
import { getCeoLandingStorage, hasCeoGrant, isRootLandingVisit, rememberCeoLanding, rootLandsOnCeo, shouldSeedCeoLanding } from './utils/ceoLanding';
import ErrorBoundary from './components/ErrorBoundary';
// Static, not lazy: it has to be on hand exactly when the lazy picking chunk fails.
import PickingErrorBoundary from './components/PickingErrorBoundary';
import RefreshDatabasePicker from './components/RefreshDatabasePicker';
import {
  REFRESH_STORE_KEYS,
  REFRESH_TARGET_LABELS,
  REFRESH_TARGET_SUMMARY,
  REFRESH_MODULE_GROUPS
} from './data/refreshTargetDetails';
import ProductCard from './components/ProductCard';
import ShelfSlotPicker from './components/ShelfSlotPicker';
import { expandLocationShorthand, isShelfOnlyBinCode } from './utils/warehouseLocationParser';
import LoginScreen from './components/LoginScreen';
import AppUpdateToast from './components/AppUpdateToast';
import PickingInstallBanner from './components/PickingInstallBanner';
import { BoxDimensionsBar } from './components/BoxDimensionsFields';
import { auth } from './firebase';
import { onAuthStateChanged, signOut } from "firebase/auth";
import packageJson from '../package.json';
import { getApiBaseUrl, isLocalDevHost } from './utils/apiClient';
import { getTodayDateString } from './utils/dateUtils';
import { PO_METADATA_CHANGED_EVENT, buildOptimisticPoMetadataEntry } from './utils/poMetadataEvents';
import { newLabelPrintRequestId } from './utils/labelPrintRequest';
import { useAppUpdateMonitor } from './hooks/useAppUpdateMonitor';
import { useAppHeaderOffset } from './hooks/useAppHeaderOffset';
// What's new (2026-10-08): header button + daily popup; entries + dialog are lazy chunks.
import WhatsNewLauncher from './components/whatsNew/WhatsNewLauncher';
import {
  clearSessionHint,
  hasSessionHint,
  isTransientAuthCheckError,
  setSessionHintActive,
  sleep,
  verifyFirebaseSession,
} from './utils/sessionRestore';

// Incoming Stock open reuses a cached page younger than this (perf audit 2026-09-29).
const PO_OPEN_CACHE_REUSE_MS = 60 * 1000;

const API_URL = getApiBaseUrl();

const PLANNER_ASYNC_REFRESH_KEYS = Object.freeze([
  'planner-sales-phaser',
  'planner-sales-ccc',
  'planner-cost-backfill',
  'planner-supplier-skus'
]);

const CACHE_REFRESH_STATUS_POLL_MS = 10000;

const EMPTY_REFRESH_STORE_SELECTION = Object.fromEntries(
  REFRESH_STORE_KEYS.map((key) => [key, false])
);

const ALL_REFRESH_STORE_SELECTION = Object.fromEntries(
  REFRESH_STORE_KEYS.map((key) => [key, true])
);

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();
const getPoItemSku = (item) => String(item?.sku || item?.variant_sku || '').trim();

const MODULE_PERMISSION_LABELS = {
  warehouse: 'Products',
  jobs: 'Jobs',
  po: 'Incoming Stock',
  poPlanning: 'Inventory Planner',
  priceMole: 'PriceMole',
  coffeeStocktake: 'Roast Stocktake',
  stocktake: 'Stocktake',
  ra: 'Returns',
  preorders: 'Preorders',
  newProducts: 'New Products',
  productTags: 'Product Tags',
  logs: 'Activity Logs',
  picking: 'Picking',
  packing: 'Packing',
  pickFromStore: 'Pick From Store',
  ship: 'Ship',
  localDeliveries: 'Local Deliveries',
  security: 'Security incidents',
  ceo: 'CEO'
};

const MODULE_KEY_BY_NAV = {
  ceo: 'ceo',
  warehouse: 'warehouse',
  jobs: 'jobs',
  po: 'po',
  'po-planning': 'poPlanning',
  pricemole: 'priceMole',
  'coffee-stocktake': 'coffeeStocktake',
  stocktake: 'stocktake',
  preorders: 'preorders',
  picking: 'picking',
  ra: 'ra',
  'new-products': 'newProducts',
  'backup-restore': 'warehouse',
  expiry: 'warehouse',
  map: 'warehouse',
  'picking-supervisor': 'picking',
  'packing-supervisor': 'picking',
  'pick-from-store': 'pickFromStore',
  ship: 'ship',
  'local-deliveries': 'localDeliveries'
};

const MODULE_KEY_BY_VIEW = {
  home: 'warehouse',
  ceo: 'ceo',
  jobs: 'jobs',
  po: 'po',
  'po-planning': 'poPlanning',
  pricemole: 'priceMole',
  'coffee-stocktake': 'coffeeStocktake',
  stocktake: 'stocktake',
  ra: 'ra',
  preorders: 'preorders',
  picking: 'picking',
  'picking-supervisor': 'picking',
  'packing-supervisor': 'picking',
  'pick-from-store': 'pickFromStore',
  ship: 'ship',
  'local-deliveries': 'localDeliveries',
  'new-products': 'newProducts',
  tags: 'productTags',
  'backup-restore': 'warehouse',
  expiry: 'warehouse',
  map: 'warehouse'
};

// Dark mode context - provided by AppWithAuth so Polaris AppProvider can use theme
const DarkModeContext = createContext({ darkMode: false, setDarkMode: () => {} });

// Arrival stamp for Arrived-tab sorting/aging. Real stamps first (arrivedAt
// set on the move into the arrived family, receivedAt from the receive step);
// a PAST expected-arrival date stands in as an estimate for POs from before
// stamping existed, so every card can show an age.
function resolvePoArrivalStamp(po) {
  const meta = po?.localMetadata || {};
  if (meta.arrivedAt) return { iso: meta.arrivedAt, estimated: false };
  // POs from before arrivedAt stamping existed: EARLIEST real receiving
  // signal wins — the first completed step marks when the goods hit the
  // bench. receivedAt (stamped when step 4's receipt modal is submitted,
  // often days later) must never outrank an earlier task timestamp: it used
  // to be checked first, so finishing step 4 reset the age to "Arrived 0h
  // ago" (SI-7901, 2026-08-25). The min can only move backwards, never
  // forwards, so later steps leave the stamp alone.
  const signalTimes = Object.values(meta.tasks || {})
    .filter((task) => task?.completed && task.completedAt)
    .map((task) => Date.parse(task.completedAt));
  if (meta.receivedAt) signalTimes.push(Date.parse(meta.receivedAt));
  const taskTimes = signalTimes.filter(Number.isFinite);
  if (taskTimes.length) return { iso: new Date(Math.min(...taskTimes)).toISOString(), estimated: false };
  const eta = meta.expectedArrivalDate;
  if (eta && Date.parse(eta) <= Date.now()) return { iso: eta, estimated: true };
  return { iso: null, estimated: true };
}
export function useDarkMode() {
  return useContext(DarkModeContext);
}
const USE_DIRECT_API = process.env.REACT_APP_USE_DIRECT_API === 'true';
const _FRONTEND_VERSION = packageJson.version; // Used for logging/debugging
const ENABLE_PERF_LOGGING = process.env.REACT_APP_ENABLE_PERF_LOGGING === 'true';
const WAREHOUSE_SKU_SEARCH_ID = 'warehouse-sku-search';
// Values are storeDisplayName as stored on cached products.
const PRODUCT_STORE_OPTIONS = [
  { label: 'All stores', value: '' },
  { label: 'Phaser', value: 'Phaser FPV' },
  { label: 'CCC', value: 'CCC' },
  { label: 'Porlex', value: 'Porlex' },
  { label: 'Redeemer', value: 'Redeemer' }
];
const PREORDER_SKU_CACHE_MAX_AGE_MS = 60 * 1000;
const MAX_BIN_LOCATION_HISTORY_ENTRIES = 10;
const AiChatWidget = React.lazy(() => import('./components/aiChat/AiChatWidget'));
const ProductTagsModal = React.lazy(() => import('./components/ProductTagsModal'));
const PreordersModal = React.lazy(() => import('./components/PreordersModal'));
const POPlanningModal = React.lazy(() => import('./components/POPlanningModal'));
const PriceMoleModal = React.lazy(() => import('./components/PriceMoleModal'));
const CoffeeStocktakeModal = React.lazy(() => import('./components/CoffeeStocktakeModal'));
const JobsHubModal = React.lazy(() => import('./components/jobs/JobsHubModal'));
const NewProductsModal = React.lazy(() => import('./components/NewProductsModal'));
const PickingModal = React.lazy(() => import('./components/PickingModal'));
const loadStocktakeView = () => import('./components/stocktake/StocktakeView');
const StocktakeView = React.lazy(loadStocktakeView);
const PickingSupervisorView = React.lazy(() => import('./components/PickingSupervisorView'));
const PickFromStoreView = React.lazy(() => import('./components/PickFromStoreView'));
// Named chunk: scripts/checkBundleSize.js gives Ship (bench PCs only, never the tablets) its own budget.
const ShipView = React.lazy(() => import(/* webpackChunkName: "ship" */ './components/ship/ShipView'));
const LocalDeliveriesView = React.lazy(() => import('./components/LocalDeliveriesView'));
// Dialogs opened on demand: loaded on first open (MountAfterFirstOpen) so they
// stay out of the entry bundle, which every device downloads before first
// paint (budget in scripts/checkBundleSize.js).
const POReceiptModal = React.lazy(() => import('./components/POReceiptModalV2'));
const POPreArrivalSplitModal = React.lazy(() => import('./components/POPreArrivalSplitModal'));
const POWorksheet = React.lazy(() => import('./components/POWorksheet'));
const SettingsModal = React.lazy(() => import('./components/SettingsModal'));
const UserPermissionsModal = React.lazy(() => import('./components/UserPermissionsModal'));
const AdminApiEndpointsModal = React.lazy(() => import('./components/AdminApiEndpointsModal'));
const CacheStatusModal = React.lazy(() => import('./components/CacheStatusModal'));
const PutAwayModal = React.lazy(() => import('./components/PutAwayModal'));
const PrintLabelQuantityModal = React.lazy(() => import('./components/PrintLabelQuantityModal'));
const BrandClashModal = React.lazy(() => import('./components/BrandClashModal'));
const SecurityIncidentsModal = React.lazy(() => import('./components/SecurityIncidentsModal'));
// Prefetched during idle time so the first MAP click doesn't wait on the chunk download.
const BackupRestorePanel = React.lazy(() => import('./components/warehouse/BackupRestorePanel'));
// Food expiry: dashboard page (/expiry) and the per-SKU batches dialog the
// product card badge opens. Both lazy so the Products chunk stays in budget.
const ExpiryDashboard = React.lazy(() => import('./components/expiry/ExpiryDashboard'));
// CEO master watchlist (/ceo, 2026-10-23): admin-only, so its own lazy chunk.
const CeoView = React.lazy(() => import('./components/ceo/CeoView'));
const ExpiryBatchesPanel = React.lazy(() => import('./components/expiry/ExpiryBatchesPanel'));
const KitSplitModal = React.lazy(() => import('./components/kitSplits/KitSplitModal'));
const ProductLinksModal = React.lazy(() => import('./components/productLinks/ProductLinksModal'));

// A grid row is the product-link chip's target when the SKU matches (case and
// whitespace blind) and the store matches by domain or display name.
const linkedRowMatches = (row, store, skuUpper) => {
  if (!row || String(row.sku || '').trim().toUpperCase() !== skuUpper) return false;
  const want = String(store || '').trim().toLowerCase();
  if (!want) return true;
  return String(row.store || '').trim().toLowerCase() === want
    || String(row.storeDisplayName || '').trim().toLowerCase() === want;
};
const WarehouseMapMockup = React.lazy(() => import(/* webpackPrefetch: true */ './components/warehouse/WarehouseMapMockup'));

const normalizeBinLocationForHistory = (value) => {
  const text = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  const lowered = text.toLowerCase();
  if (lowered === 'not set' || lowered === 'notset' || lowered === 'null' || lowered === 'undefined') return '';
  return text;
};

const binLocationHistoryKey = (value) => normalizeBinLocationForHistory(value).toUpperCase();

const buildLocalBinLocationHistory = (product, nextLocation, changedBy, changedAt = new Date().toISOString()) => {
  const previousLocation = normalizeBinLocationForHistory(product?.binLocation);
  const normalizedNextLocation = normalizeBinLocationForHistory(nextLocation);
  const existingHistory = Array.isArray(product?.binLocationHistory)
    ? product.binLocationHistory.filter(Boolean).slice(0, MAX_BIN_LOCATION_HISTORY_ENTRIES)
    : [];

  if (!previousLocation || binLocationHistoryKey(previousLocation) === binLocationHistoryKey(normalizedNextLocation)) {
    return existingHistory;
  }

  const entry = {
    from: previousLocation,
    to: normalizedNextLocation || null,
    changedAt,
    changedBy: changedBy || 'unknown'
  };

  const latest = existingHistory[0];
  const isDuplicateLatest = latest
    && binLocationHistoryKey(latest.from) === binLocationHistoryKey(entry.from)
    && binLocationHistoryKey(latest.to) === binLocationHistoryKey(entry.to);

  return isDuplicateLatest
    ? existingHistory
    : [entry, ...existingHistory].slice(0, MAX_BIN_LOCATION_HISTORY_ENTRIES);
};

const AUTH_CHECK_MAX_RETRIES = 5;
const AUTH_CHECK_RETRY_MS = 1000;

const authLoadingScreen = (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    fontSize: '18px',
    color: 'var(--app-ink-secondary)',
  }}
  >
    Loading...
  </div>
);

// Wrapper component for authentication
function AuthWrapper({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [securityEnabled, setSecurityEnabled] = useState(true);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const initialAuthDoneRef = useRef(false);

  const completeAuthSuccess = useCallback(() => {
    setIsAuthenticated(true);
    setSessionHintActive();
  }, []);

  const applyAuthCheckResponse = useCallback((response) => {
    if (response.data.securityEnabled === false) {
      console.log('[Auth] Security disabled on server');
      setIsAuthenticated(true);
      setSecurityEnabled(false);
      return true;
    }

    setSecurityEnabled(response.data.securityEnabled !== false);
    if (response.data.authenticated) {
      completeAuthSuccess();
      return true;
    }

    setIsAuthenticated(false);
    clearSessionHint();
    return false;
  }, [completeAuthSuccess]);

  const tryRestoreFromFirebase = useCallback(async (user) => {
    if (!user) return false;
    const result = await verifyFirebaseSession(user, API_URL);
    if (result.ok) {
      completeAuthSuccess();
      return true;
    }
    console.warn('[Auth] Firebase session restore failed:', result.error);
    return false;
  }, [completeAuthSuccess]);

  const checkAuth = useCallback(async ({ silent = false, allowRetry = false } = {}) => {
    if (isLocalDevHost()) {
      console.log('[Auth] Localhost detected - bypassing auth check');
      setIsAuthenticated(true);
      setSecurityEnabled(false);
      if (!silent) setIsCheckingAuth(false);
      return { authenticated: true };
    }

    const maxAttempts = allowRetry ? AUTH_CHECK_MAX_RETRIES : 1;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/check`, {
          withCredentials: true,
        });
        const authenticated = applyAuthCheckResponse(response);
        return { authenticated };
      } catch (err) {
        console.error('[Auth] Check failed:', err);
        const status = err.response?.status;
        const isAuthRejection = status === 401 || status === 403;
        const transient = isTransientAuthCheckError(err);

        if (silent && !isAuthRejection) {
          console.warn('[Auth] Silent session check failed; keeping current login state');
          return { authenticated: null };
        }

        if (allowRetry && transient && attempt < maxAttempts - 1) {
          await sleep(AUTH_CHECK_RETRY_MS);
          continue;
        }

        if (transient && allowRetry) {
          return { authenticated: false, transientFailure: true };
        }

        setIsAuthenticated(false);
        setSecurityEnabled(true);
        return { authenticated: false };
      }
    }

    return { authenticated: false };
  }, [applyAuthCheckResponse]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setFirebaseReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!firebaseReady || initialAuthDoneRef.current) return undefined;

    let cancelled = false;

    async function runInitialAuth() {
      const { authenticated, transientFailure } = await checkAuth({
        allowRetry: true,
      });

      if (cancelled) return;

      if (!authenticated) {
        const user = firebaseUser || auth.currentUser;
        if (user) {
          const restored = await tryRestoreFromFirebase(user);
          if (cancelled) return;
          if (restored) {
            initialAuthDoneRef.current = true;
            setIsCheckingAuth(false);
            return;
          }
        } else if (transientFailure || hasSessionHint()) {
          await sleep(500);
          if (cancelled) return;
          const lateUser = auth.currentUser;
          if (lateUser) {
            const restored = await tryRestoreFromFirebase(lateUser);
            if (cancelled) return;
            if (restored) {
              initialAuthDoneRef.current = true;
              setIsCheckingAuth(false);
              return;
            }
          }
        }
      }

      if (cancelled) return;
      initialAuthDoneRef.current = true;
      setIsCheckingAuth(false);
    }

    runInitialAuth();

    return () => {
      cancelled = true;
    };
  }, [firebaseReady, firebaseUser, checkAuth, tryRestoreFromFirebase]);

  useEffect(() => {
    if (!firebaseReady || !initialAuthDoneRef.current) return undefined;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuth({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [firebaseReady, checkAuth]);

  const handleLogin = () => {
    completeAuthSuccess();
  };

  // The backend-down banner rides OUTSIDE the auth gate: a dead backend also
  // breaks login ("Backend verification failed"), and that page needs the
  // banner most.
  let authContent;
  if (isCheckingAuth || !firebaseReady) {
    authContent = authLoadingScreen;
  } else if (!securityEnabled) {
    authContent = children;
  } else if (!isAuthenticated) {
    authContent = <LoginScreen onLogin={handleLogin} />;
  } else {
    authContent = children;
  }

  return (
    <>
      <BackendDownBanner />
      {authContent}
    </>
  );
}

/**
 * The quantity cell on a PO card line.
 *
 * Shows what ARRIVED. When the supplier delivered something other than what was ordered
 * (SI-7917, 2026-08-20: 9 against an order of 3, and nothing at all against an order of
 * 6) the ordered figure rides alongside it struck through, because that mismatch is
 * exactly what staff need to see before they print labels and put stock away.
 */
function PoLineQuantityCell({ item }) {
  const arrived = getPoLineArrivedQuantity(item);
  const ordered = getPoLineOrderedQuantity(item);
  const differs = poLineArrivalDiffersFromOrder(item);
  const missing = poLineDidNotArrive(item);

  // Theme tokens switch these with dark mode (docs/THEMING.md).
  const tone = missing ? 'var(--app-critical-ink)' : differs ? 'var(--app-warning-ink)' : 'inherit';
  const toneClass = missing ? 'po-line-qty--missing' : differs ? 'po-line-qty--over' : undefined;

  return (
    <div
      className={toneClass}
      title={differs ? `Ordered ${ordered}, received ${arrived}` : undefined}
      style={{
        // The shared grid reserves room for the widest quantity in this list.
        // Discrepancies keep their struck-through ordered figure visible.
        minWidth: differs ? '58px' : '30px',
        textAlign: 'right',
        flexShrink: 0,
        fontSize: '11px',
        fontWeight: 'bold',
        fontVariantNumeric: 'tabular-nums',
        color: tone,
        whiteSpace: 'nowrap',
        // Rows are top-aligned (titles may wrap) — pair the qty with the
        // title's first line, whose bodySm line box is 16px.
        lineHeight: '16px'
      }}
    >
      {differs && (
        <>
          <span style={{ fontWeight: 500, textDecoration: 'line-through', opacity: 0.65 }}>{ordered}</span>
          {/* The arrow keeps "80" and "38x" from reading as one number at 11px. */}
          <span style={{ opacity: 0.65, margin: '0 2px' }}>→</span>
        </>
      )}
      {arrived}x
    </div>
  );
}

function App() {
  // MIGRATION: Clear old localStorage data if it uses old productKey format
  // Old format: ${id}-${variantId}, New format: ${storeDisplayName}-${id}-${variantId}
  const migrateLocalStorage = () => {
    const version = localStorage.getItem('productKeyVersion');
    if (version !== '2') {
      console.log('[Migration] Clearing old localStorage data (productKey format changed)');
      localStorage.removeItem('warehouseProducts');
      localStorage.removeItem('warehouseEditableData');
      localStorage.removeItem('warehouseSelectedProducts');
      localStorage.setItem('productKeyVersion', '2');
    }

    // Additional migration: Clear any legacy editable data that might cause false green borders
    // Legacy keys had format like: "CCC-central-coast-coffee.myshopify.com_xxx-yyy"
    // New keys are: "StoreDisplayName-productId-variantId"
    const editableDataVersion = localStorage.getItem('editableDataVersion');
    if (editableDataVersion !== '2') {
      console.log('[Migration] Clearing legacy editable data to prevent stale data conflicts');
      localStorage.removeItem('warehouseEditableData');
      localStorage.setItem('editableDataVersion', '2');
    }
  };
  migrateLocalStorage();

  // State management - Initialize products from localStorage to prevent overwriting
  const [_highlightedPOId, _setHighlightedPOId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [permissionSettings, setPermissionSettings] = useState(null);
  const [permissionSettingsLoaded, setPermissionSettingsLoaded] = useState(false);
  // Whether THIS connection came in over Tailscale, straight from the server
  // (it sees the Serve identity headers; the browser cannot). See
  // backend/middleware/remoteAccess.js.
  const [permissionAccessContext, setPermissionAccessContext] = useState(null);

  // Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    // A shared office PC: the next person to sign in must not get the CEO
    // first paint this account earned (2026-10-23).
    rememberCeoLanding(getCeoLandingStorage(), false);
    try {
      clearSessionHint();
      // 1. Call backend to clear HTTP-only session cookie
      await axios.post(`${API_URL}/api/auth/logout`);

      // 2. Sign out of Firebase client SDK
      await signOut(auth);

      // 3. Force reload to clear application state and return to login info
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
      clearSessionHint();
      // Force reload anyway to ensure UI resets
      window.location.reload();
    }
  };

  const fetchPermissionSettings = useCallback(async () => {
    setPermissionSettingsLoaded(false);
    try {
      const response = await axios.get(`${API_URL}/api/settings/my-permissions`, { withCredentials: true });
      setPermissionSettings(response.data?.permissions || null);
      setPermissionAccessContext(response.data?.accessContext || null);
    } catch (err) {
      console.error('[Permissions] Failed to load settings:', err);
      setPermissionSettings(null);
      setPermissionAccessContext(null);
    } finally {
      setPermissionSettingsLoaded(true);
    }
  }, []);

  // Local Deliveries only, because this browser reached us over Tailscale and
  // the account has no remote grant. The backend enforces it; this keeps the
  // UI honest — no nav buttons that 403, no flash of the Products grid.
  const remoteRestricted = useMemo(() => computeRemoteRestricted({
    permissions: permissionSettings,
    accessContext: permissionAccessContext,
    hostname: window.location.hostname
  }), [permissionSettings, permissionAccessContext]);

  // Should a header-badge / status fetch that fires regardless of the open
  // module be skipped? Over Tailscale a restricted account may only call the
  // delivery API, so each of them would collect a 403 and log for nothing.
  // "Still loading" counts as blocked on a tailnet host — we do not yet know
  // whether this account is allowed, and every one of these callbacks re-runs
  // when permissions land. Off the tailnet this is always false, so the office
  // makes exactly the calls it always did.
  const skipRemoteBlockedFetch = useCallback(() => (
    isTailnetHost(window.location.hostname) && (!permissionSettingsLoaded || remoteRestricted)
  ), [permissionSettingsLoaded, remoteRestricted]);

  // Remember the CEO grant so the next load of `/` can paint CEO straight
  // away instead of flashing the Products grid first (2026-10-23). Only a
  // real answer updates it; a failed load leaves the last known value.
  useEffect(() => {
    if (!permissionSettingsLoaded || !permissionSettings) return;
    if (isTailnetHost(window.location.hostname)) return;
    rememberCeoLanding(getCeoLandingStorage(), hasCeoGrant(permissionSettings));
  }, [permissionSettings, permissionSettingsLoaded]);

  useEffect(() => {
    fetchPermissionSettings();

    const handlePermissionsUpdated = () => {
      fetchPermissionSettings();
    };

    window.addEventListener('task-permissions-updated', handlePermissionsUpdated);
    return () => window.removeEventListener('task-permissions-updated', handlePermissionsUpdated);
  }, [fetchPermissionSettings]);

  const hasModulePermission = useCallback((moduleKey) => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDev) return true;

    if (!moduleKey) return true;
    // CEO is granted, never defaulted (2026-10-23): "not loaded yet", a failed
    // load or a backend that predates the key must all read as no, or every
    // staff member would see an admin-only nav button that 403s.
    // Over a restricted Tailscale link it is Local Deliveries only, as below.
    if (moduleKey === 'ceo') return hasCeoGrant(permissionSettings) && !remoteRestricted;
    if (!permissionSettingsLoaded) return true;
    if (!permissionSettings) return true;
    // Admin sees every module even if the resolved map misses a newer key
    // (same bypass hasTaskPermission has) — a missed backend default once
    // locked the admin out of /local entirely.
    if (permissionSettings?.isAdmin) return true;

    // Over Tailscale it is Local Deliveries or nothing (2026-09-02). Purely
    // subtractive: localDeliveries still has to be granted below, so this can
    // never hand anyone a module they don't have in the office.
    if (remoteRestricted && moduleKey !== 'localDeliveries') return false;

    // Jobs and Returns are linked — either key grants access to both
    if (moduleKey === 'ra') {
      return (permissionSettings?.modulePermissions?.['ra'] ?? true) ||
             (permissionSettings?.modulePermissions?.['jobs'] ?? true);
    }

    return permissionSettings?.modulePermissions?.[moduleKey] ?? true;
  }, [permissionSettings, permissionSettingsLoaded, remoteRestricted]);

  const hasMapPermission = useCallback(() => (
    hasModulePermission('warehouse') || hasModulePermission('picking')
  ), [hasModulePermission]);

  const hasTaskPermission = useCallback((taskKey) => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDev) return true;

    if (!taskKey) return true;
    if (!permissionSettings) return false;
    if (permissionSettings?.isAdmin) return true;

    return permissionSettings?.taskPermissions?.[taskKey] ?? false;
  }, [permissionSettings]);

  const canManageEtaDates = hasTaskPermission('setEtaDates') || normalizeEmail(currentUser?.email) === 'rhys100@gmail.com';
  const canEditProductStock = hasTaskPermission('editProductStock');
  // Phaser cards write Lightspeed (docs/memory/products.md 2026-10-14). The
  // server also needs LIGHTSPEED_STOCK_WRITE_ENABLED, so granted users ask
  // once per session whether writes are on; with them off the card's number
  // stays put and the hover carries the reason instead of a dead click.
  const canEditLightspeedStock = hasTaskPermission('editLightspeedStock');
  const [lightspeedStockWrites, setLightspeedStockWrites] = useState(null); // { enabled, reason } | null
  useEffect(() => {
    if (!canEditLightspeedStock) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/api/products/lightspeed-stock/capabilities`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const phaser = res.data?.phaser || {};
        setLightspeedStockWrites({ enabled: phaser.enabled === true, reason: phaser.reason || null });
      })
      .catch((err) => {
        if (cancelled) return;
        setLightspeedStockWrites({
          enabled: false,
          reason: `Could not check whether Lightspeed stock writes are on (${err?.response?.data?.error || err?.message || 'request failed'})`
        });
      });
    return () => { cancelled = true; };
  }, [canEditLightspeedStock]);

  // Vendor references on the products grid (planner supplier SKU map).
  // Refs load for EVERY product on the grid. Anyone with the grid can edit:
  // supplier context comes from the loaded PO or the existing mapping, and
  // when neither exists the row shows a supplier picker. The SERVER resolves
  // the final supplier id against the planner directory by id then name —
  // PO cards carry source-system ids (a Lightspeed consignment id is not a
  // planner supplier id), and a ref keyed to a foreign id never matches a
  // bill (found live 2026-08-24: WN02-FP028 saved under "7273").
  const [vendorRefsBySku, setVendorRefsBySku] = useState({});
  const [gridPoSupplier, setGridPoSupplier] = useState(null);

  // PO putaway audit: same-brand tubs among the loaded PO's STORED locations.
  // The save-time brand guard only fires on a location CHANGE, so two PO items
  // already sharing a tub would be binned together without a word. Advisory:
  // a banner above the grid plus an amber chip on each affected card, re-run
  // whenever an audited product's saved location changes so flags clear
  // themselves as staff split the tubs.
  const [poAuditSkus, setPoAuditSkus] = useState([]); // UPPERCASE SKUs of the loaded PO
  const [poTubClashes, setPoTubClashes] = useState(null); // { bySku, lines } from mapTubClashSlots
  const poTubAuditTokenRef = useRef(0);
  // The audit is a round trip, so its warning used to drop in seconds after
  // the grid had settled and shove the cards down (Rhys 2026-09-04). A
  // placeholder holds the slot from the moment a PO is loaded, and the first
  // run skips the debounce.
  const [poTubAuditPending, setPoTubAuditPending] = useState(false);
  const poTubAuditRanRef = useRef(false);
  const vendorRefKnownSupplierFor = (sku) => {
    if (gridPoSupplier?.supplierId || gridPoSupplier?.supplierName) return gridPoSupplier;
    const mapped = vendorRefsBySku[sku];
    if (mapped?.supplierId) {
      return { supplierId: mapped.supplierId, supplierName: mapped.supplierName || null };
    }
    return null;
  };
  const vendorRefSuppliersCacheRef = useRef({});
  const loadVendorRefSuppliers = useCallback(async (product) => {
    const store = product.storeDisplayName || product.store || '';
    const cached = vendorRefSuppliersCacheRef.current[store];
    if (cached) return cached;
    const res = await axios.get(
      `${API_URL}/api/warehouse/vendor-refs/suppliers`,
      { params: { store }, withCredentials: true }
    );
    const suppliers = res.data?.suppliers || [];
    vendorRefSuppliersCacheRef.current[store] = suppliers;
    return suppliers;
  }, []);
  const handleSaveVendorRef = async (product, _productKey, supplierSku, pickedSupplier = null) => {
    const supplier = pickedSupplier || vendorRefKnownSupplierFor(product.sku);
    if (!supplier?.supplierId && !supplier?.supplierName) {
      throw new Error('Pick a supplier first');
    }
    const res = await axios.post(`${API_URL}/api/warehouse/vendor-refs/set`, {
      sku: product.sku,
      store: product.storeDisplayName || product.store,
      supplierId: supplier.supplierId || undefined,
      supplierName: supplier.supplierName || undefined,
      supplierSku,
      title: product.title
    }, { withCredentials: true });
    setVendorRefsBySku((prev) => ({
      ...prev,
      [product.sku]: {
        supplierSku: res.data?.supplierSku || supplierSku,
        supplierId: res.data?.supplierId || supplier.supplierId || null,
        supplierName: res.data?.supplierName || supplier.supplierName || null,
        source: 'grid_scan'
      }
    }));
  };

  /** Phaser card editor: the live Lightspeed level (= Shopify available) before editing. */
  const handleLoadProductStock = useCallback(async (product) => {
    const res = await axios.get(`${API_URL}/api/products/lightspeed-stock`, {
      params: { sku: product.sku },
      withCredentials: true
    });
    return res.data;
  }, []);

  // The card's strip reads stockCommitted ("N allocated") and stockAvailable
  // ("N to sell", preferred over on-hand minus allocated). Both came from the
  // overnight cache and were never touched here, so they sat on stale values
  // after every edit (Rhys 2026-10-12). utils/productStockEdit.js moves all
  // three together per system.
  const handleSaveProductStock = useCallback(async (product, productKey, targetQuantity, { expectedQuantity } = {}) => {
    const patchRow = (patch) => setProducts((prev) => prev.map((row) => (
      `${row.storeDisplayName}-${row.id}-${row.variantId}` === productKey ? applyStockEditToRow(row, patch) : row
    )));
    const toast = (message) => {
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 3000);
    };

    if (stockSystemFor(product) === 'lightspeed') {
      // Phaser: Lightspeed holds what Shopify shows as AVAILABLE (open-order
      // units already deducted). expectedQuantity is the level the editor
      // loaded; the server answers 409 stale when Lightspeed moved since.
      const res = await axios.post(`${API_URL}/api/products/lightspeed-stock`, {
        sku: product.sku,
        targetQuantity,
        expectedQuantity
      }, { withCredentials: true });
      const outcome = { after: res.data?.after, target: res.data?.target ?? targetQuantity, noop: res.data?.noop === true };
      const level = lightspeedLevelAfterWrite(outcome);
      patchRow({ system: 'lightspeed', after: level });
      if (outcome.noop) {
        toast(`Lightspeed stock for ${product.sku} is already ${level}`);
      } else if (lightspeedReadBackLags(outcome)) {
        // The adjustment posted; Lightspeed's read-back just has not caught up.
        toast(`Lightspeed stock set: ${product.sku} → ${level} (Lightspeed confirms in a few seconds)`);
      } else {
        toast(`Lightspeed stock updated: ${product.sku} → ${level}`);
      }
      return res.data;
    }

    const res = await axios.post(`${API_URL}/api/products/qoblex-stock`, {
      sku: product.sku,
      targetQuantity
    }, { withCredentials: true });
    const after = Number(res.data?.after);
    const nextStock = Number.isFinite(after) ? after : targetQuantity;
    // Qoblex's allocation comes back with the write; to sell = total − allocated.
    patchRow({ system: 'qoblex', after: nextStock, allocated: res.data?.allocated });
    toast(
      res.data?.noop
        ? `Qoblex stock for ${product.sku} is already ${nextStock}`
        : `Qoblex Total Stock updated: ${product.sku} → ${nextStock}`
    );
    return res.data;
  }, []);

  // Food expiry (openspec food-expiry-tracking). The flag lives app-side
  // (expiry_tracked_skus); rows carry expiryTracked / nextExpiry /
  // expiryUntrackedQty from the search routes. Rows are matched by store +
  // SKU so every copy of the product in the grid updates together.
  const canTrackExpiry = hasModulePermission('warehouse');
  const [expiryPanel, setExpiryPanel] = useState(null); // { store, sku, title }
  const patchExpiryRows = useCallback((targets, patchFor) => {
    const wanted = new Map(targets.map((t) => [`${t.store}|${String(t.sku || '').trim().toUpperCase()}`, t]));
    setProducts((prev) => prev.map((row) => {
      const target = wanted.get(`${row.store}|${String(row.sku || '').trim().toUpperCase()}`);
      return target ? { ...row, ...patchFor(row, target) } : row;
    }));
  }, []);
  const refreshRowExpiry = useCallback(async (store, sku) => {
    try {
      const entry = await expiryApi.getBatches(store, sku);
      patchExpiryRows([{ store, sku }], () => rowExpiryFromEntry(entry));
    } catch (err) {
      console.warn('[EXPIRY] Could not load batches for', sku, err?.message);
    }
  }, [patchExpiryRows]);
  const handleToggleExpiryTracked = useCallback(async (product, _productKey, next, linkedStores = []) => {
    if (!product?.store || !product?.sku) return;
    const before = {
      expiryTracked: Boolean(product.expiryTracked),
      nextExpiry: product.nextExpiry ?? null,
      expiryUntrackedQty: product.expiryUntrackedQty ?? 0
    };
    // Optimistic: the toggle flips now; turning tracking off clears the badge.
    patchExpiryRows([product], () => (next
      ? { expiryTracked: true }
      : { expiryTracked: false, nextExpiry: null, expiryUntrackedQty: 0 }));
    try {
      // A SKU listed in several stores is flagged in all of them, so receiving
      // asks for dates whichever store's PO (Lightspeed or Qoblex) brings it in.
      const stores = [...new Set([product.store, ...(Array.isArray(linkedStores) ? linkedStores : [])])];
      if (stores.length > 1) {
        await expiryApi.bulkSetTracked(stores.map((store) => ({ store, sku: product.sku })), next);
        patchExpiryRows(stores.slice(1).map((store) => ({ store, sku: product.sku })), () => (next
          ? { expiryTracked: true }
          : { expiryTracked: false, nextExpiry: null, expiryUntrackedQty: 0 }));
      } else {
        await expiryApi.setTracked(product.store, product.sku, next);
      }
      // Batches recorded before the flag was last turned off come back.
      if (next) refreshRowExpiry(product.store, product.sku);
    } catch (err) {
      patchExpiryRows([product], () => before);
      setError(err.response?.data?.error || 'Failed to update expiry tracking');
    }
  }, [patchExpiryRows, refreshRowExpiry]);
  const handleOpenExpiryPanel = useCallback((product) => {
    if (!product?.store || !product?.sku) return;
    setExpiryPanel({ store: product.store, sku: product.sku, title: product.title || product.sku });
  }, []);
  const handleExpiryPanelChanged = useCallback((entry) => {
    if (!entry || !expiryPanel) return;
    patchExpiryRows([expiryPanel], () => rowExpiryFromEntry(entry));
  }, [expiryPanel, patchExpiryRows]);

  // Product links (docs/memory/product-links.md): Shopify's Related /
  // Complementary product lists, mirrored in Firestore. Same shape as kit
  // splits below: GET /api/product-links once per grid session, indexed by
  // store + SKU. Declared first because kit saves also change links.
  const canEditProductLinks = hasModulePermission('warehouse');
  const canReadProductLinks = canEditProductLinks || hasModulePermission('picking');
  const [productLinks, setProductLinks] = useState(null); // Record[] | null until loaded
  const [productLinkEditor, setProductLinkEditor] = useState(null); // { product, productLink }
  const productLinksLoadedAtRef = useRef(0);
  const loadProductLinks = useCallback(async () => {
    productLinksLoadedAtRef.current = Date.now();
    try {
      const links = await fetchProductLinks();
      setProductLinks(Array.isArray(links) ? links : []);
    } catch (err) {
      console.warn('[PRODUCT-LINKS] Could not load product links:', err?.response?.data?.error || err?.message);
      setProductLinks((prev) => prev || []);
    }
  }, []);
  const productLinkIndex = useMemo(() => buildProductLinkIndex(productLinks || []), [productLinks]);
  // A save returns every mirror doc it changed (`affected`) and the ids that
  // became empty (`removed`); kit saves return the same under `links`.
  const mergeProductLinkChanges = useCallback((changes) => {
    if (!changes || (!Array.isArray(changes.affected) && !Array.isArray(changes.removed))) return;
    setProductLinks((prev) => mergeAffected(prev || [], changes.affected || [], changes.removed || []));
  }, []);
  const handleEditProductLinks = useCallback((product, productLink) => {
    setProductLinkEditor({ product, productLink: productLink || null });
  }, []);
  const handleProductLinksSaved = useCallback((result) => {
    setProductLinkEditor(null);
    mergeProductLinkChanges(result);
    loadProductLinks();
    const warnings = (result?.warnings || []).filter(Boolean);
    // Warnings are soft (e.g. a full list made a related link one-way), so
    // they ride on the success line rather than the red error line.
    setSuccessMessage(warnings.length ? `Related products saved. ${warnings.join(' ')}` : 'Related products saved');
    setTimeout(() => setSuccessMessage(''), warnings.length ? 8000 : 3000);
  }, [mergeProductLinkChanges, loadProductLinks]);

  // Kit splits (docs/memory/kit-splits.md): app-owned like Tracks expiry.
  // GET /api/kit-splits once per grid session (see the visibleProducts effect),
  // indexed by store + SKU so every card finds its kit without a request.
  // Editing = Products module; the read is open to warehouse or picking.
  const canEditKitSplit = hasModulePermission('warehouse');
  const canReadKitSplits = canEditKitSplit || hasModulePermission('picking');
  const [kitSplits, setKitSplits] = useState(null); // Kit[] | null until loaded
  const [kitSplitCapabilities, setKitSplitCapabilities] = useState(null);
  const [kitSplitEditor, setKitSplitEditor] = useState(null); // { product, kit }
  const kitSplitsLoadedAtRef = useRef(0);
  const loadKitSplits = useCallback(async () => {
    kitSplitsLoadedAtRef.current = Date.now();
    try {
      const { kits, capabilities } = await fetchKitSplits();
      setKitSplits(kits);
      setKitSplitCapabilities(capabilities);
    } catch (err) {
      console.warn('[KIT-SPLITS] Could not load kit splits:', err?.response?.data?.error || err?.message);
      setKitSplits((prev) => prev || []);
    }
  }, []);
  const kitSplitIndex = useMemo(() => buildKitSplitIndex(kitSplits || []), [kitSplits]);
  const kitSplitIndexRef = useRef(kitSplitIndex);
  kitSplitIndexRef.current = kitSplitIndex;
  const loadKitSplitsRef = useRef(loadKitSplits);
  loadKitSplitsRef.current = loadKitSplits;
  // options (2026-10-30): { mode: 'add-to-kit' } opens the "which product is
  // the kit?" picker with this product as the part; { kind: 'mirror' } starts
  // a twin link with the other store. Plain calls edit `kit` / start a new kit.
  const handleEditKitSplit = useCallback((product, kit, options = null) => {
    setKitSplitEditor({ product, kit: kit || null, options: options || null });
  }, []);
  // The add-to-kit picker asks whether the chosen kit product already has a kit.
  const findKitForParent = useCallback((store, sku) => kitLinkFor(kitSplitIndexRef.current, { store, sku })?.asParent || null, []);
  const handleKitSplitSaved = useCallback((result) => {
    setKitSplitEditor(null);
    const saved = result?.kit || null;
    const task = result?.task || null;
    // The saved kit lands at once; the refetch then brings every other kit's
    // latest state (a part can sit in several kits).
    if (saved) {
      const id = kitIdFor(saved);
      setKitSplits((prev) => [...(prev || []).filter((k) => kitIdFor(k) !== id), saved]);
    }
    loadKitSplits();
    // A kit and its parts are related automatically: merge what the kit save
    // changed now, then refetch (best effort server-side, so refetch anyway).
    mergeProductLinkChanges(result?.links);
    if (canReadProductLinks) loadProductLinks();
    const linkWarnings = (result?.links?.warnings || []).filter(Boolean);
    const kitSku = saved?.parentSku || task?.kit?.parentSku || 'kit';
    const jobText = task
      ? (task.text || `Open ${task.kit?.suggestedQty ?? ''} × ${kitSku}`.replace(/\s+/g, ' '))
      : '';
    // Kit stock engine (docs/memory/kit-stock-engine.md): an assembled kit with
    // stock first gets one "open them all" job; assembled / broken kits that
    // run straight away say the engine is on.
    let baseText;
    if (task?.kit?.changeover) {
      const all = task.kit.suggestedQty ?? task.kit.parentStock;
      baseText = `Changeover job raised: open all ${all ?? ''} × ${kitSku}`.replace(/\s+/g, ' ');
    } else if (task) {
      baseText = `Job raised for the supervisor: ${jobText}`;
    } else if (saved && kitKindInfo(saved).value === 'mirror') {
      // Twin link: the save-time run copies the root onto the twin; a
      // requested "combine first" reports its own outcome.
      const merge = result?.merge;
      const mergeText = merge?.ok ? ` Combined first: root ${merge.from} → ${merge.to}.`
        : merge && merge.ok === false ? ` The counts were NOT combined (${merge.error || 'write failed'}); set the root by hand.` : '';
      baseText = `Twin link saved: ${kitSku} copies its stock from the other store.${mergeText}`;
    } else if (result?.engine) {
      baseText = `Kit saved: ${kitKindInfo(saved).label.toLowerCase()} (stock engine running)`;
    } else {
      baseText = `Kit split saved${saved?.parentSku ? `: ${saved.parentSku}` : ''}`;
    }
    setSuccessMessage(linkWarnings.length ? `${baseText}. ${linkWarnings.join(' ')}` : baseText);
    setTimeout(() => setSuccessMessage(''), task || linkWarnings.length ? 6000 : 3000);
  }, [loadKitSplits, mergeProductLinkChanges, canReadProductLinks, loadProductLinks]);
  // KitSplitModal calls onDeleted(id); a second `result` argument (the DELETE
  // response, which carries `links`) is optional. The refetch covers it.
  const handleKitSplitDeleted = useCallback((id, result) => {
    setKitSplitEditor(null);
    setKitSplits((prev) => (prev || []).filter((k) => kitIdFor(k) !== id));
    loadKitSplits();
    mergeProductLinkChanges(result?.links);
    if (canReadProductLinks) loadProductLinks();
    setSuccessMessage('Kit split removed');
    setTimeout(() => setSuccessMessage(''), 3000);
  }, [loadKitSplits, mergeProductLinkChanges, canReadProductLinks, loadProductLinks]);

  // Pre-arrival PO splits — dedicated permission, off by default (Settings → Users & access)
  const canSplitPos = hasTaskPermission('splitPurchaseOrders') || normalizeEmail(currentUser?.email) === 'rhys100@gmail.com';

  const canOpenAdminApi = useMemo(() => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDev) return true;
    if (permissionSettings?.isAdmin) return true;
    const email = normalizeEmail(currentUser?.email);
    return email === 'rhys100@gmail.com' || email === 'rhys@phaserfpv.com.au';
  }, [currentUser, permissionSettings]);

  const canManageSettings = useMemo(() => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDev) return true;
    return normalizeEmail(currentUser?.email) === 'rhys100@gmail.com';
  }, [currentUser]);

  const canViewSecurityIncidents = useMemo(() => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isDev) return true;
    if (permissionSettings?.isAdmin) return true;
    if (!permissionSettingsLoaded) return false;
    return permissionSettings?.modulePermissions?.security === true;
  }, [permissionSettings, permissionSettingsLoaded]);

  const canShowSettings = canManageSettings || hasModulePermission('logs');

  const showPickingInternalWarnings = useMemo(
    () => canViewInternalWarnings(currentUser),
    [currentUser]
  );

  const enforceModulePermission = useCallback((moduleKey) => {
    if (hasModulePermission(moduleKey)) return true;

    setError(`You don't have permission to access ${MODULE_PERMISSION_LABELS[moduleKey] || 'this module'}`);
    setJobsHubOpenToTab(null);
    setBackdropLeaving(null);
    setModuleView('home');
    window.history.pushState({}, '', productsHomePath());
    return false;
  }, [hasModulePermission]);

  const [products, setProducts] = useState(() => {
    // Don't load from localStorage if URL has SKU params (URL loading will handle it)
    const path = window.location.pathname.replace(/\/+$/, '');
    if (isPlannerPath(path)) {
      return [];
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('sku')) {
      console.log('[State Init] URL has SKUs, starting with empty array');
      return [];
    }
    // Load from localStorage if no URL params
    const saved = localStorage.getItem('warehouseProducts');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('[State Init] Loaded from localStorage:', parsed.length, 'products');
        // Heal {value} metafield objects persisted before the 5.71.1 cache fix —
        // without this, a polluted localStorage keeps crashing the grid (React #31).
        return Array.isArray(parsed) ? parsed.map(normalizeProductLocationFields) : [];
      } catch (e) {
        console.error('[State Init] Failed to parse:', e);
        return [];
      }
    }
    console.log('[State Init] No saved data, starting empty');
    return [];
  });
  const [_recentlyEditedProducts, setRecentlyEditedProducts] = useState([]);

  // Fetch vendor refs for any grid product we haven't looked up yet (search-
  // loaded cards included). Lives BELOW the products declaration — the dep
  // array reads `products` during render, so placing it earlier is a TDZ
  // crash (learned 2026-08-24).
  useEffect(() => {
    const pending = products
      .filter((p) => p.sku && p.sku !== 'N/A' && vendorRefsBySku[p.sku] === undefined)
      .slice(0, 100);
    if (!pending.length) return;
    axios.post(`${API_URL}/api/warehouse/vendor-refs/lookup`, {
      items: pending.map((p) => ({ sku: p.sku, store: p.storeDisplayName || p.store || '' }))
    }, { withCredentials: true })
      .then((res) => setVendorRefsBySku((prev) => ({ ...prev, ...(res.data?.refs || {}) })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // Load recently edited products from localStorage on mount
  useEffect(() => {
    const startTime = performance.now();
    console.log('[Performance] App component mounting...');

    const saved = localStorage.getItem('recentlyEditedProducts');
    if (saved) {
      try {
        const items = JSON.parse(saved);
        // Only keep items from within the last 7 days (not 30+ days)
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        const validItems = items.filter(item => {
          if (!item.updatedAt) return false;
          return new Date(item.updatedAt).getTime() > sevenDaysAgo;
        });
        setRecentlyEditedProducts(validItems);
        console.log(`[LocalStorage] Loaded ${validItems.length} recently edited products (filtered from ${items.length})`);
      } catch (err) {
        console.error('[LocalStorage] Failed to load recently edited products:', err);
      }
    }

    // Backend version (`versions.backend`) is fetched by the dedicated /api/health effect below,
    // so we no longer make a duplicate health call here.

    const endTime = performance.now();
    console.log(`[Performance] App component mounted in ${(endTime - startTime).toFixed(2)}ms`);
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [loadingStage, setLoadingStage] = useState(''); // New state for loading stage name
  const [luckyStatus, setLuckyStatus] = useState(null); // Status for I'm Feeling Lucky (type, message, total)
  const [_backendVersion, setBackendVersion] = useState('loading...'); // Backend version from API
  const [showCacheModal, setShowCacheModal] = useState(false);

  // Dark mode from context (state lives in AppWithAuth so AppProvider can use theme)
  const { darkMode, setDarkMode } = useDarkMode();

  const [isMobile, setIsMobile] = useState(() => isMobileViewport());
  const [isPickingTouch, setIsPickingTouch] = useState(() => isPickingTouchViewport());
  const [isTabletPicking, setIsTabletPicking] = useState(() => isTabletViewport());
  useEffect(() => {
    const onResize = () => {
      const { innerWidth, innerHeight } = window;
      setIsMobile(isMobileViewport(innerWidth));
      setIsPickingTouch(isPickingTouchViewport(innerWidth, innerHeight));
      setIsTabletPicking(isTabletViewport(innerWidth, innerHeight));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const showMobileNoPickingAccess = isPickingTouch && permissionSettings !== null && !hasModulePermission('picking');

  const [error, setError] = useState('');
  const [locationBarcode, setLocationBarcode] = useState('');
  // Shelf-only code typed in bulk update (T-05-A): pick the slot before writing.
  const [bulkShelfPick, setBulkShelfPick] = useState(null);
  const [bulkWeight, setBulkWeight] = useState('');
  const [bulkLength, setBulkLength] = useState('');
  const [bulkWidth, setBulkWidth] = useState('');
  const [bulkHeight, setBulkHeight] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const searchInputRef = useRef(null);
  const focusSearchInput = useCallback(() => {
    const input = document.getElementById(WAREHOUSE_SKU_SEARCH_ID);
    if (input && typeof input.focus === 'function') {
      input.focus();
      searchInputRef.current = input;
    }
  }, []);

  // Request cancellation: Track active search requests to cancel when new search starts
  const activeSearchController = useRef(null);
  const searchInFlightCountRef = useRef(0);
  const beginSearchLoading = useCallback(() => {
    searchInFlightCountRef.current += 1;
    if (searchInFlightCountRef.current === 1) {
      setIsLoading(true);
    }
  }, []);
  const endSearchLoading = useCallback(() => {
    searchInFlightCountRef.current = Math.max(0, searchInFlightCountRef.current - 1);
    if (searchInFlightCountRef.current === 0) {
      setIsLoading(false);
    }
  }, []);

  // PO Card Expansion State
  const [expandedPOs, setExpandedPOs] = useState(new Set());
  // Tracking links (2026-08-22): which PO's paste-a-link input is open.
  // Component-level because cards render inside a map. The input itself is
  // UNCONTROLLED — holding its text in state here re-rendered the entire app
  // (every card) per keystroke; handlers read the value off the event instead.
  const [trackingInputPoId, setTrackingInputPoId] = useState(null);

  const saveTrackingLink = async (po, rawUrl) => {
    const url = String(rawUrl || '').trim();
    if (!url) { setTrackingInputPoId(null); return; }
    try {
      const resp = await axios.post(`${API_URL}/api/purchase-orders/${po.id}/tracking`, { url });
      applyPoMetadataEdit(po.id, { trackingLinks: resp.data.trackingLinks }, { storeKey: po.storeKey, reason: 'tracking link added' });
      setTrackingInputPoId(null);
      setSuccessMessage('Tracking link added');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Failed to add tracking link:', err);
      setError(err.response?.data?.error || 'Failed to add tracking link');
    }
  };

  // "Confirmed for today" tick (CEO round 2, 2026-10-23): marks a PO due today
  // (or overdue) as definitely arriving today. Optimistic, reverted on error.
  // The server stamps the Sydney day; a tick from an earlier day is ignored.
  const setPoArrivalConfirmed = async (po, confirmed) => {
    const previous = po.localMetadata?.arrivalConfirmed || null;
    const applyConfirmation = (value) => applyPoMetadataEdit(
      po.id, { arrivalConfirmed: value }, { storeKey: po.storeKey, reason: 'arrival confirm' }
    );
    applyConfirmation(confirmed
      ? { forDay: getTodayDateString(), by: currentUser?.email || null, byName: null, at: new Date().toISOString() }
      : null);
    try {
      const resp = await axios.post(`${API_URL}/api/purchase-orders/${po.id}/arrival-confirm`, { confirmed });
      applyConfirmation(resp.data?.arrivalConfirmed || null);
    } catch (err) {
      console.error('Failed to update arrival confirmation:', err);
      applyConfirmation(previous);
      setError(err.response?.data?.error || 'Failed to update arrival confirmation');
    }
  };

  const removeTrackingLinkFromPo = async (po, url) => {
    try {
      const resp = await axios.delete(`${API_URL}/api/purchase-orders/${po.id}/tracking`, { data: { url } });
      applyPoMetadataEdit(po.id, { trackingLinks: resp.data.trackingLinks }, { storeKey: po.storeKey, reason: 'tracking link removed' });
    } catch (err) {
      console.error('Failed to remove tracking link:', err);
      setError(err.response?.data?.error || 'Failed to remove tracking link');
    }
  };
  const togglePOExpansion = (poId) => {
    setExpandedPOs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(poId)) {
        newSet.delete(poId);
      } else {
        newSet.add(poId);
      }
      return newSet;
    });
  };

  const _activeRefreshController = useRef(null);

  // Background refresh debouncing: Wait 200ms after last product added before refreshing
  const refreshDebounceTimer = useRef(null);
  const pendingRefreshSKUs = useRef(new Set());
  const refreshPollIntervalRef = useRef(null);
  const refreshPickerOpenedAtRef = useRef(0);
  const cacheRefreshStartedAtRef = useRef(0);
  const poCardRefs = useRef({});
  const urlInitStartedRef = useRef(false);
  const initialBgRefreshScheduledRef = useRef(false);
  const executeBackgroundRefreshRef = useRef(null);

  const [editableData, setEditableData] = useState(() => {
    const saved = localStorage.getItem('warehouseEditableData');
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedProducts, setSelectedProducts] = useState(() => {
    const path = window.location.pathname.replace(/\/+$/, '');
    if (isPlannerPath(path)) {
      return {};
    }
    // Don't load selected state from localStorage if URL has SKU params
    // (URL loading is for manual searches, not PO loading)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('sku')) {
      console.log('[State Init] URL has SKUs, clearing selected products');
      return {};
    }
    // Only restore selected state if no URL params (localStorage persistence)
    const saved = localStorage.getItem('warehouseSelectedProducts');
    return saved ? JSON.parse(saved) : {};
  });

  // Label quantities for each product (default to stock quantity)
  const [labelQuantities, setLabelQuantities] = useState({});

  // Product keys a PO load filled with a high label count (100 tubes we sell
  // individually). They flash in the grid until someone edits the count or
  // taps the flag (Rhys 2026-09-04).
  const [labelQuantityReviewKeys, setLabelQuantityReviewKeys] = useState({});
  const clearLabelQuantityReview = useCallback((productKey) => {
    setLabelQuantityReviewKeys(prev => {
      if (!prev[productKey]) return prev;
      const next = { ...prev };
      delete next[productKey];
      return next;
    });
  }, []);

  const [preorderNotification, setPreorderNotification] = useState(null);
  const [showLogViewer, setShowLogViewer] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Unified Purchase Orders Manager
  // 'home' | 'jobs' | 'po' | 'preorders' | 'picking' | 'ra' | 'tags' | 'new-products' | 'map'
  // Over Tailscale the routing effect below waits for permissions before it
  // decides anything (2026-09-02), so the FIRST paint has to be safe on its own:
  // start on the delivery board rather than the Products grid, which would
  // otherwise flash — and fire its product calls — at a restricted driver.
  // CEO seed (2026-10-23): when this browser's account held the `ceo` grant
  // last time, a load of `/` (or `/ceo`) paints CEO first. handleRoute holds
  // the root decision until permissions land, and bounces home if revoked.
  // Desktop only (2026-10-23): a picking-touch viewport is never seeded.
  const ceoLandingSeededRef = useRef(false);
  const [moduleView, setModuleView] = useState(() => {
    if (isTailnetHost(window.location.hostname)) return 'local-deliveries';
    if (shouldSeedCeoLanding({
      rawPath: window.location.pathname,
      search: window.location.search,
      storage: getCeoLandingStorage(),
      pickingTouch: isPickingTouch,
    })) {
      ceoLandingSeededRef.current = true;
      return 'ceo';
    }
    return 'home';
  });
  // True while the first load was the bare root `/`: ensureCanonicalUrl
  // rewrites it to `/products` on the first handleRoute run, which may be
  // before permissions land, so the root landing decision is remembered here
  // and made once they do (2026-10-23).
  const initialRootVisitRef = useRef(normalizePathname(window.location.pathname) === '/');
  const [backdropLeaving, setBackdropLeaving] = useState(null); // keep backdrop colour briefly when leaving PO for another module (not when going home)
  const showPOViewer = moduleView === 'po';
  const showPOPlanningModal = moduleView === 'po-planning';
  const showPriceMoleModal = moduleView === 'pricemole';
  const showCoffeeStocktakeModal = moduleView === 'coffee-stocktake';
  const showPreordersModal = moduleView === 'preorders';
  const showPickingModal = moduleView === 'picking';
  const showStocktake = moduleView === 'stocktake';
  // Picking and Products & Trays hide the module header, and a picking-touch
  // home is only a stop on the way to picking, so the header badge counts are
  // never on screen there. Skipping them leaves a tablet's cold start to the
  // picking queue instead of seven badge calls racing it (2026-11-03). The
  // effect after fetchReturnsCounts catches up as soon as the header shows.
  const headerBadgesHidden = showPickingModal || showStocktake || (
    isPickingTouch && moduleView === 'home' && (permissionSettings === null || hasModulePermission('picking'))
  );
  const headerBadgesHiddenRef = useRef(headerBadgesHidden);
  headerBadgesHiddenRef.current = headerBadgesHidden;
  // `/stocktake?location=W-01-D-01` — the tray the view opens on arrival.
  const [stocktakeLocation, setStocktakeLocation] = useState(null);
  const showPickingSupervisor = moduleView === 'picking-supervisor' || moduleView === 'packing-supervisor';
  const showPickFromStore = moduleView === 'pick-from-store';
  const showShip = moduleView === 'ship';
  // Ship deep links: `/ship[/<tab>[/<orderKey>[/packaging]]]` and
  // `/ship/settings[/<section>]`. App parses the URL (first load + popstate)
  // and hands the route down; ShipView reports its own screen changes back
  // through onRouteChange, which pushes (or replaces) history here.
  const [shipRoute, setShipRoute] = useState(() => (
    parseShipRoute(
      typeof window !== 'undefined' ? window.location.pathname : '',
      typeof window !== 'undefined' ? window.location.search : ''
    )
  ));
  // Pickup kiosk pane: `/pickup-kiosk` (Pickups) or `/pickup-kiosk/lockers`.
  const [pickupKioskPane, setPickupKioskPane] = useState(() => (
    parsePickupKioskPane(typeof window !== 'undefined' ? window.location.pathname : '')
  ));
  const showLocalDeliveries = moduleView === 'local-deliveries';
  const showProductTagsModal = moduleView === 'tags';
  const showJobsHub = moduleView === 'jobs' || moduleView === 'ra';
  const showNewProductsModal = moduleView === 'new-products';
  const showBackupRestore = moduleView === 'backup-restore';
  const showExpiry = moduleView === 'expiry';
  const showCeo = moduleView === 'ceo';
  const showMapView = moduleView === 'map';
  const appHeaderOffsetPx = useAppHeaderOffset(!isMobile && !showPickingModal && !showStocktake);
  const [plannerChannel, setPlannerChannel] = useState('all');
  const [pricemoleChannel, setPricemoleChannel] = useState('centralcoastcoffee');
  // `/pricemole/{channel}?sku=…` — the deep link the Shopify admin PriceMole
  // block uses to open the exact product it was rendered for.
  const [pricemoleFocusSku, setPricemoleFocusSku] = useState(null);
  // Keep modules mounted after first open to prevent flash/glitch on re-visit;
  // CSS visibility toggles hide them instead of unmounting (preserves cached data + images).
  const everOpenedJobsRef = useRef(false);
  const everOpenedPreordersRef = useRef(false);
  const everOpenedPOPlanningRef = useRef(false);
  const everOpenedPriceMoleRef = useRef(false);
  const everOpenedPickingRef = useRef(false);
  const everOpenedNewProductsRef = useRef(false);
  if (showJobsHub) everOpenedJobsRef.current = true;
  if (showPreordersModal) everOpenedPreordersRef.current = true;
  if (showPOPlanningModal) everOpenedPOPlanningRef.current = true;
  if (showPriceMoleModal) everOpenedPriceMoleRef.current = true;
  if (showPickingModal) everOpenedPickingRef.current = true;
  if (showNewProductsModal) everOpenedNewProductsRef.current = true;
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMyAccessModal, setShowMyAccessModal] = useState(false);
  const [showAdminApiModal, setShowAdminApiModal] = useState(false);
  const [showSecurityIncidentsModal, setShowSecurityIncidentsModal] = useState(false);
  const [securityUnreadCount, setSecurityUnreadCount] = useState(0);
  const [jobsHubOpenToTab, setJobsHubOpenToTab] = useState(null); // string tab key e.g. 'board' | 'printer'
  const [jobsHubActiveTab, setJobsHubActiveTab] = useState('board'); // current tab key in Jobs Hub
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [isLoadingPOs, setIsLoadingPOs] = useState(false);
  const [isBackgroundRefreshingPOs, setIsBackgroundRefreshingPOs] = useState(false);
  const [poActiveFetchKey, setPoActiveFetchKey] = useState('');
  const [poFilter, setPoFilter] = useState('');
  const [unmatchedArrivalsRefreshKey, setUnmatchedArrivalsRefreshKey] = useState(0);
  const [unmatchedLogOpen, setUnmatchedLogOpen] = useState(false);
  const [poFilterQuery, setPoFilterQuery] = useState('');
  const [poStoreFilter, setPoStoreFilter] = useState('all'); // New State
  // Default tab is ARRIVED: the person opening this module on the warehouse
  // tablet is standing next to boxes that just landed, and taps the order to
  // load its products for put-away (Rhys 2026-08-29).
  const [poStatusTab, setPoStatusTab] = useState('arrived'); // draft, waiting_arrival, arrived, website_team, archived
  const [poPage, setPoPage] = useState(1);
  const [poMeta, setPoMeta] = useState({ page: 1, page_size: 50, total: 0, total_pages: 1, has_next_page: false, has_prev_page: false });
  const [preorderFocusSku, setPreorderFocusSku] = useState(null);
  const [preordersNavigationKey, setPreordersNavigationKey] = useState(0);
  const [preorderSourcePoId, setPreorderSourcePoId] = useState(null); // when opening from "part of a preorder", sync this PO's ETA first
  const [poNavigateTarget, setPoNavigateTarget] = useState(null); // { poId, status } when opening PO view from Preorders
  const [preorderSkusSet, setPreorderSkusSet] = useState(new Set());
  const [preorderUnreadCount, setPreorderUnreadCount] = useState(0);
  const [jobsUnreadCount, setJobsUnreadCount] = useState(0);
  const [jobsUnreadByHub, setJobsUnreadByHub] = useState({ jobs: 0, ra: 0 });
  const poFetchAbortControllerRef = useRef(null);
  const poFetchRequestIdRef = useRef(0);
  const poBackgroundFetchRequestIdRef = useRef(0);
  const poTabPrefetchRef = useRef(new Map());
  const poResponseCacheRef = useRef(new Map());
  const poLastFetchedAtRef = useRef(0);
  const poOptimisticMetadataRef = useRef(new Map());
  const preorderSkuCacheRef = useRef({
    fetchedAt: 0,
    promise: null,
    skus: new Set()
  });
  const PO_CLIENT_CACHE_MAX_AGE_MS = 15 * 60 * 1000;
  const PO_CLIENT_STALE_CACHE_MAX_AGE_MS = 60 * 60 * 1000;
  const PO_CLIENT_CACHE_STORAGE_KEY = 'warehousePurchaseOrderResponseCache:v3';
  const PO_CLIENT_CACHE_LEGACY_KEYS = [
    'warehousePurchaseOrderResponseCache:v1',
    'warehousePurchaseOrderResponseCache:v2'
  ];
  const PO_CLIENT_CACHE_MAX_ENTRIES = 40;
  const PO_AUTO_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
  const PO_VISIBLE_REFRESH_STALE_MS = 15 * 60 * 1000;
  const PO_PREFETCH_COOLDOWN_MS = 5 * 60 * 1000;
  const PO_OPTIMISTIC_STATUS_MAX_AGE_MS = 2 * 60 * 1000;

  // Sync Jobs Hub active tab when opening (so header shows Repairs / 3D correctly)
  useEffect(() => {
    if (showJobsHub && jobsHubOpenToTab != null) {
      setJobsHubActiveTab(jobsHubOpenToTab);
    } else if (showJobsHub) {
      setJobsHubActiveTab(0);
    }
  }, [showJobsHub, jobsHubOpenToTab]);

  useEffect(() => {
    return () => {
      poFetchAbortControllerRef.current?.abort();
    };
  }, []);

  // Clear "leaving" backdrop after a short delay (stops flash when leaving PO for another module)
  useEffect(() => {
    if (!backdropLeaving) return;
    const t = setTimeout(() => setBackdropLeaving(null), 100);
    return () => clearTimeout(t);
  }, [backdropLeaving]);

  // URL-based routing for modules (single setModuleView = no flash)
  useEffect(() => {
    const handleRoute = () => {
      setBackdropLeaving(null);
      // Read before ensureCanonicalUrl, which rewrites `/` to `/products`:
      // the CEO root landing needs to know the bare root was asked for.
      const rawPath = window.location.pathname;
      const path = ensureCanonicalUrl(window.location.pathname, window.location.search);

      // Tailscale (2026-09-02): hold the whole routing decision until we know
      // who this is. Routing first and correcting later would flash the
      // Products grid — and fire its product calls — at a driver who is only
      // allowed the delivery board. Non-tailnet hosts are untouched: they never
      // enter this branch, so office boot behaviour is exactly as it was.
      if (isTailnetHost(window.location.hostname) && !permissionSettingsLoaded) return;

      if (remoteRestricted) {
        setJobsHubOpenToTab(null);
        setModuleView('local-deliveries');
        if (!isLocalDeliveriesPath(path)) {
          window.history.replaceState({}, '', LOCAL_DELIVERIES_PATH);
        }
        return;
      }

      // Tailnet root → delivery board even WITH the Remote grant (Rhys
      // 2026-09-03): out and about on a phone, warehouse.taile5c1c0.ts.net/
      // should open Local Deliveries, not picking. Picking only auto-opens on
      // the office/home hostname. Direct links (/picking, /products, …) over
      // Tailscale are untouched, so the full app is still reachable.
      if (tailnetRootLandsOnDeliveries({ hostname: window.location.hostname, isRootPath: isProductsPath(path) })
        && hasModulePermission('localDeliveries')) {
        setJobsHubOpenToTab(null);
        setModuleView('local-deliveries');
        window.history.replaceState({}, '', LOCAL_DELIVERIES_PATH);
        return;
      }

      // CEO root landing (2026-10-23): `/` opens the CEO page for anyone
      // holding the `ceo` grant, on desktop only (phones and tablets keep
      // landing on Picking; a typed /ceo still opens there). Decided once permissions are known; before
      // that a seeded CEO first paint is held as is (no Products flash), and
      // an unseeded load routes as it always did and is corrected here when
      // they land. `/products` typed explicitly is never redirected.
      // The late correction is one-shot: the first run after permissions
      // land spends it whatever the URL is by then, so a later re-run (a
      // permission refetch) never pulls someone off a /products they chose.
      const initialRootVisit = initialRootVisitRef.current;
      if (permissionSettingsLoaded) initialRootVisitRef.current = false;
      if (isRootLandingVisit({ rawPath, path, initialRootVisit })) {
        if (!permissionSettingsLoaded) {
          if (ceoLandingSeededRef.current) return;
        } else if (rootLandsOnCeo({
          rawPath: '/',
          search: window.location.search,
          // Real grant only: hasModulePermission says yes to everything on
          // localhost, which would hijack every dev load of `/`.
          hasCeo: hasCeoGrant(permissionSettings),
          remoteRestricted,
          tailnetRoot: isTailnetHost(window.location.hostname),
          pickingTouch: isPickingTouch,
        })) {
          setJobsHubOpenToTab(null);
          setModuleView('ceo');
          window.history.replaceState({}, '', CEO_PATH);
          return;
        }
      }

      const denyHome = () => {
        setJobsHubOpenToTab(null);
        // Home renders nothing on a picking-touch viewport, so a denied URL
        // falls back to picking there instead of a blank screen.
        if (isPickingTouch && hasModulePermission('picking')) {
          setModuleView('picking');
          window.history.replaceState({}, '', '/picking');
          return;
        }
        setModuleView('home');
        window.history.replaceState({}, '', productsHomePath());
      };

      // Picking touch (phone + tablet): only the ROOT auto-opens picking —
      // every other URL routes normally, so direct links (/local, the pickup
      // kiosk, supervisor) are never hijacked by the picking redirect and the
      // original URL is never rewritten away.
      if (isPickingTouch && isProductsPath(path)) {
        setJobsHubOpenToTab(null);
        if (!hasModulePermission('picking')) {
          // Stay home; the "Mobile picking only" blocker explains why.
          setModuleView('home');
          return;
        }
        setModuleView('picking');
        window.history.replaceState({}, '', '/picking');
        return;
      }

      if (isProductsPath(path)) {
        setJobsHubOpenToTab(null);
        setModuleView('home');
      } else if (isJobs3dPath(path)) {
        if (!hasModulePermission('jobs')) {
          denyHome();
          return;
        }
        // Respect ?tab= deep links (e.g. /3d?tab=printer) — only force the
        // Kanban tab when the URL doesn't name one.
        const jobsUrlTab = new URLSearchParams(window.location.search).get('tab');
        setJobsHubOpenToTab(jobsUrlTab || 'board');
        setModuleView('jobs');
      } else if (isRepairsPath(path)) {
        if (!hasModulePermission('ra')) {
          denyHome();
          return;
        }
        const repairsUrlTab = new URLSearchParams(window.location.search).get('tab');
        setJobsHubOpenToTab(repairsUrlTab || 'board');
        setModuleView('ra');
      } else if (path === '/tags' || path === '/tags/') {
        if (!hasModulePermission('productTags')) {
          denyHome();
          return;
        }
        setModuleView('tags');
      } else if (isPlannerPath(path)) {
        if (!hasModulePermission('poPlanning')) {
          denyHome();
          return;
        }
        setPlannerChannel(parsePlannerChannelFromPath(path) || 'all');
        setModuleView('po-planning');
      } else if (isPricemolePath(path)) {
        if (!hasModulePermission('priceMole')) {
          denyHome();
          return;
        }
        setPricemoleChannel(parsePricemoleChannelFromPath(path));
        setPricemoleFocusSku(new URLSearchParams(window.location.search).get('sku') || null);
        setModuleView('pricemole');
      } else if (path === '/coffee-stocktake' || path === '/coffee-stocktake/') {
        if (!hasModulePermission('coffeeStocktake')) {
          denyHome();
          return;
        }
        setModuleView('coffee-stocktake');
      } else if (path === '/preorders' || path === '/preorders/') {
        if (!hasModulePermission('preorders')) {
          denyHome();
          return;
        }
        setModuleView('preorders');
      } else if (path === '/picking/supervisor/packing' || path === '/picking/supervisor/packing/') {
        if (!hasModulePermission('picking')) {
          denyHome();
          return;
        }
        setModuleView('picking-supervisor');
        window.history.replaceState({}, '', '/picking/supervisor?mode=forecast');
      } else if (path === '/picking/supervisor' || path === '/picking/supervisor/') {
        if (!hasModulePermission('picking')) {
          denyHome();
          return;
        }
        setModuleView('picking-supervisor');
      } else if (path === '/picking' || path === '/picking/') {
        if (!hasModulePermission('picking')) {
          denyHome();
          return;
        }
        setModuleView('picking');
      } else if (isStocktakePath(path)) {
        if (!hasModulePermission('stocktake')) {
          denyHome();
          return;
        }
        setJobsHubOpenToTab(null);
        setStocktakeLocation(parseStocktakeLocation(window.location.search));
        setModuleView('stocktake');
      } else if (path === '/new-products' || path === '/new-products/' || path.startsWith('/new-products?') || path.startsWith('/new-products/?')) {
        if (!hasModulePermission('newProducts')) {
          denyHome();
          return;
        }
        setModuleView('new-products');
      } else if (isIncomingStockPath(path)) {
        if (!hasModulePermission('po')) {
          denyHome();
          return;
        }
        setModuleView('po');
        // Read ?tab= query param for deep linking (e.g. /incoming-stock?tab=waiting_arrival)
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        const validTabs = ['all', 'draft', 'waiting_arrival', 'arrived', 'website_team', 'archived'];
        const normalizedTabParam = tabParam === 'print_labels' ? 'arrived' : tabParam;
        if (normalizedTabParam && validTabs.includes(normalizedTabParam)) {
          setPoStatusTab(normalizedTabParam);
        } else {
          // No ?tab= means someone just opened the module — land on Arrived,
          // the put-away starting point (Rhys 2026-08-29).
          setPoStatusTab('arrived');
        }
        // `?po=<reference>` (CEO "Arriving soon" rows, 2026-10-23): open the
        // PO manager filtered to that PO, the same way Preorders'
        // onNavigateToPO focuses one. Without it the filter is left alone.
        const poParam = String(urlParams.get('po') || '').trim();
        if (poParam) {
          setPoPage(1);
          setPoStoreFilter('all');
          setPoFilter(poParam);
          setPoFilterQuery(poParam);
        }
        // `&poId=<id>` (PO notification emails, 2026-10-27): also scroll to
        // that exact card, in case the reference filter matches several.
        const poIdParam = String(urlParams.get('poId') || '').trim();
        if (poIdParam) {
          setPoNavigateTarget({ poId: poIdParam, status: normalizedTabParam || 'arrived' });
        }
      } else if (path === '/backup-restore' || path === '/backup-restore/') {
        if (!hasModulePermission('warehouse')) { denyHome(); return; }
        setJobsHubOpenToTab(null);
        setModuleView('backup-restore');
      } else if (isExpiryPath(path)) {
        if (!hasModulePermission('warehouse')) { denyHome(); return; }
        setJobsHubOpenToTab(null);
        setModuleView('expiry');
      } else if (isCeoPath(path)) {
        // The grant is strict (no "allowed until loaded"), so wait for
        // permissions rather than bouncing a direct /ceo load home; this
        // re-runs when they land (2026-10-23).
        if (!permissionSettingsLoaded) return;
        if (!hasModulePermission('ceo')) { denyHome(); return; }
        setJobsHubOpenToTab(null);
        setModuleView('ceo');
      } else if (path === '/map' || path === '/map/') {
        if (!hasModulePermission('warehouse') && !hasModulePermission('picking')) {
          denyHome();
          return;
        }
        setJobsHubOpenToTab(null);
        setModuleView('map');
      } else if (isPickupKioskPath(path)) {
        if (!hasModulePermission('pickFromStore')) {
          denyHome();
          return;
        }
        setJobsHubOpenToTab(null);
        setPickupKioskPane(parsePickupKioskPane(path));
        setModuleView('pick-from-store');
      } else if (isShipPath(path)) {
        if (!hasModulePermission('ship')) {
          denyHome();
          return;
        }
        setJobsHubOpenToTab(null);
        setShipRoute(parseShipRoute(path, window.location.search));
        setModuleView('ship');
      } else if (isLocalDeliveriesPath(path)) {
        if (!hasModulePermission('localDeliveries')) {
          denyHome();
          return;
        }
        setJobsHubOpenToTab(null);
        setModuleView('local-deliveries');
      } else {
        // Unknown path: picking-touch falls back to picking (home is blank
        // on that viewport); desktop falls back to home.
        if (isPickingTouch && hasModulePermission('picking')) {
          setJobsHubOpenToTab(null);
          setModuleView('picking');
          window.history.replaceState({}, '', '/picking');
          return;
        }
        setModuleView('home');
      }
    };

    handleRoute();
    window.addEventListener('popstate', handleRoute);
    return () => window.removeEventListener('popstate', handleRoute);
  }, [hasModulePermission, isPickingTouch, remoteRestricted, permissionSettingsLoaded, permissionSettings]);

  // Navigation helpers — single setModuleView for seamless switching
  const navigateToJobs = useCallback(() => {
    if (!enforceModulePermission('jobs')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setJobsHubOpenToTab('board');
    setModuleView('jobs');
    window.history.pushState({}, '', JOBS_3D_PATH);
  }, [moduleView, enforceModulePermission]);

  const navigateToTags = useCallback(() => {
    if (!enforceModulePermission('productTags')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setModuleView('tags');
    window.history.pushState({}, '', '/tags');
  }, [moduleView, enforceModulePermission]);

  const navigateToPOPlanning = useCallback(() => {
    if (!enforceModulePermission('poPlanning')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setPlannerChannel('all');
    setModuleView('po-planning');
    window.history.pushState({}, '', plannerChannelPath('all'));
  }, [moduleView, enforceModulePermission]);

  const navigateToPriceMole = useCallback(() => {
    if (!enforceModulePermission('priceMole')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setModuleView('pricemole');
    setPricemoleFocusSku(null);
    window.history.pushState({}, '', pricemoleChannelPath(pricemoleChannel || 'centralcoastcoffee'));
  }, [moduleView, enforceModulePermission, pricemoleChannel]);

  const navigateToCoffeeStocktake = useCallback(() => {
    if (!enforceModulePermission('coffeeStocktake')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setModuleView('coffee-stocktake');
    window.history.pushState({}, '', '/coffee-stocktake');
  }, [moduleView, enforceModulePermission]);

  const navigateToStocktake = useCallback(() => {
    if (!enforceModulePermission('stocktake')) return;
    setStocktakeLocation(null);
    setModuleView('stocktake');
    window.history.pushState({}, '', STOCKTAKE_PATH);
  }, [enforceModulePermission]);

  const handlePricemoleChannelChange = useCallback((channel) => {
    setPricemoleChannel(channel);
    // Switching store abandons the deep-linked product — its SKU belongs to
    // the other store, so drop it from state and the URL together.
    setPricemoleFocusSku(null);
    window.history.replaceState({}, '', pricemoleChannelPath(channel));
  }, []);

  const navigateToPreorders = useCallback(() => {
    if (!enforceModulePermission('preorders')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setPreorderFocusSku(null);
    setPreorderSourcePoId(null);
    setPreordersNavigationKey(value => value + 1);
    setModuleView('preorders');
    window.history.pushState({}, '', '/preorders');
  }, [moduleView, enforceModulePermission]);

  const navigateToPickingSupervisor = useCallback(() => {
    if (!enforceModulePermission('picking')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setModuleView('picking-supervisor');
    window.history.pushState({}, '', '/picking/supervisor');
  }, [moduleView, enforceModulePermission]);

  const navigateToPicking = useCallback(() => {
    if (!enforceModulePermission('picking')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setModuleView('picking');
    window.history.pushState({}, '', '/picking');
  }, [moduleView, enforceModulePermission]);

  const navigateToPickFromStore = useCallback(() => {
    if (!enforceModulePermission('pickFromStore')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setPickupKioskPane(parsePickupKioskPane(PICKUP_KIOSK_PATH));
    setModuleView('pick-from-store');
    window.history.pushState({}, '', PICKUP_KIOSK_PATH);
  }, [moduleView, enforceModulePermission]);

  // The kiosk reports pane taps/swipes here so `/pickup-kiosk/lockers` is
  // bookmarkable and browser back returns to the other pane.
  const handlePickupKioskPaneChange = useCallback((pane) => {
    const next = parsePickupKioskPane(pickupKioskPanePath(pane));
    setPickupKioskPane(next);
    const path = pickupKioskPanePath(next);
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
  }, []);

  const navigateToShip = useCallback(() => {
    if (!enforceModulePermission('ship')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setShipRoute(parseShipRoute(SHIP_PATH));
    setModuleView('ship');
    window.history.pushState({}, '', SHIP_PATH);
  }, [moduleView, enforceModulePermission]);

  // ShipView hands back the route it is now showing plus its canonical path;
  // `replace` is for URL corrections (a stale order link) rather than steps.
  const handleShipRouteChange = useCallback((nextRoute, path, options = {}) => {
    setShipRoute(nextRoute);
    // Statistics carries its filters in the query, so compare the whole URL
    // there; other Ship paths have none and keep comparing the path alone
    // (the Shipped tab's own ?view= must not force a push).
    // Packaging (2026-10-08) keeps its sub-tab in ?view= the same way.
    const current = /^\/ship\/(statistics|packaging)/.test(String(path || ''))
      ? `${window.location.pathname}${window.location.search}`
      : window.location.pathname;
    if (!path || current === path) return;
    if (options.replace) window.history.replaceState({}, '', path);
    else window.history.pushState({}, '', path);
  }, []);

  const navigateToLocalDeliveries = useCallback(() => {
    if (!enforceModulePermission('localDeliveries')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setModuleView('local-deliveries');
    window.history.pushState({}, '', LOCAL_DELIVERIES_PATH);
  }, [moduleView, enforceModulePermission]);

  const navigateToMap = useCallback(() => {
    if (!hasMapPermission()) {
      setError("You don't have permission to access the warehouse map");
      setJobsHubOpenToTab(null);
      setBackdropLeaving(null);
      setModuleView('home');
      window.history.pushState({}, '', productsHomePath());
      return;
    }
    if (moduleView === 'po') setBackdropLeaving('po');
    setJobsHubOpenToTab(null);
    setModuleView('map');
    window.history.pushState({}, '', '/map');
  }, [moduleView, hasMapPermission]);

  const navigateToExpiry = useCallback(() => {
    if (!enforceModulePermission('warehouse')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setJobsHubOpenToTab(null);
    setModuleView('expiry');
    window.history.pushState({}, '', EXPIRY_PATH);
  }, [moduleView, enforceModulePermission]);

  const navigateToCeo = useCallback(() => {
    if (!enforceModulePermission('ceo')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setJobsHubOpenToTab(null);
    setModuleView('ceo');
    window.history.pushState({}, '', CEO_PATH);
  }, [moduleView, enforceModulePermission]);

  // In-app link from a page that only knows URLs (the CEO watchlist,
  // 2026-10-23): push the path, then fire popstate so handleRoute and the
  // modules' own URL-sync listeners read it exactly like back/forward would.
  const navigateToPath = useCallback((targetPath) => {
    // Same-origin app paths only; pushState throws on another origin.
    if (typeof targetPath !== 'string' || !targetPath.startsWith('/') || targetPath.startsWith('//')) return;
    window.history.pushState({}, '', targetPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const navigateToNewProducts = useCallback(() => {
    if (!enforceModulePermission('newProducts')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setModuleView('new-products');
    window.history.pushState({}, '', '/new-products');
  }, [moduleView, enforceModulePermission]);

  const navigateToPOs = useCallback(() => {
    if (!enforceModulePermission('po')) return;
    setBackdropLeaving(null);
    setJobsHubOpenToTab(null);
    setModuleView('po');
    window.history.pushState({}, '', incomingStockPath());
  }, [enforceModulePermission]);

  // AI copilot citation chips ([[po:...]], [[sku:...]], ...) deep-link to the
  // EXACT record, not just the module. Deliberately NOT memoized: it must see
  // the current render's handleSearch (declared below), whose closure reads
  // live products/loading state.
  const handleAiNavigate = (cite) => {
    if (!cite?.type) return;
    const label = String(cite.label || '').replace(/^#/, '').trim();
    if (cite.type === 'po') {
      if (!enforceModulePermission('po')) return;
      setBackdropLeaving(null);
      setJobsHubOpenToTab(null);
      setModuleView('po');
      // ALL tab + reference filter finds the PO whatever workflow stage it's
      // on (website_team/archived POs live outside the default tab).
      setPoStatusTab('all');
      setPoPage(1);
      setPoFilter(label);
      window.history.pushState({}, '', incomingStockPath());
    } else if (cite.type === 'preorder') {
      if (!enforceModulePermission('preorders')) return;
      const orderNumber = String(cite.id || label).replace(/^#/, '').trim();
      // Store tab from the order-number prefix so the search hits its store.
      const storeSlug = orderNumber.toUpperCase().startsWith('CCC') ? 'ccc'
        : orderNumber.toUpperCase().startsWith('FPV') ? 'phaser' : 'all';
      setBackdropLeaving(null);
      setJobsHubOpenToTab(null);
      setModuleView('preorders');
      window.history.pushState({}, '', `/preorders?store=${storeSlug}`);
      // PreordersModal owns its search state and is a shared WIP file, so
      // focus the order by driving its controlled search input directly once
      // it mounts (native setter + input event = a real React change).
      const started = Date.now();
      const inject = () => {
        const input = document.querySelector('.PreordersModal-searchInput--order');
        if (input) {
          const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
          setter.call(input, orderNumber);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        if (Date.now() - started < 6000) setTimeout(inject, 200);
      };
      setTimeout(inject, 150);
    } else if (cite.type === 'planner' || cite.type === 'report') {
      navigateToPOPlanning();
    } else if (cite.type === 'sku') {
      setBackdropLeaving(null);
      setJobsHubOpenToTab(null);
      setModuleView('home');
      window.history.pushState({}, '', productsHomePath());
      void handleSearch(String(cite.id || label).trim(), false);
    }
  };

  // Clear PO filter when leaving the PO module so staff don't forget they're filtered.
  // On leaving only (2026-10-23): the mount run used to wipe the filter a
  // fresh /incoming-stock?po= load had just set.
  const prevModuleViewForPoFilterRef = useRef(moduleView);
  useEffect(() => {
    const leftPo = prevModuleViewForPoFilterRef.current === 'po' && moduleView !== 'po';
    prevModuleViewForPoFilterRef.current = moduleView;
    if (leftPo) {
      setPoFilter('');
      setPoFilterQuery('');
    }
  }, [moduleView]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPoFilterQuery(poFilter.trim());
    }, 250);

    return () => clearTimeout(timer);
  }, [poFilter]);

  // When PO tab changes, reset pagination and clear bulk selections.
  useEffect(() => {
    setPoPage(1);
    setSelectedPOs(new Set());
  }, [poStatusTab]);

  // Clear preorder focus and reset preorders view when leaving so filters don't persist on return
  useEffect(() => {
    if (moduleView !== 'preorders') { setPreorderFocusSku(null); setPreorderSourcePoId(null); }
  }, [moduleView]);

  // Preorder SMS unread count for header badge.
  // Centralised coalescing guard: the mount fetch, the module-view refresh, and (in dev) StrictMode
  // all fire within the same load burst — collapse those into ONE network call. An explicit refresh
  // (e.g. after marking messages read) passes force=true to bypass the window. Badge behaviour is
  // unchanged; only redundant duplicate calls on load are removed.
  const preorderUnreadLastFetchRef = useRef(0);
  const fetchPreorderUnreadCount = useCallback(async (force = false) => {
    if (skipRemoteBlockedFetch() || headerBadgesHiddenRef.current) return;
    const now = Date.now();
    if (!force && now - preorderUnreadLastFetchRef.current < 1500) return;
    preorderUnreadLastFetchRef.current = now;
    try {
      const res = await axios.get(`${API_URL}/api/preorders/unread-count`, { withCredentials: true });
      setPreorderUnreadCount(res.data.count ?? 0);
    } catch {
      setPreorderUnreadCount(0);
    }
  }, [skipRemoteBlockedFetch]);
  useEffect(() => { fetchPreorderUnreadCount(); }, [fetchPreorderUnreadCount]);
  useEffect(() => {
    if (moduleView !== 'preorders') fetchPreorderUnreadCount();
  }, [moduleView, fetchPreorderUnreadCount]);

  // PriceMole "needs updating" count for the header badge (like RA unread messages).
  const [pricemoleReviewCount, setPricemoleReviewCount] = useState(0);
  const pricemoleReviewLastFetchRef = useRef(0);
  const fetchPricemoleReviewCount = useCallback(async (force = false) => {
    if (skipRemoteBlockedFetch() || headerBadgesHiddenRef.current) return;
    if (!hasModulePermission('priceMole')) return;
    const now = Date.now();
    if (!force && now - pricemoleReviewLastFetchRef.current < 1500) return;
    pricemoleReviewLastFetchRef.current = now;
    try {
      const res = await axios.get(`${API_URL}/api/pricemole/badge-count`, { withCredentials: true });
      setPricemoleReviewCount(res.data.count ?? 0);
    } catch (err) {
      setPricemoleReviewCount(0);
    }
  }, [hasModulePermission, skipRemoteBlockedFetch]);
  useEffect(() => { fetchPricemoleReviewCount(); }, [fetchPricemoleReviewCount]);
  useEffect(() => {
    if (moduleView !== 'pricemole') fetchPricemoleReviewCount();
  }, [moduleView, fetchPricemoleReviewCount]);

  // Jobs (Kanban + RA) unread customer messages count for header badge.
  // Same centralised coalescing guard as the preorder count: mount + module-view + StrictMode fire
  // in one load burst and collapse into a single call. Navigation refresh behaviour is unchanged.
  const jobsUnreadLastFetchRef = useRef(0);
  const fetchJobsUnreadCount = useCallback(async (force = false) => {
    if (skipRemoteBlockedFetch() || headerBadgesHiddenRef.current) return;
    const now = Date.now();
    if (!force && now - jobsUnreadLastFetchRef.current < 1500) return;
    jobsUnreadLastFetchRef.current = now;
    try {
      const res = await axios.get(`${API_URL}/api/jobs/unread-messages/count`, { withCredentials: true });
      setJobsUnreadCount(res.data.count ?? 0);
      setJobsUnreadByHub({
        jobs: res.data.byHub?.jobs ?? 0,
        ra: res.data.byHub?.ra ?? 0
      });
    } catch {
      setJobsUnreadCount(0);
      setJobsUnreadByHub({ jobs: 0, ra: 0 });
    }
  }, [skipRemoteBlockedFetch]);
  useEffect(() => { fetchJobsUnreadCount(); }, [fetchJobsUnreadCount]);
  useEffect(() => {
    if (moduleView !== 'jobs' && moduleView !== 'ra') fetchJobsUnreadCount();
  }, [moduleView, fetchJobsUnreadCount]);

  // "My jobs" header badges: active jobs assigned to the signed-in user, split 3D vs Repairs.
  // The staff list is fetched here (cheap, cached by the hub anyway) so we can resolve the
  // user → staff id with the same heuristic the intake form uses; the backend falls back to
  // matching on email when we cannot.
  const [jobsAssignedCounts, setJobsAssignedCounts] = useState({ jobs: 0, ra: 0 });
  const jobsAssignedLastFetchRef = useRef(0);
  const fetchJobsAssignedCounts = useCallback(async (force = false) => {
    if (skipRemoteBlockedFetch() || headerBadgesHiddenRef.current) return;
    const now = Date.now();
    if (!force && now - jobsAssignedLastFetchRef.current < 1500) return;
    jobsAssignedLastFetchRef.current = now;
    try {
      const staffRes = await axios.get(`${API_URL}/api/jobs/staff`, { withCredentials: true });
      const staffId = resolveSelfStaffId(staffRes.data?.staff || [], currentUser);
      const res = await axios.get(`${API_URL}/api/jobs/assigned/counts`, {
        params: staffId && staffId !== 'unassigned' ? { staffId } : {},
        withCredentials: true
      });
      setJobsAssignedCounts({
        jobs: res.data.counts?.jobs ?? 0,
        ra: res.data.counts?.ra ?? 0
      });
    } catch {
      setJobsAssignedCounts({ jobs: 0, ra: 0 });
    }
  }, [currentUser, skipRemoteBlockedFetch]);
  useEffect(() => { fetchJobsAssignedCounts(); }, [fetchJobsAssignedCounts]);
  useEffect(() => {
    if (moduleView !== 'jobs' && moduleView !== 'ra') fetchJobsAssignedCounts();
  }, [moduleView, fetchJobsAssignedCounts]);

  // Returns & Repairs badge: customer returns lodged on the portal and waiting
  // for approval (red), plus approved returns AusPost has delivered that are not
  // yet marked Received (in the tooltip). Polled every 2 min while the tab is
  // visible, because a new return arrives without anyone navigating.
  const [returnsCounts, setReturnsCounts] = useState({ awaitingApproval: 0, deliveredNotReceived: 0 });
  const fetchReturnsCounts = useCallback(async () => {
    if (skipRemoteBlockedFetch() || headerBadgesHiddenRef.current || !hasModulePermission('ra')) return;
    try {
      const res = await axios.get(`${API_URL}/api/jobs/returns/counts`, { withCredentials: true });
      setReturnsCounts({
        awaitingApproval: Number(res.data?.awaitingApproval) || 0,
        deliveredNotReceived: Number(res.data?.deliveredNotReceived) || 0
      });
    } catch {
      // keep the last counts; a failed poll is not "no returns"
    }
  }, [hasModulePermission, skipRemoteBlockedFetch]);
  useEffect(() => {
    if (!currentUser) return undefined;
    fetchReturnsCounts();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchReturnsCounts();
    }, 120000);
    return () => window.clearInterval(intervalId);
  }, [currentUser, fetchReturnsCounts]);
  useEffect(() => { fetchReturnsCounts(); }, [moduleView, fetchReturnsCounts]);
  // The header just came back (left picking/trays, or permissions landed
  // without picking): fill the badges the skipped calls left at zero. The
  // 1.5 s coalescing guards fold this into the moduleView refreshes above.
  useEffect(() => {
    if (headerBadgesHidden) return;
    fetchPreorderUnreadCount();
    fetchPricemoleReviewCount();
    fetchJobsUnreadCount();
    fetchJobsAssignedCounts();
    fetchReturnsCounts();
  }, [headerBadgesHidden, fetchPreorderUnreadCount, fetchPricemoleReviewCount, fetchJobsUnreadCount, fetchJobsAssignedCounts, fetchReturnsCounts]);

  const refreshSecurityIncidentSummary = useCallback(async () => {
    if (!canViewSecurityIncidents) {
      setSecurityUnreadCount(0);
      return;
    }
    try {
      const summary = await fetchSecurityIncidentSummary();
      setSecurityUnreadCount(summary.unreadCount ?? 0);
    } catch (err) {
      console.warn('[SECURITY] Failed to refresh incident summary:', err?.message || err);
    }
  }, [canViewSecurityIncidents]);

  useEffect(() => {
    if (!canViewSecurityIncidents || !currentUser) return undefined;
    refreshSecurityIncidentSummary();
    // A badge refreshed when the tab comes back is strictly better than one
    // refreshed while nobody is looking at it.
    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      refreshSecurityIncidentSummary();
    }, 60000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshSecurityIncidentSummary();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [canViewSecurityIncidents, currentUser, refreshSecurityIncidentSummary]);

  const navigateToRAs = useCallback(() => {
    if (!enforceModulePermission('ra')) return;
    if (moduleView === 'po') setBackdropLeaving('po');
    setJobsHubOpenToTab('board');
    setModuleView('ra');
    window.history.pushState({}, '', REPAIRS_PATH);
  }, [moduleView, enforceModulePermission]);

  const navigateToHome = useCallback(() => {
    if (isPickingTouch) {
      if (!hasModulePermission('picking')) return;
      setJobsHubOpenToTab(null);
      setModuleView('picking');
      window.history.pushState({}, '', '/picking');
      return;
    }
    setJobsHubOpenToTab(null);
    setModuleView('home');
    window.history.pushState({}, '', productsHomePath());
  }, [isPickingTouch, hasModulePermission]);

  // Tablets live in picking and hop to Products & Trays all shift. Warm the
  // trays chunks (and Lookup item's) once picking is idle so the first tap
  // paints without a ~110 KB chunk download on warehouse Wi-Fi (2026-11-03).
  const stocktakeChunksWarmedRef = useRef(false);
  useEffect(() => {
    if (!isPickingTouch || !showPickingModal || stocktakeChunksWarmedRef.current) return undefined;
    const warm = () => {
      stocktakeChunksWarmedRef.current = true;
      loadStocktakeView().catch(() => {});
      import('./components/stocktake/StocktakeLookupSheet').catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(warm, { timeout: 8000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const timer = window.setTimeout(warm, 4000);
    return () => window.clearTimeout(timer);
  }, [isPickingTouch, showPickingModal]);

  // Active module for header bar colour and title (jobs | ra | po | preorders | home)
  const activeModule = useMemo(() => {
    if (moduleView === 'ra' || moduleView === 'jobs') return moduleView;
    if (moduleView === 'po' || moduleView === 'preorders' || moduleView === 'picking' || moduleView === 'picking-supervisor' || moduleView === 'packing-supervisor' || moduleView === 'pick-from-store' || moduleView === 'ship' || moduleView === 'local-deliveries' || moduleView === 'tags' || moduleView === 'new-products' || moduleView === 'map' || moduleView === 'backup-restore' || moduleView === 'expiry' || moduleView === 'ceo' || moduleView === 'po-planning') return moduleView;
    return 'home';
  }, [moduleView]);

  useEffect(() => {
    document.title = getDocumentTitle(moduleView, jobsHubActiveTab);
  }, [moduleView, jobsHubActiveTab]);

  const visibleNavKeys = useMemo(() => {
    return NAV_KEYS.filter((key) => {
      if (key === 'map' || key === 'picking-supervisor') {
        if (isMobile) return false;
      }
      if (key === 'map') {
        return hasMapPermission();
      }
      if (key === 'picking-supervisor') {
        return hasModulePermission('picking');
      }
      const moduleKey = MODULE_KEY_BY_NAV[key];
      if (!moduleKey) return true;
      return hasModulePermission(moduleKey);
    });
  }, [hasModulePermission, hasMapPermission, isMobile]);

  const visibleHeaderToolKeys = useMemo(() => {
    return HEADER_TOOL_KEYS.filter((key) => {
      const moduleKey = MODULE_KEY_BY_NAV[key];
      if (!moduleKey) return false;
      return hasModulePermission(moduleKey);
    });
  }, [hasModulePermission]);


  useEffect(() => {
    if (!permissionSettings) return;
    if (moduleView === 'map') {
      if (hasMapPermission()) return;
      setError("You don't have permission to access the warehouse map");
      setJobsHubOpenToTab(null);
      setBackdropLeaving(null);
      setModuleView('home');
      window.history.replaceState({}, '', productsHomePath());
      return;
    }
    const moduleKey = MODULE_KEY_BY_VIEW[moduleView];
    if (!moduleKey) return;
    // handleRoute owns CEO (2026-10-23): it holds a seeded first paint until
    // permissions land, then routes on. Bouncing here as well would flash a
    // "no permission" error at whoever just opened `/` on a shared browser.
    if (moduleKey === 'ceo') return;
    if (hasModulePermission(moduleKey)) return;

    if (isMobile) {
      setJobsHubOpenToTab(null);
      setBackdropLeaving(null);
      return;
    }

    setError(`You don't have permission to access ${MODULE_PERMISSION_LABELS[moduleKey] || 'this module'}`);
    setJobsHubOpenToTab(null);
    setBackdropLeaving(null);
    setModuleView('home');
    window.history.replaceState({}, '', productsHomePath());
  }, [moduleView, permissionSettings, hasModulePermission, hasMapPermission, isMobile]);

  const headerModuleKey = activeModule === 'home' ? 'warehouse' : activeModule;
  const focusRingColor = getModuleFocusRingColor(headerModuleKey);
  const moduleBaseColor = getModuleBaseColor(headerModuleKey);
  const moduleAccentColor = getModuleAccentBadge(headerModuleKey);

  const PURCHASE_ORDER_TABS = ['all', 'draft', 'waiting_arrival', 'arrived', 'website_team', 'archived'];
  const ARRIVED_WORKFLOW_TASK_KEYS = ['printWorksheet', 'printLabels', 'verifyProductData', 'notifyArrival', 'awaitingStockin', 'checkPreorders', 'putAway'];
  const WEBSITE_TEAM_TASK_KEYS = ['checkCostingsRetailPricing'];
  const WEBSITE_TEAM_TASK_DEFINITIONS = [
    {
      key: 'checkCostingsRetailPricing',
      label: '7. Check costings and retail pricing',
      shortLabel: '7. Check costings and retail pricing',
      description: 'Confirm the final costings and retail pricing are correct before these products go live.',
      recommendedBefore: []
    }
    // Step 8 "Add to Recent Products" was removed 2026-10-27: ticking step 5
    // now moves the PO's products into the recent arrivals collections.
  ];

  const normalizePoWorkflowStatus = useCallback((status) => String(status || 'draft').trim().toLowerCase(), []);

  const resolvePoWorkflowStatus = useCallback((po) => {
    // Incoming Stock workflow tab: Firestore po_metadata only (D23). Never map source API status.
    const raw = normalizePoWorkflowStatus(po?.localMetadata?.status || 'draft');

    const knownStatuses = [
      'draft', 'not_shipped', 'open', 'partial', 'print_labels',
      'waiting_arrival', 'awaiting arrival', 'arrived', 'input_data',
      'receive_goods', 'awaiting_stockin', 'put_away', 'received',
      'closed', 'website_team', 'archived'
    ];

    return knownStatuses.includes(raw) ? raw : 'draft';
  }, [normalizePoWorkflowStatus]);

  const matchesPoStatusTab = useCallback((status, tab) => {
    const normalizedStatus = normalizePoWorkflowStatus(status);
    const draftStatuses = ['draft', 'not_shipped', 'open', 'partial'];
    const arrivedStatuses = ['print_labels', 'arrived', 'input_data', 'receive_goods', 'awaiting_stockin', 'put_away', 'received', 'closed'];

    if (tab === 'all') {
      return draftStatuses.includes(normalizedStatus) ||
        normalizedStatus === 'waiting_arrival' || normalizedStatus === 'awaiting arrival' ||
        arrivedStatuses.includes(normalizedStatus);
    }
    if (tab === 'draft') return draftStatuses.includes(normalizedStatus);
    if (tab === 'waiting_arrival') return normalizedStatus === 'waiting_arrival' || normalizedStatus === 'awaiting arrival';
    if (tab === 'arrived') return arrivedStatuses.includes(normalizedStatus);
    if (tab === 'website_team') return normalizedStatus === 'website_team';
    if (tab === 'archived') return normalizedStatus === 'archived';

    return true;
  }, [normalizePoWorkflowStatus]);

  // PO Tab Display Names - ALL + workflow tabs
  const getPoTabDisplayName = (tab) => {
    const names = {
      'all': 'ALL',
      'draft': 'Drafts',
      'waiting_arrival': 'Waiting Arrival',
      'arrived': 'Arrived',
      'website_team': 'Website Team',
      'archived': 'Archived'
    };
    return names[tab] || tab;
  };

  const getPoIdentityKey = (po) => `${po?.storeKey || 'unknown'}::${String(po?.id || '')}`;

  const getRecentOptimisticPOMetadata = useCallback((po) => {
    const key = getPoIdentityKey(po);
    const entry = poOptimisticMetadataRef.current.get(key);
    if (!entry) return null;

    if ((Date.now() - entry.savedAt) > PO_OPTIMISTIC_STATUS_MAX_AGE_MS) {
      poOptimisticMetadataRef.current.delete(key);
      return null;
    }

    return entry.metadata;
  }, [PO_OPTIMISTIC_STATUS_MAX_AGE_MS]);

  const mergePOWithOptimisticMetadata = useCallback((po) => {
    const optimisticMeta = getRecentOptimisticPOMetadata(po);
    if (!optimisticMeta) return po;

    const serverMeta = po.localMetadata || {};
    const optimisticUpdated = optimisticMeta.updatedAt ? new Date(optimisticMeta.updatedAt).getTime() : 0;
    const serverUpdated = serverMeta.updatedAt ? new Date(serverMeta.updatedAt).getTime() : 0;

    if (serverUpdated > optimisticUpdated) return po;

    return {
      ...po,
      localMetadata: {
        ...serverMeta,
        ...optimisticMeta
      }
    };
  }, [getRecentOptimisticPOMetadata]);

  const matchesPoSearchQuery = useCallback((po, query) => {
    const term = String(query || '').trim().toLowerCase();
    if (!term) return true;

    const items = po.items || po.line_items || [];
    const skuMatch = items.some(item =>
      String(item.sku || '').toLowerCase().includes(term) ||
      String(item.title || '').toLowerCase().includes(term)
    );

    return (
      (po.reference && po.reference.toLowerCase().includes(term)) ||
      (po.lightspeed_reference && po.lightspeed_reference.toLowerCase().includes(term)) ||
      (po.display_name && po.display_name.toLowerCase().includes(term)) ||
      (po.supplier_invoice && String(po.supplier_invoice).toLowerCase().includes(term)) ||
      (po.source_reference && String(po.source_reference).toLowerCase().includes(term)) ||
      (po.vendor_display_name && po.vendor_display_name.toLowerCase().includes(term)) ||
      (po.supplier_name && po.supplier_name.toLowerCase().includes(term)) ||
      (po.vendor && String(po.vendor).toLowerCase().includes(term)) ||
      (String(po.id || '').toLowerCase().includes(term)) ||
      (po.status && po.status.toLowerCase().includes(term)) ||
      skuMatch
    );
  }, []);

  const getPoTabStyle = (tab, isActive) => {
    const palette = {
      all: {
        activeBg: darkMode ? '#334155' : '#ececec',
        activeBorder: darkMode ? '#cbd5e1' : '#6b7280',
        activeUnderline: darkMode ? '#e2e8f0' : '#6b7280',
        activeText: darkMode ? '#f8fafc' : '#111827',
        inactiveBg: darkMode ? 'rgba(148, 163, 184, 0.14)' : '#f5f5f5',
        inactiveBorder: darkMode ? 'rgba(148, 163, 184, 0.26)' : 'transparent',
        inactiveText: darkMode ? '#94a3b8' : '#6b7280'
      },
      draft: {
        activeBg: darkMode ? '#78350f' : '#fff4cf',
        activeBorder: darkMode ? '#fbbf24' : '#d4a017',
        activeUnderline: darkMode ? '#f59e0b' : '#d4a017',
        activeText: darkMode ? '#fffbeb' : '#5f4500',
        inactiveBg: darkMode ? 'rgba(245, 158, 11, 0.12)' : '#fff9db',
        inactiveBorder: darkMode ? 'rgba(251, 191, 36, 0.24)' : 'transparent',
        inactiveText: darkMode ? '#fcd34d' : '#9a7b00'
      },
      waiting_arrival: {
        activeBg: darkMode ? '#1d4ed8' : '#e3f2fd',
        activeBorder: darkMode ? '#60a5fa' : '#2563eb',
        activeUnderline: darkMode ? '#3b82f6' : '#2563eb',
        activeText: darkMode ? '#eff6ff' : '#0f3d91',
        inactiveBg: darkMode ? 'rgba(59, 130, 246, 0.12)' : '#f1f7fe',
        inactiveBorder: darkMode ? 'rgba(96, 165, 250, 0.24)' : 'transparent',
        inactiveText: darkMode ? '#93c5fd' : '#4b77b9'
      },
      arrived: {
        activeBg: darkMode ? '#166534' : '#e8f5e9',
        activeBorder: darkMode ? '#4ade80' : '#256f3a',
        activeUnderline: darkMode ? '#22c55e' : '#256f3a',
        activeText: darkMode ? '#f0fdf4' : '#1f5c2f',
        inactiveBg: darkMode ? 'rgba(34, 197, 94, 0.12)' : '#f2fbf4',
        inactiveBorder: darkMode ? 'rgba(74, 222, 128, 0.24)' : 'transparent',
        inactiveText: darkMode ? '#86efac' : '#418a53'
      },
      website_team: {
        activeBg: darkMode ? '#115e59' : '#e0f7f4',
        activeBorder: darkMode ? '#2dd4bf' : '#0f766e',
        activeUnderline: darkMode ? '#14b8a6' : '#0f766e',
        activeText: darkMode ? '#f0fdfa' : '#134e4a',
        inactiveBg: darkMode ? 'rgba(45, 212, 191, 0.11)' : '#eefcf9',
        inactiveBorder: darkMode ? 'rgba(45, 212, 191, 0.24)' : 'transparent',
        inactiveText: darkMode ? '#5eead4' : '#0f766e'
      },
      archived: {
        activeBg: darkMode ? '#9a3412' : '#fff0e0',
        activeBorder: darkMode ? '#fdba74' : '#d97706',
        activeUnderline: darkMode ? '#ea580c' : '#d97706',
        activeText: darkMode ? '#fff7ed' : '#8a4b08',
        inactiveBg: darkMode ? 'rgba(251, 146, 60, 0.11)' : '#fff7f0',
        inactiveBorder: darkMode ? 'rgba(253, 186, 116, 0.24)' : 'transparent',
        inactiveText: darkMode ? '#fdba74' : '#b96b2a'
      }
    };

    const theme = palette[tab] || palette.all;
    const activeUnderline = theme.activeUnderline || theme.activeBorder;

    return {
      padding: '10px 16px',
      cursor: 'pointer',
      border: `1px solid ${isActive ? theme.activeBorder : (darkMode ? (theme.inactiveBorder || 'rgba(148, 163, 184, 0.14)') : 'transparent')}`,
      borderBottom: `4px solid ${isActive ? activeUnderline : 'transparent'}`,
      backgroundColor: isActive ? theme.activeBg : theme.inactiveBg,
      color: isActive ? theme.activeText : theme.inactiveText,
      fontWeight: isActive ? '700' : '500',
      borderRadius: '8px 8px 0 0',
      marginBottom: '-1px',
      boxShadow: isActive
        ? (darkMode
            ? `0 10px 18px -16px ${theme.activeBorder}, inset 0 -4px 0 ${activeUnderline}, inset 0 1px 0 rgba(255,255,255,0.08)`
            : `0 6px 14px -14px ${theme.activeBorder}`)
        : 'none',
      transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
      transform: isActive ? 'translateY(1px)' : 'none'
    };
  };

  // When on ALL tab, each card uses this to show the right actions for its status
  const getEffectiveTabForPO = (po) => {
    const status = resolvePoWorkflowStatus(po);
    if (matchesPoStatusTab(status, 'draft')) return 'draft';
    if (matchesPoStatusTab(status, 'waiting_arrival')) return 'waiting_arrival';
    if (matchesPoStatusTab(status, 'arrived')) return 'arrived';
    if (matchesPoStatusTab(status, 'website_team')) return 'website_team';
    if (matchesPoStatusTab(status, 'archived')) return 'archived';
    return 'draft';
  };

  const [selectedPOForWorksheet, setSelectedPOForWorksheet] = useState(null);
  const [worksheetCompletionContext, setWorksheetCompletionContext] = useState(null);
  const [selectedPOForReceipt, setSelectedPOForReceipt] = useState(null);
  // Pre-arrival split (Waiting Arrival tab): move lines to a new -PARTx with its own ETA
  const [selectedPOForPreArrivalSplit, setSelectedPOForPreArrivalSplit] = useState(null);
  const [selectedPOs, setSelectedPOs] = useState(new Set());
  const [poLimit, _setPoLimit] = useState(50);

  const buildPoResponseCacheKey = useCallback((page, statusTab, query, includeItems) => (
    JSON.stringify({
      page,
      pageSize: poLimit,
      statusTab: String(statusTab || 'all'),
      query: String(query || '').trim(),
      includeItems: !!includeItems
    })
  ), [poLimit]);

  const persistPoResponseCache = useCallback(() => {
    try {
      const now = Date.now();
      const entries = Array.from(poResponseCacheRef.current.entries())
        .filter(([, cached]) => cached?.cachedAt && (now - cached.cachedAt) <= PO_CLIENT_STALE_CACHE_MAX_AGE_MS)
        .slice(-PO_CLIENT_CACHE_MAX_ENTRIES);
      window.localStorage.setItem(PO_CLIENT_CACHE_STORAGE_KEY, JSON.stringify({ version: 3, entries }));
    } catch (err) {
      console.warn('[PO] Failed to persist PO response cache:', err.message);
    }
  }, [PO_CLIENT_CACHE_MAX_ENTRIES, PO_CLIENT_CACHE_STORAGE_KEY, PO_CLIENT_STALE_CACHE_MAX_AGE_MS]);

  const hydrateStoredPoResponseCache = useCallback(() => {
    if (poResponseCacheRef.current.size > 0) return;
    try {
      PO_CLIENT_CACHE_LEGACY_KEYS.forEach((legacyKey) => {
        try {
          window.localStorage.removeItem(legacyKey);
        } catch (_) {
          // ignore
        }
      });

      const raw = window.localStorage.getItem(PO_CLIENT_CACHE_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const entries = Array.isArray(parsed?.entries) ? parsed.entries : [];
      const now = Date.now();
      entries.forEach(([key, cached]) => {
        if (!key || !cached?.data || !cached?.cachedAt) return;
        if ((now - cached.cachedAt) > PO_CLIENT_STALE_CACHE_MAX_AGE_MS) return;
        poResponseCacheRef.current.set(key, cached);
      });
    } catch (err) {
      console.warn('[PO] Failed to load stored PO response cache:', err.message);
    }
  }, [PO_CLIENT_CACHE_STORAGE_KEY, PO_CLIENT_STALE_CACHE_MAX_AGE_MS]);

  const getCachedPoResponseEntry = useCallback((cacheKey, options = {}) => {
    hydrateStoredPoResponseCache();
    const cached = poResponseCacheRef.current.get(cacheKey);
    if (!cached) return null;

    const age = Date.now() - cached.cachedAt;
    if (age > PO_CLIENT_STALE_CACHE_MAX_AGE_MS) {
      poResponseCacheRef.current.delete(cacheKey);
      persistPoResponseCache();
      return null;
    }

    const isStale = age > PO_CLIENT_CACHE_MAX_AGE_MS;
    if (isStale && !options.allowStale) return null;

    return { ...cached, age, isStale };
  }, [PO_CLIENT_CACHE_MAX_AGE_MS, PO_CLIENT_STALE_CACHE_MAX_AGE_MS, hydrateStoredPoResponseCache, persistPoResponseCache]);

  const getCachedPoResponse = useCallback((cacheKey, options = {}) => {
    return getCachedPoResponseEntry(cacheKey, options)?.data || null;
  }, [getCachedPoResponseEntry]);

  const storeCachedPoResponse = useCallback((cacheKey, data) => {
    poResponseCacheRef.current.set(cacheKey, {
      cachedAt: Date.now(),
      cacheEpoch: data?.meta?.cache_epoch || 0,
      data
    });

    if (poResponseCacheRef.current.size > PO_CLIENT_CACHE_MAX_ENTRIES) {
      const oldestKey = poResponseCacheRef.current.keys().next().value;
      if (oldestKey) poResponseCacheRef.current.delete(oldestKey);
    }

    persistPoResponseCache();
  }, [PO_CLIENT_CACHE_MAX_ENTRIES, persistPoResponseCache]);

  const clearPoResponseCache = useCallback((reason = '') => {
    poResponseCacheRef.current.clear();
    poTabPrefetchRef.current.clear();
    try {
      window.localStorage.removeItem(PO_CLIENT_CACHE_STORAGE_KEY);
    } catch (_) { /* ignore storage failures */ }
    if (reason) {
      console.log(`[PO] Cleared client PO response cache (${reason})`);
    }
  }, [PO_CLIENT_CACHE_STORAGE_KEY]);

  // ETA / confirm / tracking edits (Rhys 2026-10-28: changed ETAs came back
  // with the old date after a trip to another module). Drop the client page
  // copy and hold the edit as optimistic metadata, as status moves do, so
  // neither the reused page nor an older server copy brings the old value back.
  const applyPoMetadataEdit = useCallback((poId, patch, { storeKey = null, reason = 'PO edit' } = {}) => {
    clearPoResponseCache(reason);
    setPurchaseOrders(prev => prev.map(p => {
      if (String(p.id) !== String(poId) || (storeKey && p.storeKey !== storeKey)) return p;
      const entry = buildOptimisticPoMetadataEntry(getRecentOptimisticPOMetadata(p), patch);
      poOptimisticMetadataRef.current.set(getPoIdentityKey(p), entry);
      return { ...p, localMetadata: { ...p.localMetadata, ...entry.metadata } };
    }));
  }, [clearPoResponseCache, getRecentOptimisticPOMetadata]);

  // The CEO tab writes the same fields (utils/poMetadataEvents).
  useEffect(() => {
    const onPoMetadataChanged = (event) => {
      const { poId, patch } = event.detail || {};
      if (poId) applyPoMetadataEdit(poId, patch || {}, { reason: 'CEO edit' });
    };
    window.addEventListener(PO_METADATA_CHANGED_EVENT, onPoMetadataChanged);
    return () => window.removeEventListener(PO_METADATA_CHANGED_EVENT, onPoMetadataChanged);
  }, [applyPoMetadataEdit]);

  const applyPurchaseOrderResponse = useCallback((responseData, requestedPage, replaceOrders = false, options = {}) => {
    const newOrders = responseData?.purchase_orders || [];
    const meta = responseData?.meta || {};
    const requestedStatusTab = options.statusTab || meta.status_tab || poStatusTab || 'all';
    const requestedQuery = options.query !== undefined ? String(options.query || '').trim() : poFilterQuery;

    setPoMeta({
      page: meta.page || requestedPage,
      page_size: meta.page_size || poLimit,
      total: meta.total || newOrders.length,
      total_pages: meta.total_pages || 1,
      has_next_page: !!meta.has_next_page,
      has_prev_page: !!meta.has_prev_page
    });

    if (replaceOrders) {
      setPurchaseOrders(newOrders.map(mergePOWithOptimisticMetadata));
      return;
    }

    setPurchaseOrders(prevOrders => {
      const existingMetadataMap = new Map();
      prevOrders.forEach(po => {
        if (po.localMetadata) {
          existingMetadataMap.set(getPoIdentityKey(po), po.localMetadata);
        }
      });
      const incomingKeys = new Set();
      const mergedOrders = newOrders.map(newPo => {
        incomingKeys.add(getPoIdentityKey(newPo));
        const existingMeta = existingMetadataMap.get(getPoIdentityKey(newPo));
        let mergedPo = newPo;
        if (existingMeta) {
          const serverMeta = newPo.localMetadata || {};
          const serverUpdated = serverMeta.updatedAt ? new Date(serverMeta.updatedAt).getTime() : 0;
          const localUpdated = existingMeta.updatedAt ? new Date(existingMeta.updatedAt).getTime() : 0;
          if (serverUpdated < localUpdated) {
            mergedPo = { ...newPo, localMetadata: existingMeta };
          }
        }

        return mergePOWithOptimisticMetadata(mergedPo);
      });

      const optimisticCarryOvers = prevOrders
        .map(mergePOWithOptimisticMetadata)
        .filter(po => {
          const key = getPoIdentityKey(po);
          if (incomingKeys.has(key)) return false;
          if (!getRecentOptimisticPOMetadata(po)) return false;

          const status = resolvePoWorkflowStatus(po);
          return matchesPoStatusTab(status, requestedStatusTab) && matchesPoSearchQuery(po, requestedQuery);
        });

      return [...mergedOrders, ...optimisticCarryOvers];
    });
  }, [poFilterQuery, poLimit, poStatusTab, getRecentOptimisticPOMetadata, matchesPoSearchQuery, mergePOWithOptimisticMetadata, matchesPoStatusTab, resolvePoWorkflowStatus]);

  const prefetchPurchaseOrdersTab = useCallback(async (statusTab) => {
    const cacheKey = buildPoResponseCacheKey(1, statusTab, '', true);
    if (getCachedPoResponse(cacheKey)) return;

    const response = await axios.get(`${API_URL}/api/purchase-orders`, {
      params: {
        page: 1,
        pageSize: poLimit,
        status_tab: statusTab,
        include_items: true
      }
    });

    storeCachedPoResponse(cacheKey, response.data);
  }, [buildPoResponseCacheKey, getCachedPoResponse, poLimit, storeCachedPoResponse]);

  /**
   * Patch one PO inside every cached page instead of emptying the whole
   * client cache (a single task tick used to force every tab to refetch).
   * Pages whose tab membership changes with the new status are dropped:
   * the PO must leave (or join) that tab, and paging makes a local splice
   * unreliable, so those few entries refetch on next view.
   */
  const patchCachedPoResponses = useCallback((targetId, targetStoreKey, patchMetadata, newStatus) => {
    let touched = false;
    for (const [cacheKey, entry] of Array.from(poResponseCacheRef.current.entries())) {
      const orders = entry?.data?.purchase_orders;
      let statusTab = 'all';
      try { statusTab = JSON.parse(cacheKey)?.statusTab || 'all'; } catch (_) { /* legacy key */ }
      const belongsAfter = newStatus ? matchesPoStatusTab(newStatus, statusTab) : null;
      const index = Array.isArray(orders)
        ? orders.findIndex((po) => String(po?.id) === targetId && (!targetStoreKey || po?.storeKey === targetStoreKey))
        : -1;
      if (index < 0) {
        if (belongsAfter) {
          poResponseCacheRef.current.delete(cacheKey);
          poTabPrefetchRef.current.delete(statusTab);
          touched = true;
        }
        continue;
      }
      if (belongsAfter === false) {
        poResponseCacheRef.current.delete(cacheKey);
        poTabPrefetchRef.current.delete(statusTab);
        touched = true;
        continue;
      }
      const nextOrders = orders.slice();
      nextOrders[index] = {
        ...orders[index],
        localMetadata: patchMetadata(orders[index].localMetadata || {})
      };
      poResponseCacheRef.current.set(cacheKey, {
        ...entry,
        data: { ...entry.data, purchase_orders: nextOrders }
      });
      touched = true;
    }
    if (touched) persistPoResponseCache();
  }, [matchesPoStatusTab, persistPoResponseCache]);

  // Handler for task checklist updates from POTaskChecklist component
  const handleTaskUpdate = useCallback((poRef, updatedTasks, newStatus) => {
    const targetId = String(poRef?.id || poRef || '');
    const targetStoreKey = poRef?.storeKey || null;
    patchCachedPoResponses(targetId, targetStoreKey, (metadata) => ({
      ...metadata,
      tasks: updatedTasks,
      status: newStatus
    }), newStatus);
    setPurchaseOrders(prev => prev.map(po => {
      const sameId = String(po.id) === targetId;
      const sameStore = !targetStoreKey || po.storeKey === targetStoreKey;
      if (sameId && sameStore) {
        return {
          ...po,
          localMetadata: {
            ...po.localMetadata,
            tasks: updatedTasks,
            status: newStatus
          }
        };
      }
      return po;
    }));
  }, [patchCachedPoResponses]);

  const [editingFields, setEditingFields] = useState({});

  // Auto-focus workflow: track which product needs barcode/weight focus after being added
  const [_autoFocusProduct, setAutoFocusProduct] = useState(null); // { productKey, field: 'barcode'|'weight' }

  // Multi-store selection modal
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [multiStoreProducts, setMultiStoreProducts] = useState([]);
  const [selectedStoreProducts, setSelectedStoreProducts] = useState([]);

  // Location search warning modal
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [pendingLocationSearch, setPendingLocationSearch] = useState(null);

  // Track if we're loading from URL to prevent URL sync from interfering
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false);

  // Show all variants checkbox
  const [showAllVariants, setShowAllVariants] = useState(false);
  // Show all products from this brand checkbox (same one-shot behaviour)
  const [showAllFromBrand, setShowAllFromBrand] = useState(false);
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false);

  // Hide out of stock checkbox
  const [hideOutOfStock, setHideOutOfStock] = useState(false);

  // Store picker beside Search: '' = all stores, else a storeDisplayName.
  // Scopes title searches server-side AND filters the grid (Rhys 2026-10-07:
  // a keyword search pulls in every store). Remembered per browser.
  const [storeFilter, setStoreFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('productsStoreFilter') || '';
      return PRODUCT_STORE_OPTIONS.some((o) => o.value === saved) ? saved : '';
    } catch {
      return '';
    }
  });
  const storeFilterRef = useRef(storeFilter);
  storeFilterRef.current = storeFilter;

  // Label size settings
  const [_showLabelSizeModal, _setShowLabelSizeModal] = useState(false);
  const [labelSize, setLabelSize] = useState(() => {
    const saved = localStorage.getItem('labelSize');
    return saved || '40x28';
  });
  const [customLabelWidth, setCustomLabelWidth] = useState(() => {
    return localStorage.getItem('customLabelWidth') || '40';
  });
  const [customLabelHeight, setCustomLabelHeight] = useState(() => {
    return localStorage.getItem('customLabelHeight') || '28';
  });

  // The label print popup is a separate window on this origin: when it regenerates
  // at a new size it writes the choice to localStorage, and this picks it up so the
  // next print job starts there without a page reload.
  useEffect(() => {
    const applyStoredLabelSize = (event) => {
      if (event.storageArea !== localStorage) return;
      if (event.key === 'labelSize') setLabelSize(event.newValue || '40x28');
      else if (event.key === 'customLabelWidth') setCustomLabelWidth(event.newValue || '40');
      else if (event.key === 'customLabelHeight') setCustomLabelHeight(event.newValue || '28');
    };
    window.addEventListener('storage', applyStoredLabelSize);
    return () => window.removeEventListener('storage', applyStoredLabelSize);
  }, []);

  // Preset keys must stay in sync with PRODUCT_LABEL_PRESETS in backend/server.js —
  // the print popup writes these values back as the saved size.
  const resolveLabelSize = useCallback(() => {
    if (labelSize === 'custom') {
      return {
        labelWidth: parseInt(customLabelWidth, 10) || 40,
        labelHeight: parseInt(customLabelHeight, 10) || 28
      };
    }
    const [width, height] = String(labelSize).split('x').map(n => parseInt(n, 10));
    if (Number.isFinite(width) && Number.isFinite(height)) {
      return { labelWidth: width, labelHeight: height };
    }
    return { labelWidth: 40, labelHeight: 28 };
  }, [labelSize, customLabelWidth, customLabelHeight]);

  // Cache status for database refresh
  const [cacheStatus, setCacheStatus] = useState(null);
  const [isRefreshingCache, setIsRefreshingCache] = useState(false);

  // Bulk update tab selection (0 = location, 1 = weight)
  const [bulkUpdateTab, setBulkUpdateTab] = useState(0);
  const [bulkExpiryBusy, setBulkExpiryBusy] = useState(null); // 'on' | 'off' while the bulk expiry toggle runs

  // Confirmation for bulk-clearing W/H locations (blank writes are otherwise rejected)
  const [showBulkClearLocations, setShowBulkClearLocations] = useState(false);

  // Same-brand box rule: the pending placement the guard stopped, if any.
  // { mode: 'single' | 'bulk' | 'field', location, result, product?, productKey?, editable? }
  const [brandClashPrompt, setBrandClashPrompt] = useState(null);

  // "Put it here anyway" on the early (field-close) prompt, remembered as
  // productKey -> UPPERCASE tub, so the Save-time backstop does not ask the
  // same question twice. Typing a different tub voids the approval.
  const brandClashApprovedRef = useRef({});

  // Version tracking
  const [_versions, setVersions] = useState({
    frontend: packageJson.version,
    backend: 'loading...'
  });

  // Refresh Database modal
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [selectedStores, setSelectedStores] = useState(EMPTY_REFRESH_STORE_SELECTION);
  const [refreshRun, setRefreshRun] = useState(null);
  const [refreshTargetStatuses, setRefreshTargetStatuses] = useState({});
  const [refreshTargetErrors, setRefreshTargetErrors] = useState({});
  const refreshTargetStatusesRef = useRef({});
  const [plannerSyncJobs, setPlannerSyncJobs] = useState([]);
  const [plannerCostBackfillStatus, setPlannerCostBackfillStatus] = useState(null);
  const [plannerSupplierSkuSyncStatus, setPlannerSupplierSkuSyncStatus] = useState(null);

  // Save queue for non-blocking saves
  const [saveQueue, setSaveQueue] = useState([]);  // Array of { productKey, status: 'pending'|'saving'|'success'|'error', message }
  const [recentlySaved, setRecentlySaved] = useState(new Set());  // Track recently saved products to hide save button
  const isProcessingSaveRef = useRef(false);  // Serial-processing lock so one save runs at a time

  // Centralized product normalization and deduplication helpers
  // Normalize Shopify GID format to numeric ID
  const normalizeShopifyId = useCallback((id) => {
    if (!id) return '';
    const idStr = String(id);
    // If it's a Shopify GID like "gid://shopify/Product/7273944973348"
    const match = idStr.match(/\/(\d+)$/);
    return match ? match[1] : idStr.trim();
  }, []);

  const normalizeProduct = useCallback((p) => ({
    // Heal any raw {value} metafield objects (polluted cache / stale localStorage)
    // BEFORE they can reach a render — see utils/productLocationFields.js.
    ...normalizeProductLocationFields(p),
    id: normalizeShopifyId(p.id),
    variantId: normalizeShopifyId(p.variantId),
    storeDisplayName: (p.storeDisplayName || '').trim(),
  }), [normalizeShopifyId]);

  const mergeUniqueProducts = useCallback((existing, incoming) => {
    const map = new Map();
    const existingCount = existing.length;
    const incomingCount = incoming.length;

    [...existing, ...incoming].forEach(p => {
      const n = normalizeProduct(p);
      // Use SKU + store as the unique key, fall back to barcode or variantId for products without SKU.
      const skuPart = (n.sku || '').trim();
      const key = skuPart && skuPart !== 'N/A'
        ? `${n.storeDisplayName}-${skuPart}`
        : `${n.storeDisplayName}-${(n.barcode || '').trim() || n.variantId || n.id || Math.random()}`;
      if (!map.has(key)) {
        map.set(key, n);
      }
    });

    const result = Array.from(map.values());
    console.log(`  [mergeUniqueProducts] Summary: ${existingCount} existing + ${incomingCount} incoming = ${result.length} final (${existingCount + incomingCount - result.length} duplicates removed)`);
    return result;
  }, [normalizeProduct]);

  // Unified badge helpers for consistent store badges across all search types
  const getStoreBadgeTone = useCallback((storeDisplayName) => {
    if (storeDisplayName === 'Phaser FPV') return 'info';
    if (storeDisplayName === 'CCC') return 'warning-strong';
    if (storeDisplayName === 'Porlex') return 'attention';
    if (storeDisplayName === 'Redeemer') return 'magic';
    return 'info';
  }, []);

  const getStoreBadgeLabel = useCallback((storeDisplayName) => {
    if (storeDisplayName === 'Phaser FPV') return 'Phaser';
    return storeDisplayName; // CCC will display as CCC
  }, []);

  // Fetch backend version on mount (single source — also populates `versions.backend` so the
  // separate mount effect above no longer needs its own duplicate /api/health call).
  useEffect(() => {
    const fetchBackendVersion = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/health`);
        const v = response.data.version || 'unknown';
        setBackendVersion(v);
        setVersions(prev => ({ ...prev, backend: response.data.version }));
      } catch (error) {
        console.error('Failed to fetch backend version:', error);
        setBackendVersion('unknown');
        setVersions(prev => ({ ...prev, backend: 'error' }));
      }
    };
    fetchBackendVersion();
  }, []);

  // Debounced localStorage save (reduces I/O by 80%)
  const debouncedSaveProducts = useMemo(
    () => debounce((products) => {
      if (ENABLE_PERF_LOGGING) {
        console.log('[localStorage] Saving products:', products.length, products.map(p => p.sku));
      }
      const dataToSave = JSON.stringify(products);
      if (ENABLE_PERF_LOGGING) {
        console.log('[localStorage] Data size:', dataToSave.length, 'bytes');
      }
      localStorage.setItem('warehouseProducts', dataToSave);
      if (ENABLE_PERF_LOGGING) {
        console.log('[localStorage] Saved successfully');
      }
    }, 500),
    []
  );

  // localStorage persistence with debouncing
  useEffect(() => {
    debouncedSaveProducts(products);
  }, [products, debouncedSaveProducts]);

  const debouncedSaveEditableData = useMemo(
    () => debounce((data) => {
      localStorage.setItem('warehouseEditableData', JSON.stringify(data));
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSaveEditableData(editableData);
  }, [editableData, debouncedSaveEditableData]);

  const debouncedSaveSelectedProducts = useMemo(
    () => debounce((data) => {
      localStorage.setItem('warehouseSelectedProducts', JSON.stringify(data));
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSaveSelectedProducts(selectedProducts);
  }, [selectedProducts, debouncedSaveSelectedProducts]);

  // URL sync: Update URL when products change (but not during initial load)
  useEffect(() => {
    if (!hasLoadedInitially) return;
    const path = window.location.pathname.replace(/\/+$/, '');
    if (!isWarehousePath(path)) return;

    if (products.length > 0) {
      const skus = products.map(p => p.sku).filter(Boolean);
      const uniqueSkus = [...new Set(skus)];
      const params = new URLSearchParams();
      uniqueSkus.forEach(sku => params.append('sku', sku));
      const newSearch = params.toString();
      const currentSearch = window.location.search.replace(/^\?/, '');
      if (currentSearch === newSearch) return;

      const newUrl = `${window.location.pathname}?${newSearch}`;
      if (ENABLE_PERF_LOGGING) {
        console.log('[URL Sync] Syncing', uniqueSkus.length, 'unique SKUs');
      }
      window.history.replaceState({}, '', newUrl);
    } else if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [products, hasLoadedInitially]);

  // Strip warehouse ?sku= params when viewing other modules (they must not leak via history or URL builders).
  useEffect(() => {
    if (moduleView === 'home') return;
    const normalized = normalizeModuleUrl(window.location.pathname, window.location.search);
    if (!normalized) return;
    const current = `${window.location.pathname}${window.location.search || ''}`;
    if (current === normalized) return;
    window.history.replaceState({}, '', normalized);
  }, [moduleView]);

  // URL parse: Load products from URL on mount OR from localStorage if no URL
  useEffect(() => {
    if (urlInitStartedRef.current) return;
    urlInitStartedRef.current = true;

    const loadProductsFromUrl = async () => {
      const path = window.location.pathname.replace(/\/+$/, '');
      if (isPlannerPath(path)) {
        console.log('[Init] Inventory Planner URL — SKU params handled by planner module');
        setTimeout(() => setHasLoadedInitially(true), 100);
        return;
      }
      if (!isWarehousePath(path)) {
        console.log('[Init] Non-warehouse URL — ignoring ?sku= params');
        setTimeout(() => setHasLoadedInitially(true), 100);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      // Deduplicate SKUs from URL to prevent redundant API calls and UI glitches
      const rawSkus = urlParams.getAll('sku');
      const skus = [...new Set(rawSkus.map(s => s.trim()).filter(Boolean))];

      if (skus.length > 0) {
        // URL has SKUs - load from API CONCURRENTLY
        console.log('[Init] Loading from URL (CONCURRENT):', skus.length, 'SKUs');
        const startTime = ENABLE_PERF_LOGGING ? performance.now() : null;

        setIsLoadingFromUrl(true);
        setLoadingProgress({ current: 0, total: skus.length });

        // Load ALL products concurrently using Promise.allSettled
        // This is MUCH faster than one-by-one loading
        const canUseDirectAPI = USE_DIRECT_API && isDirectAPIAvailable();
        console.log('[Init] Using', canUseDirectAPI ? 'Direct API' : 'Backend API');

        const loadPromises = skus.map(async (sku, index) => {
          try {
            let productData;

            if (canUseDirectAPI) {
              productData = await searchProductDirect(sku.trim());
            } else {
              const response = await axios.get(`${API_URL}/api/products/search`, {
                params: { query: sku.trim() }
              });
              productData = response.data;
            }

            let productToAdd;
            if (Array.isArray(productData)) {
              // Multiple stores - add first one (or handle selection)
              productToAdd = productData[0];
            } else {
              productToAdd = productData;
            }

            // Increment progress
            setLoadingProgress(prev => ({ ...prev, current: prev.current + 1 }));

            console.log(`[Init] Loaded SKU ${sku}:`, productToAdd ? (productToAdd.title + ' (' + (productToAdd.variantTitle || 'Default') + ')') : 'NOT FOUND');
            return { success: true, product: productToAdd, sku, index };
          } catch (error) {
            console.error(`[Init] Failed to load SKU ${sku}:`, error.response?.data || error.message);
            // Even on failure, increment progress to keep the bar moving
            setLoadingProgress(prev => ({ ...prev, current: prev.current + 1 }));
            return { success: false, error, sku, index };
          }
        });

        // Wait for ALL to complete
        const results = await Promise.allSettled(loadPromises);

        if (ENABLE_PERF_LOGGING && startTime) {
          const duration = Math.round(performance.now() - startTime);
          console.log(`[PERF] URL loading (concurrent): ${duration}ms for ${skus.length} SKUs (${canUseDirectAPI ? 'Direct API' : 'Backend'})`);
        }

        // Process results in ORDER (maintains URL order)
        const items = results
          .filter(r => r.status === 'fulfilled' && r.value.success)
          .map(r => r.value.product)
          .filter(Boolean);

        console.log('[Init] Loaded', items.length, 'valid products from', skus.length, 'SKUs');

        if (items.length > 0) {
          setProducts(items); // Set directly instead of merging to prevent initial duplicates

          // Clear stale editable data from localStorage for these products
          // This prevents false "unsaved changes" when fresh API data differs from cached editable values
          setEditableData(prev => {
            const updated = { ...prev };
            items.forEach(item => {
              const productKey = `${item.storeDisplayName}-${item.id}-${item.variantId}`;
              if (updated[productKey]) {
                const fields = ['weight', 'barcode', 'binLocation', 'overflow1', 'overflow2', 'length', 'width', 'height'];
                fields.forEach(field => {
                  const editingKey = `${productKey}-${field}`;
                  // Clear field from editable state if not actively being edited
                  if (!editingFields[editingKey]) {
                    delete updated[productKey][field];
                  }
                });
                // Remove product entry if no fields remain
                if (Object.keys(updated[productKey]).length === 0) {
                  delete updated[productKey];
                }
              }
            });
            return updated;
          });
        } else {
          console.warn('[Init] No valid products were loaded from the URL SKUs');
        }

        console.log('[Init] Finished loading from URL');
        setLoadingProgress({ current: 0, total: 0 });
        setIsLoadingFromUrl(false);
        // Mark initial load as complete after products are IN STATE
        // Use a longer delay to ensure the products state has been applied before URL sync triggers
        setTimeout(() => {
          setHasLoadedInitially(true);
          console.log('[Init] Initial load marked complete');
        }, 500);
      } else {
        // No URL SKUs - products already loaded from localStorage in state initialization
        console.log('[Init] No URL params, products already loaded from localStorage:', products.length);
        // Just mark as complete
        setTimeout(() => setHasLoadedInitially(true), 100);
      }
    };

    loadProductsFromUrl();
  }, []); // Only run once on mount

  // Refresh all products
  // Retained but unwired: the Refresh button was replaced by Put Away Stock
  // (Rhys 2026-08-29). Re-loading a search or PO refetches the same data, so
  // nothing is lost — kept here because it is the only concurrent multi-store
  // refresh we have, and cheap to re-attach if a refresh action is wanted.
  // eslint-disable-next-line no-unused-vars
  const _refreshAllProducts = useCallback(async () => {
    if (products.length === 0) return;

    console.log('[refreshAllProducts] Starting refresh of', products.length, 'products');
    const startTime = ENABLE_PERF_LOGGING ? performance.now() : null;

    setIsLoading(true);

    try {
      const canUseDirectAPI = USE_DIRECT_API && isDirectAPIAvailable();
      let refreshedProducts = [];

      if (canUseDirectAPI) {
        console.log('[refreshAllProducts] Using Direct API for concurrent refresh');

        // Use Direct API - refresh all products concurrently
        const refreshPromises = products.map(async (product) => {
          try {
            const productData = await refreshProductDirect(product);

            // Preserve linkedStores if it exists
            if (product.linkedStores) {
              productData.linkedStores = product.linkedStores;
            }

            // Preserve editable data
            const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
            if (editableData[productKey]) {
              if (editableData[productKey].weight !== undefined) {
                productData.weight = editableData[productKey].weight;
              }
              if (editableData[productKey].barcode !== undefined) {
                productData.barcode = editableData[productKey].barcode;
              }
            }

            return { status: 'fulfilled', value: productData };
          } catch (error) {
            console.error(`Failed to refresh product ${product.sku}:`, error);
            return { status: 'fulfilled', value: product }; // Keep original on error
          }
        });

        const results = await Promise.all(refreshPromises);
        refreshedProducts = results.map(r => r.value);

      } else {
        console.log('[refreshAllProducts] Using backend API');

        // Use backend - refresh sequentially
        for (const product of products) {
          try {
            const response = await axios.get(`${API_URL}/api/products/refresh`, {
              params: {
                query: product.sku || product.barcode,
                store: product.store
              }
            });

            const productData = response.data;
            const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;

            // Preserve linkedStores if it exists
            if (product.linkedStores) {
              productData.linkedStores = product.linkedStores;
            }

            if (editableData[productKey]) {
              if (editableData[productKey].weight !== undefined) {
                productData.weight = editableData[productKey].weight;
              }
              if (editableData[productKey].barcode !== undefined) {
                productData.barcode = editableData[productKey].barcode;
              }
            }

            refreshedProducts.push(productData);
          } catch (error) {
            console.error(`Failed to refresh product ${product.sku}:`, error);
            refreshedProducts.push(product);
          }
        }
      }

      if (ENABLE_PERF_LOGGING && startTime) {
        const duration = Math.round(performance.now() - startTime);
        console.log(`[PERF] refreshAllProducts: ${duration}ms for ${products.length} products (${canUseDirectAPI ? 'Direct API' : 'Backend'})`);
      }

      if (refreshedProducts.length > 0) {
        setProducts(refreshedProducts);
        setSuccessMessage(`Refreshed ${refreshedProducts.length} products`);
      }

    } catch (error) {
      console.error('[refreshAllProducts] Error:', error);
      setError('Failed to refresh products');
    } finally {
      setIsLoading(false);
    }
  }, [products, editableData]);

  // Refresh on mount - ensure fresh data for restored product list (once per session)
  useEffect(() => {
    if (products.length === 0 || initialBgRefreshScheduledRef.current) return;
    initialBgRefreshScheduledRef.current = true;

    if (ENABLE_PERF_LOGGING) {
      console.log('🔄 [Init] Scheduling background refresh for existing products');
    }
    products.forEach(p => {
      if (p.sku && p.sku !== 'N/A') {
        pendingRefreshSKUs.current.add(p.sku);
      }
    });
    scheduleBackgroundRefresh();
    focusSearchInput();
  }, [products.length]); // scheduleBackgroundRefresh defined below; closure captures binding

  // Clear messages
  useEffect(() => {
    if (error || successMessage) {
      const timer = setTimeout(() => {
        setError('');
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, successMessage]);

  // Perform location search (extracted to be reusable)
  const performLocationSearch = async (query, signal = null) => {
    const startTime = ENABLE_PERF_LOGGING ? performance.now() : null;
    console.log('[performLocationSearch] Searching for location:', query);
    const locationType = getLocationType(query);
    const normalizedLocation = normalizeLocation(query);

    // CRITICAL: Clear all products BEFORE starting location search
    // This prevents products from previous search mixing with new search
    console.log('[performLocationSearch] Clearing existing products before search');
    setProducts([]);
    setSuccessMessage(''); // Clear previous success message

    try {
      const response = await retryWithBackoff(
        async () => {
          return await axios.get(`${API_URL}/api/products/search-by-location`, {
            params: { location: normalizedLocation },
            signal // Pass abort signal
          });
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          shouldRetry: isRetryableError
        }
      );

      const locationProducts = response.data;

      if (ENABLE_PERF_LOGGING && startTime) {
        const duration = Math.round(performance.now() - startTime);
        console.log(`[PERF] Location search (${locationType}): ${duration}ms, found ${locationProducts.length} products`);
      }

      if (locationProducts.length === 0) {
        setError(`No products found at location ${normalizedLocation}`);
        setSearchQuery('');
      } else {
        // OPTIMIZATION: Set all products at once instead of one-by-one
        // This is much faster and prevents flashing as products appear incrementally
        console.log(`[performLocationSearch] Setting ${locationProducts.length} products at once`);
        setProducts(locationProducts);
        setSuccessMessage(`Loaded ${locationProducts.length} product(s) from ${normalizedLocation}`);
        setSearchQuery('');

        // Queue background refresh for these products
        locationProducts.forEach(p => {
          if (p.sku && p.sku !== 'N/A') {
            pendingRefreshSKUs.current.add(p.sku);
          }
        });
        scheduleBackgroundRefresh();
      }
    } catch (err) {
      // Don't show error if request was cancelled (user started new search)
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.message?.includes('cancel')) {
        console.log('[performLocationSearch] Request cancelled - user started new search');
        return;
      }

      setError(err.response?.data?.error || err.message || 'Location search failed');
    } finally {
      focusSearchInput();
    }
  };

  // "Load in grid" from the warehouse map insights panel: batch-load the tab's SKUs
  // into the Products grid (replaces the grid, like a location search does).
  // Cap kept in sync with MAX_GRID_LOAD_SKUS in WarehouseInsightsPanel.js.
  const loadMapSkusIntoGrid = async (skus) => {
    const seen = new Set();
    const uniqueSkus = [];
    (Array.isArray(skus) ? skus : []).forEach((raw) => {
      const sku = String(raw || '').trim();
      const key = sku.toUpperCase();
      if (!sku || key === 'N/A' || seen.has(key)) return;
      seen.add(key);
      uniqueSkus.push(sku);
    });
    const cappedSkus = uniqueSkus.slice(0, 100);
    if (cappedSkus.length === 0) return;

    // Switch to the Products view first so staff see the grid fill in.
    setBackdropLeaving(null);
    setJobsHubOpenToTab(null);
    setModuleView('home');
    window.history.pushState({}, '', productsHomePath());

    beginSearchLoading();
    setError('');
    setSuccessMessage('');
    setLoadingProgress({ current: 0, total: cappedSkus.length });

    try {
      const response = await axios.post(`${API_URL}/api/products/batch-search-cache`, {
        skus: cappedSkus
      });
      const loadedProducts = response.data?.products || [];
      const notFoundCount = (response.data?.notFound || []).length;
      setLoadingProgress({ current: cappedSkus.length, total: cappedSkus.length });

      if (loadedProducts.length === 0) {
        setError('None of the map products were found in the product database');
        return;
      }

      setProducts(loadedProducts);
      const missingNote = notFoundCount > 0
        ? ` — ${notFoundCount} SKU${notFoundCount === 1 ? '' : 's'} not found`
        : '';
      setSuccessMessage(`Loaded ${loadedProducts.length} product${loadedProducts.length === 1 ? '' : 's'} from the map${missingNote}`);

      // Queue live refresh so stock levels top up in the background (same as scan flow).
      loadedProducts.forEach((p) => {
        if (p.sku && p.sku !== 'N/A') {
          pendingRefreshSKUs.current.add(p.sku);
        }
      });
      scheduleBackgroundRefresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load map products into the grid');
    } finally {
      setLoadingProgress({ current: 0, total: 0 });
      endSearchLoading();
    }
  };

  // Unified background refresh system with 200ms debounce
  const scheduleBackgroundRefresh = useCallback(() => {
    // Clear existing timer
    if (refreshDebounceTimer.current) {
      clearTimeout(refreshDebounceTimer.current);
    }

    // Schedule new refresh after 200ms of no new products
    refreshDebounceTimer.current = setTimeout(() => {
      executeBackgroundRefreshRef.current?.();
    }, 200);
  }, []);

  const poViewerOpenGenerationRef = useRef(0);
  const poViewerLastFetchedGenerationRef = useRef(-1);

  // Fetch POs for current tab/page while PO viewer is open.
  useEffect(() => {
    if (!showPOViewer) {
      poViewerOpenGenerationRef.current += 1;
      return;
    }

    const pageToFetch = poFilterQuery ? 1 : poPage;
    const generation = poViewerOpenGenerationRef.current;
    const firstFetchThisOpen = poViewerLastFetchedGenerationRef.current !== generation;
    // On open, reuse a page fetched < 60 s ago; older pages refetch (skipCache)
    // but the rest of the client cache is kept. Opening Incoming Stock used
    // to wipe every cached tab.
    let skipCache = false;
    if (firstFetchThisOpen) {
      const cachedEntry = getCachedPoResponseEntry(
        buildPoResponseCacheKey(pageToFetch, poStatusTab, poFilterQuery, true),
        { allowStale: true }
      );
      skipCache = !(cachedEntry && cachedEntry.age < PO_OPEN_CACHE_REUSE_MS);
    }

    fetchPurchaseOrders(false, {
      page: pageToFetch,
      statusTab: poStatusTab,
      query: poFilterQuery,
      skipCache
    }).finally(() => {
      poViewerLastFetchedGenerationRef.current = generation;
    });
  }, [showPOViewer, poPage, poStatusTab, poFilterQuery, getCachedPoResponseEntry, buildPoResponseCacheKey]);

  useEffect(() => {
    if (!showPOViewer || poFilterQuery) return;

    let cancelled = false;
    const warmTabs = PURCHASE_ORDER_TABS.filter(tab => tab !== poStatusTab);

    const timer = setTimeout(async () => {
      const prefetchResults = await Promise.allSettled(warmTabs.map(async (tab) => {
        if (cancelled) return;

        const warmKey = `${tab}|${poLimit}`;
        const lastWarmedAt = poTabPrefetchRef.current.get(warmKey) || 0;
        if ((Date.now() - lastWarmedAt) < PO_PREFETCH_COOLDOWN_MS) return;

        await prefetchPurchaseOrdersTab(tab);
        poTabPrefetchRef.current.set(warmKey, Date.now());
      }));

      prefetchResults.forEach((result, index) => {
        if (result.status === 'rejected' && !cancelled) {
          console.warn(`[PO] Prefetch failed for tab ${warmTabs[index]}:`, result.reason?.message || result.reason);
        }
      });
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showPOViewer, poFilterQuery, poLimit, poStatusTab, prefetchPurchaseOrdersTab, PO_PREFETCH_COOLDOWN_MS]);

  useEffect(() => {
    if (!poFilterQuery) return;
    if (poPage !== 1) setPoPage(1);
  }, [poFilterQuery, poPage]);

  // Fetch preorder SKU list when PO view is shown (for "part of a preorder" links)
  useEffect(() => {
    if (!showPOViewer) return;

    let cancelled = false;
    const cache = preorderSkuCacheRef.current;
    const applySkuSet = (skuSet) => {
      if (!cancelled) {
        setPreorderSkusSet(new Set(skuSet));
      }
    };

    if (cache.skus.size > 0 && (Date.now() - cache.fetchedAt) < PREORDER_SKU_CACHE_MAX_AGE_MS) {
      applySkuSet(cache.skus);
      return () => { cancelled = true; };
    }

    if (!cache.promise) {
      cache.promise = axios.get(`${API_URL}/api/preorders/skus`, { withCredentials: true })
        .then(res => {
          const nextSkus = new Set(
            Object.entries(res.data?.skusByStore || {}).flatMap(([store, skus]) =>
              (Array.isArray(skus) ? skus : []).map(sku => `${store}:${String(sku).trim().toUpperCase()}`))
          );
          cache.skus = nextSkus;
          cache.fetchedAt = Date.now();
          return nextSkus;
        })
        .catch(() => {
          if (cache.skus.size > 0) {
            return cache.skus;
          }
          return new Set();
        })
        .finally(() => {
          cache.promise = null;
        });
    }

    cache.promise.then(applySkuSet);
    return () => { cancelled = true; };
  }, [showPOViewer]);

  // Scroll to PO card when opening from Preorders (poNavigateTarget)
  useEffect(() => {
    if (!showPOViewer || !poNavigateTarget) return;
    const { poId } = poNavigateTarget;
    const timer = requestAnimationFrame(() => {
      const el = poCardRefs.current[poId];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        setPoNavigateTarget(null);
      }
    });
    return () => cancelAnimationFrame(timer);
  }, [showPOViewer, poNavigateTarget, purchaseOrders.length]);

  // Keep POs current without interrupting staff returning to the tab after a short break.
  useEffect(() => {
    if (!showPOViewer || showCacheModal || showRefreshModal) return;

    const fetchCurrentViewIfStale = (reason) => {
      const age = Date.now() - (poLastFetchedAtRef.current || 0);
      if (age < PO_VISIBLE_REFRESH_STALE_MS) return;

      console.log(`🔄 [PO] Refreshing purchase orders after ${Math.round(age / 1000)}s (${reason})`);
      fetchPurchaseOrders(false, { page: poFilterQuery ? 1 : poPage, statusTab: poStatusTab, query: poFilterQuery });
    };

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchCurrentViewIfStale('interval');
      }
    }, PO_AUTO_REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && showPOViewer) {
        fetchCurrentViewIfStale('visible');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showPOViewer, showCacheModal, showRefreshModal, poPage, poStatusTab, poFilterQuery, PO_AUTO_REFRESH_INTERVAL_MS, PO_VISIBLE_REFRESH_STALE_MS]);

  const executeBackgroundRefresh = useCallback(async () => {
    if (pendingRefreshSKUs.current.size === 0) return;

    const skusToRefresh = Array.from(pendingRefreshSKUs.current);
    pendingRefreshSKUs.current.clear();

    if (ENABLE_PERF_LOGGING) {
      console.log(`🔄 [BG-REFRESH] Starting refresh for ${skusToRefresh.length} SKUs after 200ms debounce`);
    }
    const startTime = performance.now();

    try {
      // ONE batch-refresh-live call for every queued SKU (was one
      // /products/search per SKU). Fields the batch endpoint does not carry
      // (dbBinLocation, history, noBarcodeAck, imageUrl) keep the cached row's
      // values, which came from the same product cache seconds earlier.
      const liveBySku = await fetchLiveStockBatch(axios, API_URL, skusToRefresh);
      const successfulRefreshes = skusToRefresh.filter((sku) => getLiveStockUpdate(liveBySku, sku));

      if (successfulRefreshes.length > 0) {
        setProducts((prev) => applyLiveStockUpdates(prev, liveBySku, { isPlaceholderBarcode }));

        if (ENABLE_PERF_LOGGING) {
          const duration = Math.round(performance.now() - startTime);
          console.log(`🎉 [BG-REFRESH] Complete! Refreshed ${successfulRefreshes.length}/${skusToRefresh.length} products in ${duration}ms`);
        }
      }
    } catch (error) {
      console.error('❌ [BG-REFRESH] Error:', error);
    }
  }, []);

  executeBackgroundRefreshRef.current = executeBackgroundRefresh;

  // Helper function for title/keyword search
  const performTitleSearch = async (query, signal, _isMultiSearch = false) => {
    console.log('🔍 [TITLE-SEARCH] Searching for:', query);

    const store = storeFilterRef.current;
    const inStoreLabel = store ? ` in ${getStoreBadgeLabel(store)}` : '';
    try {
      const titleResponse = await axios.get(`${API_URL}/api/products/search-by-title`, {
        params: store ? { query, store } : { query },
        signal
      });

      const matchedProducts = titleResponse.data.products || [];
      console.log(`✅ [TITLE-SEARCH] Found ${matchedProducts.length} products`);

      // Deduplicate by SKU alone - keep only the first occurrence (highest priority store)
      const seenSkus = new Set();
      const deduplicatedProducts = matchedProducts.filter(p => {
        const sku = (p.sku || '').trim().toUpperCase();
        if (seenSkus.has(sku)) {
          return false;
        }
        seenSkus.add(sku);
        return true;
      });
      console.log(`  [TITLE-SEARCH] After SKU deduplication: ${deduplicatedProducts.length} products`);

      if (deduplicatedProducts.length === 0) {
        setError(`No products found for: "${query}"${inStoreLabel}`);
        return;
      }

      // STEP 1: Display products immediately (no waiting for live data)
      console.log(`⚡ [TITLE-SEARCH] Displaying ${deduplicatedProducts.length} products immediately`);

      if (deduplicatedProducts.length === 1) {
        addProductToList(deduplicatedProducts[0], { fromSearch: true });
      } else {
        setProducts(prev => {
          const merged = mergeUniqueProducts(prev, deduplicatedProducts);
          return merged;
        });
        setSuccessMessage(`Added ${deduplicatedProducts.length} products matching: "${query}"${inStoreLabel}`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }

      // STEP 2: Background batch refresh for live inventory data (ONE API call)
      const skusToRefresh = deduplicatedProducts.map(p => p.sku).filter(Boolean);
      if (skusToRefresh.length > 0) {
        console.log(`🔄 [TITLE-SEARCH] Starting background batch refresh for ${skusToRefresh.length} SKUs...`);

        // Fire and forget - don't await, let it run in background
        axios.post(`${API_URL}/api/products/batch-refresh-live`, { skus: skusToRefresh })
          .then(refreshResponse => {
            const updates = refreshResponse.data.updates || [];
            console.log(`✅ [TITLE-SEARCH] Background refresh complete: ${updates.length} products updated in ${refreshResponse.data.duration}ms`);

            if (updates.length > 0) {
              // Update products with live data
              setProducts(prev => prev.map(p => {
                const update = updates.find(u => u.sku === p.sku);
                if (update) {
                  return {
                    ...p,
                    stockOnHand: update.stockOnHand ?? p.stockOnHand,
                    stockAvailable: update.stockAvailable ?? p.stockAvailable,
                    stockCommitted: update.stockCommitted ?? p.stockCommitted,
                    barcode: update.barcode || p.barcode,
                    barcodes: Array.isArray(update.barcodes) && update.barcodes.length ? update.barcodes : p.barcodes,
                    binLocation: update.binLocation || p.binLocation,
                    length: update.length ?? p.length,
                    width: update.width ?? p.width,
                    height: update.height ?? p.height,
                    inventoryLevels: update.inventoryLevels || p.inventoryLevels,
                    fromCache: false
                  };
                }
                return p;
              }));
            }
          })
          .catch(err => {
            console.warn('⚠️ [TITLE-SEARCH] Background refresh failed:', err.message);
          });
      }
    } catch (titleError) {
      if (titleError.response?.status === 404 || titleError.response?.data?.products?.length === 0) {
        setError(`No products found for: "${query}"${inStoreLabel}`);
      } else {
        throw titleError;
      }
    }
  };

  const handleSearch = async (query, isMultiSearch = false) => {
    if (!query.trim()) return;

    console.log('[handleSearch] Searching for:', query);
    console.log('[handleSearch] isLoadingFromUrl:', isLoadingFromUrl);
    console.log('[handleSearch] Current products:', products.length);
    console.log('[handleSearch] isMultiSearch:', isMultiSearch);

    // OPTIMIZATION: Cancel any pending search request ONLY if not in multi-search mode
    if (!isMultiSearch && activeSearchController.current) {
      console.log('[handleSearch] Cancelling previous search request');
      activeSearchController.current.abort();
      activeSearchController.current = null;
    }

    // Create new cancel token for this search ONLY if not in multi-search mode
    // In multi-search mode, let all searches complete without abort capability
    let signal;
    if (!isMultiSearch) {
      activeSearchController.current = new AbortController();
      signal = activeSearchController.current.signal;
    }

    // Performance monitoring
    const _startTime = ENABLE_PERF_LOGGING ? performance.now() : null;

    beginSearchLoading();
    setError('');

    try {
      // Check if query is a location (P1-P200 or Z-01 to S-01 format)
      const isLocation = isLocationQuery(query);

      if (isLocation) {
        // Location search - check if we need to warn about clearing existing products
        if (products.length > 0) {
          // Store the search details and show warning
          setPendingLocationSearch({ query, isLocation: true });
          setShowLocationWarning(true);
          endSearchLoading();
          // Clear the active controller since we're not proceeding
          activeSearchController.current = null;
          return; // Wait for user confirmation
        }

        // No existing products, proceed with search
        await performLocationSearch(query, signal);
      } else {
        // Check if query looks like a keyword/brand search (only letters, 3+ chars, no digits)
        const isKeywordSearch = /^[a-zA-Z]{3,}$/.test(query.trim());

        if (isKeywordSearch) {
          // Go directly to title search for keyword queries (e.g., "polymaker", "emax")
          console.log('🔍 [TITLE-SEARCH] Keyword detected, searching by title:', query.trim());
          await performTitleSearch(query.trim(), signal, isMultiSearch);
        } else {
          // Try regular barcode/SKU search first
          console.log('🔍 [SEARCH] Trying SKU/barcode search for:', query.trim());

          const searchStartTime = performance.now();
          let foundProduct = false;

          try {
            // Phase 1: Try to load from cache instantly
            const cacheResponse = await axios.post(`${API_URL}/api/products/batch-search-cache`, {
              skus: [query.trim()]
            });

            if (cacheResponse.data.products.length > 0) {
              console.log(`⚡ [SEARCH] Found in cache in ${cacheResponse.data.duration}ms`);
              const cachedProducts = cacheResponse.data.products;
              foundProduct = true;

              // Check if multiple stores (array length > 1 means linked stores)
              if (cachedProducts.length > 1) {
                if (isMultiSearch) {
                  // Auto-select highest priority store
                  addProductToList(cachedProducts[0], { fromSearch: true });
                  // Queue for background refresh
                  pendingRefreshSKUs.current.add(query.trim());
                } else {
                  // Show store selector
                  setMultiStoreProducts(cachedProducts);
                  setSelectedStoreProducts([]);
                  setShowStoreSelector(true);
                  // Queue for background refresh
                  pendingRefreshSKUs.current.add(query.trim());
                }
              } else {
                // Single store
                addProductToList(cachedProducts[0], { fromSearch: true });
                // Queue for background refresh
                pendingRefreshSKUs.current.add(query.trim());
              }

              // Schedule unified background refresh (200ms debounce)
              scheduleBackgroundRefresh();

            } else {
              // Not in cache - try live API
              console.log('🔍 [SEARCH] Not in cache, searching live API...');
              try {
                const liveResponse = await axios.get(`${API_URL}/api/products/search`, {
                  params: { query: query.trim() },
                  signal
                });

                const productData = liveResponse.data;
                foundProduct = true;

                // Check if it's an array (multiple stores) or single product
                if (Array.isArray(productData) && productData.length > 1) {
                  if (isMultiSearch) {
                    addProductToList(productData[0], { fromSearch: true });
                  } else {
                    setMultiStoreProducts(productData);
                    setSelectedStoreProducts([]);
                    setShowStoreSelector(true);
                  }
                } else {
                  const singleProduct = Array.isArray(productData) ? productData[0] : productData;
                  addProductToList(singleProduct, { fromSearch: true });
                }
              } catch (liveError) {
                // Live search failed (404 = not found)
                if (liveError.response?.status === 404) {
                  console.log('🔍 [SEARCH] Not found by SKU/barcode, will try title search');
                  foundProduct = false;
                } else {
                  throw liveError; // Re-throw other errors
                }
              }
            }

            // If not found by SKU/barcode, fall back to the shared title search
            // (displays matches immediately, then ONE batch-refresh-live call —
            // this inline copy used to fire one /products/search per match).
            if (!foundProduct) {
              console.log('🔍 [TITLE-SEARCH] Fallback to title keyword search:', query.trim());
              await performTitleSearch(query.trim(), signal, isMultiSearch);
            }

            const totalDuration = Math.round(performance.now() - searchStartTime);
            console.log(`🎉 [SEARCH] Complete in ${totalDuration}ms`);

          } catch (searchError) {
            // General search error (not 404)
            console.error('⚠️ [SEARCH] Error:', searchError.message);
            throw searchError;
          }
        } // End of else (not keyword search)
      }
    } catch (err) {
      // Don't show error if request was cancelled (user started new search)
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.message?.includes('cancel')) {
        console.log('[handleSearch] Request cancelled - user started new search');
        return;
      }

      setError(err.response?.data?.error || err.message || 'Product not found');
    } finally {
      endSearchLoading();
      // Clear the active controller
      if (activeSearchController.current) {
        activeSearchController.current = null;
      }
      focusSearchInput();
    }
  };

  // Handle "Show All Variants" toggle
  const handleShowAllVariantsToggle = async (checked) => {
    setShowAllVariants(checked);

    if (!checked || products.length === 0) {
      return; // If unchecked or no products, just toggle state
    }

    // Get the first product's productId and store to fetch all its variants
    const firstProduct = products[0];
    if (!firstProduct) {
      setError('No product selected to show variants');
      return;
    }

    // productId is the parent product GID like "gid://shopify/Product/123"
    // id is the variant GID like "gid://shopify/ProductVariant/456"
    const productIdentifier = firstProduct.productId || firstProduct.id;
    if (!productIdentifier) {
      setError('Product ID not found');
      return;
    }

    console.log(`🔍 [VARIANTS] Product data:`, {
      id: firstProduct.id,
      productId: firstProduct.productId,
      variantId: firstProduct.variantId,
      sku: firstProduct.sku,
      store: firstProduct.store,
      storeDisplayName: firstProduct.storeDisplayName
    });

    // Map store display name to store URL if needed
    const getStoreUrl = (product) => {
      if (product.store && product.store.includes('.myshopify.com')) {
        return product.store;
      }
      // Map from display name to URL
      const storeMap = {
        'Phaser FPV': 'phaser-fpv.myshopify.com',
        'CCC': 'central-coast-coffee.myshopify.com',
        'Central Coast Coffee': 'central-coast-coffee.myshopify.com',
        'Porlex': 'porlex-australia.myshopify.com',
        'Redeemer': 'ace176-c7.myshopify.com'
      };
      return storeMap[product.storeDisplayName] || product.store;
    };

    const storeUrl = getStoreUrl(firstProduct);
    if (!storeUrl) {
      setError('Could not determine store for product');
      return;
    }

    try {
      setIsLoading(true);
      console.log(`🔍 [VARIANTS] Fetching all variants for product ${productIdentifier} from store ${storeUrl}`);

      const response = await axios.get(`${API_URL}/api/products/variants`, {
        params: {
          productId: productIdentifier,
          store: storeUrl
        }
      });

      const variants = response.data.variants || [];
      console.log(`✅ [VARIANTS] Found ${variants.length} total variants`);

      if (variants.length > 0) {
        // Live inventory for all variants in ONE batch-refresh-live call (was
        // one /products/search per variant). Price / image / store come from
        // the /products/variants payload itself.
        const liveBySku = await fetchLiveStockBatch(axios, API_URL, variants.map((variant) => variant.sku));
        const enrichedVariants = variants.map((variant) => {
          const liveData = getLiveStockUpdate(liveBySku, variant.sku);
          if (!liveData) return variant;
          return {
            ...variant,
            stockAvailable: liveData.stockAvailable || 0,
            stockOnHand: liveData.stockOnHand || 0,
            stockCommitted: liveData.stockCommitted || 0,
            inventoryLevels: liveData.inventoryLevels || [],
            price: variant.price || '0.00',
            storeDisplayName: variant.storeDisplayName || 'Unknown',
            imageUrl: variant.imageUrl || null
          };
        });

        // Replace current products with all variants
        setProducts(enrichedVariants);

        // Clear any stale editable data for variant products (unless actively editing)
        setEditableData(prev => {
          const updated = { ...prev };
          enrichedVariants.forEach(variant => {
            const productKey = `${variant.storeDisplayName}-${variant.id}-${variant.variantId}`;
            if (updated[productKey]) {
              const fields = ['weight', 'barcode', 'binLocation', 'overflow1', 'overflow2'];
              fields.forEach(field => {
                const editingKey = `${productKey}-${field}`;
                if (!editingFields[editingKey]) {
                  delete updated[productKey][field];
                }
              });
              if (Object.keys(updated[productKey]).length === 0) {
                delete updated[productKey];
              }
            }
          });
          return updated;
        });

        setSuccessMessage(`Showing all ${enrichedVariants.length} variants of ${firstProduct.title}`);
        setTimeout(() => setSuccessMessage(''), 3000);

        // Auto-uncheck the checkbox so it doesn't apply to next search
        setShowAllVariants(false);
      } else {
        setError(`No variants found for "${firstProduct.title}"`);
        setShowAllVariants(false);
      }
    } catch (err) {
      console.error('Failed to fetch variants:', err);
      setError('Failed to load all variants: ' + (err.response?.data?.error || err.message));
      setShowAllVariants(false); // Revert checkbox
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "Show all products from this brand" toggle - sibling of the variants
  // toggle: replaces the grid with every cached product of the first card's
  // vendor in that store, then unchecks itself so it doesn't apply to the next
  // search. Served from the Firestore cache (stock as of the last refresh).
  const handleShowAllFromBrandToggle = async (checked) => {
    setShowAllFromBrand(checked);

    if (!checked || products.length === 0) {
      return;
    }

    // The card may lack `vendor` (live search results); the server resolves
    // the brand from its cache by SKU / product id in that case.
    const firstProduct = products[0];
    const vendor = String(firstProduct?.vendor || '').trim();
    const skuLabel = firstProduct?.sku || firstProduct?.title || 'this product';

    const getStoreUrl = (product) => {
      if (product.store && product.store.includes('.myshopify.com')) {
        return product.store;
      }
      const storeMap = {
        'Phaser FPV': 'phaser-fpv.myshopify.com',
        'CCC': 'central-coast-coffee.myshopify.com',
        'Central Coast Coffee': 'central-coast-coffee.myshopify.com',
        'Porlex': 'porlex-australia.myshopify.com',
        'Redeemer': 'ace176-c7.myshopify.com'
      };
      return storeMap[product.storeDisplayName] || product.store;
    };

    const storeUrl = getStoreUrl(firstProduct);
    if (!storeUrl) {
      setError(`Could not determine store for ${skuLabel}`);
      setShowAllFromBrand(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log(`🔍 [BRAND] Fetching all products for vendor "${vendor || '(resolve from ' + skuLabel + ')'}" from store ${storeUrl}`);

      const response = await axios.get(`${API_URL}/api/products/by-vendor`, {
        params: {
          vendor: vendor || undefined,
          sku: firstProduct.sku || undefined,
          productId: firstProduct.productId || firstProduct.id || undefined,
          store: storeUrl
        }
      });

      const brand = response.data.vendor || vendor;
      const brandProducts = (response.data.products || []).map(normalizeProduct);
      console.log(`✅ [BRAND] Found ${brandProducts.length} products for "${brand}"`);

      if (brandProducts.length > 0) {
        setProducts(brandProducts);

        // Drop stale edits for the incoming cards (unless actively editing),
        // same as the variants toggle.
        setEditableData(prev => {
          const updated = { ...prev };
          brandProducts.forEach(product => {
            const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
            if (updated[productKey]) {
              const fields = ['weight', 'barcode', 'binLocation', 'overflow1', 'overflow2'];
              fields.forEach(field => {
                if (!editingFields[`${productKey}-${field}`]) {
                  delete updated[productKey][field];
                }
              });
              if (Object.keys(updated[productKey]).length === 0) {
                delete updated[productKey];
              }
            }
          });
          return updated;
        });

        const truncatedNote = response.data.truncated
          ? ` (first ${response.data.limit || brandProducts.length} only - the grid caps at ${response.data.limit || 200})`
          : '';
        setSuccessMessage(`Showing all ${brandProducts.length} ${brand} products${truncatedNote}`);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        // Keep the grid, but remember the resolved brand on the first card.
        if (!vendor && brand) {
          setProducts(prev => prev.map((p, i) => (i === 0 ? { ...p, vendor: brand } : p)));
        }
        setError(`No products found for brand "${brand}" in ${storeUrl}`);
      }
    } catch (err) {
      console.error('Failed to fetch brand products:', err);
      setError(`Failed to load brand products for ${skuLabel} (${storeUrl}): ${err.response?.data?.error || err.message}`);
    } finally {
      setShowAllFromBrand(false); // One-shot, like the variants toggle
      setIsLoading(false);
    }
  };

  const addProductToList = (productData, { fromSearch = false } = {}) => {
    const addStartTime = performance.now();
    console.log('➕ [addProductToList] Starting add for SKU:', productData.sku, 'Store:', productData.storeDisplayName);

    // Check for duplicates and limits BEFORE merging
    setProducts(prev => {
      // Check for duplicates using SKU + store (same as mergeUniqueProducts)
      const normalized = normalizeProduct(productData);
      const key = `${normalized.storeDisplayName}-${(normalized.sku || '').trim()}`;
      const isDuplicate = prev.some(p => {
        const n = normalizeProduct(p);
        const existingKey = `${n.storeDisplayName}-${(n.sku || '').trim()}`;
        return existingKey === key;
      });

      if (isDuplicate) {
        console.log('  ⚠️  [addProductToList] Duplicate detected - not adding');
        setError(`Product "${productData.title}" (${productData.sku}) is already in the list!`);
        return prev;
      }

      if (prev.length >= 60) {
        console.log('  ⚠️  [addProductToList] Maximum products reached (60/60)');
        setError('Maximum 60 products allowed!');
        return prev;
      }

      // Use centralized merge to add product
      const merged = mergeUniqueProducts(prev, [productData]);
      // Normalize the product data to ensure consistent keys
      const normalizedProductData = normalizeProduct(productData);
      const productKey = `${normalizedProductData.storeDisplayName}-${normalizedProductData.id}-${normalizedProductData.variantId}`;

      // DO NOT set editable data here - it should only be set when user actually edits a field
      // Setting it here with current values causes false "unsaved changes" green border

      setSelectedProducts(prevSel => ({ ...prevSel, [productKey]: true }));

      // Initialize label quantity to 1 for search results
      setLabelQuantities(prev => ({ ...prev, [productKey]: 1 }));

      setSuccessMessage('Product added!');

      // Auto-focus workflow: skip when adding via search scan so the search bar keeps focus
      if (!fromSearch) {
        if (!productData.barcode) {
          console.log('  🎯 [addProductToList] Barcode missing - triggering auto-focus on barcode field');
          setTimeout(() => {
            setEditingFields(prev => ({ ...prev, [`${productKey}-barcode`]: true }));
            setAutoFocusProduct({ productKey, field: 'barcode' });
          }, 100);
        } else if (!productData.weight || productData.weight === 0) {
          // If barcode exists but weight is missing, auto-focus weight
          console.log('  🎯 [addProductToList] Weight missing - triggering auto-focus on weight field');
          setTimeout(() => {
            setEditingFields(prev => ({ ...prev, [`${productKey}-weight`]: true }));
            setAutoFocusProduct({ productKey, field: 'weight' });
          }, 100);
        }
      }

      const totalDuration = Math.round(performance.now() - addStartTime);
      console.log('  ✅ [addProductToList] Product added! Total duration:', totalDuration, 'ms | New count:', merged.length);
      return merged.slice(0, 60); // Enforce 60 product limit
    });
  };

  const confirmStoreSelection = () => {
    if (selectedStoreProducts.length === 0) {
      setError('Please select at least one product');
      return;
    }

    // Add all selected products
    selectedStoreProducts.forEach(product => {
      addProductToList(product, { fromSearch: true });
    });

    setShowStoreSelector(false);
    setMultiStoreProducts([]);
    setSelectedStoreProducts([]);
    setSuccessMessage(`Added ${selectedStoreProducts.length} product(s)!`);
  };

  const toggleStoreProductSelection = (product) => {
    const isSelected = selectedStoreProducts.some(p =>
      p.id === product.id && p.variantId === product.variantId && p.storeDisplayName === product.storeDisplayName
    );

    if (isSelected) {
      setSelectedStoreProducts(selectedStoreProducts.filter(p =>
        !(p.id === product.id && p.variantId === product.variantId && p.storeDisplayName === product.storeDisplayName)
      ));
    } else {
      setSelectedStoreProducts([...selectedStoreProducts, product]);
    }
  };

  const runCommaBatchSearch = async (queries) => {
    console.log('🔍 [MULTI-SEARCH] Starting batch search for', queries.length, 'SKUs:', queries);
    const batchStartTime = performance.now();

    // Phase 1: Load from cache instantly
    console.log('⚡ [BATCH-CACHE] Loading all products from cache...');
    const cacheStartTime = performance.now();

    try {
      const response = await axios.post(`${API_URL}/api/products/batch-search-cache`, {
        skus: queries
      });

      const cacheDuration = Math.round(performance.now() - cacheStartTime);
      console.log(`⚡ [BATCH-CACHE] Loaded ${response.data.products.length}/${queries.length} products in ${cacheDuration}ms`);

      // Add all products using centralized merge
      const newProducts = response.data.products;
      setProducts(prev => {
        const merged = mergeUniqueProducts(prev, newProducts);
        const addedCount = merged.length - prev.length;
        console.log(`➕ [BATCH-CACHE] Added ${addedCount} products (${newProducts.length - addedCount} duplicates skipped)`);
        return merged.slice(0, 60);
      });

      // Update selection for all new products (no need to initialize editableData - it's only for user edits)
      newProducts.forEach(product => {
        const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
        setSelectedProducts(prev => ({ ...prev, [productKey]: true }));
      });

      // Phase 2: Refresh with live data in background (parallel)
      if (response.data.products.length > 0) {
        console.log('🔄 [BATCH-REFRESH] Starting background refresh of live data...');
        const refreshStartTime = performance.now();

        // ONE batch-refresh-live call (was one /products/search per SKU).
        const liveBySku = await fetchLiveStockBatch(axios, API_URL, response.data.products.map((p) => p.sku));
        const refreshDuration = Math.round(performance.now() - refreshStartTime);
        console.log(`🔄 [BATCH-REFRESH] Refreshed ${liveBySku.size}/${response.data.products.length} SKUs in ${refreshDuration}ms`);
        if (liveBySku.size > 0) {
          setProducts((prev) => applyLiveStockUpdates(prev, liveBySku, { isPlaceholderBarcode }));
        }
      }

      // Handle products not found in cache - search live API
      const notFoundSkus = Array.isArray(response.data.notFound) ? response.data.notFound : [];
      if (notFoundSkus.length > 0) {
        console.log(`🔍 [BATCH-LIVE] Searching live API for ${notFoundSkus.length} SKUs not in cache`);
        for (const sku of notFoundSkus) {
          await handleSearch(sku, true);
        }
      }

      const totalDuration = Math.round(performance.now() - batchStartTime);
      console.log(`🎉 [MULTI-SEARCH] Batch complete! ${queries.length} SKUs processed in ${totalDuration}ms`);

    } catch (error) {
      console.error('❌ [BATCH-CACHE] Failed, falling back to sequential search:', error);
      // Fallback to sequential search
      for (let i = 0; i < queries.length; i++) {
        const query = queries[i];
        const searchStartTime = performance.now();
        console.log(`🔍 [MULTI-SEARCH] [${i + 1}/${queries.length}] Starting search for:`, query);

        await handleSearch(query, true);

        const searchDuration = Math.round(performance.now() - searchStartTime);
        console.log(`✅ [MULTI-SEARCH] [${i + 1}/${queries.length}] Completed in ${searchDuration}ms`);
      }
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery || products.length >= 200) return;

    // Clear input and refocus immediately so the next SKU can be typed without waiting
    setSearchQuery('');
    focusSearchInput();

    const isMultiSearch = products.length > 0 || isLoading || trimmedQuery.includes(',');

    if (trimmedQuery.includes(',')) {
      const queries = trimmedQuery.split(',')
        .map(q => q.trim())
        .filter(q => q.length > 0);
      void runCommaBatchSearch(queries);
    } else {
      void handleSearch(trimmedQuery, isMultiSearch);
    }
  };

  // Handle location warning confirmation
  const confirmLocationSearch = async () => {
    setShowLocationWarning(false);
    if (pendingLocationSearch) {
      // Clear existing products
      setProducts([]);
      setEditableData({});
      setSelectedProducts({});

      // Perform the location search
      setIsLoading(true);
      try {
        await performLocationSearch(pendingLocationSearch.query);
      } finally {
        setIsLoading(false);
      }
      setPendingLocationSearch(null);
    }
  };

  const cancelLocationSearch = () => {
    setShowLocationWarning(false);
    setPendingLocationSearch(null);
    setSearchQuery('');
    focusSearchInput();
  };

  const handleFeelingLucky = async () => {
    setIsLoading(true);
    setError('');
    setLoadingStage('🍀 Finding products...');

    try {
      const response = await axios.get(`${API_URL}/api/products/feeling-lucky`);
      const { products: luckyProducts, type, message, total } = response.data;

      // Check if empty
      if (type === 'empty') {
        setError(message);
        setIsLoading(false);
        setLoadingStage('');
        return;
      }

      // Set the lucky status (persists until Clear All)
      setLuckyStatus({ type, message, total });

      // Clear existing products
      setProducts([]);
      setEditableData({});
      setSelectedProducts({});

      const skus = luckyProducts.map(p => p.sku).filter(Boolean);
      console.log('🍀 [FEELING-LUCKY] Loading', skus.length, 'products via batch cache');

      // Phase 1: Load from cache instantly
      setLoadingStage('⚡ Loading from cache...');
      const batchStartTime = performance.now();
      const cacheResponse = await axios.post(`${API_URL}/api/products/batch-search-cache`, { skus });
      console.log(`⚡ [FEELING-LUCKY] Loaded ${cacheResponse.data.products.length} from cache in ${cacheResponse.data.duration}ms`);

      // Add all products in a single batch update
      const newProducts = cacheResponse.data.products;
      setProducts(newProducts);

      // Update selection for all new products (no need to initialize editableData - it's only for user edits)
      newProducts.forEach(product => {
        const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
        setSelectedProducts(prev => ({ ...prev, [productKey]: true }));
        // Queue each product for background refresh
        if (product.sku && product.sku !== 'N/A') {
          pendingRefreshSKUs.current.add(product.sku);
        }
      });

      // Schedule unified background refresh (200ms debounce)
      setLoadingStage(''); // Clear loading stage immediately after cache load
      scheduleBackgroundRefresh();

      // Handle not found in cache
      if (cacheResponse.data.notFound && cacheResponse.data.notFound.length > 0) {
        setLoadingStage('🔍 Loading missing products...');
        for (const sku of cacheResponse.data.notFound) {
          await handleSearch(sku, true);
        }
      }

      const totalDuration = Math.round(performance.now() - batchStartTime);
      console.log(`🎉 [FEELING-LUCKY] Complete! ${skus.length} products loaded in ${totalDuration}ms`);

      const emoji = type === 'resync' ? '🔄' : type === 'random' ? '🎲' : '📝';
      setSuccessMessage(`${emoji} ${message}`);
      setLoadingStage(''); // Clear loading stage

    } catch (error) {
      console.error('Feeling Lucky failed:', error);
      setError(error.response?.data?.error || 'Failed to load products');
      setLoadingStage('');
    } finally {
      setIsLoading(false);
    }
  };

  const removeProduct = (productKey) => {
    console.log('[removeProduct] Removing product with key:', productKey);

    // Use functional setState to avoid race conditions
    setProducts(prev => {
      // productKey format: storeDisplayName-id-variantId
      const index = prev.findIndex(p => `${p.storeDisplayName}-${p.id}-${p.variantId}` === productKey);

      if (index === -1) {
        console.log('[removeProduct] Product not found');
        return prev; // No change
      }

      console.log('[removeProduct] Found product at index', index, 'SKU:', prev[index].sku);

      // Filter out the product at this index
      const newProducts = prev.filter((_, i) => i !== index);
      console.log('[removeProduct] Products after removal:', newProducts.length);

      return newProducts;
    });

    // Clean up related state
    setEditableData(prev => {
      const newData = { ...prev };
      delete newData[productKey];
      return newData;
    });

    setSelectedProducts(prev => {
      const newData = { ...prev };
      delete newData[productKey];
      return newData;
    });

    setSuccessMessage('Product removed');
  };

  const fetchLogs = async () => {
    setIsLoadingLogs(true);
    try {
      const response = await axios.get(`${API_URL}/api/logs?limit=200`);
      setLogs(response.data.logs);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const clearLogs = async () => {
    if (window.confirm('Clear all activity logs?')) {
      try {
        await axios.post(`${API_URL}/api/logs/clear`);
        setLogs([]);
      } catch (error) {
        console.error('Error clearing logs:', error);
      }
    }
  };

  const openLogViewer = () => {
    if (!hasModulePermission('logs')) {
      setError("You don't have permission to view activity logs");
      return;
    }
    setShowLogViewer(true);
    fetchLogs();
  };

  const fetchPurchaseOrders = async (forceRefresh = false, options = {}) => {
    const requestedPage = Math.max(parseInt(options.page, 10) || poPage || 1, 1);
    const requestedStatusTab = (options.statusTab || poStatusTab || 'all');
    const requestedQuery = options.query !== undefined ? String(options.query || '').trim() : poFilterQuery;
    const includeItems = options.includeItems !== undefined ? !!options.includeItems : true;
    const skipCache = options.skipCache === true;
    const isBackgroundRefresh = options.backgroundRefresh === true;
    const cacheKey = buildPoResponseCacheKey(requestedPage, requestedStatusTab, requestedQuery, includeItems);
    const shouldRetryEmptyDraft = requestedStatusTab === 'draft' && requestedPage === 1 && !requestedQuery && options.retryEmptyDraft !== false;
    const requestId = poFetchRequestIdRef.current + 1;
    poFetchRequestIdRef.current = requestId;
    if (poFetchAbortControllerRef.current) {
      poFetchAbortControllerRef.current.abort();
    }

    poFetchAbortControllerRef.current = null;

    if (forceRefresh) {
      clearPoResponseCache('force refresh');
    } else if (!skipCache) {
      const cachedEntry = getCachedPoResponseEntry(cacheKey, { allowStale: true });
      if (cachedEntry?.data) {
        if (shouldRetryEmptyDraft && (cachedEntry.data.purchase_orders || []).length === 0) {
          poResponseCacheRef.current.delete(cacheKey);
          persistPoResponseCache();
          return fetchPurchaseOrders(true, {
            ...options,
            page: requestedPage,
            statusTab: requestedStatusTab,
            query: requestedQuery,
            includeItems,
            retryEmptyDraft: false,
            suppressSuccess: true
          });
        }
        applyPurchaseOrderResponse(cachedEntry.data, requestedPage, false, { statusTab: requestedStatusTab, query: requestedQuery });
        setIsLoadingPOs(false);
        setPoActiveFetchKey('');

        if (cachedEntry.isStale && options.revalidateCached !== false) {
          setTimeout(() => {
            fetchPurchaseOrders(false, {
              ...options,
              page: requestedPage,
              statusTab: requestedStatusTab,
              query: requestedQuery,
              includeItems,
              skipCache: true,
              backgroundRefresh: true,
              retryEmptyDraft: false,
              suppressSuccess: true
            });
          }, 0);
        }

        return cachedEntry.data;
      }
    }

    const controller = new AbortController();
    poFetchAbortControllerRef.current = controller;
    if (isBackgroundRefresh) {
      poBackgroundFetchRequestIdRef.current = requestId;
      setIsBackgroundRefreshingPOs(true);
    } else {
      setPoActiveFetchKey(cacheKey);
      setIsLoadingPOs(true);
    }
    try {
      const params = new URLSearchParams();
      params.set('page', String(requestedPage));
      params.set('pageSize', String(poLimit));
      params.set('status_tab', String(requestedStatusTab));
      if (requestedQuery) params.set('query', requestedQuery);
      params.set('include_items', includeItems ? 'true' : 'false');
      if (forceRefresh) params.set('refresh', 'true');
      else params.set('stale_while_revalidate', 'true');

      const url = `${API_URL}/api/purchase-orders?${params.toString()}`;
      const response = await axios.get(url, { signal: controller.signal });
      if (requestId !== poFetchRequestIdRef.current) return;
      poLastFetchedAtRef.current = Date.now();
      if (shouldRetryEmptyDraft && (response.data?.purchase_orders || []).length === 0) {
        poResponseCacheRef.current.delete(cacheKey);
        return fetchPurchaseOrders(true, {
          ...options,
          page: requestedPage,
          statusTab: requestedStatusTab,
          query: requestedQuery,
          includeItems,
          retryEmptyDraft: false,
          suppressSuccess: true
        });
      }
      storeCachedPoResponse(cacheKey, response.data);
      applyPurchaseOrderResponse(response.data, requestedPage, forceRefresh, { statusTab: requestedStatusTab, query: requestedQuery });

      if (forceRefresh && response.data?.meta?.live_refresh_pending) {
        window.setTimeout(() => {
          fetchPurchaseOrders(false, {
            ...options,
            page: requestedPage,
            statusTab: requestedStatusTab,
            query: requestedQuery,
            includeItems,
            skipCache: true,
            backgroundRefresh: true,
            retryEmptyDraft: false,
            suppressSuccess: true
          });
        }, 45000);
      }

      if (forceRefresh && !options.suppressSuccess) {
        setSuccessMessage('Purchase orders refreshed!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      if (requestId !== poFetchRequestIdRef.current) return;
      console.error('Error fetching purchase orders:', error);
      setError('Failed to load purchase orders: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
      setPoMeta(prev => ({ ...prev, has_next_page: false, has_prev_page: prev.page > 1 }));
    } finally {
      if (isBackgroundRefresh && poBackgroundFetchRequestIdRef.current === requestId) {
        poBackgroundFetchRequestIdRef.current = 0;
        setIsBackgroundRefreshingPOs(false);
      }
      if (requestId === poFetchRequestIdRef.current) {
        setIsLoadingPOs(false);
        setPoActiveFetchKey('');
        if (poFetchAbortControllerRef.current === controller) {
          poFetchAbortControllerRef.current = null;
        }
      }
    }
  };

  const handleUpdatePOStatus = async (poRef, newStatus, follow = true) => {
    const poId = poRef?.id || poRef;
    const targetStoreKey = poRef?.storeKey || null;
    // 1. Capture previous state for rollback
    const previousOrders = [...purchaseOrders];
    const previousTab = poStatusTab;
    const optimisticTimestamp = new Date().toISOString();
    const po = purchaseOrders.find(p => String(p.id) === String(poId) && (!targetStoreKey || p.storeKey === targetStoreKey));
    // Mirror the backend (DB.updatePOMetadata): a true move INTO the arrived
    // family stamps arrivedAt now, so the optimistic card never ages off a
    // stale stamp or old task timestamps (SI-7956 showed "Arrived 18d 12h ago"
    // minutes after the click, 2026-09-29).
    const arrivedAtPatch = matchesPoStatusTab(newStatus, 'arrived')
      && !matchesPoStatusTab(po?.localMetadata?.status, 'arrived')
      ? { arrivedAt: optimisticTimestamp }
      : {};

    try {
      clearPoResponseCache('status update');

      // 2. Optimistically update UI with timestamp to prevent fetch overwrites
      setPurchaseOrders(prevOrders =>
        prevOrders.map(po =>
          String(po.id) === String(poId) && (!targetStoreKey || po.storeKey === targetStoreKey)
            ? {
              ...po,
              localMetadata: {
                ...po.localMetadata,
                status: newStatus,
                ...arrivedAtPatch,
                updatedAt: optimisticTimestamp
              }
            }
            : po
        )
      );

      if (po) {
        poOptimisticMetadataRef.current.set(getPoIdentityKey(po), {
          savedAt: Date.now(),
          metadata: {
            ...po.localMetadata,
            status: newStatus,
            ...arrivedAtPatch,
            updatedAt: optimisticTimestamp
          }
        });
      }

      // Removed auto-navigation to next tab - user requested cards stay in place

      setSuccessMessage(`Order marked as ${newStatus}`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // 3. Perform API call in background
      await axios.post(`${API_URL}/api/purchase-orders/${poId}/update-stage`, {
        status: newStatus,
        receivedBy: 'User',
        storeKey: po?.storeKey || targetStoreKey,
        poReference: po ? (po.reference || po.number || po.id) : poId,
        vendor: po ? (po.vendor_display_name || po.supplier_name || po.vendor || po.from || '') : '',
        notes: po ? po.notes : ''
      });

      clearPoResponseCache('status update saved');

    } catch (error) {
      console.error('Error updating PO status:', error);
      setError('Failed to update PO status - changes reverted');
      if (po) {
        poOptimisticMetadataRef.current.delete(getPoIdentityKey(po));
      }

      // 4. Revert UI on failure
      setPurchaseOrders(previousOrders);
      if (follow) setPoStatusTab(previousTab);
    }
  };

  // Guarded status change: shows a confirm prompt before reversing a workflow
  // stage or archiving, so a mis-click on a prominent card button can't silently
  // move a PO backwards or into Archived. Forward "happy path" steps stay 1-click.
  const confirmAndUpdatePOStatus = (po, newStatus, message) => {
    if (message && !window.confirm(message)) return;
    handleUpdatePOStatus(po, newStatus);
  };

  // Card footer button hierarchy: the forward "next stage" button is the primary
  // (filled green); reversing a stage is a secondary, de-emphasised action — a
  // subtle outline, not a loud solid-red block that out-shouts the primary action.
  // Both are dark-mode aware.
  const poReverseButtonStyle = {
    backgroundColor: 'transparent',
    color: 'var(--app-ink-secondary)',
    border: '1px solid var(--app-border)',
    borderRadius: '8px',
    padding: '4px 10px',
    fontWeight: 600,
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    height: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    transition: 'all 0.15s ease'
  };

  // The forward "next stage" button — the card's one primary action. Shared shape
  // so Drafts/Waiting/Arrived/Website Team stay identical; callers layer on their
  // own completed-state border/glow. Visual only — handlers live at each call site.
  // Teal is the Products accent (fieldIcons.js ACCENT / the Print label button) —
  // the two modules are linked, so their primary actions wear the same colour.
  const poForwardButtonStyle = (enabled) => ({
    backgroundColor: enabled ? '#0d9488' : (darkMode ? '#3a3a3a' : '#e1e3e5'),
    color: enabled ? 'white' : (darkMode ? '#b0b6be' : '#8c9196'),
    border: 'none',
    borderRadius: '8px',
    padding: '4px 14px',
    fontWeight: 700,
    fontSize: '12px',
    cursor: enabled ? 'pointer' : 'not-allowed',
    minWidth: '118px',
    whiteSpace: 'nowrap',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    boxShadow: enabled ? '0 1px 2px rgba(13, 148, 136, 0.3)' : 'none',
    transition: 'box-shadow 0.15s ease, background-color 0.15s ease'
  });

  // Helper: get visible POs for the current tab/filters
  const getVisiblePOs = (tabFilter) => {
    return purchaseOrders.filter(po => {
      if (poStoreFilter !== 'all' && po.storeKey !== poStoreFilter) return false;
      if (!matchesPoSearchQuery(po, poFilter)) return false;
      const status = resolvePoWorkflowStatus(po);
      return matchesPoStatusTab(status, tabFilter);
    });
  };

  const visiblePurchaseOrderItems = useMemo(() => {
    return purchaseOrders
      .filter(po => {
        if (poStoreFilter !== 'all' && po.storeKey !== poStoreFilter) return false;
        if (!matchesPoSearchQuery(po, poFilter)) return false;

        const status = resolvePoWorkflowStatus(po);
        return matchesPoStatusTab(status, poStatusTab);
      })
      .sort((a, b) => {
        if (poStatusTab === 'waiting_arrival') {
          const dateA = a.localMetadata?.expectedArrivalDate;
          const dateB = b.localMetadata?.expectedArrivalDate;

          if (dateA && dateB) return new Date(dateA) - new Date(dateB);
          if (dateA && !dateB) return -1;
          if (!dateA && dateB) return 1;
        }

        if (poStatusTab === 'arrived') {
          // Freshest arrivals first; unstamped cards sink to the end. The
          // age pill + border colour flag the long-sitters as you scroll.
          const stampA = resolvePoArrivalStamp(a).iso;
          const stampB = resolvePoArrivalStamp(b).iso;
          if (stampA && stampB && stampA !== stampB) return Date.parse(stampB) - Date.parse(stampA);
          if (stampA && !stampB) return -1;
          if (!stampA && stampB) return 1;
        }

        return new Date(b.created_date) - new Date(a.created_date);
      })
      .reduce((acc, po, index, array) => {
        if (poStatusTab === 'waiting_arrival') {
          const hasDate = po.localMetadata?.expectedArrivalDate;
          const prevPO = array[index - 1];
          const prevHasDate = prevPO?.localMetadata?.expectedArrivalDate;

          if (index > 0 && prevHasDate && !hasDate) {
            acc.push({ isSeparator: true, key: 'separator-no-date' });
          }
        }

        acc.push(po);
        return acc;
      }, []);
  }, [purchaseOrders, poStoreFilter, poFilter, poStatusTab, matchesPoSearchQuery, matchesPoStatusTab, resolvePoWorkflowStatus]);

  const visiblePurchaseOrderCount = useMemo(
    () => visiblePurchaseOrderItems.filter(item => !item.isSeparator).length,
    [visiblePurchaseOrderItems]
  );
  const currentPoViewCacheKey = useMemo(() => (
    buildPoResponseCacheKey(poFilterQuery ? 1 : poPage, poStatusTab, poFilterQuery, true)
  ), [buildPoResponseCacheKey, poFilterQuery, poPage, poStatusTab]);
  const isRefreshingCurrentPOView = isLoadingPOs && poActiveFetchKey === currentPoViewCacheKey && visiblePurchaseOrderCount > 0;
  const isLoadingCurrentPOView = isLoadingPOs && poActiveFetchKey === currentPoViewCacheKey && visiblePurchaseOrderCount === 0;
  // Feeds the Force refresh button's spinner — the "refreshing in background"
  // text line went with the toolbar's second row (Rhys 2026-08-29).
  const showPORefreshIndicator = isBackgroundRefreshingPOs || isRefreshingCurrentPOView;

  const handleConfirmReceipt = async (receiptData) => {
    try {
      clearPoResponseCache('confirm receipt');
      // ---------------------------------------------------------
      // PREORDER NOTIFICATION LOGIC
      // ---------------------------------------------------------
      try {
        // 1. Extract SKUs from received items
        const receivedSkus = receiptData.receivedItems
          .map(item => item.sku)
          .filter(Boolean); // Remove null/undefined

        if (receivedSkus.length > 0) {
          // 2. Fetch fresh stock data (StockOnHand & StockCommitted)
          // We use the cache endpoint which is fast; for critical checks live might be better but cache is sufficient generally
          console.log('🔍 [PREORDER-CHECK] Checking for preorders on', receivedSkus.length, 'SKUs...');
          const stockResponse = await axios.post(`${API_URL}/api/products/batch-search-cache`, { skus: receivedSkus });
          console.log('🔍 [PREORDER-CHECK] API Response:', stockResponse.data);
          const freshProducts = stockResponse.data.products;

          const preorderAlerts = [];

          // 3. Check each received item for backorders
          receiptData.receivedItems.forEach(item => {
            if (!item.sku) return;

            const product = freshProducts.find(p => p.sku === item.sku);
            if (product) {
              const onHand = product.stockOnHand || 0;
              const committed = product.stockCommitted || 0;
              // If Committed > OnHand, it means we have outstanding preorders/backorders
              const neededForPreorders = Math.max(0, committed - onHand);

              console.log(`🔍 [PREORDER-CHECK] SKU: ${item.sku} | OnHand: ${onHand} | Committed: ${committed} | Needed: ${neededForPreorders} | Qty: ${item.quantity_received}`);

              if (neededForPreorders > 0 && item.quantity_received > 0) {
                // We only need to alert for the amount we are actually receiving
                // e.g. Needed 5, Receiving 2 -> Set aside 2
                // e.g. Needed 2, Receiving 5 -> Set aside 2
                const quantityToSetAside = Math.min(neededForPreorders, item.quantity_received);

                preorderAlerts.push(`• ${item.sku}: Set aside ${quantityToSetAside} (Needed: ${neededForPreorders})`);
              }
            } else {
              console.warn(`⚠️ [PREORDER-CHECK] Product not found in fresh data for SKU: ${item.sku}`);
            }
          });

          // 4. Alert the user if needed
          if (preorderAlerts.length > 0) {
            console.log('⚠️ [PREORDER-CHECK] Alerts generated:', preorderAlerts);
            // Replace alert with Banner state
            setPreorderNotification({
              poId: receiptData.poId,
              items: preorderAlerts
            });
          } else {
            console.log('✅ [PREORDER-CHECK] No preorder alerts needed.');
          }
        }
      } catch (preorderError) {
        console.error('❌ [PREORDER-CHECK] Failed to check preorders:', preorderError);
        // Don't block the receipt process if this check fails
      }
      // ---------------------------------------------------------

      // Snapshot the PO's prior metadata so we can roll the optimistic flip back
      // if the server receive fails — the UI must never show a receive that did
      // not actually persist.
      const targetPo = purchaseOrders.find(p => String(p.id) === String(receiptData.poId));
      const previousMetadata = targetPo?.localMetadata;
      const previousItems = targetPo?.items;

      setSuccessMessage('Arrival notified - awaiting stock-in confirmation');

      // Optimistic UI Update - update both status and the notifyArrival task.
      // Extras go onto the item list straight away (flagged, never as PO lines) so
      // staff can print their labels without waiting for the background refresh.
      const optimisticExtraItems = buildExtraPoItems(
        receiptData.extraItems || [],
        (targetPo?.items || []).filter(item => !item?.isExtraItem)
      );
      setPurchaseOrders(prevOrders =>
        prevOrders.map(po =>
          String(po.id) === String(receiptData.poId)
            ? {
              ...po,
              items: [
                ...(po.items || []).filter(item => !item?.isExtraItem),
                ...optimisticExtraItems
              ],
              localMetadata: {
                ...po.localMetadata,
                status: 'awaiting_stockin',
                extraItemsReceived: optimisticExtraItems.map(item => ({
                  sku: item.sku,
                  title: item.title,
                  variantTitle: item.variantTitle,
                  quantity: item.quantity,
                  source: item.extraSource
                })),
                hasExtraItems: optimisticExtraItems.length > 0,
                tasks: {
                  ...(po.localMetadata?.tasks || {}),
                  notifyArrival: {
                    completed: true,
                    completedAt: new Date().toISOString(),
                    completedBy: currentUser?.email || 'User'
                  }
                }
              }
            }
            : po
        )
      );
      // Removed highlighting - user requested cards stay in place

      try {
        await axios.post(`${API_URL}/api/purchase-orders/${receiptData.poId}/receive`, {
          receivedItems: receiptData.receivedItems,
          extraItems: receiptData.extraItems || [],
          // Store hint for the expiry check on cold instances (food-expiry-tracking).
          ...(receiptData.storeKey ? { storeKey: receiptData.storeKey } : {}),
          notes: receiptData.notes,
          poReference: receiptData.poReference,
          vendor: receiptData.vendor
        });

        // Also mark the notifyArrival task as complete in the backend
        try {
          await axios.post(`${API_URL}/api/purchase-orders/${receiptData.poId}/task`, {
            taskKey: 'notifyArrival',
            completed: true
          }, { withCredentials: true });
        } catch (taskErr) {
          console.warn('Failed to mark notifyArrival task:', taskErr);
        }

        setTimeout(() => setSuccessMessage(''), 3000);

        // Background refresh to ensure consistency
        fetchPurchaseOrders(false);
      } catch (error) {
        console.error('Error confirming receipt:', error);
        // Roll back the optimistic flip so the card does not falsely show as received,
        // and re-throw so the receipt modal stays open and reports the real failure
        // instead of silently "succeeding".
        setSuccessMessage('');
        setPurchaseOrders(prevOrders =>
          prevOrders.map(po =>
            String(po.id) === String(receiptData.poId)
              ? { ...po, items: previousItems ?? po.items, localMetadata: previousMetadata }
              : po
          )
        );
        // Expiry-tracked lines without (complete) dates: the server names the
        // SKUs so the modal can mark those lines inline.
        const body = error?.response?.data;
        if (error?.response?.status === 400 && body?.code === 'expiry_dates_required') {
          const expiryError = new Error(`Expiry dates required for ${(body.skus || []).join(', ') || 'some lines'} — nothing was saved.`);
          expiryError.code = 'expiry_dates_required';
          expiryError.skus = Array.isArray(body.skus) ? body.skus : [];
          throw expiryError;
        }
        throw new Error('Failed to record receipt — nothing was saved. Please try again.');
      }
    } catch (error) {
      console.error('Error confirming receipt:', error);
      // Propagate to the receipt modal (POReceiptModalV2.handleSubmit) so it keeps
      // the modal open and shows the error rather than closing on a fake success.
      throw error;
    }
  };

  const handleTogglePOSelection = (poId) => {
    const newSelected = new Set(selectedPOs);
    if (newSelected.has(poId)) {
      newSelected.delete(poId);
    } else {
      newSelected.add(poId);
    }
    setSelectedPOs(newSelected);
  };

  const handleSelectAllPOs = () => {
    const visiblePOs = getVisiblePOs(poStatusTab);
    const newSelected = new Set(visiblePOs.map(po => po.id));
    setSelectedPOs(newSelected);
  };

  const handleBulkAction = async (action) => {
    if (selectedPOs.size === 0) return;
    if (!window.confirm(`Are you sure you want to ${action} ${selectedPOs.size} orders?`)) return;

    setIsLoadingPOs(true);
    try {
      clearPoResponseCache('bulk action');
      const promises = Array.from(selectedPOs).map(poId =>
        axios.post(`${API_URL}/api/purchase-orders/${poId}/status`, {
          status: action,
          receivedBy: 'User'
        })
      );

      await Promise.all(promises);

      // Update local PO state immediately so cards filter out dynamically
      setPurchaseOrders(prev => prev.map(po => {
        if (selectedPOs.has(po.id)) {
          return {
            ...po,
            localMetadata: {
              ...po.localMetadata,
              status: action
            }
          };
        }
        return po;
      }));

      setSuccessMessage(`Successfully ${action === 'archived' ? 'archived' : 'updated'} ${selectedPOs.size} orders`);
      setTimeout(() => setSuccessMessage(''), 3000);
      setSelectedPOs(new Set());
      // Local state is already patched above; the status route cleared the
      // server's query cache, so a plain (non-forced) fetch returns fresh
      // workflow data without a full Lightspeed/Qoblex rebuild.
      fetchPurchaseOrders(false, { skipCache: true, backgroundRefresh: true, suppressSuccess: true });
    } catch (error) {
      console.error('Bulk action error:', error);
      setError('Failed to process bulk action');
    } finally {
      setIsLoadingPOs(false);
    }
  };

  const _openPOViewer = () => {
    setModuleView('po');
    setPoStatusTab('waiting_arrival');
    setPoPage(1);
    fetchPurchaseOrders(false, { page: 1, statusTab: 'waiting_arrival' });
  };

  const handlePOClick = async (po, options = {}) => {
    // Extract all SKUs from the purchase order items (including items without SKU)
    const allItems = Array.isArray(po?.items)
      ? po.items
      : Array.isArray(po?.line_items)
        ? po.line_items
        : [];
    const skus = allItems.map(item => getPoItemSku(item)).filter(Boolean);

    if (allItems.length > 0) {
      if (options.resetLabelSelection) {
        setSelectedProducts({});
        setLabelQuantities({});
        setLabelQuantityReviewKeys({});
      }

      const skuString = skus.join(', ');
      setSearchQuery(skuString);
      setModuleView('home');

      // Supplier context for the grid's Vendor Ref field — scans are keyed to
      // this PO's supplier in the planner supplier SKU map.
      setGridPoSupplier({
        supplierId: po.supplier_id != null ? String(po.supplier_id) : null,
        supplierName: po.vendor_display_name || po.supplier_name || null,
        store: po.store || po.storeDisplayName || null
      });
      const storeName = po.store || po.storeDisplayName || '';
      axios.post(`${API_URL}/api/warehouse/vendor-refs/lookup`, {
        items: skus.map((sku) => ({ sku, store: storeName }))
      }, { withCredentials: true })
        .then((res) => setVendorRefsBySku((prev) => ({ ...prev, ...(res.data?.refs || {}) })))
        .catch(() => {});

      console.log('📦 [PO-CLICK] Loading', allItems.length, 'items from PO:', po.number, `(${skus.length} with SKU, ${allItems.length - skus.length} without SKU)`);
      const batchStartTime = performance.now();

      // Create SKU to quantity map from PO items — the ARRIVED count, so the grid's
      // label quantities match the delivery rather than the original order.
      const skuQuantityMap = {};
      allItems.forEach(item => {
        const itemSku = getPoItemSku(item);
        if (itemSku && !poLineDidNotArrive(item)) {
          const skuKey = itemSku.toUpperCase();
          skuQuantityMap[skuKey] = (skuQuantityMap[skuKey] || 0) + (getPoLineArrivedQuantity(item) || 1);
        }
      });

      // Arm the PO putaway audit for these SKUs — the signature effect runs it
      // once the products land in state, then keeps it fresh across saves.
      // The placeholder goes up now so the tub warning has a slot waiting for
      // it rather than shoving the grid down when the check returns.
      poTubAuditRanRef.current = false;
      setPoTubAuditPending(Object.keys(skuQuantityMap).length > 0);
      setPoAuditSkus(Object.keys(skuQuantityMap));

      // Add items without SKU directly to products list
      const itemsWithoutSku = allItems.filter(item => !getPoItemSku(item));
      if (itemsWithoutSku.length > 0) {
        console.log(`⚠️ [PO-CLICK] Adding ${itemsWithoutSku.length} items without SKU`);
        const noSkuProducts = itemsWithoutSku.map(item => ({
          id: `no-sku-${Date.now()}-${Math.random()}`,
          variantId: `no-sku-${Date.now()}-${Math.random()}`,
          sku: null, // Explicitly null to mark as missing
          title: item.productName || item.description || 'Unknown Product',
          variantTitle: null,
          binLocation: 'Not Set',
          stockOnHand: item.quantity || 0,
          stockAvailable: 0,
          stockCommitted: 0,
          barcode: null,
          imageUrl: null,
          weight: 0,
          weightUnit: 'GRAMS',
          price: '0.00',
          storeDisplayName: 'Phaser FPV',
          store: 'phaser-fpv.myshopify.com',
          fromCache: false,
          noSku: true // Flag to identify products without SKU
        }));

        setProducts(prev => {
          const merged = mergeUniqueProducts(prev, noSkuProducts);
          return merged.slice(0, 60);
        });
      }

      // Continue with normal loading for items with SKU
      if (skus.length === 0) {
        console.log('⚠️ [PO-CLICK] No SKUs found in PO, only items without SKU loaded');
        return;
      }

      // Phase 1: Load from cache instantly
      try {
        const cacheResponse = await axios.post(`${API_URL}/api/products/batch-search-cache`, { skus });
        console.log(`⚡ [PO-CLICK] Loaded ${cacheResponse.data.products.length} from cache in ${cacheResponse.data.duration}ms`);

        // Add all products using centralized merge (prevents duplicates)
        const newProducts = Array.isArray(cacheResponse.data.products) ? cacheResponse.data.products : [];

        setProducts(prev => {
          const merged = mergeUniqueProducts(prev, newProducts);
          const addedCount = merged.length - prev.length;
          const skippedCount = newProducts.length - addedCount;

          // PO grid loads show newest products first (createdAt = Shopify
          // product creation); undated rows (no-SKU lines) sink to the end.
          const sorted = [...merged].sort((a, b) =>
            String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

          console.log(`➕ [PO-CLICK] Added ${addedCount} products (${skippedCount} duplicates skipped)`);
          return sorted.slice(0, 60);
        });

        // Select every product on the PO and set its label count to the PO
        // quantity. Computed from the grid as it was at the click plus the
        // rows the lookup just returned — NOT from inside the setProducts
        // updater. React only runs an updater synchronously when nothing else
        // is pending on this component; here the Products view is usually
        // still rendering when a cached lookup returns (40ms), so the updater
        // ran later and the old code selected 0 of 16 (Rhys 2026-09-03).
        const selectionCandidates = mergeUniqueProducts(products, newProducts);
        const selectedKeys = {};
        const poLabelQuantities = {};
        selectionCandidates.forEach(product => {
          const productSku = String(product.sku || '').trim().toUpperCase();
          if (skuQuantityMap[productSku]) {
            // Match the product key format used in rendering
            const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
            selectedKeys[productKey] = true;
            poLabelQuantities[productKey] = skuQuantityMap[productSku];
          }
        });
        console.log('🎯 [PO-AUTO-SELECT] Selecting', Object.keys(selectedKeys).length, 'of', selectionCandidates.length, 'products');
        setSelectedProducts(prev => ({ ...prev, ...selectedKeys }));
        setLabelQuantities(prev => ({ ...prev, ...poLabelQuantities }));

        // Anything that arrived in bulk gets flagged for a human count check —
        // 100 tubes on the PO is 4 labels on the shelf (Rhys 2026-09-04).
        const reviewKeys = {};
        Object.entries(poLabelQuantities).forEach(([key, qty]) => {
          if (Number(qty) >= HIGH_PO_LABEL_QUANTITY_THRESHOLD) reviewKeys[key] = true;
        });
        if (Object.keys(reviewKeys).length > 0) {
          console.log('⚠️ [PO-QTY-REVIEW]', Object.keys(reviewKeys).length, 'line(s) over', HIGH_PO_LABEL_QUANTITY_THRESHOLD, 'labels — flagged in grid');
          setLabelQuantityReviewKeys(prev => ({ ...prev, ...reviewKeys }));
        }

        // Phase 2: Background batch refresh for live inventory data (ONE API call)
        const cachedProducts = cacheResponse.data.products;
        const skusToRefresh = cachedProducts.map(p => p.sku).filter(Boolean);

        if (skusToRefresh.length > 0) {
          console.log(`🔄 [PO-CLICK] Starting background batch refresh for ${skusToRefresh.length} SKUs...`);

          // Fire and forget - don't block the UI
          axios.post(`${API_URL}/api/products/batch-refresh-live`, { skus: skusToRefresh })
            .then(refreshResponse => {
              const liveUpdates = refreshResponse.data.updates || [];
              console.log(`✅ [PO-CLICK] Background refresh complete: ${liveUpdates.length} products updated in ${refreshResponse.data.duration}ms`);

              if (liveUpdates.length > 0) {
                setProducts(prev => {
                  // Update existing products by matching SKU
                  return prev.map(p => {
                    const liveUpdate = liveUpdates.find(lu => lu.sku === p.sku);

                    if (liveUpdate) {
                      return {
                        ...p,
                        stockAvailable: liveUpdate.stockAvailable ?? p.stockAvailable,
                        stockOnHand: liveUpdate.stockOnHand ?? p.stockOnHand,
                        stockCommitted: liveUpdate.stockCommitted ?? p.stockCommitted,
                        barcode: liveUpdate.barcode !== undefined && liveUpdate.barcode !== null
                          ? liveUpdate.barcode
                          : (isPlaceholderBarcode(p.barcode) ? '' : p.barcode),
                        barcodes: Array.isArray(liveUpdate.barcodes) ? liveUpdate.barcodes : p.barcodes,
                        binLocation: liveUpdate.binLocation || p.binLocation,
                        length: liveUpdate.length ?? p.length,
                        width: liveUpdate.width ?? p.width,
                        height: liveUpdate.height ?? p.height,
                        inventoryLevels: liveUpdate.inventoryLevels || p.inventoryLevels,
                        fromCache: false,
                      };
                    }
                    return p;
                  });
                });
              }
            })
            .catch(err => {
              console.warn('⚠️ [PO-CLICK] Background refresh failed:', err.message);
            });
        }

        // Handle not found in cache
        for (const sku of (cacheResponse.data.notFound || [])) {
          await handleSearch(sku, true);
        }

        const totalDuration = Math.round(performance.now() - batchStartTime);
        console.log(`🎉 [PO-CLICK] Complete! ${skus.length} products loaded in ${totalDuration}ms`);
      } catch (error) {
        console.error('❌ [PO-CLICK] Batch failed, falling back to sequential:', error);
        // Fallback to sequential
        for (const sku of skus) {
          await handleSearch(sku, true);
        }
      }
    }
  };

  const handlePOGridLoad = async (po) => {
    await handlePOClick(po, {
      resetLabelSelection: Boolean(buildHighQuantityPoLabelTaskOverride(po))
    });
  };

  const loadPOLabelsInGridForReview = async (po) => {
    const poItems = Array.isArray(po?.items)
      ? po.items
      : Array.isArray(po?.line_items)
        ? po.line_items
        : [];
    const reference = po?.reference || po?.number || po?.id || 'PO';
    if (poItems.length === 0) {
      throw new Error(`No line items found for ${reference}.`);
    }
    await handlePOGridLoad(po);
  };



  const toggleProductSelection = (productKey) => {
    setSelectedProducts(prev => {
      const isCurrentlySelected = prev[productKey];
      const willBeSelected = !isCurrentlySelected;

      // If selecting a product, initialize label quantity if not set
      // Default to 1 for manual selections (PO loading sets quantities explicitly)
      if (willBeSelected && (labelQuantities[productKey] === undefined || labelQuantities[productKey] === null)) {
        setLabelQuantities(prevQty => ({
          ...prevQty,
          [productKey]: 1
        }));
      }

      return {
        ...prev,
        [productKey]: willBeSelected
      };
    });
  };

  const _toggleAllProducts = () => {
    const allSelected = products.every(p => selectedProducts[`${p.storeDisplayName}-${p.id}-${p.variantId}`]);
    const newSelection = {};
    products.forEach(p => {
      newSelection[`${p.storeDisplayName}-${p.id}-${p.variantId}`] = !allSelected;
    });
    setSelectedProducts(newSelection);
  };

  // labelPlan: null = the label count entered on each card (1 when nothing
  // was entered — never stock on hand, Rhys 2026-09-03); {mode:'one'} = one
  // each; {mode:'custom', perProduct:n} = n each.
  // Asked up front by PrintLabelQuantityModal (Rhys 2026-08-29) - printing
  // silently used actual quantities, which is right for a delivery and wrong
  // every other time.
  const printLabels = async (labelPlan = null, scopeKey = null) => {
    try {
      // Get selected products
      const selectedProductsList = products.filter(p => {
        const productKey = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
        // scopeKey = the single card whose Print label button was pressed.
        return scopeKey ? productKey === scopeKey : selectedProducts[productKey];
      });

      if (selectedProductsList.length === 0) {
        setError('Please select at least one product to print labels');
        return;
      }

      // Prepare label data. Pending edits overlay the saved values: the card
      // queues its save on Print, but the queue is async, so the label reads
      // what is on screen rather than racing the server (Rhys 2026-08-29 -
      // never print stale data the card visibly disagrees with).
      const labelData = selectedProductsList.map(product => {
        const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
        const pending = editableData[productKey] || {};

        return {
          sku: product.sku || '',
          // Lets the backend barcode the EAN when the SKU can't be CODE128-encoded
          barcode: (pending.barcode !== undefined ? pending.barcode : product.barcode) || '',
          title: product.title || 'No Title',
          variantTitle: getDisplayVariantTitle(product.variantTitle, product.title),
          location: (pending.binLocation !== undefined ? pending.binLocation : product.binLocation) || '',
          weight: pending.weight !== undefined ? pending.weight : (product.weight ?? 0),
          // Which store's label stock usage this is (Ship → Packaging usage by store).
          store: product.storeDisplayName || '',
          quantity: labelPlan?.mode === 'one'
            ? 1
            : labelPlan?.mode === 'custom'
              ? Math.max(1, parseInt(labelPlan.perProduct, 10) || 1)
              : resolveLabelQuantity(labelQuantities[productKey])
        };
      });

      // Use default size or saved size from localStorage
      const { labelWidth, labelHeight } = resolveLabelSize();

      // Send to backend to generate labels. printRequestId: 40x28 label stock is
      // counted once per print (Ship → Packaging), even if this request is retried.
      const response = await axios.post(`${API_URL}/api/labels/generate`, {
        products: labelData,
        labelWidth,
        labelHeight,
        darkMode,
        printRequestId: newLabelPrintRequestId()
      }, {
        responseType: 'text' // We're getting HTML
      });

      // Open in new window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(response.data);
        printWindow.document.close();
      } else {
        setError('Please allow popups to print labels');
      }

    } catch (err) {
      console.error('Print labels error:', err);
      setError(`Failed to generate labels: ${err.response?.data?.details || err.message}`);
    }
  };

  // Reset every selected product's label quantity to 1 (PO loading and the
  // stock-on-hand fallback can leave much higher counts queued up).
  const setSelectedLabelQuantitiesToOne = () => {
    setLabelQuantities(prev => {
      const next = { ...prev };
      products.forEach(p => {
        const key = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
        if (selectedProducts[key]) {
          next[key] = 1;
        }
      });
      return next;
    });
    // Every count is now deliberate — nothing left to review.
    setLabelQuantityReviewKeys({});
  };

  // A4 list of the selected products (name / SKU / W-H location), sorted by location.
  const printA4List = () => {
    const selectedProductsList = products.filter(p => selectedProducts[`${p.storeDisplayName}-${p.id}-${p.variantId}`]);

    if (selectedProductsList.length === 0) {
      setError('Please select at least one product to print');
      return;
    }

    // Cancel on the prompt just means "no note" — the print still goes ahead.
    const note = window.prompt('Optional note for this printout (why you printed it):', '') || '';

    const html = buildA4ProductListHtml(selectedProductsList.map(p => ({
      title: p.title || 'No Title',
      variantTitle: getDisplayVariantTitle(p.variantTitle, p.title),
      sku: p.sku || '',
      location: p.binLocation || '',
      stock: p.stockOnHand ?? 0,
      stockCommitted: p.stockCommitted || 0
    })), {
      printedBy: permissionSettings?.name || currentUser?.displayName || currentUser?.email || '',
      note
    });

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      // The page carries no inline script (its CSP blocks script), so print from here.
      printWindow.focus();
      printWindow.print();
    } else {
      setError('Please allow popups to print the product list');
    }
  };

  // Push one selected product's details to a spare pick-to-light e-ink label
  // ("Product reference" role) instead of printing paper (Rhys 2026-08-27).
  // Shows SKU / location / QTY:{on hand} / {available} LEFT on the tag.
  // The modal always opens (even with one display) — it also hosts the
  // "Locate this item" tick box that lights the product's bay white.
  const [putAwayOpen, setPutAwayOpen] = useState(false);
  const [printLabelDialogOpen, setPrintLabelDialogOpen] = useState(false);
  const [eDisplayChooser, setEDisplayChooser] = useState(null); // { product, displays }
  const [eDisplayLocate, setEDisplayLocate] = useState(false);

  const pushToEDisplay = async (product, target, locate) => {
    try {
      const stockOnHand = product.stockOnHand ?? 0;
      const push = await axios.post(`${API_URL}/api/esl/product-display`, {
        mac: target.mac,
        sku: product.sku || '',
        location: product.binLocation || '',
        stockOnHand,
        stockAvailable: Math.max(0, stockOnHand - (product.stockCommitted || 0)),
        locate: Boolean(locate && product.binLocation)
      }, { withCredentials: true });
      if (push.data?.success === false) {
        setError(push.data.error || 'Failed to send to E-Ink display');
        return;
      }
      setError(null);
      const baySuffix = push.data?.locatedBay ? ` · bay ${push.data.locatedBay} lit white` : '';
      setSuccessMessage(`${product.sku || 'Product'} → ${target.displayName} (e-ink takes ~10s to repaint)${baySuffix}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send to E-Ink display');
    } finally {
      setEDisplayChooser(null);
    }
  };

  const sendToEDisplay = async () => {
    const selectedProductsList = products.filter(p => selectedProducts[`${p.storeDisplayName}-${p.id}-${p.variantId}`]);
    if (selectedProductsList.length === 0) {
      setError('Select a product to send to the E-Ink display');
      return;
    }
    const product = selectedProductsList[0];
    try {
      const res = await axios.get(`${API_URL}/api/esl/product-displays`, { withCredentials: true });
      const displays = res.data?.displays || [];
      if (displays.length === 0) {
        setError('No Product reference displays set up — assign a spare label in Picking Supervisor → Pick to Light.');
        return;
      }
      setEDisplayChooser({ product, displays });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load E-Ink displays');
    }
  };

  const printPOLabels = async (po) => {
    try {
      if (!po || !po.items || po.items.length === 0) {
        setError('No items in this PO to print');
        return;
      }

      // Label what actually turned up, not what was ordered — a line the supplier did
      // not send gets no labels, and one they over-shipped gets a label per unit.
      const labelLines = getArrivedPoLines(po.items);
      if (labelLines.length === 0) {
        setError(`Nothing on ${po.reference || 'this PO'} was recorded as received — no labels to print`);
        return;
      }

      // Get all SKUs from the PO items
      const skus = labelLines.map(item => item.sku).filter(Boolean);

      // Build SKU maps from local products first, then fill gaps from the backend cache.
      const binLocationMap = {};
      const productDetailsMap = {};
      products.forEach(p => {
        if (!p.sku) return;

        const skuKey = p.sku.toUpperCase();
        productDetailsMap[skuKey] = p;

        if (p.binLocation) {
          binLocationMap[skuKey] = p.binLocation;
        }
      });

      const skusNeedingCache = skus.filter(sku => {
        const skuKey = sku.toUpperCase();
        const cachedProduct = productDetailsMap[skuKey];
        return !binLocationMap[skuKey] || !(Number(cachedProduct?.weight) > 0);
      });

      if (skusNeedingCache.length > 0) {
        try {
          const cacheResponse = await axios.post(`${API_URL}/api/products/batch-search-cache`, { skus: skusNeedingCache });
          if (cacheResponse.data?.products) {
            cacheResponse.data.products.forEach(p => {
              if (!p.sku) return;

              const skuKey = p.sku.toUpperCase();
              productDetailsMap[skuKey] = {
                ...(productDetailsMap[skuKey] || {}),
                ...p
              };

              if (p.binLocation && p.binLocation !== 'Not Set') {
                binLocationMap[skuKey] = p.binLocation;
              }
            });
          }
        } catch (cacheErr) {
          console.warn('Failed to fetch label details from cache, continuing with available data:', cacheErr.message);
        }
      }

      const labelData = labelLines.map(product => {
        // Lookup bin location from our map
        const skuKey = product.sku ? product.sku.toUpperCase() : '';
        const cachedProduct = skuKey ? productDetailsMap[skuKey] : null;
        const binLocation = skuKey ? (binLocationMap[skuKey] || '') : '';

        return {
          sku: product.sku || '',
          // Lets the backend barcode the EAN when the SKU can't be CODE128-encoded
          barcode: cachedProduct?.barcode || product.barcode || '',
          title: product.title || 'No Title',
          variantTitle: getDisplayVariantTitle(
            product.variantTitle || cachedProduct?.variantTitle,
            product.title || cachedProduct?.title
          ),
          location: binLocation, // Use fetched location or blank
          weight: cachedProduct?.weight ?? product.weight ?? 0,
          quantity: Math.max(1, getPoLineArrivedQuantity(product) || 1)
        };
      });

      const { labelWidth, labelHeight } = resolveLabelSize();

      const response = await axios.post(`${API_URL}/api/labels/generate`, {
        products: labelData,
        labelWidth,
        labelHeight,
        darkMode,
        // Label stock: counted once per print, against the PO's store.
        printRequestId: newLabelPrintRequestId(),
        storeKey: po.storeKey || null
      }, {
        responseType: 'text'
      });

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(response.data);
        printWindow.document.close();
        // Auto-tick the "Print Labels" task in the receiving workflow
        try {
          const taskRes = await axios.post(
            `${API_URL}/api/purchase-orders/${po.id}/task`,
            { taskKey: 'printLabels', completed: true, storeKey: po.storeKey },
            { withCredentials: true }
          );
          if (taskRes.data?.success) {
            handleTaskUpdate(po, taskRes.data.tasks, taskRes.data.status);
          }
        } catch (taskErr) {
          console.warn('Failed to auto-tick print labels task:', taskErr.message);
        }
      } else {
        setError('Please allow popups to print labels');
      }

    } catch (err) {
      console.error('Print PO labels error:', err);
      setError(`Failed to generate labels: ${err.message}`);
    }
  };

  const completePOTask = async (po, taskKey) => {
    if (!po?.id || !taskKey) return;

    try {
      const taskRes = await axios.post(
        `${API_URL}/api/purchase-orders/${po.id}/task`,
        { taskKey, completed: true, storeKey: po.storeKey },
        { withCredentials: true }
      );

      if (taskRes.data?.success) {
        handleTaskUpdate(po, taskRes.data.tasks, taskRes.data.status);
      }
    } catch (taskErr) {
      console.error(`Failed to complete PO task ${taskKey}:`, taskErr);
      setError(taskErr.response?.data?.error || `Failed to complete ${taskKey}`);
      throw taskErr;
    }
  };

  const handlePOTaskAction = async (po, taskDef) => {
    if (taskDef?.actionType === 'printWorksheet') {
      setWorksheetCompletionContext({ poId: po.id, taskKey: taskDef.key });
      setSelectedPOForWorksheet(po);
      return true;
    }

    if (taskDef?.actionType === 'printLabels') {
      await printPOLabels(po);
      return true;
    }

    if (taskDef?.actionType === 'loadLabelsInGrid') {
      await loadPOLabelsInGridForReview(po);
      return true;
    }

    return false;
  };

  const handlePOWorksheetPrinted = async () => {
    if (!worksheetCompletionContext || !selectedPOForWorksheet) return;

    try {
      await completePOTask(selectedPOForWorksheet, worksheetCompletionContext.taskKey);
      setWorksheetCompletionContext(null);
      setSelectedPOForWorksheet(null);
    } catch (_err) {
      // completePOTask already surfaces the error to the user.
    }
  };

  const closePOWorksheet = () => {
    setSelectedPOForWorksheet(null);
    setWorksheetCompletionContext(null);
  };

  const updateEditableField = (productKey, field, value) => {
    setEditableData(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        [field]: value
      }
    }));

    // Remove from recentlySaved when user makes a new edit
    // This allows the Save button to reappear
    setRecentlySaved(prev => {
      const next = new Set(prev);
      next.delete(productKey);
      return next;
    });
  };

  const toggleEditMode = (productKey, field) => {
    const key = `${productKey}-${field}`;
    const closing = Boolean(editingFields[key]);
    setEditingFields(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    // Same-brand guard fires as soon as the location field closes (Enter or
    // click-out) rather than waiting for Save.
    if (closing && field === 'binLocation') {
      checkBrandClashOnFieldClose(productKey);
    }
  };

  // Handle Enter key press in barcode field - auto-save and focus weight if empty
  const handleBarcodeEnter = (product, productKey, editable) => {
    console.log('🎯 [handleBarcodeEnter] Barcode entered for:', product.sku);

    // Get the current barcode value
    const barcodeValue = editable.barcode !== undefined ? editable.barcode : product.barcode;

    // Only proceed if barcode has a value
    if (!barcodeValue || barcodeValue.trim() === '') {
      console.log('  ⚠️ [handleBarcodeEnter] No barcode value - skipping auto-save');
      return;
    }

    // Close the barcode editing field
    setEditingFields(prev => ({
      ...prev,
      [`${productKey}-barcode`]: false
    }));

    // Check if weight is missing - if so, we'll focus it after save
    const weightMissing = !product.weight || product.weight === 0;

    // Add barcode to save queue (non-blocking)
    // Create a focused editable object with ONLY the barcode
    const barcodeOnlyEditable = { barcode: barcodeValue };

    console.log('  💾 [handleBarcodeEnter] Adding barcode to save queue:', barcodeValue);

    // Add to save queue without clearing all editable data
    setSaveQueue(prev => [...prev, {
      id: nextSaveQueueItemId(),
      productKey,
      product,
      editable: barcodeOnlyEditable,
      sku: product.sku,
      status: 'pending',
      timestamp: Date.now()
    }]);

    // Clear just the barcode from editable data since it's being saved
    setEditableData(prev => {
      const current = prev[productKey] || {};
      const updated = { ...current };
      delete updated.barcode;
      if (Object.keys(updated).length === 0) {
        const newData = { ...prev };
        delete newData[productKey];
        return newData;
      }
      return { ...prev, [productKey]: updated };
    });

    // Update the product in the list with the new barcode value immediately
    setProducts(prevProducts => prevProducts.map(p => {
      const pKey = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
      if (pKey === productKey) {
        // The server puts the new primary in front of any extra barcodes (APP-06);
        // mirror that so the card's list and +N chip survive the save.
        return { ...p, barcode: barcodeValue, barcodes: replacePrimaryBarcode(productBarcodeList(p), barcodeValue) };
      }
      return p;
    }));

    // If weight is missing, auto-focus the weight field immediately
    // This happens while the barcode save is processing in the background
    if (weightMissing) {
      console.log('  🎯 [handleBarcodeEnter] Weight missing - auto-focusing weight field');
      setTimeout(() => {
        setEditingFields(prev => ({ ...prev, [`${productKey}-weight`]: true }));
        setAutoFocusProduct({ productKey, field: 'weight' });
      }, 50); // Small delay to ensure barcode field closes first
    } else {
      // Weight exists, focus back to search input
      console.log('  🔍 [handleBarcodeEnter] Weight exists - focusing search input');
      setTimeout(() => {
        focusSearchInput();
      }, 50);
    }
  };

  // Handle Enter key press in SKU field - save SKU to Shopify and update local state
  const handleSkuEnter = (product, productKey, editable) => {
    console.log('🏷️ [handleSkuEnter] SKU entered for:', product.sku || '(no sku)', 'variantId:', product.variantId);

    // Get the new SKU value
    const newSkuValue = editable.sku !== undefined ? editable.sku : '';

    // Validate
    if (!newSkuValue || newSkuValue.trim() === '') {
      console.log('  ⚠️ [handleSkuEnter] No SKU value - skipping');
      return;
    }

    const trimmedSku = newSkuValue.trim();

    // Close the SKU editing field
    setEditingFields(prev => ({
      ...prev,
      [`${productKey}-sku`]: false
    }));

    // Build save data - use newSku field for the SKU update + include variantId for fallback lookup
    const skuSaveData = { newSku: trimmedSku };

    console.log('  💾 [handleSkuEnter] Adding SKU to save queue:', trimmedSku);

    // Add to save queue
    setSaveQueue(prev => [...prev, {
      id: nextSaveQueueItemId(),
      productKey,
      product,
      editable: skuSaveData,
      sku: product.sku,
      status: 'pending',
      timestamp: Date.now()
    }]);

    // Clear the SKU from editable data since it's being saved
    setEditableData(prev => {
      const current = prev[productKey] || {};
      const updated = { ...current };
      delete updated.sku;
      if (Object.keys(updated).length === 0) {
        const newData = { ...prev };
        delete newData[productKey];
        return newData;
      }
      return { ...prev, [productKey]: updated };
    });

    // Update the product in the list with the new SKU immediately
    setProducts(prevProducts => prevProducts.map(p => {
      const pKey = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
      if (pKey === productKey) {
        return { ...p, sku: trimmedSku, noSku: false };
      }
      return p;
    }));

    // Focus search input after setting SKU
    setTimeout(() => {
      focusSearchInput();
    }, 50);
  };

  // Same-brand box rule (Rhys 2026-08-31): two different items from one brand must
  // never share a tub. Asks the backend before a new W/H location is written.
  //
  // FAILS OPEN by design: a network error, a server error or an unchecked location
  // returns null, and the save goes ahead. Returns the response only when there is
  // something real to show the user.
  const checkBrandClash = async (location, skus) => {
    const cleanSkus = (skus || [])
      .map(sku => String(sku || '').trim())
      .filter(sku => sku && sku.toUpperCase() !== 'N/A')
      .slice(0, 200);
    if (!location || cleanSkus.length === 0) return null;

    try {
      const response = await axios.post(
        `${API_URL}/api/warehouse/locations/brand-check`,
        { location, skus: cleanSkus },
        // Short timeout: a cold catalog cache can take seconds to rebuild, and a
        // putaway must never wait on it. A timeout falls through to the save.
        { withCredentials: true, timeout: 8000 }
      );
      const data = response.data;
      if (!data || data.checked !== true) return null;
      const clashes = Array.isArray(data.clashes) ? data.clashes : [];
      const batchClashes = Array.isArray(data.batchClashes) ? data.batchClashes : [];
      if (clashes.length === 0 && batchClashes.length === 0) return null;
      return { ...data, clashes, batchClashes };
    } catch (err) {
      console.warn('[brand-check] skipped:', err.message);
      return null;
    }
  };

  // Same-brand guard, early edition: fires the moment the location FIELD closes
  // (Enter or click-out), so staff hear about a clash while still standing at
  // the shelf - not later when they press Save. "Put it here anyway" on this
  // early prompt is remembered per product+tub so Save does not ask again.
  const checkBrandClashOnFieldClose = async (productKey) => {
    const product = products.find(p => `${p.storeDisplayName}-${p.id}-${p.variantId}` === productKey);
    if (!product) return;
    const pending = editableData[productKey];
    if (!pending || pending.binLocation === undefined) return;
    const nextLocation = String(pending.binLocation || '').trim();
    const currentLocation = String(product.binLocation || '').trim();
    if (!nextLocation || nextLocation.toUpperCase() === currentLocation.toUpperCase()) return;
    if (brandClashApprovedRef.current[productKey] === nextLocation.toUpperCase()) return;
    const result = await checkBrandClash(nextLocation, [product.sku]);
    if (result) {
      setBrandClashPrompt({ mode: 'field', location: nextLocation, product, productKey, editable: pending, result });
    }
  };

  // PO putaway audit (see the state block): one string that changes exactly
  // when an audited SKU's SAVED location does — single saves, bulk scans and
  // the background live refresh all land in `products`, so flags appear and
  // clear on their own as staff split the tubs.
  const poAuditSignature = useMemo(() => {
    if (poAuditSkus.length === 0) return '';
    return collectPoAuditItems(products, poAuditSkus)
      .map((item) => `${item.sku.toUpperCase()}|${item.location.toUpperCase()}`)
      .sort()
      .join(',');
  }, [products, poAuditSkus]);

  useEffect(() => {
    if (poAuditSkus.length === 0) {
      setPoTubClashes(null);
      setPoTubAuditPending(false);
      return undefined;
    }
    // First pass for a PO goes straight out; the debounce only exists to
    // coalesce the edits and background refreshes that follow.
    const timer = setTimeout(async () => {
      const items = collectPoAuditItems(products, poAuditSkus);
      const token = ++poTubAuditTokenRef.current;
      if (items.length === 0) {
        setPoTubClashes(null);
        setPoTubAuditPending(false);
        return;
      }
      try {
        const response = await axios.post(
          `${API_URL}/api/warehouse/locations/brand-check-batch`,
          { items },
          { withCredentials: true, timeout: 15000 }
        );
        if (token !== poTubAuditTokenRef.current) return;
        poTubAuditRanRef.current = true;
        setPoTubAuditPending(false);
        const data = response.data;
        // Advisory and fail-open like the single check: an unchecked answer
        // keeps whatever flags are already showing rather than lying "clean".
        if (!data || data.checked !== true) return;
        const slots = Array.isArray(data.slots) ? data.slots : [];
        setPoTubClashes(slots.length > 0 ? mapTubClashSlots(slots) : null);
      } catch (err) {
        console.warn('[po-tub-audit] skipped:', err.message);
        if (token === poTubAuditTokenRef.current) setPoTubAuditPending(false);
      }
    }, poTubAuditRanRef.current ? 600 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poAuditSignature]);

  // SKUs the bulk bar would act on, with the same selection and dedupe rules as
  // runBulkWarehouseUpdate, so the guard checks exactly what would be written.
  const collectSelectedSkus = () => {
    const seen = new Set();
    const skus = [];
    products
      .filter(p => selectedProducts[`${p.storeDisplayName}-${p.id}-${p.variantId}`])
      .forEach(p => {
        const sku = String(p.sku || '').trim();
        if (!sku || sku.toUpperCase() === 'N/A' || seen.has(sku.toUpperCase())) return;
        seen.add(sku.toUpperCase());
        skus.push(sku);
      });
    return skus;
  };

  // Non-blocking save - adds to queue and returns immediately.
  // Options:
  //   skipBrandCheck    - the same-brand guard already ran (override given).
  //   markRecentlySaved - false keeps the card's Save button visible, for
  //                       partial saves (Put Away writes binLocation only) so
  //                       staged weight/barcode edits never look saved.
  //   awaitCompletion   - true returns a promise that settles when the queue
  //                       has actually written to Shopify/Firestore (or when
  //                       the brand-clash prompt is declined), so a caller can
  //                       show honest "saved" state instead of assuming it.
  const handleSave = async (product, productKey, editable, options = {}) => {
    const { skipBrandCheck = false, markRecentlySaved = true, awaitCompletion = false } = options;
    // Same-brand guard runs only on a real location CHANGE, before anything is
    // queued. Backstop for paths that skip the field-close prompt (Put Away,
    // eDisplay saves) - an override already given at field close is honoured.
    if (!skipBrandCheck && editable && editable.binLocation !== undefined) {
      const nextLocation = String(editable.binLocation || '').trim();
      const currentLocation = String(product.binLocation || '').trim();
      const approved = brandClashApprovedRef.current[productKey] === nextLocation.toUpperCase();
      if (nextLocation && !approved && nextLocation.toUpperCase() !== currentLocation.toUpperCase()) {
        const result = await checkBrandClash(nextLocation, [product.sku]);
        if (result) {
          const prompt = {
            mode: 'single',
            location: nextLocation,
            product,
            productKey,
            editable,
            result,
            // Carried through to the override re-save so Put Away's
            // location-only / awaited semantics survive the prompt.
            saveOptions: { markRecentlySaved, awaitCompletion }
          };
          if (awaitCompletion) {
            return new Promise((resolve, reject) => {
              setBrandClashPrompt({ ...prompt, deferred: { resolve, reject } });
            });
          }
          setBrandClashPrompt(prompt);
          return;
        }
      }
    }
    delete brandClashApprovedRef.current[productKey];

    // Hide the Save button while the save is queued/in flight.
    // Editable data is NOT cleared here - it is cleared only after the
    // server confirms the save, so failed saves keep the user's edits.
    if (markRecentlySaved) {
      setRecentlySaved(prev => new Set(prev).add(productKey));
    }

    // Close any open inline edit fields
    setEditingFields(prev => {
      const newFields = { ...prev };
      delete newFields[`${productKey}-sku`];
      delete newFields[`${productKey}-weight`];
      delete newFields[`${productKey}-barcode`];
      delete newFields[`${productKey}-binLocation`];
      delete newFields[`${productKey}-overflow1`];
      delete newFields[`${productKey}-overflow2`];
      delete newFields[`${productKey}-dimensions`];
      return newFields;
    });

    const queueItem = {
      id: nextSaveQueueItemId(), // Collision-safe ID (Date.now() collided on bulk saves)
      productKey,
      product,
      editable,
      sku: product.sku, // Store SKU to identify all related products
      userEmail: currentUser?.email || 'dev@localhost',
      status: 'pending',
      timestamp: Date.now()
    };

    if (options.awaitCompletion === true) {
      return new Promise((resolve, reject) => {
        setSaveQueue(prev => [...prev, { ...queueItem, resolve, reject }]);
      });
    }

    setSaveQueue(prev => [...prev, queueItem]);
    return undefined;
  };

  // Process save queue in background - strictly one save at a time.
  // The in-flight lock prevents this effect (which re-runs on every queue
  // state change) from starting a second save or double-picking an item.
  useEffect(() => {
    const processSave = async () => {
      if (isProcessingSaveRef.current) return;

      // Find first pending item
      const pendingItem = saveQueue.find(item => item.status === 'pending');
      if (!pendingItem) return;

      isProcessingSaveRef.current = true;

      // Mark as saving
      setSaveQueue(prev => prev.map(item =>
        item.id === pendingItem.id ? { ...item, status: 'saving' } : item
      ));

      try {
        const { product, productKey: _productKey, editable } = pendingItem;

        // Build save data - include ALL current values to propagate to other stores
        const saveData = buildProductSaveData(product, editable);

        console.log('[SaveQueue] Processing save:', saveData);

        // Use the multi-store endpoint to update ALL stores with this SKU
        // Add a 60 second timeout for multi-store updates (they can take a while)
        const response = await axios.post(`${API_URL}/api/products/update-metafields-all-stores`, saveData, {
          timeout: 60000 // 60 seconds
        });

        console.log('[SaveQueue] Response received:', response.status);
        console.log('[SaveQueue] Response:', response.data);

        if (!response.data || !response.data.results) {
          throw new Error('Invalid response format from server');
        }

        const responseResults = Array.isArray(response.data.results) ? response.data.results : [];
        const { failedResults, successfulStores } = summarizeStoreResults(responseResults);

        if (successfulStores === 0 || failedResults.length > 0) {
          const firstFailure = failedResults[0];
          const fieldError = firstFailure?.errors?.[0];
          throw new Error(
            fieldError?.error ||
            firstFailure?.error ||
            'Save did not complete for Shopify'
          );
        }

        // Update ALL products with the same SKU (or same variantId for SKU-less) in state with the saved values
        const affectedProductKeys = [];
        setProducts(prev => prev.map(p => {
          const isMatch = product.sku && product.sku !== 'N/A'
            ? p.sku === product.sku
            : (p.variantId === product.variantId && p.store === product.store);

          if (isMatch) {
            // Use the same key format as rendering: ${storeDisplayName}-${product.id}-${product.variantId}
            const key = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
            affectedProductKeys.push(key);
            const updatedProduct = { ...p };
            if (saveData.newSku !== undefined) {
              updatedProduct.sku = saveData.newSku;
              updatedProduct.noSku = false;
            }
            if (saveData.weight !== undefined) updatedProduct.weight = saveData.weight;
            if (saveData.barcode !== undefined) {
              updatedProduct.barcode = saveData.barcode;
              // A single new primary keeps the extras behind it (the store's saved set wins).
              const savedForStore = responseResults.find(result => result.store === p.store)?.updates?.barcodes;
              updatedProduct.barcodes = Array.isArray(savedForStore) ? savedForStore : replacePrimaryBarcode(productBarcodeList(p), saveData.barcode);
            }
            if (saveData.barcodes !== undefined) {
              // Full typed set (APP-06): first = the barcode everything else reads.
              // The store's saved set wins: the server merges the edit onto
              // Shopify's live set, which can hold barcodes the editor never saw.
              const savedForStore = responseResults.find(result => result.store === p.store)?.updates?.barcodes;
              updatedProduct.barcodes = Array.isArray(savedForStore) ? savedForStore : saveData.barcodes;
              updatedProduct.barcode = updatedProduct.barcodes[0]?.value || '';
            }
            if (saveData.binLocation !== undefined) {
              const matchingResult = responseResults.find(result =>
                result.store === p.store || result.store === p.storeUrl || result.store === p.storeDisplayName
              );
              updatedProduct.binLocationHistory = Array.isArray(matchingResult?.binLocationHistory)
                ? matchingResult.binLocationHistory
                : buildLocalBinLocationHistory(p, saveData.binLocation, pendingItem.userEmail, new Date(pendingItem.timestamp).toISOString());
              updatedProduct.binLocation = saveData.binLocation;
              // The save also writes Firestore, so the tooltip's database value follows suit
              updatedProduct.dbBinLocation = saveData.binLocation;
              updatedProduct.dbBinLocationSource = 'database';
            }
            if (saveData.overflow1 !== undefined) updatedProduct.overflow1 = saveData.overflow1;
            if (saveData.overflow2 !== undefined) updatedProduct.overflow2 = saveData.overflow2;
            if (saveData.length !== undefined) updatedProduct.length = saveData.length === '' ? null : parseFloat(saveData.length);
            if (saveData.width !== undefined) updatedProduct.width = saveData.width === '' ? null : parseFloat(saveData.width);
            if (saveData.height !== undefined) updatedProduct.height = saveData.height === '' ? null : parseFloat(saveData.height);

            // Also update storeMetafields so other stores show as in sync (removes green borders)
            if (updatedProduct.storeMetafields) {
              const updatedStoreMetafields = { ...updatedProduct.storeMetafields };
              // Update all other stores' metafields with the new values
              Object.keys(updatedStoreMetafields).forEach(storeName => {
                if (storeName !== p.storeDisplayName) {
                  updatedStoreMetafields[storeName] = {
                    ...updatedStoreMetafields[storeName],
                    ...(saveData.binLocation !== undefined && { binLocation: saveData.binLocation }),
                    ...(saveData.overflow1 !== undefined && { overflow1: saveData.overflow1 }),
                    ...(saveData.overflow2 !== undefined && { overflow2: saveData.overflow2 })
                  };
                }
              });
              updatedProduct.storeMetafields = updatedStoreMetafields;
            }

            return updatedProduct;
          }
          return p;
        }));

        // A part of an assembled kit moved: the server has refreshed that kit's
        // "parts" location, so refetch the kits and the kit's card follows.
        if ((saveData.binLocation !== undefined || saveData.overflow1 !== undefined || saveData.overflow2 !== undefined)
          && kitLinkFor(kitSplitIndexRef.current, product)?.asChild?.length) {
          loadKitSplitsRef.current();
        }

        // Clear only the fields confirmed saved for ALL products with this SKU.
        // Fields the user edited while the save was in flight are kept.
        setEditableData(prev => {
          const newData = { ...prev };
          affectedProductKeys.forEach(key => {
            const remaining = clearSavedFieldsFromEditable(newData[key], pendingItem.editable);
            if (remaining === null) {
              delete newData[key];
            } else {
              newData[key] = remaining;
            }
          });
          return newData;
        });

        // Clear editing fields for ALL products with this SKU
        // BUT preserve the weight field if it's currently being edited (auto-focus workflow)
        setEditingFields(prev => {
          const newFields = { ...prev };
          affectedProductKeys.forEach(key => {
            // Only clear the barcode field that was just saved
            // Keep weight field active if user is entering weight
            delete newFields[`${key}-barcode`];
            delete newFields[`${key}-binLocation`];
            delete newFields[`${key}-overflow1`];
            delete newFields[`${key}-overflow2`];
            delete newFields[`${key}-dimensions`];
            // NOTE: We intentionally do NOT delete weight here to keep it active
            // during the barcode auto-save workflow
          });
          return newFields;
        });

        // Keep products in recentlySaved to hide the Save button
        // It will be cleared when user makes a new edit (see updateEditableField)

        // Soft per-store warnings (APP-09: Shopify packed dimensions not
        // pushed). The save itself succeeded; the status line says so.
        const warning = responseResults.flatMap(r => (Array.isArray(r.warnings) ? r.warnings : []))[0]?.warning || null;

        // Mark as success
        setSaveQueue(prev => prev.map(item =>
          item.id === pendingItem.id
            ? { ...item, status: 'success', message: `Saved to ${successfulStores} store(s)`, storeCount: successfulStores, warning }
            : item
        ));
        pendingItem.resolve?.({ response: response.data, successfulStores });

        // Remove from queue after 3 seconds
        setTimeout(() => {
          setSaveQueue(prev => prev.filter(item => item.id !== pendingItem.id));
        }, 3000);

      } catch (error) {
        console.error('[SaveQueue] Error:', error);
        const errorMessage = error.response?.data?.error || error.message;

        // Mark as error
        setSaveQueue(prev => prev.map(item =>
          item.id === pendingItem.id
            ? { ...item, status: 'error', message: errorMessage }
            : item
        ));

        // Remove from recentlySaved so user can try again.
        // Editable data was never cleared, so the edits are still on screen.
        setRecentlySaved(prev => {
          const next = new Set(prev);
          next.delete(pendingItem.productKey);
          return next;
        });
        pendingItem.reject?.(error);

        // Remove from queue after 5 seconds
        setTimeout(() => {
          setSaveQueue(prev => prev.filter(item => item.id !== pendingItem.id));
        }, 5000);
      } finally {
        // Release the lock and re-trigger the effect so the next pending
        // item is picked up (the status update above already changes state,
        // but this guards the case where that state landed before release).
        isProcessingSaveRef.current = false;
        setSaveQueue(prev => [...prev]);
      }
    };

    processSave();
  }, [saveQueue]); // Re-run when queue changes

  // Bulk Update bar (locations, clears, weights): one batched request for all
  // selected products. The backend groups writes into GraphQL metafieldsSet /
  // metafieldsDelete / productVariantsBulkUpdate calls instead of per-product
  // REST round-trips, and writes the product cache back itself.
  const runBulkWarehouseUpdate = async (fields, buildMessage) => {
    const selectedProductsList = products
      .filter(p => selectedProducts[`${p.storeDisplayName}-${p.id}-${p.variantId}`]);

    if (selectedProductsList.length === 0) {
      setError('No products selected');
      return;
    }

    const items = [];
    const seenSkus = new Set();
    selectedProductsList.forEach(p => {
      const sku = String(p.sku || '').trim();
      if (!sku || sku.toUpperCase() === 'N/A' || seenSkus.has(sku.toUpperCase())) return;
      seenSkus.add(sku.toUpperCase());
      items.push({ sku, ...fields });
    });

    if (items.length === 0) {
      setError('Selected products have no usable SKUs');
      return;
    }

    setIsUpdating(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await axios.post(
        `${API_URL}/api/products/bulk-update-warehouse-fields`,
        { items },
        { withCredentials: true }
      );
      const summary = response.data?.summary || { updatedSkus: [], errorSkus: [], skippedSkus: [] };
      const updatedSet = new Set(summary.updatedSkus.map(s => s.toUpperCase()));

      // Reflect the new values on the cards and drop matching pending edits so
      // stale editable overrides don't mask what was just saved.
      if (updatedSet.size > 0) {
        setProducts(prev => prev.map(p => {
          if (!p.sku || !updatedSet.has(String(p.sku).toUpperCase())) return p;
          const changes = {};
          if (fields.binLocation !== undefined) changes.binLocation = fields.binLocation;
          if (fields.weight !== undefined) {
            changes.weight = fields.weight;
            changes.weightUnit = 'GRAMS';
          }
          ['length', 'width', 'height'].forEach(dim => {
            if (fields[dim] !== undefined) changes[dim] = fields[dim];
          });
          return { ...p, ...changes };
        }));

        setEditableData(prev => {
          const next = { ...prev };
          selectedProductsList.forEach(p => {
            if (!p.sku || !updatedSet.has(String(p.sku).toUpperCase())) return;
            const productKey = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
            if (!next[productKey]) return;
            const cleaned = { ...next[productKey] };
            if (fields.binLocation !== undefined) delete cleaned.binLocation;
            if (fields.weight !== undefined) delete cleaned.weight;
            ['length', 'width', 'height'].forEach(dim => {
              if (fields[dim] !== undefined) delete cleaned[dim];
            });
            if (Object.keys(cleaned).length === 0) delete next[productKey];
            else next[productKey] = cleaned;
          });
          return next;
        });
      }

      const listSkus = (skus) => {
        const shown = skus.slice(0, 3).join(', ');
        return skus.length > 3 ? `${shown} +${skus.length - 3} more` : shown;
      };
      const suffixParts = [];
      if (summary.errorSkus.length > 0) suffixParts.push(`failed: ${listSkus(summary.errorSkus)}`);
      if (summary.skippedSkus.length > 0) suffixParts.push(`not in cache: ${listSkus(summary.skippedSkus)}`);
      const suffix = suffixParts.length > 0 ? ` — ${suffixParts.join('; ')}` : '';

      if (summary.updatedSkus.length === 0) {
        setError(`No products were updated${suffix}`);
      } else {
        setSuccessMessage(`${buildMessage(summary.updatedSkus.length, items.length)}${suffix}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Bulk update failed');
    } finally {
      setIsUpdating(false);
    }
  };

  const applyBulkLocation = async (location) => {
    await runBulkWarehouseUpdate(
      { binLocation: location },
      (updated, total) => `✅ Updated W/H location to "${location}" on ${updated} of ${total} product(s)`
    );
    setLocationBarcode('');
    focusSearchInput();
  };

  const handleLocationUpdate = async (e) => {
    e.preventDefault();
    // Shorthand like the other entry points: t5a1 -> T-05-A-01, p12 -> P12.
    const location = expandLocationShorthand(locationBarcode.trim()) || locationBarcode.trim();
    if (location && isShelfOnlyBinCode(location)) {
      // Never bulk-write a shelf with no slot (Rhys 2026-10-01): pick the slot first.
      setLocationBarcode(location);
      setBulkShelfPick(location);
      return;
    }
    await commitBulkLocation(location);
  };

  const commitBulkLocation = async (location) => {
    if (!location) {
      setError('Please enter a location barcode');
      return;
    }

    if (products.length === 0) {
      setError('No products to update');
      return;
    }

    // Same-brand guard: checks the whole batch against the tub before writing.
    const selectedSkus = collectSelectedSkus();
    const clashResult = await checkBrandClash(location, selectedSkus);
    if (clashResult) {
      setBrandClashPrompt({ mode: 'bulk', location, skuCount: selectedSkus.length, result: clashResult });
      return;
    }

    await applyBulkLocation(location);
  };

  // Override for the blank-field guard above: bulk-remove the W/H location from
  // every selected product (confirmed via modal — it pushes to all linked stores).
  const handleBulkClearLocations = async () => {
    setShowBulkClearLocations(false);

    await runBulkWarehouseUpdate(
      { binLocation: '' },
      (updated, total) => `🧹 Cleared W/H location on ${updated} of ${total} product(s)`
    );
    setLocationBarcode('');
    focusSearchInput();
  };

  // Bulk "Track expiry" / "Stop tracking expiry" for the selected rows
  // (POST /api/expiry/tracked/bulk, 500 per call). Optimistic like the card toggle.
  const handleBulkExpiryTracking = async (tracked) => {
    const seen = new Set();
    const targets = [];
    const beforeOf = (row) => ({ expiryTracked: Boolean(row?.expiryTracked), nextExpiry: row?.nextExpiry ?? null, expiryUntrackedQty: row?.expiryUntrackedQty ?? 0 });
    // Same rule as the card toggle: a SKU listed in several stores is flagged
    // in each of them, so either store's PO asks for dates.
    const linkedStoresOf = (p) => linkedExpiryStores(p.storeMetafields
      ? Object.entries(p.storeMetafields).map(([name, data]) => ({ store: name, storeUrl: data?.storeUrl || null }))
      : products
        .filter(o => p.inventoryItemId && o.inventoryItemId === p.inventoryItemId && o.store !== p.store)
        .map(o => ({ storeUrl: o.store })), p.store);
    products
      .filter(p => selectedProducts[`${p.storeDisplayName}-${p.id}-${p.variantId}`])
      .forEach(p => {
        const sku = String(p.sku || '').trim();
        if (!p.store || !sku || sku.toUpperCase() === 'N/A') return;
        [p.store, ...linkedStoresOf(p)].forEach((store) => {
          const key = `${store}|${sku.toUpperCase()}`;
          if (seen.has(key)) return;
          seen.add(key);
          const row = store === p.store ? p : products.find(o => o.store === store && String(o.sku || '').trim().toUpperCase() === sku.toUpperCase());
          targets.push({ store, sku, before: beforeOf(row) });
        });
      });
    if (targets.length === 0) {
      setError('Selected products have no usable SKUs');
      return;
    }
    setBulkExpiryBusy(tracked ? 'on' : 'off');
    setError('');
    patchExpiryRows(targets, () => (tracked
      ? { expiryTracked: true }
      : { expiryTracked: false, nextExpiry: null, expiryUntrackedQty: 0 }));
    try {
      const result = await expiryApi.bulkSetTracked(targets.map(({ store, sku }) => ({ store, sku })), tracked);
      setSuccessMessage(`${tracked ? 'Tracking' : 'Stopped tracking'} expiry on ${result.count || targets.length} product(s)`);
      setTimeout(() => setSuccessMessage(''), 3000);
      // Newly tracked rows pick up any batches recorded before (small selections only).
      if (tracked && targets.length <= 30) targets.forEach(t => refreshRowExpiry(t.store, t.sku));
    } catch (err) {
      patchExpiryRows(targets, (_row, target) => target.before);
      setError(err.response?.data?.error || 'Failed to update expiry tracking');
    } finally {
      setBulkExpiryBusy(null);
    }
  };

  const handleBulkWeightUpdate = async (e) => {
    e.preventDefault();

    if (!bulkWeight.trim()) {
      setError('Please enter a weight value');
      return;
    }

    // Validate that it's a number
    const weightValue = parseFloat(bulkWeight);
    if (isNaN(weightValue) || weightValue < 0) {
      setError('Please enter a valid weight (positive number)');
      return;
    }

    if (products.length === 0) {
      setError('No products to update');
      return;
    }

    const weightGrams = Math.round(weightValue);
    await runBulkWarehouseUpdate(
      { weight: weightGrams },
      (updated, total) => `⚖️ Updated weight to ${weightGrams}g on ${updated} of ${total} product(s)`
    );
    setBulkWeight('');
    focusSearchInput();
  };

  // Bulk shipping dimensions (cm). Only the filled fields are written, so staff can
  // set a single side or a full carton size in one pass.
  const handleBulkDimsUpdate = async (e) => {
    e?.preventDefault?.();

    const dims = [
      { field: 'length', label: 'L', value: bulkLength },
      { field: 'width', label: 'W', value: bulkWidth },
      { field: 'height', label: 'H', value: bulkHeight }
    ];
    const filled = dims.filter(d => d.value.trim() !== '');

    if (filled.length === 0) {
      setError('Enter at least one dimension (cm)');
      return;
    }
    for (const dim of filled) {
      const parsed = parseFloat(dim.value);
      if (isNaN(parsed) || parsed <= 0) {
        setError(`Please enter a valid ${dim.field} in cm (positive number)`);
        return;
      }
    }
    if (products.length === 0) {
      setError('No products to update');
      return;
    }

    const fields = {};
    filled.forEach(dim => {
      fields[dim.field] = parseFloat(dim.value);
    });
    const dimsLabel = filled.map(dim => `${dim.label} ${parseFloat(dim.value)}`).join(' × ');

    await runBulkWarehouseUpdate(
      fields,
      (updated, total) => `📐 Updated dims (${dimsLabel} cm) on ${updated} of ${total} product(s)`
    );
    setBulkLength('');
    setBulkWidth('');
    setBulkHeight('');
    focusSearchInput();
  };

  const clearAll = () => {
    setProducts([]);
    setEditableData({});
    setSelectedProducts({});
    setLocationBarcode('');
    setLuckyStatus(null); // Clear the I'm Feeling Lucky status
    // The PO is gone with the grid: end its putaway audit and stop keying
    // future vendor-ref scans to a supplier that is no longer on screen.
    setPoAuditSkus([]);
    setPoTubClashes(null);
    setGridPoSupplier(null);
    localStorage.removeItem('warehouseProducts');
    localStorage.removeItem('warehouseEditableData');
    localStorage.removeItem('warehouseSelectedProducts');
    setSuccessMessage('All products cleared');
    focusSearchInput();
  };

  // Fetch cache status (stable for useEffect deps). Optional signal to abort on cleanup.
  const fetchCacheStatus = useCallback(async (signal) => {
    if (skipRemoteBlockedFetch()) return;
    try {
      const response = await axios.get(`${API_URL}/api/cache/status`, {
        params: { probe: '1' },
        ...(signal && { signal })
      });
      if (signal?.aborted) return;
      setCacheStatus(response.data);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'AbortError') return;
      console.error('Failed to fetch cache status:', err);
    }
  }, [skipRemoteBlockedFetch]);

  // Refresh location database cache — opens picker only; never starts a refresh itself.
  const refreshDatabase = () => {
    refreshPickerOpenedAtRef.current = Date.now();
    if (shouldRestoreRefreshPickerRun({ isRefreshingCache, refreshRun })) {
      setSelectedStores(buildRefreshStoreSelection(refreshRun.selectedTargets, EMPTY_REFRESH_STORE_SELECTION));
    } else {
      setSelectedStores({ ...EMPTY_REFRESH_STORE_SELECTION });
      setRefreshTargetStatuses({});
      setRefreshTargetErrors({});
    }
    setShowCacheModal(false);
    setShowRefreshModal(true);
  };

  const openDbStatusOrRefreshProgress = () => {
    if (shouldRestoreRefreshPickerRun({ isRefreshingCache, refreshRun })) {
      refreshDatabase();
      return;
    }
    setShowCacheModal(true);
  };

  // Handle actual refresh after store selection
  const getTargetRunStatusTone = useCallback((status) => {
    if (status === 'failed') return 'critical';
    if (status === 'complete') return 'success';
    if (status === 'refreshing' || status === 'started_background') return 'warning';
    return 'subdued';
  }, []);

  const getTargetRunStatusLabel = useCallback((status) => {
    if (status === 'failed') return 'Failed';
    if (status === 'complete') return 'Complete';
    if (status === 'refreshing' || status === 'started_background') return 'Running…';
    return 'Queued';
  }, []);

  const updateRefreshTargetStatus = useCallback((key, status) => {
    setRefreshTargetStatuses((previous) => ({
      ...previous,
      [key]: status
    }));
  }, []);

  const updateRefreshTargetError = useCallback((key, message) => {
    setRefreshTargetErrors((previous) => {
      const next = { ...previous };
      if (message) {
        next[key] = message;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  useEffect(() => {
    refreshTargetStatusesRef.current = refreshTargetStatuses;
  }, [refreshTargetStatuses]);

  const runPlannerAsyncTargetKickoff = useCallback(async (targetKey) => {
    if (targetKey === 'planner-sales-phaser') {
      const response = await axios.post(`${API_URL}/api/planner/sync/start`, {
        channel: 'phaserfpv',
        mode: 'incremental'
      });
      return response.data;
    }
    if (targetKey === 'planner-sales-ccc') {
      const response = await axios.post(`${API_URL}/api/planner/sync/start`, {
        channel: 'centralcoastcoffee',
        mode: 'incremental'
      });
      return response.data;
    }
    if (targetKey === 'planner-cost-backfill') {
      const response = await axios.post(`${API_URL}/api/planner/costs/backfill/start`, {});
      return response.data;
    }
    if (targetKey === 'planner-supplier-skus') {
      const response = await axios.post(`${API_URL}/api/planner/supplier-skus/sync/start`, {});
      return response.data;
    }
    return { started: false, reason: 'unsupported_target' };
  }, []);

  const fetchPlannerStatusSnapshot = useCallback(async () => {
    const [syncRes, costRes, supplierSkuRes] = await Promise.all([
      axios.get(`${API_URL}/api/planner/sync/status`),
      axios.get(`${API_URL}/api/planner/costs/backfill/status`),
      axios.get(`${API_URL}/api/planner/supplier-skus/sync/status`)
    ]);
    const jobs = Array.isArray(syncRes.data?.jobs) ? syncRes.data.jobs : [];
    return {
      syncJobs: jobs,
      costBackfillStatus: costRes.data || null,
      supplierSkuSyncStatus: supplierSkuRes.data || null
    };
  }, []);

  const evaluateRefreshRunStatuses = useCallback((runConfig, cacheSnapshot, plannerSnapshot) => {
    if (!runConfig) {
      return { statuses: {}, errors: {}, allDone: true, anyFailed: false };
    }
    const thresholdMs = runConfig.startedAtMs - 2000;
    const nextStatuses = {};
    const nextErrors = {};
    let allDone = true;
    let anyFailed = false;

    const syncJobs = plannerSnapshot?.syncJobs || [];
    const costBackfillStatus = plannerSnapshot?.costBackfillStatus || null;
    const supplierSkuSyncStatus = plannerSnapshot?.supplierSkuSyncStatus || null;

    const plannerSyncByChannel = {
      phaserfpv: syncJobs.find((job) => String(job.channel || '').trim() === 'phaserfpv') || null,
      centralcoastcoffee: syncJobs.find((job) => String(job.channel || '').trim() === 'centralcoastcoffee') || null
    };

    runConfig.selectedTargets.forEach((targetKey) => {
      let status = refreshTargetStatusesRef.current[targetKey] || 'queued';

      if (targetKey === 'inventory-planner') {
        const plannerRefresh = cacheSnapshot?.plannerRefresh;
        const plannerAttemptMs = plannerRefresh?.lastAttempt ? Date.parse(plannerRefresh.lastAttempt) : null;
        if (plannerRefresh?.isRefreshing) {
          status = 'refreshing';
        } else if (plannerAttemptMs && plannerAttemptMs >= thresholdMs) {
          status = plannerRefresh?.lastError ? 'failed' : 'complete';
          if (plannerRefresh?.lastError) {
            nextErrors[targetKey] = String(plannerRefresh.lastError);
          }
        }
      } else if (targetKey === 'planner-catalog-ccc') {
        const catalogRefresh = cacheSnapshot?.plannerCatalogRefresh;
        const catalogAttemptMs = catalogRefresh?.lastAttempt ? Date.parse(catalogRefresh.lastAttempt) : null;
        if (catalogRefresh?.isRefreshing) {
          status = 'refreshing';
        } else if (catalogAttemptMs && catalogAttemptMs >= thresholdMs) {
          status = catalogRefresh?.lastError ? 'failed' : 'complete';
          if (catalogRefresh?.lastError) {
            nextErrors[targetKey] = String(catalogRefresh.lastError);
          }
        }
      } else if (targetKey === 'purchase-orders-phaser') {
        if (isIncomingStockTargetRefreshing(cacheSnapshot, 'phaser')) {
          status = 'refreshing';
        } else if (cacheSnapshot?.poRefresh?.lastAttempt) {
          status = cacheSnapshot?.poCache?.phaser?.lastError ? 'failed' : 'complete';
          if (cacheSnapshot?.poCache?.phaser?.lastError) {
            nextErrors[targetKey] = String(cacheSnapshot.poCache.phaser.lastError);
          }
        }
      } else if (targetKey === 'purchase-orders-ccc') {
        if (isIncomingStockTargetRefreshing(cacheSnapshot, 'ccc')) {
          status = 'refreshing';
        } else if (cacheSnapshot?.poRefresh?.lastAttempt) {
          status = cacheSnapshot?.poCache?.ccc?.lastError ? 'failed' : 'complete';
          if (cacheSnapshot?.poCache?.ccc?.lastError) {
            nextErrors[targetKey] = String(cacheSnapshot.poCache.ccc.lastError);
          }
        }
      } else if (targetKey === 'qoblox-suppliers') {
        if (isIncomingStockTargetRefreshing(cacheSnapshot, 'suppliers')) {
          status = 'refreshing';
        } else if (cacheSnapshot?.poRefresh?.lastAttempt) {
          status = 'complete';
        }
      } else if (targetKey === 'planner-sales-phaser') {
        status = resolvePlannerAsyncRefreshTarget(plannerSyncByChannel.phaserfpv, thresholdMs, {
          currentStatus: status,
          runStartedAtMs: runConfig.startedAtMs
        });
        if (status === 'failed' && !nextErrors[targetKey]) {
          nextErrors[targetKey] = 'Timed out waiting for Phaser planner ingest status';
        }
      } else if (targetKey === 'planner-sales-ccc') {
        status = resolvePlannerAsyncRefreshTarget(plannerSyncByChannel.centralcoastcoffee, thresholdMs, {
          currentStatus: status,
          runStartedAtMs: runConfig.startedAtMs
        });
        if (status === 'failed' && !nextErrors[targetKey]) {
          nextErrors[targetKey] = 'Timed out waiting for CCC planner ingest status';
        }
      } else if (targetKey === 'planner-cost-backfill') {
        status = resolvePlannerCostBackfillAsyncTarget(costBackfillStatus, thresholdMs, {
          currentStatus: status,
          runStartedAtMs: runConfig.startedAtMs
        });
        if (status === 'failed' && !nextErrors[targetKey]) {
          nextErrors[targetKey] = 'Timed out waiting for Lightspeed cost sync status';
        }
      } else if (targetKey === 'planner-supplier-skus') {
        status = resolvePlannerSupplierSkuSyncAsyncTarget(supplierSkuSyncStatus, thresholdMs, {
          currentStatus: status,
          runStartedAtMs: runConfig.startedAtMs
        });
        if (status === 'failed' && !nextErrors[targetKey]) {
          nextErrors[targetKey] = 'Timed out waiting for Lightspeed supplier SKU sync status';
        }
      } else {
        const storeDomainByKey = {
          'phaser-fpv': 'phaser-fpv.myshopify.com',
          'central-coast-coffee': 'central-coast-coffee.myshopify.com',
          'porlex-australia': 'porlex-australia.myshopify.com',
          'ace176-c7': 'ace176-c7.myshopify.com'
        };
        const storeDomain = storeDomainByKey[targetKey];
        const storeStatus = cacheSnapshot?.stores?.find((entry) => entry.store === storeDomain);
        const refreshedAtMs = cacheSnapshot?.lastRefresh ? Date.parse(cacheSnapshot.lastRefresh) : null;
        if (storeStatus?.isRefreshing) {
          status = 'refreshing';
        } else if (storeStatus?.error) {
          status = 'failed';
          nextErrors[targetKey] = String(storeStatus.error);
        } else if (refreshedAtMs && refreshedAtMs >= thresholdMs) {
          status = 'complete';
        }
      }

      if (status !== 'complete' && status !== 'failed') {
        allDone = false;
      }
      if (status === 'failed') {
        anyFailed = true;
      }
      nextStatuses[targetKey] = status;
    });

    return {
      statuses: nextStatuses,
      errors: nextErrors,
      allDone,
      anyFailed
    };
  }, []);

  const handleRefreshStores = async () => {
    if (Date.now() - refreshPickerOpenedAtRef.current < 400) {
      return;
    }

    if (isRefreshingCache) {
      setError('Cache refresh already in progress');
      return;
    }

    // console.log('🔄 [RefreshDebug] handleRefreshStores called');
    // console.log('🔄 [RefreshDebug] isRefreshingCache:', isRefreshingCache);

    const selectedTargets = Object.entries(selectedStores)
      .filter(([_, isSelected]) => isSelected)
      .map(([store]) => store);
    const cacheRefreshTargets = selectedTargets.filter((key) => !PLANNER_ASYNC_REFRESH_KEYS.includes(key));
    const plannerAsyncTargets = selectedTargets.filter((key) => PLANNER_ASYNC_REFRESH_KEYS.includes(key));

    if (selectedTargets.length === 0) {
      setError('Please select at least one store or cache to refresh');
      return;
    }

    const readableNames = selectedTargets.map((target) => REFRESH_TARGET_LABELS[target] || target).join(', ');

    cacheRefreshStartedAtRef.current = Date.now();
    const initialStatuses = Object.fromEntries(
      selectedTargets.map((target) => [target, 'queued'])
    );
    plannerAsyncTargets.forEach((target) => {
      initialStatuses[target] = 'started_background';
    });
    setRefreshTargetStatuses(initialStatuses);
    setRefreshTargetErrors({});
    setRefreshRun({
      startedAtMs: cacheRefreshStartedAtRef.current,
      selectedTargets
    });
    setIsRefreshingCache(true);
    setSuccessMessage(`Database refresh started for: ${readableNames}...`);

    if (cacheRefreshTargets.length > 0) {
      try {
        await axios.post(`${API_URL}/api/cache/refresh`, {
          stores: cacheRefreshTargets
        });
        cacheRefreshTargets.forEach((target) => updateRefreshTargetError(target, null));
      } catch (err) {
        const apiErrorMessage = err.response?.data?.message
          || err.response?.data?.error
          || err.message
          || 'Failed to start selected cache refresh targets';
        cacheRefreshTargets.forEach((target) => updateRefreshTargetStatus(target, 'failed'));
        cacheRefreshTargets.forEach((target) => updateRefreshTargetError(target, apiErrorMessage));
        setError(apiErrorMessage);
      }
    }

    if (plannerAsyncTargets.length > 0) {
      const kickoffResults = await Promise.allSettled(
        plannerAsyncTargets.map((targetKey) => runPlannerAsyncTargetKickoff(targetKey))
      );
      kickoffResults.forEach((result, index) => {
        const targetKey = plannerAsyncTargets[index];
        if (result.status === 'rejected') {
          updateRefreshTargetStatus(targetKey, 'failed');
          updateRefreshTargetError(targetKey, String(result.reason?.message || result.reason || 'Failed to start background planner job'));
          return;
        }
        const payload = result.value || {};
        if (payload.started === false && payload.reason && payload.reason !== 'already_running') {
          updateRefreshTargetStatus(targetKey, 'failed');
          updateRefreshTargetError(targetKey, String(payload.message || payload.error || payload.reason));
          return;
        }
        updateRefreshTargetStatus(targetKey, 'started_background');
        updateRefreshTargetError(targetKey, null);
      });
    }
  };

  useEffect(() => {
    if (!refreshRun) {
      if (refreshPollIntervalRef.current) {
        clearInterval(refreshPollIntervalRef.current);
        refreshPollIntervalRef.current = null;
      }
      return;
    }

    let cancelled = false;
    const runSnapshot = refreshRun;
    const poll = async () => {
      try {
        const [cacheResponse, plannerSnapshot] = await Promise.all([
          axios.get(`${API_URL}/api/cache/status`, { params: { probe: '1' } }),
          fetchPlannerStatusSnapshot()
        ]);
        if (cancelled) return;
        const cacheSnapshot = cacheResponse.data;
        setCacheStatus(cacheSnapshot);
        setPlannerSyncJobs(plannerSnapshot.syncJobs);
        setPlannerCostBackfillStatus(plannerSnapshot.costBackfillStatus);
        setPlannerSupplierSkuSyncStatus(plannerSnapshot.supplierSkuSyncStatus);

        const evaluation = evaluateRefreshRunStatuses(runSnapshot, cacheSnapshot, plannerSnapshot);
        setRefreshTargetStatuses(evaluation.statuses);
        setRefreshTargetErrors((previous) => {
          const next = { ...previous };
          runSnapshot.selectedTargets.forEach((target) => {
            const nextError = evaluation.errors?.[target];
            if (nextError) {
              next[target] = nextError;
            } else if (evaluation.statuses[target] !== 'failed') {
              delete next[target];
            }
          });
          return next;
        });

        if (evaluation.allDone) {
          if (refreshPollIntervalRef.current) {
            clearInterval(refreshPollIntervalRef.current);
            refreshPollIntervalRef.current = null;
          }
          setRefreshRun(null);
          setIsRefreshingCache(false);
          if (evaluation.anyFailed) {
            setError('One or more selected refresh targets failed. Check status details above.');
          } else {
            setSuccessMessage(`Refresh completed for: ${runSnapshot.selectedTargets.map((target) => REFRESH_TARGET_LABELS[target] || target).join(', ')}`);
          }
        }
      } catch (pollErr) {
        if (cancelled) return;
        console.error('Refresh status poll failed:', pollErr);
      }
    };

    poll();
    const interval = setInterval(poll, CACHE_REFRESH_STATUS_POLL_MS);
    refreshPollIntervalRef.current = interval;

    return () => {
      cancelled = true;
      if (refreshPollIntervalRef.current === interval) {
        clearInterval(interval);
        refreshPollIntervalRef.current = null;
      }
    };
  }, [refreshRun, evaluateRefreshRunStatuses, fetchPlannerStatusSnapshot]);

  const activeRunStatusRows = useMemo(() => {
    const runTargets = refreshRun?.selectedTargets?.length
      ? refreshRun.selectedTargets
      : Object.keys(selectedStores).filter((key) => selectedStores[key]);
    if (!runTargets.length) return [];
    return runTargets.map((target) => ({
      key: target,
      label: REFRESH_TARGET_LABELS[target] || target,
      description: REFRESH_TARGET_SUMMARY[target] || '',
      status: refreshTargetStatuses[target] || (refreshRun ? 'refreshing' : 'queued'),
      error: refreshTargetErrors[target] || null
    }));
  }, [refreshRun, selectedStores, refreshTargetStatuses, refreshTargetErrors]);

  const activeRefreshTargetLabels = useMemo(() => {
    if (!refreshRun?.selectedTargets?.length) return [];
    return refreshRun.selectedTargets.map((target) => REFRESH_TARGET_LABELS[target] || target);
  }, [refreshRun]);

  const isRefreshTargetInCurrentRun = useCallback((targetKey) => {
    if (selectedStores[targetKey]) {
      return true;
    }
    return !!refreshRun?.selectedTargets?.includes(targetKey);
  }, [selectedStores, refreshRun]);

  const refreshRunActive = !!refreshRun;

  // Fetch cache status on mount; poll while cache/refresh modal is open and no active run poll is already running.
  // Use AbortController so Strict Mode double-mount doesn't leave two in-flight requests
  // both calling setCacheStatus (which caused DB icon to flash on server boot).
  useEffect(() => {
    const ac = new AbortController();
    const signal = ac.signal;
    // Small delay so initial fetch doesn't race with server cold start
    const t = setTimeout(() => {
      fetchCacheStatus(signal);
    }, 150);
    // While a refresh run is active its own poller (above) already fetches
    // cache status every CACHE_REFRESH_STATUS_POLL_MS; do not double-poll.
    const shouldPoll = (showCacheModal || showRefreshModal) && !refreshRunActive;
    if (!shouldPoll) {
      return () => {
        clearTimeout(t);
        ac.abort();
      };
    }
    const interval = setInterval(
      () => fetchCacheStatus(),
      showRefreshModal ? CACHE_REFRESH_STATUS_POLL_MS : 60000
    );
    return () => {
      clearTimeout(t);
      clearInterval(interval);
      ac.abort();
    };
  }, [showCacheModal, showRefreshModal, fetchCacheStatus, refreshRunActive]);

  useEffect(() => {
    if (!showCacheModal && !showRefreshModal) return undefined;
    // The active run poller already loads the planner snapshot.
    if (refreshRunActive) return undefined;

    let cancelled = false;
    const loadPlannerSnapshot = async () => {
      try {
        const snapshot = await fetchPlannerStatusSnapshot();
        if (cancelled) return;
        setPlannerSyncJobs(snapshot.syncJobs);
        setPlannerCostBackfillStatus(snapshot.costBackfillStatus);
        setPlannerSupplierSkuSyncStatus(snapshot.supplierSkuSyncStatus);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch planner status snapshot:', err);
        }
      }
    };

    loadPlannerSnapshot();
    const intervalMs = showRefreshModal ? CACHE_REFRESH_STATUS_POLL_MS : 30000;
    const interval = setInterval(loadPlannerSnapshot, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [showCacheModal, showRefreshModal, fetchPlannerStatusSnapshot, refreshRunActive]);

  useEffect(() => {
    if (!showRefreshModal) {
      document.body.classList.remove('refresh-database-modal-open');
      document.body.classList.remove('refresh-database-modal-dark');
      return undefined;
    }
    document.body.classList.add('refresh-database-modal-open');
    if (darkMode) document.body.classList.add('refresh-database-modal-dark');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('refresh-database-modal-open');
      document.body.classList.remove('refresh-database-modal-dark');
      document.body.style.overflow = previousOverflow;
    };
  }, [showRefreshModal, darkMode]);

  // Clear refresh poll interval on unmount to avoid leaking timers
  useEffect(() => {
    return () => {
      if (refreshPollIntervalRef.current) {
        clearInterval(refreshPollIntervalRef.current);
        refreshPollIntervalRef.current = null;
      }
    };
  }, []);

  // Memoize expensive calculations - only count selected products that are currently in the products list
  const selectedCount = useMemo(() => {
    const productKeys = new Set(products.map(p => `${p.storeDisplayName}-${p.id}-${p.variantId}`));
    return Object.entries(selectedProducts)
      .filter(([key, isSelected]) => isSelected && productKeys.has(key))
      .length;
  }, [selectedProducts, products]);

  // Cards still flashing "check qty" — only counts rows actually on screen and
  // still selected, so deselecting or clearing the grid settles the banner.
  const labelQuantityReviewCount = useMemo(() => {
    return products.reduce((count, p) => {
      const key = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
      return count + (labelQuantityReviewKeys[key] && selectedProducts[key] ? 1 : 0);
    }, 0);
  }, [products, selectedProducts, labelQuantityReviewKeys]);

  // Total labels the Print Labels button will actually produce (mirrors printLabels)
  const selectedLabelTotal = useMemo(() => {
    return products.reduce((total, p) => {
      const key = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
      if (!selectedProducts[key]) return total;
      return total + resolveLabelQuantity(labelQuantities[key]);
    }, 0);
  }, [products, selectedProducts, labelQuantities]);

  // What the grid shows after the store picker and "Hide out of stock".
  const visibleProducts = useMemo(() => products.filter((p) => {
    if (storeFilter && p.storeDisplayName !== storeFilter) return false;
    if (hideOutOfStock && !((p.stockOnHand || 0) > 0)) return false;
    return true;
  }), [products, storeFilter, hideOutOfStock]);

  // Kit splits load when the grid first shows rows, and again when rows come
  // back after the grid was cleared if the copy is over 5 minutes old.
  const gridHasRows = visibleProducts.length > 0;
  useEffect(() => {
    if (!gridHasRows || !canReadKitSplits) return;
    if (kitSplitsLoadedAtRef.current && Date.now() - kitSplitsLoadedAtRef.current < 5 * 60 * 1000) return;
    loadKitSplits();
  }, [gridHasRows, canReadKitSplits, loadKitSplits]);
  // Product links follow the same rule (docs/memory/product-links.md).
  useEffect(() => {
    if (!gridHasRows || !canReadProductLinks) return;
    if (productLinksLoadedAtRef.current && Date.now() - productLinksLoadedAtRef.current < 5 * 60 * 1000) return;
    loadProductLinks();
  }, [gridHasRows, canReadProductLinks, loadProductLinks]);

  // Per-store counts for the picker labels ("CCC (4)").
  const productCountByStore = useMemo(() => {
    const counts = {};
    products.forEach((p) => { counts[p.storeDisplayName] = (counts[p.storeDisplayName] || 0) + 1; });
    return counts;
  }, [products]);

  const productsHiddenByStore = storeFilter
    ? products.length - (productCountByStore[storeFilter] || 0)
    : 0;

  const handleStoreFilterChange = (value) => {
    setStoreFilter(value);
    try { localStorage.setItem('productsStoreFilter', value); } catch { /* private mode */ }
    // Drop selections the filter hides so Print Labels never prints cards
    // that are off screen.
    if (value) {
      setSelectedProducts((prev) => {
        const next = {};
        products.forEach((p) => {
          const key = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
          if (prev[key] && p.storeDisplayName === value) next[key] = true;
        });
        return next;
      });
    }
    focusSearchInput();
  };

  // Related / Complements chip click-through (docs/memory/product-links.md):
  // scroll to the card if that SKU is already listed, else pull it from the
  // product cache for that store, add it (60-card cap and duplicate guard
  // live in addProductToList), then scroll to it and flash it once it renders.
  const [linkedProductTarget, setLinkedProductTarget] = useState(null); // { store, sku }
  const productCardFlashRef = useRef({ el: null, timer: null });
  const flashProductCard = useCallback((key) => {
    if (!key || typeof document === 'undefined') return false;
    const escape = window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape
      : (s) => String(s).replace(/["\\]/g, '\\$&');
    const el = document.querySelector(`[data-product-key="${escape(key)}"]`);
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // No wrapper around ProductCard (it is a grid cell), so the class goes on
    // the card's own root and comes off on the timer.
    const flash = productCardFlashRef.current;
    if (flash.timer) clearTimeout(flash.timer);
    if (flash.el && flash.el !== el) flash.el.classList.remove('product-card-flash');
    el.classList.remove('product-card-flash');
    void el.offsetWidth; // restart the animation on a repeat click
    el.classList.add('product-card-flash');
    flash.el = el;
    flash.timer = setTimeout(() => {
      el.classList.remove('product-card-flash');
      productCardFlashRef.current = { el: null, timer: null };
    }, 1600);
    return true;
  }, []);
  useEffect(() => () => {
    if (productCardFlashRef.current.timer) clearTimeout(productCardFlashRef.current.timer);
  }, []);
  // Make sure the grid's own filters do not hide the card we are jumping to.
  const revealLinkedRow = (row) => {
    if (storeFilter && row.storeDisplayName && row.storeDisplayName !== storeFilter) handleStoreFilterChange('');
    if (hideOutOfStock && !((row.stockOnHand || 0) > 0)) setHideOutOfStock(false);
  };
  // Un-memoised like handleSearch (it reads products / filters fresh); the card
  // gets the stable wrapper below so memoised cards never see a stale copy.
  const openLinkedProduct = async (store, sku) => {
    const skuUpper = String(sku || '').trim().toUpperCase();
    if (!skuUpper) return;
    const listed = products.find((p) => linkedRowMatches(p, store, skuUpper));
    if (listed) {
      revealLinkedRow(listed);
      setLinkedProductTarget({ store, sku: skuUpper });
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/products/batch-search-cache`, { skus: [String(sku).trim()] });
      const rows = Array.isArray(response.data?.products) ? response.data.products : [];
      const sameSku = rows.filter((r) => String(r?.sku || '').trim().toUpperCase() === skuUpper);
      const candidates = sameSku.length ? sameSku : rows;
      const row = candidates.find((r) => linkedRowMatches({ ...r, sku: skuUpper }, store, skuUpper)) || candidates[0];
      if (!row) {
        setError(`${sku} is not in the catalogue for ${store}`);
        return;
      }
      addProductToList(row, { fromSearch: true });
      pendingRefreshSKUs.current.add(String(row.sku || sku).trim());
      scheduleBackgroundRefresh();
      revealLinkedRow(normalizeProduct(row));
      setLinkedProductTarget({ store: row.store || store, sku: String(row.sku || skuUpper).trim().toUpperCase() });
    } catch (err) {
      setError(`Could not open ${sku}: ${err.response?.data?.error || err.message}`);
    }
  };
  const openLinkedProductRef = useRef(openLinkedProduct);
  openLinkedProductRef.current = openLinkedProduct;
  const handleOpenLinkedProduct = useCallback((store, sku, entry) => openLinkedProductRef.current(store, sku, entry), []);
  // Resolve the pending target once its card is in the DOM (a just-added row
  // renders on the next commit). Gives up quietly after ~10 frames, e.g. when
  // the 60-card cap refused the add.
  useEffect(() => {
    if (!linkedProductTarget) return undefined;
    const row = products.find((p) => linkedRowMatches(p, linkedProductTarget.store, linkedProductTarget.sku));
    const key = row ? `${row.storeDisplayName}-${row.id}-${row.variantId}` : null;
    let frame = 0;
    let attempts = 0;
    const tick = () => {
      if (flashProductCard(key) || attempts >= 10) {
        setLinkedProductTarget(null);
        return;
      }
      attempts += 1;
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
    // visibleProducts: a filter cleared by revealLinkedRow re-renders the card.
  }, [linkedProductTarget, products, visibleProducts, flashProductCard]);

  return (
    <div className={darkMode ? 'dark-mode' : 'light-mode'}>

        <style>{`
          /* Theme system lives in theme.css (tokens + Polaris dark gap
             patches). This block is LAYOUT + app-specific chrome only —
             per docs/THEMING.md: no raw-tag dark rules, no [style*=]
             matching, no filters, nothing that fights inline styles. */

          /* Product card border states - wrapper around Card */
          .product-card-success {
            border: 3px solid #16a34a !important;
            border-radius: 8px !important;
          }
          .product-card-error {
            border: 3px solid #dc2626 !important;
            border-radius: 8px !important;
          }
          .product-card-warning {
            border: 3px solid #f97316 !important;
            border-radius: 8px !important;
          }
          .product-card-success .Polaris-Card,
          .product-card-error .Polaris-Card,
          .product-card-warning .Polaris-Card {
            border: none !important;
          }
          .dark-mode .product-card-success { border-color: #22c55e !important; }
          .dark-mode .product-card-error { border-color: #ff6b6b !important; }
          .dark-mode .product-card-warning { border-color: #fb923c !important; }

          /* High label counts loaded off a PO (100 tubes, 4 labels) flash
             until a human checks them — the card AND a banner on the card,
             because staff scan the wall of cards, not each footer input
             (Rhys 2026-09-04). */
          /* The ring is static and only the small flag pulses: animating a
             whole card's shadow repaints the card on every frame, and a grid
             of 60 of them is a lot of paint for a warehouse PC. */
          .product-card-qty-review {
            border-radius: 8px !important;
            box-shadow: 0 0 0 3px #f59e0b;
          }
          .qty-review-flag {
            display: flex; align-items: center; gap: 8px; width: 100%;
            margin-bottom: 4px; padding: 5px 8px;
            border: none; border-radius: 6px; cursor: pointer;
            font-size: 11px; font-weight: 800; letter-spacing: 0.04em;
            text-align: left; color: #7c2d12; background: #fcd34d;
            animation: qtyReviewFlag 1.1s ease-in-out infinite;
          }
          @keyframes qtyReviewFlag {
            0%, 100% { background: #fcd34d; }
            50% { background: #f59e0b; }
          }
          .qty-review-flag__box {
            display: inline-flex; align-items: center; justify-content: center;
            min-width: 26px; height: 20px; padding: 0 5px;
            border-radius: 4px; background: #7c2d12; color: #fff;
            font-size: 11px; font-weight: 800;
          }
          .qty-review-flag__hint {
            margin-left: auto; font-weight: 600; letter-spacing: 0;
            text-transform: none; opacity: 0.75; white-space: nowrap;
          }
          .qty-review-bar {
            display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
            margin-top: 8px; padding: 7px 10px; border-radius: 6px;
            font-size: 13px; font-weight: 700;
            color: #7c2d12; background: #fde68a; border: 1px solid #f59e0b;
          }
          .qty-review-bar__jump {
            padding: 3px 10px; border-radius: 5px; cursor: pointer;
            font-size: 12px; font-weight: 700;
            border: 1px solid #b45309; background: #fff7ed; color: #7c2d12;
          }
          .qty-review-bar__jump:hover { background: #ffedd5; }
          .store-filter-hidden-note {
            padding: 2px 8px; border-radius: 5px; cursor: pointer;
            font-size: 12px; white-space: nowrap;
            color: var(--app-ink-secondary); background: transparent;
            border: 1px dashed var(--app-border-strong);
          }
          .store-filter-hidden-note:hover { color: var(--app-ink); }
          @media (prefers-reduced-motion: reduce) {
            .qty-review-flag { animation: none; }
          }

          /* Incoming Stock PO card grid — explicit column caps, NOT pure
             auto-fill: a 390px auto-fill floor packed 8 columns on an
             ultrawide and truncated every product name (Rhys 2026-08-29:
             "I want to see all the product names in full"). Column count
             follows the monitor; card width goes to the names. */
          .po-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 10px;
            align-items: stretch;
          }
          @media (max-width: 1500px) { .po-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
          @media (max-width: 950px) { .po-grid { grid-template-columns: minmax(0, 1fr); } }
          @media (min-width: 1900px) { .po-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
          @media (min-width: 2700px) { .po-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); } }

          /* Shared tracks keep names aligned across mixed SKU/quantity lengths. */
          .po-item-list {
            display: grid;
            grid-template-columns: max-content minmax(0, min(112px, 26%)) minmax(0, 1fr) auto;
            gap: 4px 8px;
            align-content: start;
          }
          .po-item-list > * { grid-column: 1 / -1; }
          .po-item-row {
            display: grid;
            grid-template-columns: subgrid;
            align-items: start;
            min-width: 0;
          }
          .po-item-sku {
            min-width: 0;
            display: flex;
            flex-wrap: nowrap;
            align-items: flex-start;
            gap: 4px;
          }
          .po-item-sku .Polaris-Badge {
            min-width: 0;
            max-width: 100%;
          }
          .po-item-sku .Polaris-Text--root {
            /* One line per SKU chip (Rhys 2026-10-23); the chip grows and the title column gives way. */
            white-space: nowrap;
          }
          .po-item-content { display: contents; }
          .po-item-content > .Polaris-Text--root {
            grid-column: 3;
            min-width: 0;
            overflow-wrap: anywhere;
          }
          .po-item-content > button {
            grid-column: 4;
            justify-self: end;
          }

          /* Incoming Stock PO cards — hover elevation (beats inline boxShadow) */
          .po-card {
            transition: box-shadow 0.18s ease;
          }
          /* Completed forward button breathes in the accent teal. The global
             App.css "pulse" keyframes animate border-color to RED (an error
             affordance) — never point this button back at them. */
          @keyframes po-complete-pulse {
            0%, 100% { border-color: #14b8a6; }
            50% { border-color: #5eead4; }
          }
          .po-card:hover {
            box-shadow: 0 0 0 1px rgba(23, 24, 24, 0.08), 0 6px 16px rgba(23, 24, 24, 0.12) !important;
          }
          .dark-mode .po-card:hover {
            box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 6px 16px rgba(0, 0, 0, 0.55) !important;
          }

          /* Layout tweaks */
          .Polaris-Page { padding: 1rem 1rem !important; position: relative; padding-top: 0 !important; }
          .Polaris-Layout__Section { padding: 0 !important; }
          .Polaris-Card {
            margin-bottom: 0.5rem !important;
          }
          .Polaris-Page > .Polaris-Box {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          .Polaris-Page-Header { display: none !important; }
          .Polaris-Header-Title__TitleAndSubtitleWrapper,
          .Polaris-Page-Header__TitleWrapper,
          .Polaris-Page-Header__Row,
          .Polaris-Page-Header__MainContent,
          .Polaris-Page-Header--isSingleRow,
          .Polaris-Page-Header--noBreadcrumbs {
             display: none !important;
          }

          /* Store tooltip (same look both themes: dark panel) */
          .store-tooltip {
            position: relative;
            display: inline-block;
            cursor: help;
          }
          .store-tooltip .tooltip-content {
            visibility: hidden;
            background-color: #1a1a1a;
            color: #fff;
            text-align: left;
            border-radius: 8px;
            padding: 12px 16px;
            position: fixed;
            z-index: 99999;
            min-width: 280px;
            max-width: 450px;
            white-space: normal;
            opacity: 0;
            transition: opacity 0.2s, visibility 0.2s;
            box-shadow: 0 10px 25px rgba(0,0,0,0.5);
            font-size: 13px;
            line-height: 1.7;
            border: 1px solid #555;
            pointer-events: none;
          }
          .store-tooltip .tooltip-content::after {
            content: "";
            position: absolute;
            bottom: -8px;
            left: 20px;
            border-width: 8px 8px 0 8px;
            border-style: solid;
            border-color: #1a1a1a transparent transparent transparent;
          }
          .store-tooltip:hover .tooltip-content {
            visibility: visible;
            opacity: 1;
          }

          .sticky-search-section {
            position: sticky;
            /* Directly under the sticky .module-header. --app-header-offset is
               its live bottom edge (hooks/useAppHeaderOffset.js). */
            top: var(--app-header-offset, 56px);
            z-index: 900; /* Lower than Polaris Modals */
            padding: 8px 16px;
            margin: 0;
            background-color: var(--app-bg);
            border-bottom: 1px solid var(--app-border);
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
          }
        `}</style>
      <ErrorBoundary>
        {/* Pick From Store is a counter-tablet board granted independently of
            picking, so the "mobile picking only" blocker must not cover it —
            otherwise pickup-only staff on the very device it was built for see
            nothing but this screen. */}
        {showMobileNoPickingAccess && !showCeo && !showBackupRestore && !showExpiry && !showPickFromStore && !showLocalDeliveries && !showShip && !showStocktake && (

          <div className={`mobile-picking-only-blocked ${darkMode ? 'dark' : ''}`}>
            <h1>Mobile picking only</h1>
            <p>
              On mobile, this app is for order picking only. Your account does not have picking access.
            </p>
            {currentUser && (
              <button type="button" className="mobile-picking-only-signout" onClick={handleSignOut}>
                Sign out
              </button>
            )}
          </div>
        )}
        {/* Custom Header Bar — hidden on mobile and during focused picking */}
        {!isMobile && !showPickingModal && !showStocktake && !showPickFromStore && !showLocalDeliveries && !showShip && (
        <div
          className="module-header"
          style={{
            ...getHeaderContainerStyle({ gridTemplateColumns: isDevMode() ? 'auto minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto' }),
            // getHeaderContainerStyle() sets position: 'relative' inline, which
            // silently beat App.css's `.module-header { position: sticky }` —
            // the nav scrolled away on long pages (Products grid). Pin it here.
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--header-bg)',
            color: 'var(--header-text)',
            '--module-focus-ring': focusRingColor,
            '--module-base': moduleBaseColor,
            '--module-accent': moduleAccentColor,
            '--module-accent-indicator': getSectionAccentColor(headerModuleKey),
            '--app-header-height': `${HEADER_HEIGHT_PX}px`,
            zIndex: 1001,
            ...getDevModeHeaderStyles()
          }}
        >
          {/* Left: dev pill (takes its own grid column, never overlays buttons) */}
          {isDevMode() && (
            <div className="module-header-dev-slot">
              <DevModePill />
            </div>
          )}

          {/* Middle: every module button, overflowing into "More ▾" when tight */}
          <ModuleNavBar
            className="module-header-module-nav"
            items={[
              ...visibleNavKeys.map((key, index) => {
                const isActive = key === 'warehouse' ? activeModule === 'home' : activeModule === key;
                const nav = key === 'warehouse'
                  ? navigateToHome
                  : key === 'jobs'
                    ? navigateToJobs
                    : key === 'po'
                      ? navigateToPOs
                      : key === 'po-planning'
                        ? navigateToPOPlanning
                        : key === 'preorders'
                        ? navigateToPreorders
                        : key === 'picking-supervisor'
                          ? navigateToPickingSupervisor
                          : key === 'ship'
                            ? navigateToShip
                          : key === 'pick-from-store'
                            ? navigateToPickFromStore
                            : key === 'picking'
                              ? navigateToPicking
                              : key === 'map'
                                ? navigateToMap
                                : key === 'ra'
                                  ? navigateToRAs
                                  : key === 'expiry'
                                    ? navigateToExpiry
                                    : key === 'ceo'
                                      ? navigateToCeo
                                      : navigateToNewProducts;
                const showPreorderUnread = key === 'preorders' && preorderUnreadCount > 0;
                // Job hubs carry two independent signals: a ✉ when customers have messaged us,
                // and a count of the *newly assigned, unacknowledged* jobs waiting for you in
                // that hub (cleared by opening the job or tapping Acknowledge).
                const isJobHub = key === 'jobs' || key === 'ra';
                const hubUnread = isJobHub ? (jobsUnreadByHub[key] ?? 0) : 0;
                const hubAssigned = isJobHub ? (jobsAssignedCounts[key] ?? 0) : 0;
                const newReturns = key === 'ra' ? returnsCounts.awaitingApproval : 0;
                const deliveredReturns = key === 'ra' ? returnsCounts.deliveredNotReceived : 0;
                const returnsBadge = newReturns + deliveredReturns;
                const prevKey = index > 0 ? visibleNavKeys[index - 1] : null;
                return {
                  key,
                  group: 'nav',
                  label: getNavLabel(key),
                  href: navHrefForModule(key),
                  onClick: nav,
                  active: isActive,
                  spacerBefore: NAV_JOB_HUB_KEYS.includes(key) && !!prevKey && !NAV_JOB_HUB_KEYS.includes(prevKey),
                  alert: showPreorderUnread || hubUnread > 0 || hubAssigned > 0 || returnsBadge > 0,
                  badges: (
                    <>
                      {showPreorderUnread && (
                        <span className="module-header-nav-badge-dot" title="Unread preorder messages" aria-hidden />
                      )}
                      {hubUnread > 0 && (
                        <span
                          className="module-header-nav-badge-msg"
                          title={`${hubUnread} job${hubUnread === 1 ? '' : 's'} with unread customer messages`}
                        >
                          ✉
                        </span>
                      )}
                      {returnsBadge > 0 && (
                        <span
                          className="module-header-nav-badge-count module-header-nav-badge-count--returns"
                          title={[
                            newReturns > 0 ? `${newReturns} new return${newReturns === 1 ? '' : 's'} waiting for approval` : null,
                            deliveredReturns > 0 ? `${deliveredReturns} return${deliveredReturns === 1 ? '' : 's'} delivered, not marked Received yet` : null
                          ].filter(Boolean).join(' · ')}
                        >
                          {returnsBadge > 99 ? '99+' : returnsBadge}
                        </span>
                      )}
                      {hubAssigned > 0 && (
                        <span
                          className="module-header-nav-badge-count module-header-nav-badge-count--mine"
                          title={`${hubAssigned} new job${hubAssigned === 1 ? '' : 's'} assigned to you — open or acknowledge to clear`}
                        >
                          {hubAssigned > 99 ? '99+' : hubAssigned}
                        </span>
                      )}
                    </>
                  ),
                };
              }),
              ...visibleHeaderToolKeys.map((key) => {
                const toolNav = key === 'po-planning'
                  ? navigateToPOPlanning
                  : key === 'pricemole'
                    ? navigateToPriceMole
                    : key === 'coffee-stocktake'
                      ? navigateToCoffeeStocktake
                      : key === 'local-deliveries'
                        ? navigateToLocalDeliveries
                        : key === 'backup-restore'
                          ? () => { setModuleView('backup-restore'); window.history.pushState({}, '', '/backup-restore'); }
                          : navigateToNewProducts;
                const showPricemoleCount = key === 'pricemole' && pricemoleReviewCount > 0;
                return {
                  key,
                  group: 'admin',
                  label: getNavLabel(key),
                  href: navHrefForModule(key, { pricemoleChannel }),
                  onClick: toolNav,
                  active: activeModule === key,
                  style: activeModule === key ? { '--module-accent-indicator': getSectionAccentColor(key) } : undefined,
                  alert: showPricemoleCount,
                  badges: showPricemoleCount ? (
                    <span
                      className="module-header-nav-badge-count"
                      title={`${pricemoleReviewCount} product price(s) need updating`}
                    >
                      {pricemoleReviewCount > 99 ? '99+' : pricemoleReviewCount}
                    </span>
                  ) : null,
                };
              }),
              ...(hasModulePermission('productTags') ? [{
                key: 'tags',
                group: 'admin',
                label: '🏷️ Tags',
                href: navHrefForModule('tags'),
                onClick: navigateToTags,
                active: activeModule === 'tags',
                style: activeModule === 'tags' ? { '--module-accent-indicator': getSectionAccentColor('tags') } : undefined,
              }] : []),
            ]}
          />

          {/* Right: system utilities (pinned; labels collapse to icons when tight) */}
          <div className="module-header-utils-row module-header-zone--right">

            <div className="module-header-system-row" role="group" aria-label="Account and system">
              <button
                type="button"
                className="module-header-util-btn"
                onClick={() => setShowMyAccessModal(true)}
                title="My Access"
                aria-label="My Access"
              >
                <span aria-hidden="true">👤</span>
                <span className="module-header-util-btn__label">My Access</span>
              </button>

              {canShowSettings && (
                <button
                  type="button"
                  className="module-header-util-btn module-header-util-btn--icon-only"
                  onClick={() => setShowSettingsModal(true)}
                  title="Settings"
                  aria-label="Settings"
                >
                  <span className="module-header-util-btn__icon" aria-hidden="true">⚙️</span>
                </button>
              )}

              {canViewSecurityIncidents && (
                <button
                  type="button"
                  className={`module-header-util-btn module-header-util-btn--icon-only${securityUnreadCount > 0 ? ' module-header-util-btn--has-alert' : ''}`}
                  onClick={() => setShowSecurityIncidentsModal(true)}
                  title={securityUnreadCount > 0 ? `${securityUnreadCount} unread security incident(s)` : 'Security incident log'}
                  aria-label={securityUnreadCount > 0 ? `Security — ${securityUnreadCount} unread incident(s)` : 'Security'}
                >
                  <span className="module-header-util-btn__icon" aria-hidden="true">🛡</span>
                  {securityUnreadCount > 0 && (
                    <span className="module-header-security-badge-dot" aria-hidden="true" />
                  )}
                </button>
              )}

              <button
                type="button"
                className={`module-header-db-pill module-header-db-pill--${getDbStatusTier(cacheStatus, isRefreshingCache)}`}
                onClick={openDbStatusOrRefreshProgress}
                title={isRefreshingCache ? 'View refresh progress' : 'Database and cache status'}
              >
                <span className="module-header-db-pill__icon" aria-hidden="true">
                  {getDbStatusTier(cacheStatus, isRefreshingCache) === 'error' ? '!' : getDbStatusTier(cacheStatus, isRefreshingCache) === 'loading' ? '…' : '✓'}
                </span>
                {getDbStatusLabel(cacheStatus, isRefreshingCache)}
              </button>

              <UserProfileBadge
                currentUser={currentUser}
                displayName={permissionSettings?.name}
                onSignOut={handleSignOut}
                onAdminApiClick={() => setShowAdminApiModal(true)}
                canOpenAdminApi={canOpenAdminApi}
              />

              <button
                type="button"
                className="module-header-theme-toggle"
                onClick={() => setDarkMode(!darkMode)}
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
        )}
        {(!isMobile || showPickFromStore || showLocalDeliveries || showShip || showBackupRestore || showExpiry || showCeo) && !showPickingModal && !showStocktake && (
        showCeo ? (
          <div style={{ position: 'relative', zIndex: 1000 }}>
            {/* A seeded first paint waits for a real grant before CeoView
                mounts and calls the API (2026-10-23). */}
            {hasModulePermission('ceo') ? (
              <Suspense fallback={<div style={{ padding: 24 }}>Loading CEO…</div>}>
                <CeoView onNavigate={navigateToPath} />
              </Suspense>
            ) : (
              <div style={{ padding: 24 }}>Loading CEO…</div>
            )}
          </div>
        ) : showExpiry ? (
          <div style={{ position: 'relative', zIndex: 1000 }}>
            <Suspense fallback={<div style={{ padding: 24 }}>Loading expiry…</div>}>
              <ExpiryDashboard canEdit={hasModulePermission('warehouse')} />
            </Suspense>
          </div>
        ) : showBackupRestore ? (
          <div style={{ position: 'relative', zIndex: 1000 }}>
            <Suspense fallback={<div style={{ padding: 24 }}>Loading backups…</div>}>
              <BackupRestorePanel darkMode={darkMode} isAdmin={Boolean(permissionSettings?.isAdmin)} initialRestore={new URLSearchParams(window.location.search).get('restore')} />
            </Suspense>
          </div>
        ) : showMapView ? (

          <div style={{ position: 'relative', zIndex: 1000 }}>
            <Suspense fallback={<div style={{ padding: 16, color: 'var(--text-muted, #6d7175)' }}>Loading map…</div>}>
              <WarehouseMapMockup inApp darkMode={darkMode} onLoadSkusInGrid={loadMapSkusIntoGrid} />
            </Suspense>
          </div>
        ) : showPickingSupervisor ? (
          <div className="PickingSupervisorView-shell" style={{ position: 'relative', zIndex: 1000 }}>
            <Suspense fallback={<div style={{ padding: 16, color: 'var(--text-muted, #6d7175)' }}>Loading picking supervisor…</div>}>
              <PickingSupervisorView
                open={showPickingSupervisor}
                darkMode={darkMode}
              />
            </Suspense>
          </div>
        ) : showPickFromStore ? (
          <div style={{ position: 'relative', zIndex: 1000 }}>
            <Suspense fallback={<div style={{ padding: 16, color: 'var(--text-muted, #6d7175)' }}>Loading pickup bay…</div>}>
              <PickFromStoreView
                open={showPickFromStore}
                darkMode={darkMode}
                currentUser={currentUser}
                onExit={navigateToHome}
                pane={pickupKioskPane}
                onPaneChange={handlePickupKioskPaneChange}
              />
            </Suspense>
          </div>
        ) : showShip ? (
          <div style={{ position: 'relative', zIndex: 1000 }}>
            <Suspense fallback={<div style={{ padding: 16, color: 'var(--text-muted, #6d7175)' }}>Loading ship bench…</div>}>
              <ShipView
                onSetDarkMode={setDarkMode}
                open={showShip}
                darkMode={darkMode}
                onToggleDarkMode={() => setDarkMode((current) => !current)}
                currentUser={currentUser}
                onExit={navigateToHome}
                canManage={hasTaskPermission('shipManage')}
                route={shipRoute}
                onRouteChange={handleShipRouteChange}
              />
            </Suspense>
          </div>
        ) : showLocalDeliveries ? (
          <Suspense fallback={<div style={{ padding: 16, color: 'var(--text-muted, #6d7175)' }}>Loading deliveries…</div>}>
            <LocalDeliveriesView
              open={showLocalDeliveries}
              darkMode={darkMode}
            />
          </Suspense>
        ) : (
        <Page
          fullWidth
          subtitle={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {error && (
                <span style={{
                  fontSize: '12px',
                  color: darkMode ? '#fca5a5' : '#dc2626',
                  backgroundColor: darkMode ? 'rgba(220, 38, 38, 0.2)' : '#fef2f2',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                  onClick={() => setError('')}>
                  ❌ {error}
                </span>
              )}
              {successMessage && (
                <span style={{
                  fontSize: '12px',
                  color: darkMode ? '#4ade80' : '#16a34a',
                  backgroundColor: darkMode ? 'rgba(22, 163, 74, 0.2)' : '#f0fdf4',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                  onClick={() => setSuccessMessage('')}>
                  ✅ {successMessage}
                </span>
              )}
            </span>
          }
        >
          {/* Sticky Search Section */}
          {!showPOViewer && !selectedPOForWorksheet && (
            <div className={`sticky-search-section ${darkMode ? 'dark-mode' : ''}`}>
              <Card>
                <BlockStack gap="400">
                  <form onSubmit={handleSearchSubmit}>
                    <InlineStack gap="400" wrap={false}>
                      <div style={{ flex: 1 }}>
                        <TextField
                          id={WAREHOUSE_SKU_SEARCH_ID}
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder={products.length >= 200
                            ? `Grid is full (${products.length} loaded, search stops at 200). Clear All to search again.`
                            : 'Scan barcode, SKU, or location (P1-P200, Z-01, U-01-H, etc.)...'}
                          autoComplete="off"
                          disabled={products.length >= 200}
                          prefix={<Icon source={SearchIcon} />}
                          suffix={loadingProgress.total > 0 && (
                            <span style={{ fontSize: '12px', color: 'var(--app-ink-secondary)' }}>
                              Loading {loadingProgress.current}/{loadingProgress.total}
                            </span>
                          )}
                        />
                      </div>
                      <div style={{ minWidth: '150px' }}>
                        <Select
                          label="Store"
                          labelHidden
                          options={PRODUCT_STORE_OPTIONS.map((o) => ({
                            ...o,
                            label: o.value && productCountByStore[o.value]
                              ? `${o.label} (${productCountByStore[o.value]})`
                              : o.label
                          }))}
                          value={storeFilter}
                          onChange={handleStoreFilterChange}
                        />
                      </div>
                      <Button
                        submit
                        primary
                        disabled={!searchQuery.trim() || products.length >= 200}
                      >
                        Search
                      </Button>
                      <Button
                        onClick={handleFeelingLucky}
                        disabled={isLoading || products.length >= 200}
                        loading={isLoading}
                      >
                        🍀 I'm Feeling Lucky
                      </Button>
                      <WhatsNewLauncher module="products" active={moduleView === 'home' && !isMobile && permissionSettingsLoaded} darkMode={darkMode} />
                    </InlineStack>
                  </form>

                  {/* Sticky Button Group */}
                  {products.length > 0 && (
                    <div style={{ borderTop: `1px solid ${darkMode ? '#444' : '#e1e3e5'}`, paddingTop: '8px' }}>
                      <InlineStack align="space-between" wrap={false}>
                        <ButtonGroup>
                          <Button
                            onClick={() => {
                              // Select every card on screen (store picker /
                              // hide-out-of-stock leave the rest alone)
                              const newSelection = {};
                              visibleProducts.forEach(p => {
                                const key = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
                                newSelection[key] = true;
                              });
                              setSelectedProducts(newSelection);
                              // Default unset label quantities to 1, matching manual
                              // selection (otherwise printLabels falls back to
                              // stock-on-hand). PO-loaded quantities are preserved.
                              setLabelQuantities(prev => {
                                const next = { ...prev };
                                visibleProducts.forEach(p => {
                                  const key = `${p.storeDisplayName}-${p.id}-${p.variantId}`;
                                  if (next[key] === undefined || next[key] === null) {
                                    next[key] = 1;
                                  }
                                });
                                return next;
                              });
                            }}
                            size="slim"
                          >
                            Select All
                          </Button>
                          <Button
                            onClick={() => {
                              // Deselect all products
                              setSelectedProducts({});
                            }}
                            size="slim"
                          >
                            Deselect All
                          </Button>
                          <Button
                            onClick={() => {
                              clearAll();
                              setSearchQuery('');
                            }}
                            destructive
                            size="slim"
                          >
                            Clear All
                          </Button>
                          {/* Replaced Refresh (Rhys 2026-08-29): staff load a
                              delivery here and then walk it onto the shelves,
                              so the useful action is starting that walk. */}
                          <Button
                            onClick={() => setPutAwayOpen(true)}
                            disabled={products.length === 0}
                            size="slim"
                          >
                            📦 Put Away Stock ({products.length})
                          </Button>
                          {selectedCount > 0 && (
                            <Button
                              onClick={() => setPrintLabelDialogOpen(true)}
                              variant="primary"
                              size="slim"
                            >
                              🏷️ Print Labels ({selectedLabelTotal === selectedCount
                                ? selectedCount
                                : `${selectedCount} items · ${selectedLabelTotal} labels`})
                            </Button>
                          )}
                          {selectedCount > 0 && (
                            <Button
                              onClick={setSelectedLabelQuantitiesToOne}
                              size="slim"
                            >
                              1 Label Each
                            </Button>
                          )}
                          {selectedCount > 0 && (
                            <Button
                              onClick={printA4List}
                              size="slim"
                            >
                              📄 A4 List ({selectedCount})
                            </Button>
                          )}
                          {selectedCount > 0 && (
                            <Button
                              onClick={sendToEDisplay}
                              size="slim"
                            >
                              🖥️ E-Display
                            </Button>
                          )}
                        </ButtonGroup>
                        {/* The variant/stock filters share the action line -
                            they used to be their own row above it. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="show-all-variants"
                          checked={showAllVariants}
                          onChange={(e) => handleShowAllVariantsToggle(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label
                          htmlFor="show-all-variants"
                          style={{
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: darkMode ? '#e5e5e5' : '#333'
                          }}
                        >
                          Show all variants of this product
                        </label>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="show-all-from-brand"
                          checked={showAllFromBrand}
                          onChange={(e) => handleShowAllFromBrandToggle(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label
                          htmlFor="show-all-from-brand"
                          title={products[0]?.vendor ? `Brand: ${products[0].vendor}` : 'Uses the brand (vendor) of the first product in the grid'}
                          style={{
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: darkMode ? '#e5e5e5' : '#333'
                          }}
                        >
                          Show all products from this brand
                        </label>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="hide-out-of-stock"
                          checked={hideOutOfStock}
                          onChange={(e) => setHideOutOfStock(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label
                          htmlFor="hide-out-of-stock"
                          style={{
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: darkMode ? '#e5e5e5' : '#333'
                          }}
                        >
                          Hide out of stock
                        </label>
                      </div>
                    </div>
                        <InlineStack gap="200" blockAlign="center">
                          {productsHiddenByStore > 0 && (
                            <button
                              type="button"
                              className="store-filter-hidden-note"
                              onClick={() => handleStoreFilterChange('')}
                              title="Show every store again"
                            >
                              {productsHiddenByStore} in other stores · show all
                            </button>
                          )}
                          <Badge tone="info">
                            {selectedCount} / {visibleProducts.length} selected
                          </Badge>
                        </InlineStack>
                      </InlineStack>
                      {/* Bulk lines off a PO: say how many need a count check
                          and jump to the first, so nobody hunts the grid for
                          the flashing card (Rhys 2026-09-04). */}
                      {labelQuantityReviewCount > 0 && (
                        <div className="qty-review-bar">
                          <span className="qty-review-flag__box">{labelQuantityReviewCount}</span>
                          <span>
                            {labelQuantityReviewCount === 1 ? 'item has' : 'items have'} a big label count from the PO — check the quantity before printing
                          </span>
                          <button
                            type="button"
                            className="qty-review-bar__jump"
                            onClick={() => {
                              const first = document.querySelector('[data-label-qty-review="true"]');
                              if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                          >
                            Jump to it
                          </button>
                          <button
                            type="button"
                            className="qty-review-bar__jump"
                            onClick={() => setLabelQuantityReviewKeys({})}
                          >
                            All checked
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </BlockStack>
              </Card>
            </div>
          )}

          <Layout>

            {/* Loading Stage Indicator */}
            {loadingStage && (
              <Layout.Section>
                <Banner tone="info">
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    {loadingStage}
                  </Text>
                </Banner>
              </Layout.Section>
            )}

            {/* I'm Feeling Lucky Status (persists until Clear All) */}
            {luckyStatus && !loadingStage && (
              <Layout.Section>
                <div style={darkMode ? {
                  backgroundColor: '#1a1a2e',
                  border: '1px solid #404060',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#e0e0e0'
                } : {}}>
                  <Banner
                    tone={
                      luckyStatus.type === 'resync' ? 'warning' :
                        luckyStatus.type === 'malformed-location' ? 'warning' :
                          luckyStatus.type === 'high-weight-low-price' ? 'warning' :
                            luckyStatus.type === 'missing-weight' ? 'info' :
                              luckyStatus.type === 'missing-barcode-location' ? 'info' :
                                luckyStatus.type === 'random' ? 'success' :
                                  'info'
                    }
                  >
                    <div style={{ fontWeight: 600, fontSize: '14px', color: darkMode ? '#e0e0e0' : 'inherit' }}>
                      {luckyStatus.type === 'resync' && '🔄 Priority A: Location Sync Required'}
                      {luckyStatus.type === 'missing-weight' && '⚖️ Priority B: Missing Weight'}
                      {luckyStatus.type === 'malformed-location' && '⚠️ Priority C: Malformed Location'}
                      {luckyStatus.type === 'missing-barcode-location' && '🏷️ Priority D: Missing Barcode or Location'}
                      {luckyStatus.type === 'high-weight-low-price' && '⚖️ Priority D.5: Heavy (>20kg) & Cheap (<$500)'}
                      {luckyStatus.type === 'random' && '🎲 Priority E: Random Selection'}
                      {' - '}{luckyStatus.message}
                    </div>
                  </Banner>
                </div>
              </Layout.Section>
            )}

            {/* PO putaway audit: same-brand tubs among this PO's stored
                locations. No dismiss — the strip clears itself as staff split
                the tubs (each save re-runs the audit). */}
            {poTubClashes?.lines?.length > 0 && !loadingStage && (
              <Layout.Section>
                <div style={{
                  backgroundColor: 'var(--app-wash-warning)',
                  border: '1px solid var(--app-warning-ink)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: 'var(--app-ink)'
                }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px', color: 'var(--app-warning-ink)' }}>
                    ⚠️ Same-brand tub{poTubClashes.lines.length === 1 ? '' : 's'} on this PO — change one location in each pair before putaway
                  </div>
                  {poTubClashes.lines.map((line) => (
                    <div key={line} style={{ fontSize: '13px', lineHeight: '20px' }}>{line}</div>
                  ))}
                  <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--app-ink-secondary)' }}>
                    Look-alike stock from one brand in one tub is how mispicks happen. The flagged cards below carry the same warning.
                  </div>
                </div>
              </Layout.Section>
            )}

            {/* The audit is a round trip: say it is running rather than
                dropping its verdict into the page a few seconds later
                (Rhys 2026-09-04). */}
            {poTubAuditPending && !poTubClashes?.lines?.length && !loadingStage && (
              <Layout.Section>
                <div style={{
                  backgroundColor: 'var(--app-surface-2)',
                  border: '1px dashed var(--app-border-strong)',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: '13px',
                  color: 'var(--app-ink-secondary)'
                }}>
                  ⏳ Checking tub locations for same-brand clashes…
                </div>
              </Layout.Section>
            )}

            {/* Products List */}
            {products.length > 0 && (
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    {/* ButtonGroup moved to Sticky Header */}

                    {/* Grid of Product Cards */}
                    <div className="products-grid">
                      {visibleProducts
                        .map((product) => {
                          const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
                          const editable = editableData[productKey] || {};
                          const isSelected = selectedProducts[productKey];

                          // Use storeMetafields if available (from live enrichment), otherwise fallback to inventoryItemId matching
                          let otherStoresLocations = [];

                          if (product.storeMetafields) {
                            // Convert storeMetafields object to array format
                            otherStoresLocations = Object.entries(product.storeMetafields)
                              .filter(([storeName]) => storeName !== product.storeDisplayName)
                              .map(([storeName, data]) => ({
                                store: storeName,
                                productId: data.productId || null, // Include productId if available
                                variantId: data.variantId || null, // Include variantId if available
                                storeUrl: data.storeUrl || null, // Include storeUrl if available
                                binLocation: data.binLocation || 'Not set',
                                overflow1: data.overflow1 || null,
                                overflow2: data.overflow2 || null
                              }));
                          } else {
                            // Fallback: Find other stores with same product (by inventoryItemId)
                            // Guard: both must have a valid inventoryItemId to avoid null===null false matches
                            otherStoresLocations = products
                              .filter(p =>
                                product.inventoryItemId &&
                                p.inventoryItemId === product.inventoryItemId &&
                                p.store !== product.store
                              )
                              .map(p => ({
                                store: p.storeDisplayName,
                                productId: p.id, // Include the product ID for this store
                                variantId: p.variantId, // Include the variant ID for this store
                                storeUrl: p.store, // Include the store URL
                                binLocation: p.binLocation || 'Not set',
                                overflow1: p.overflow1 || null,
                                overflow2: p.overflow2 || null
                              }));
                          }

                          return (
                            <ProductCard
                              key={productKey}
                              product={product}
                              productKey={productKey}
                              isSelected={isSelected}
                              editable={editable}
                              editingFields={editingFields}
                              tubClash={product.sku ? poTubClashes?.bySku?.[String(product.sku).trim().toUpperCase()] : undefined}
                              otherStoresLocations={otherStoresLocations}
                              onToggleSelection={toggleProductSelection}
                              onRemove={removeProduct}
                              onUpdateField={updateEditableField}
                              onToggleEdit={toggleEditMode}
                              onSave={handleSave}
                              onBarcodeEnter={handleBarcodeEnter}
                              onSkuEnter={handleSkuEnter}
                              darkMode={darkMode}
                              recentlySaved={recentlySaved}
                              isBulkUpdateTarget={isSelected && locationBarcode.trim().length > 0}
                              getStoreBadgeTone={getStoreBadgeTone}
                              getStoreBadgeLabel={getStoreBadgeLabel}
                              labelQuantity={labelQuantities[productKey]}
                              onLabelQuantityChange={(key, qty) => {
                                setLabelQuantities(prev => ({
                                  ...prev,
                                  [key]: qty
                                }));
                                // Touching the count IS the review.
                                clearLabelQuantityReview(key);
                              }}
                              needsLabelQuantityReview={Boolean(labelQuantityReviewKeys[productKey])}
                              onLabelQuantityReviewed={clearLabelQuantityReview}
                              canEditEta={canManageEtaDates}
                              canEditStock={canEditProductStock}
                              canEditLightspeedStock={canEditLightspeedStock}
                              lightspeedStockWrites={lightspeedStockWrites}
                              onSaveStock={handleSaveProductStock}
                              onLoadStock={handleLoadProductStock}
                              onPrintLabel={(_p, key, plan) => printLabels(plan?.mode === 'actual' ? null : plan, key)}
                              onToggleNoBarcodeAck={async (p, key, next) => {
                                try {
                                  if (next) {
                                    await axios.post(`${API_URL}/api/products/barcode-ack`, { store: p.store, sku: p.sku });
                                  } else {
                                    await axios.delete(`${API_URL}/api/products/barcode-ack`, { data: { store: p.store, sku: p.sku } });
                                  }
                                  setProducts(prev => prev.map(x => (
                                    `${x.storeDisplayName}-${x.id}-${x.variantId}` === key
                                      ? { ...x, noBarcodeAck: next, ...(next ? { barcode: '' } : {}) }
                                      : x
                                  )));
                                } catch (err) {
                                  setError(err.response?.data?.error || 'Failed to update the no-barcode flag');
                                }
                              }}
                              canTrackExpiry={canTrackExpiry}
                              onToggleExpiryTracked={handleToggleExpiryTracked}
                              onOpenExpiry={handleOpenExpiryPanel}
                              vendorRef={product.sku ? vendorRefsBySku[product.sku] : null}
                              canEditVendorRef={!!product.sku}
                              vendorRefKnownSupplier={product.sku ? vendorRefKnownSupplierFor(product.sku) : null}
                              onLoadVendorRefSuppliers={loadVendorRefSuppliers}
                              onSaveVendorRef={handleSaveVendorRef}
                              kitLink={kitLinkFor(kitSplitIndex, product)}
                              canEditKitSplit={canEditKitSplit}
                              onEditKitSplit={handleEditKitSplit}
                              productLink={productLinkFor(productLinkIndex, product)}
                              canEditProductLinks={canEditProductLinks}
                              onEditProductLinks={handleEditProductLinks}
                              onOpenLinkedProduct={handleOpenLinkedProduct}
                            />
                          );
                        })}
                    </div>
                  </BlockStack>
                </Card>
              </Layout.Section>
            )}

            {/* Empty State */}
            {products.length === 0 && !isLoading && (
              <Layout.Section>
                <Card>
                  <EmptyState
                    heading="No products scanned yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>Scan a product barcode or enter a SKU to get started</p>
                  </EmptyState>
                </Card>
              </Layout.Section>
            )}

            {/* Bulk Save All Changes Button */}
            {(() => {
              const normalize = (value) => {
                if (!value || value === '' || value === 'Not set') return null;
                return value;
              };
              const normalizeDim = (value) => {
                if (value === undefined || value === null || value === '') return null;
                const num = parseFloat(value);
                return Number.isFinite(num) ? num : null;
              };
              const productsWithChanges = products.filter(product => {
                const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
                const editable = editableData[productKey] || {};
                if (recentlySaved.has(productKey)) return false;
                return (
                  (editable.weight !== undefined && editable.weight !== product.weight) ||
                  (editable.barcode !== undefined && editable.barcode !== product.barcode) ||
                  (editable.binLocation !== undefined && normalize(editable.binLocation) !== normalize(product.binLocation)) ||
                  (editable.overflow1 !== undefined && normalize(editable.overflow1) !== normalize(product.overflow1)) ||
                  (editable.overflow2 !== undefined && normalize(editable.overflow2) !== normalize(product.overflow2)) ||
                  (editable.length !== undefined && normalizeDim(editable.length) !== normalizeDim(product.length)) ||
                  (editable.width !== undefined && normalizeDim(editable.width) !== normalizeDim(product.width)) ||
                  (editable.height !== undefined && normalizeDim(editable.height) !== normalizeDim(product.height))
                );
              });
              if (productsWithChanges.length < 2) return null;
              return (
                <Layout.Section>
                  <Card>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '8px 0' }}>
                      <Button
                        variant="primary"
                        tone="success"
                        size="large"
                        onClick={() => {
                          productsWithChanges.forEach(product => {
                            const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
                            const editable = editableData[productKey] || {};
                            handleSave(product, productKey, editable);
                          });
                        }}
                      >
                        💾 Save All Changes ({productsWithChanges.length})
                      </Button>
                    </div>
                  </Card>
                </Layout.Section>
              );
            })()}

            {/* Unified Bulk Update Section with Tabs */}
            {selectedCount > 0 && (
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                      Bulk Update ({selectedCount} selected)
                    </Text>

                    {/* Segmented control, not Polaris Tabs: the tabs took a
                        string so they could only carry emoji, which is exactly
                        what the card moved away from. Same stroke icons and
                        teal accent as the card, so the two read as one design
                        (Rhys 2026-08-29). */}
                    <div
                      role="tablist"
                      aria-label="Bulk update field"
                      style={{
                        display: 'inline-flex', gap: '4px', padding: '3px',
                        borderRadius: '10px', width: 'fit-content', maxWidth: '100%',
                        flexWrap: 'wrap',
                        background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f2f4'
                      }}
                    >
                      {[
                        { id: 'location', label: 'W/H Location', icon: <IconPin /> },
                        { id: 'weight', label: 'Weight', icon: <IconWeight /> },
                        { id: 'dims', label: 'Dimensions', icon: <IconBox /> },
                        ...(canTrackExpiry ? [{ id: 'expiry', label: 'Expiry', icon: <IconCalendar /> }] : [])
                      ].map((tab, index) => {
                        const active = bulkUpdateTab === index;
                        const accent = darkMode ? ACCENT.dark : ACCENT.light;
                        return (
                          <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setBulkUpdateTab(index)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                              padding: '6px 12px', borderRadius: '8px', cursor: 'pointer',
                              fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                              border: active ? `1px solid ${accent}` : '1px solid transparent',
                              background: active
                                ? (darkMode ? 'rgba(13,148,136,0.22)' : '#ffffff')
                                : 'transparent',
                              color: active ? accent : 'var(--app-ink-secondary)',
                              boxShadow: active && !darkMode ? '0 1px 2px rgba(15,23,42,0.08)' : 'none'
                            }}
                          >
                            {tab.icon}
                            <span>{tab.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Location Tab Content */}
                    {bulkUpdateTab === 0 && (
                      <form onSubmit={handleLocationUpdate}>
                        <BlockStack gap="150">
                          <InlineStack gap="400" wrap={false}>
                            <div style={{ flex: 1 }}>
                              <TextField
                                value={locationBarcode}
                                onChange={setLocationBarcode}
                                placeholder="Scan location barcode..."
                                autoComplete="off"
                                disabled={isUpdating}
                                prefix={<Icon source={LocationIcon} />}
                                helpText="Scan or enter location (e.g., P12, U-01-H-04 or u1h4). A shelf without a slot asks where on the shelf."
                              />
                            </div>
                            {bulkShelfPick && (
                              <ShelfSlotPicker
                                shelfCode={bulkShelfPick}
                                onPick={(full) => { setBulkShelfPick(null); setLocationBarcode(full); commitBulkLocation(full); }}
                                onCancel={() => setBulkShelfPick(null)}
                              />
                            )}
                            <Button
                              submit
                              primary
                              loading={isUpdating}
                              disabled={!locationBarcode.trim()}
                            >
                              Update ({selectedCount})
                            </Button>
                          </InlineStack>
                          {/* Rarely-used destructive action: keep it a small text link,
                              right-aligned away from the scan flow, behind a confirm modal. */}
                          <InlineStack align="end">
                            <Button
                              variant="plain"
                              tone="critical"
                              disabled={Boolean(locationBarcode.trim()) || isUpdating}
                              onClick={() => setShowBulkClearLocations(true)}
                            >
                              Clear W/H locations ({selectedCount})…
                            </Button>
                          </InlineStack>
                        </BlockStack>
                      </form>
                    )}

                    {/* Weight Tab Content */}
                    {bulkUpdateTab === 1 && (
                      <form onSubmit={handleBulkWeightUpdate}>
                        <InlineStack gap="400" wrap={false}>
                          <div style={{ flex: 1 }}>
                            <TextField
                              value={bulkWeight}
                              onChange={setBulkWeight}
                              placeholder="Enter weight in grams..."
                              autoComplete="off"
                              disabled={isUpdating}
                              type="number"
                              min="0"
                              step="0.01"
                              helpText="Enter weight value (will be saved in grams)"
                            />
                          </div>
                          <Button
                            submit
                            primary
                            loading={isUpdating}
                            disabled={!bulkWeight.trim()}
                          >
                            Update ({selectedCount})
                          </Button>
                        </InlineStack>
                      </form>
                    )}

                    {/* Dimensions Tab Content */}
                    {bulkUpdateTab === 2 && (
                      // Same edge-coloured box widget as the card/Ship dims popover
                      // (BoxDimensionsFields). Partial input allowed: only filled
                      // fields are written, via handleBulkDimsUpdate.
                      <BoxDimensionsBar
                        values={{ length: bulkLength, width: bulkWidth, height: bulkHeight }}
                        onChange={(field, value) => {
                          if (field === 'length') setBulkLength(value);
                          else if (field === 'width') setBulkWidth(value);
                          else setBulkHeight(value);
                        }}
                        onSubmit={() => handleBulkDimsUpdate()}
                        submitLabel={`Update (${selectedCount})`}
                        submittingLabel="Updating…"
                        submitting={isUpdating}
                      >
                        <div style={{ marginTop: 4 }}>
                          <Text as="p" tone="subdued" variant="bodySm">
                            Shipping dims in cm (L × W × H) — only filled fields are updated, and they push to all linked stores.
                          </Text>
                        </div>
                      </BoxDimensionsBar>
                    )}

                    {/* Expiry Tab Content: bulk "Tracks expiry" flag (app-side, never Shopify). */}
                    {bulkUpdateTab === 3 && canTrackExpiry && (
                      <BlockStack gap="200">
                        <InlineStack gap="300" blockAlign="center" wrap>
                          <Button
                            variant="primary"
                            loading={bulkExpiryBusy === 'on'}
                            disabled={Boolean(bulkExpiryBusy)}
                            onClick={() => handleBulkExpiryTracking(true)}
                          >
                            Track expiry ({selectedCount})
                          </Button>
                          <Button
                            loading={bulkExpiryBusy === 'off'}
                            disabled={Boolean(bulkExpiryBusy)}
                            onClick={() => handleBulkExpiryTracking(false)}
                          >
                            Stop tracking expiry ({selectedCount})
                          </Button>
                        </InlineStack>
                        <Text as="p" tone="subdued" variant="bodySm">
                          Tracked products ask for an expiry date when a PO is received and appear on the Expiry page. Stopping keeps their recorded batches.
                        </Text>
                      </BlockStack>
                    )}
                  </BlockStack>
                </Card>
              </Layout.Section>
            )}
          </Layout>
        </Page>
        )
        )}

        {/* Location Search Warning Modal */}
        <Modal
          open={showLocationWarning}
          onClose={cancelLocationSearch}
          title="⚠️ Clear Existing Products?"
          primaryAction={{
            content: 'Yes, Clear & Search Location',
            onAction: confirmLocationSearch,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: cancelLocationSearch,
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <Banner tone="warning">
                <p><strong>You currently have {products.length} product(s) loaded.</strong></p>
              </Banner>

              <Text as="p">
                Location searches will <strong>replace all current products</strong> with products found at the searched location.
              </Text>

              <Text as="p">
                This action cannot be undone. Do you want to:
              </Text>

              <BlockStack gap="200">
                <Text as="p">• <strong>Clear</strong> the current {products.length} product(s)</Text>
                <Text as="p">• <strong>Search</strong> for products at location <Badge>{pendingLocationSearch?.query}</Badge></Text>
              </BlockStack>

              <Banner tone="info">
                <p><strong>Tip:</strong> Consider finishing work on current products before doing location searches, or export/save your current list first.</p>
              </Banner>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Put Away Stock — walks the loaded products onto the shelves in
            picking-route order, lighting each bay in turn. */}
        <MountAfterFirstOpen open={putAwayOpen}>
          <PutAwayModal
            open={putAwayOpen}
            onClose={() => setPutAwayOpen(false)}
            products={products}
            darkMode={darkMode}
            onSaveLocation={(product, location) => {
              const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
              // Save from the LIVE grid row, not the modal's frozen snapshot.
              // Put-away is a location-only workflow: staged barcode/weight
              // edits stay on the card until the staff member saves them there.
              const fresh = products.find((x) => `${x.storeDisplayName}-${x.id}-${x.variantId}` === productKey) || product;
              return handleSave(fresh, productKey, { binLocation: location }, { markRecentlySaved: false, awaitCompletion: true });
            }}
          />
        </MountAfterFirstOpen>

        {/* Asked before every label print: the counts entered on the cards,
            one each, or a typed number. The generated page then goes straight
            to the print dialog. */}
        <MountAfterFirstOpen open={printLabelDialogOpen}>
          <PrintLabelQuantityModal
            open={printLabelDialogOpen}
            productCount={selectedCount}
            enteredTotal={selectedLabelTotal}
            darkMode={darkMode}
            onCancel={() => setPrintLabelDialogOpen(false)}
            onConfirm={(plan) => {
              setPrintLabelDialogOpen(false);
              printLabels(plan.mode === 'actual' ? null : plan);
            }}
          />
        </MountAfterFirstOpen>

        {/* Same-brand box rule. Shown instead of writing a location that would put
            two different items from one brand in the same tub. */}
        <MountAfterFirstOpen open={Boolean(brandClashPrompt)}>
          <BrandClashModal
            open={Boolean(brandClashPrompt)}
            location={brandClashPrompt?.location || ''}
            clashes={brandClashPrompt?.result?.clashes || []}
            batchClashes={brandClashPrompt?.result?.batchClashes || []}
            bulkCount={brandClashPrompt?.mode === 'bulk' ? (brandClashPrompt.skuCount || 0) : 0}
            darkMode={darkMode}
            onChooseAnother={() => {
              const prompt = brandClashPrompt;
              setBrandClashPrompt(null);
              if (prompt?.deferred) {
                // Awaited save (Put Away): the location was NOT written - let the
                // caller show that and keep its own input open for another tub.
                prompt.deferred.reject(new Error(`Not saved - ${prompt.location} already holds another brand. Choose another tub.`));
                return;
              }
              // Card flows: reopen the bin field with their typed value intact
              // so the next tub can be entered straight away.
              if ((prompt?.mode === 'single' || prompt?.mode === 'field') && prompt.productKey) {
                setEditingFields(prev => ({ ...prev, [`${prompt.productKey}-binLocation`]: true }));
              }
            }}
            onOverride={() => {
              const prompt = brandClashPrompt;
              setBrandClashPrompt(null);
              if (!prompt) return;
              if (prompt.mode === 'bulk') {
                applyBulkLocation(prompt.location);
                return;
              }
              if (prompt.mode === 'field') {
                // Early prompt (field just closed, nothing queued yet): remember
                // the override so the Save-time backstop stays quiet, and leave
                // the normal Save step to the user.
                brandClashApprovedRef.current[prompt.productKey] = String(prompt.location || '').trim().toUpperCase();
                return;
              }
              const resave = handleSave(prompt.product, prompt.productKey, prompt.editable, {
                ...(prompt.saveOptions || {}),
                skipBrandCheck: true
              });
              if (prompt.deferred) {
                resave.then(prompt.deferred.resolve, prompt.deferred.reject);
              }
            }}
          />
        </MountAfterFirstOpen>

        {/* E-Ink display chooser — one button per Product reference display */}
        <Modal
          open={Boolean(eDisplayChooser)}
          onClose={() => setEDisplayChooser(null)}
          title={`🖥️ Send ${eDisplayChooser?.product?.sku || 'product'} to which E-Ink display?`}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setEDisplayChooser(null),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text as="p" tone="subdued">
                The chosen tag lights its big button white for 15s and takes ~10s to repaint.
              </Text>
              {eDisplayChooser?.product?.binLocation ? (
                <Checkbox
                  label={`Locate this item — light bay lamp at ${eDisplayChooser.product.binLocation} solid white for 30s`}
                  checked={eDisplayLocate}
                  onChange={setEDisplayLocate}
                />
              ) : (
                <Text as="p" tone="subdued">No W/H location on this product — bay locate unavailable.</Text>
              )}
              {(eDisplayChooser?.displays || []).map((display) => (
                <Button
                  key={display.mac}
                  fullWidth
                  size="large"
                  onClick={() => pushToEDisplay(eDisplayChooser.product, display, eDisplayLocate)}
                >
                  {`${display.displayName}${display.lastSku ? ` — showing ${display.lastSku}` : ''}`}
                </Button>
              ))}
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Bulk Clear W/H Locations Confirmation Modal */}
        <Modal
          open={showBulkClearLocations}
          onClose={() => setShowBulkClearLocations(false)}
          title="🧹 Clear W/H locations?"
          primaryAction={{
            content: `Clear locations (${selectedCount})`,
            onAction: handleBulkClearLocations,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowBulkClearLocations(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <Banner tone="warning">
                <p><strong>This removes the W/H location from {selectedCount} selected product(s).</strong></p>
              </Banner>

              <Text as="p">
                Each product is added to the save queue with a blank W/H location, which
                deletes the location in Shopify across <strong>all linked stores</strong>.
              </Text>

              <Text as="p">
                Overflow locations (OF1 / OF2) are not touched. Products keep their spot in
                the grid, so you can scan a new location straight after.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Multi-Store Selection Modal */}
        <Modal
          open={showStoreSelector}
          onClose={() => {
            setShowStoreSelector(false);
            setMultiStoreProducts([]);
            setSelectedStoreProducts([]);
          }}
          title="Select Product Store"
          primaryAction={{
            content: `Add ${selectedStoreProducts.length} Product(s)`,
            onAction: confirmStoreSelection,
            disabled: selectedStoreProducts.length === 0,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setShowStoreSelector(false);
                setMultiStoreProducts([]);
                setSelectedStoreProducts([]);
              },
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <Banner>
                <p>This SKU/Barcode was found in multiple stores. Select which product(s) to add:</p>
              </Banner>

              {multiStoreProducts.map((product, _idx) => {
                const productKey = `${product.storeDisplayName}-${product.id}-${product.variantId}`;
                const isSelected = selectedStoreProducts.some(p =>
                  p.id === product.id && p.variantId === product.variantId && p.storeDisplayName === product.storeDisplayName
                );

                return (
                  <Card key={productKey}>
                    <div
                      onClick={() => toggleStoreProductSelection(product)}
                      style={{
                        padding: '16px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#f6f6f7' : 'transparent',
                        border: isSelected ? '2px solid #2c6ecb' : '2px solid transparent',
                        borderRadius: '8px'
                      }}
                    >
                      <InlineStack gap="400" wrap={false} blockAlign="start">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleStoreProductSelection(product)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            marginTop: '4px'
                          }}
                        />

                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.title}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              backgroundColor: '#f0f0f0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '60px',
                            height: '60px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Text variant="bodySm" tone="subdued">No Image</Text>
                          </div>
                        )}

                        <div style={{ flex: 1 }}>
                          <BlockStack gap="200">
                            <Text variant="headingMd" as="h3">
                              {product.title}
                            </Text>

                            <InlineStack gap="200" wrap>
                              <Badge tone={getStoreBadgeTone(product.storeDisplayName)}>
                                {getStoreBadgeLabel(product.storeDisplayName)}
                              </Badge>
                              <Badge>SKU: {product.sku}</Badge>
                              <Badge tone="info">Stock: {product.stockOnHand || 0}{product.stockCommitted > 0 && <span style={{ color: 'var(--app-critical-ink)' }}> (A:{product.stockCommitted})</span>}</Badge>
                            </InlineStack>

                            <Text variant="bodySm" as="p" tone="subdued">
                              Location: {product.binLocation} | Weight: {product.weight || 0} {product.weightUnit || 'g'}
                            </Text>
                          </BlockStack>
                        </div>
                      </InlineStack>
                    </div>
                  </Card>
                );
              })}
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Persistent module backdrop — prevents flash when switching modules (map renders inline above backdrop) */}
        {(moduleView !== 'home' || backdropLeaving) && moduleView !== 'map' && moduleView !== 'backup-restore' && moduleView !== 'expiry' && moduleView !== 'ceo' && moduleView !== 'picking-supervisor' && moduleView !== 'packing-supervisor' && moduleView !== 'pick-from-store' && moduleView !== 'ship' && moduleView !== 'local-deliveries' && !isMobile && !showPickingModal && !showStocktake && (

          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 999,
              pointerEvents: 'none',
              backgroundColor: (() => {
                const key = backdropLeaving && moduleView !== 'home'
                  ? backdropLeaving
                  : (moduleView !== 'home' ? moduleView : backdropLeaving);
                return MODULE_COLORS[key]?.header ?? DEFAULT_HEADER_COLOR;
              })(),
              transition: 'background-color 0.12s ease'
            }}
            aria-hidden
          />
        )}

        {/* Incoming Stock — fixed overlay below shared header (same pattern as Preorders) */}
        {showPOViewer && (
          <div
            className={`incoming-stock-overlay${darkMode ? ' dark' : ''}`}
            style={{ top: appHeaderOffsetPx }}
          >
            <div className="incoming-stock-overlay__inner">

            {/* Bulk Actions */}
            {selectedPOs.size > 0 && (
              <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: darkMode ? '#333' : '#e8f4fd', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text fontWeight="bold">{selectedPOs.size} orders selected</Text>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button size="micro" onClick={() => setSelectedPOs(new Set())}>Clear</Button>
                  {poStatusTab !== 'archived' && (
                    <Button size="micro" onClick={() => handleBulkAction('archived')}>Archive Selected</Button>
                  )}
                  {poStatusTab === 'archived' && (
                    <Button size="micro" onClick={() => handleBulkAction('draft')}>Restore Selected</Button>
                  )}
                </div>
              </div>
            )}

            {/* Status Tabs - ALL + workflow tabs. "Notify purchasing" sits at
                the right end of this row, above the store filter (Rhys 2026-10-09). */}
            <div style={{ marginBottom: '12px', borderBottom: '1px solid rgba(128,128,128,0.2)', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <InlineStack gap="400">
                {PURCHASE_ORDER_TABS.map(tab => (
                  <div
                    key={tab}
                    onClick={() => {
                      setPoStatusTab(tab);
                      setPoPage(1);
                      // Update URL with tab for deep linking
                      const tabUrl = incomingStockPath(tab);
                      window.history.replaceState({}, '', tabUrl);
                    }}
                    style={getPoTabStyle(tab, poStatusTab === tab)}
                  >
                    {getPoTabDisplayName(tab)}
                  </div>
                ))}
              </InlineStack>
              <div style={{ paddingBottom: '8px' }}>
                <Button onClick={() => setUnmatchedLogOpen(true)}>📦 No PO? Notify purchasing</Button>
              </div>
            </div>

            {/* Search & Store Filter — ONE row (Rhys 2026-08-29): the count/
                pagination line cost a full row of card height. Force refresh
                sits beside Select All Visible; Previous/Next only render when
                a second page actually exists. */}
            <div style={{ marginBottom: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <TextField
                  placeholder="Filter by PO number, vendor, or SKU..."
                  value={poFilter}
                  onChange={setPoFilter}
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setPoFilter('')}
                  prefix={<Icon source={SearchIcon} />}
                />
              </div>
              <Button onClick={handleSelectAllPOs}>Select All Visible</Button>
              <Button
                onClick={() => { fetchPurchaseOrders(true); setUnmatchedArrivalsRefreshKey(k => k + 1); }}
                disabled={isLoadingPOs}
                loading={isLoadingPOs || showPORefreshIndicator}
              >
                Force refresh
              </Button>
              {Math.max(1, poMeta.total_pages || 1) > 1 && (
                <>
                  <Button
                    disabled={isLoadingPOs || !poMeta.has_prev_page}
                    onClick={() => setPoPage(prev => Math.max(1, prev - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={isLoadingPOs || !poMeta.has_next_page}
                    onClick={() => setPoPage(prev => prev + 1)}
                  >
                    {`Next (p${poMeta.page || 1}/${Math.max(1, poMeta.total_pages || 1)})`}
                  </Button>
                </>
              )}
              <ButtonGroup segmented>
                <Button pressed={poStoreFilter === 'all'} onClick={() => setPoStoreFilter('all')}>All Stores</Button>
                <Button pressed={poStoreFilter === 'phaser'} onClick={() => setPoStoreFilter('phaser')}>Phaser FPV</Button>
                <Button pressed={poStoreFilter === 'ccc'} onClick={() => setPoStoreFilter('ccc')}>CCC</Button>
              </ButtonGroup>
            </div>

            {/* Deliveries that arrived with no PO: temp cards + "Goods arrived,
                no PO" logger that emails purchasing (2026-10-09). */}
            <UnmatchedArrivalsPanel
              darkMode={darkMode}
              statusTab={poStatusTab}
              storeFilter={poStoreFilter}
              filterText={poFilter}
              purchaseOrders={purchaseOrders}
              refreshKey={unmatchedArrivalsRefreshKey}
              logOpen={unmatchedLogOpen}
              onLogOpenChange={setUnmatchedLogOpen}
              onOpenPo={(reference) => {
                setPoStatusTab('all');
                setPoPage(1);
                setPoFilter(reference);
                window.history.replaceState({}, '', incomingStockPath('all'));
              }}
            />

            {/* Content */}
            {isLoadingCurrentPOView ? (
              <Box padding="400"><Text alignment="center">Loading purchase orders...</Text></Box>
            ) : visiblePurchaseOrderCount === 0 ? (
              (() => {
                const isFiltered = poStoreFilter !== 'all' || Boolean(poFilter && poFilter.trim());
                const tabName = getPoTabDisplayName(poStatusTab);
                return (
                  <Box padding="400">
                    <div style={{ textAlign: 'center', maxWidth: '440px', margin: '0 auto', padding: '24px 0' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{isFiltered ? '🔍' : '📭'}</div>
                      <Text variant="headingSm" as="p">
                        {isFiltered ? `No ${tabName} orders match your filters` : `No orders in ${tabName}`}
                      </Text>
                      <div style={{ marginTop: '6px' }}>
                        <Text tone="subdued" variant="bodySm">
                          {isFiltered
                            ? `The ${tabName} tab may still have orders for a different store or search term.`
                            : `Purchase orders appear here as they reach the ${tabName} stage.`}
                        </Text>
                      </div>
                      {isFiltered && (
                        <div style={{ marginTop: '12px' }}>
                          <Button size="slim" onClick={() => { setPoStoreFilter('all'); setPoFilter(''); }}>
                            Clear filters
                          </Button>
                        </div>
                      )}
                    </div>
                  </Box>
                );
              })()
            ) : (
              <BlockStack gap="400">
                {/* Preorder Notification Banner */}
                {poStatusTab === 'awaiting_stockin' && preorderNotification && (
                  <div style={{ marginBottom: '16px' }}>
                    <Banner
                      title="⚠️ Attention: Outstanding Preorders Detected"
                      tone="warning"
                      onDismiss={() => setPreorderNotification(null)}
                    >
                      <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                        <p><strong>Please set aside the following items immediately:</strong></p>
                        <ul style={{ marginTop: '8px', paddingLeft: '20px', listStyleType: 'disc' }}>
                          {preorderNotification.items.map((item, idx) => (
                            <li key={idx} style={{ marginBottom: '4px' }}>{item.replace(/^• /, '')}</li>
                          ))}
                        </ul>
                        <div style={{ marginTop: '12px' }}>
                          <Button size="slim" onClick={() => setPreorderNotification(null)}>I have set these aside</Button>
                        </div>
                      </div>
                    </Banner>
                  </div>
                )}

                {/* Column caps live in the injected .po-grid CSS: 3 across on a
                    plain desktop, 4 from ~1900px, 5 on ultrawide and never more —
                    product names must stay readable in full. */}
                <div className="po-grid">
                  {visiblePurchaseOrderItems.map((item) => {
                      // Render separator
                      if (item.isSeparator) {
                        return (
                          <div key={item.key} style={{
                            gridColumn: '1 / -1',
                            padding: '8px 12px',
                            margin: '2px 0',
                            background: darkMode ? '#1a1a1a' : '#f5f5f5',
                            borderRadius: '10px',
                            border: `1px dashed ${darkMode ? '#555' : '#ccc'}`,
                            textAlign: 'center'
                          }}>
                            <Text variant="bodySm" tone="subdued" fontWeight="semibold">
                              ⬇️ No Expected Arrival Date Set
                            </Text>
                          </div>
                        );
                      }

                      const po = item;
                      const effectiveTab = poStatusTab === 'all' ? getEffectiveTabForPO(po) : poStatusTab;
                      // Card design tokens (visual only). One pill scale for every custom
                      // chip on the card: value pills 12px, status pills 11px, micro-tags
                      // 10px — all sharing the same full radius and padding rhythm.
                      // Micro-labels are the quiet uppercase field names ("ETA",
                      // "Tracking") so the VALUES are what the eye lands on.
                      const cardHairline = darkMode ? '#3f3f46' : '#e9ebee';
                      const pillBase = {
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '2px 10px', borderRadius: '999px',
                        fontSize: '12px', fontWeight: 600, lineHeight: '18px', whiteSpace: 'nowrap'
                      };
                      const statusPillBase = { ...pillBase, padding: '1px 9px', fontSize: '11px', lineHeight: '16px' };
                      // Same micro-label recipe as the Products card field labels
                      // (10px/700, 0.06em, --app-ink-secondary) — linked modules,
                      // one label voice.
                      const microLabelStyle = {
                        fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                        color: 'var(--app-ink-secondary)', flexShrink: 0
                      };
                      // Arrived-tab aging: how long since this PO physically
                      // landed and entered receiving (see resolvePoArrivalStamp
                      // — past ETA stands in as an estimate, marked ~).
                      const arrivalStamp = resolvePoArrivalStamp(po);
                      const arrivedStampIso = arrivalStamp.iso;
                      const arrivedAgeHours = arrivedStampIso ? Math.max(0, Math.floor((Date.now() - Date.parse(arrivedStampIso)) / 3600000)) : null;
                      const arrivedAgeColor = arrivedAgeHours == null
                        ? null
                        : (arrivedAgeHours < 24 ? '#1a874f' : arrivedAgeHours < 48 ? '#e8730c' : '#d82c0d');
                      const arrivedAgePrefix = arrivalStamp.estimated ? '~' : '';
                      const arrivedAgeLabel = arrivedAgeHours == null
                        ? null
                        : (arrivedAgeHours < 24 ? `${arrivedAgePrefix}Arrived ${arrivedAgeHours}h ago` : `${arrivedAgePrefix}Arrived ${Math.floor(arrivedAgeHours / 24)}d ${arrivedAgeHours % 24}h ago`);
                      const showArrivedAge = effectiveTab === 'arrived' && arrivedAgeColor != null;
                      // Full border + background wash so the age reads across
                      // the room — the left bar alone was too subtle.
                      const arrivedAgeTint = showArrivedAge ? ({
                        '#1a874f': darkMode ? 'rgba(26, 135, 79, 0.16)' : '#f0faf4',
                        '#e8730c': darkMode ? 'rgba(232, 115, 12, 0.14)' : '#fdf6ec',
                        '#d82c0d': darkMode ? 'rgba(216, 44, 13, 0.16)' : '#fdf0ed'
                      })[arrivedAgeColor] : null;
                      return (
                        <div key={po.id}>

                          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div
                              ref={el => { if (el) poCardRefs.current[po.id] = el; }}
                              className="po-card"
                              style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: arrivedAgeTint || (darkMode ? '#2a2a2a' : 'white'),
                                borderRadius: '10px',
                                boxShadow: darkMode
                                  ? '0 0 0 1px rgba(255, 255, 255, 0.07), 0 1px 3px 0 rgba(0, 0, 0, 0.4)'
                                  : '0 0 0 1px rgba(23, 24, 24, 0.06), 0 1px 3px 0 rgba(23, 24, 24, 0.1)',
                                // Full store-colour frame, not a left stripe (Rhys
                                // 2026-08-29): the stripe was easy to miss when scanning
                                // — the whole border now says whose PO this is. Phaser
                                // brand red (phaserfpv.com.au logo red); CCC gold.
                                // Arrived-age cards keep the age border instead; there
                                // the store pill carries the store.
                                border: showArrivedAge
                                  ? `2px solid ${arrivedAgeColor}`
                                  : (po.storeKey === 'phaser' ? '2px solid #e8192e' : (po.storeKey === 'ccc' ? '2px solid #ffcc00' : `2px solid ${cardHairline}`)),
                                padding: '10px 12px',
                                cursor: 'pointer',
                                position: 'relative'
                              }}
                              onClick={() => handlePOGridLoad(po)}
                            >
                              {/* Header Section. The title side must be the one that
                                  shrinks (flex 1 + minWidth 0) — without it, a long
                                  supplier name pushed the store/preorder badges clean
                                  off the card's right edge (Rhys 2026-08-29). */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'flex-start', gap: '8px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      label=""
                                      checked={selectedPOs.has(po.id)}
                                      onChange={() => handleTogglePOSelection(po.id)}
                                    />
                                  </div>
                                  <div style={{ paddingTop: '2px', display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px 14px', minWidth: 0 }}>
                                    <a
                                      href={(() => {
                                        // Build URL based on store type
                                        if (po.storeKey === 'phaser') {
                                          const lightspeedPoName = String(po.reference || '').replace(/-PART\d+$/i, '');
                                          return `https://phasercomputers.retail.lightspeed.app/inventory?order=created_at&dir=desc&outlet_id=&name=${encodeURIComponent(lightspeedPoName)}`;
                                        } else if (po.storeKey === 'ccc') {
                                          // Split POs get a -PARTx suffix locally; Qoblex only knows the root PO id
                                          const qoblexPoId = String(po.originalPoId || po.localMetadata?.originalPoId || po.id || '').replace(/-PART\d+$/i, '');
                                          return `https://go.qoblex.com/#purchase/${qoblexPoId}`;
                                        }
                                        return '#';
                                      })()}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                                      title={po.storeKey === 'phaser' ? 'Open in Lightspeed Retail' : 'Open in Qoblex'}
                                    >
                                      {/* Type scale: PO reference is the card's strongest element;
                                          the supplier reads as a clearly secondary sub-part of it. */}
                                      <div style={{ lineHeight: '20px' }}>
                                        <span style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.2px', color: darkMode ? '#f3f4f6' : '#1a1c1f' }}>
                                          {po.reference}
                                        </span>
                                        {/* The literal space keeps copied text / screen readers reading
                                            "SI-7918 CR Kennedy", not "SI-7918CR Kennedy". */}
                                        {' '}
                                        <span style={{ fontSize: '12px', fontWeight: 500, marginLeft: '4px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                                          {po.vendor_display_name || po.supplier_name}
                                        </span>
                                      </div>
                                      <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {/* Source-system context (Lightspeed/Qoblex status + their
                                            reference/invoice/notes) — ADMIN ONLY. It kept reading as
                                            one of our workflow badges and staff don't need it; a
                                            muted named line can't be mistaken for our state. */}
                                        {canManageSettings && (() => {
                                          const systemName = po.storeKey === 'phaser' ? 'Lightspeed' : (po.storeKey === 'ccc' ? 'Qoblex' : 'Source');
                                          const sourceStatus = String(po.status || 'unknown').replace(/_/g, ' ');
                                          // Qoblex notes can carry raw markup — "<span>4,734.02</span>"
                                          // rendered literally on CCC2198 (2026-08-24). Strip tags.
                                          const stripHtml = (v) => String(v || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || null;
                                          const sourceRef = stripHtml(po.lightspeed_reference || po.source_reference);
                                          const sourceInvoice = stripHtml(po.supplier_invoice);
                                          const sourceNotes = stripHtml(po.source_notes);
                                          const extras = [
                                            sourceRef && sourceRef !== po.reference ? `ref ${sourceRef}` : null,
                                            // CCC maps the Qoblex supplier reference into both fields.
                                            sourceInvoice && sourceInvoice !== sourceRef ? `inv ${sourceInvoice}` : null,
                                            // Staff often key the invoice ref into the note — don't echo it.
                                            sourceNotes && sourceNotes !== sourceInvoice && sourceNotes !== sourceRef ? sourceNotes : null
                                          ].filter(Boolean).join(' · ');
                                          const s = (po.status || '').toLowerCase();
                                          const statusColor = (s.includes('received') || s.includes('closed'))
                                            ? (darkMode ? '#6ee7a0' : '#2e7d32')
                                            : (darkMode ? '#9ca3af' : '#6b7280');
                                          return (
                                            <span
                                              title={`${systemName} (purchasing system) — status: ${po.status || 'unknown'}${sourceNotes ? `\nNotes: ${sourceNotes}` : ''}${sourceRef ? `\nTheir reference: ${sourceRef}` : ''}${sourceInvoice ? `\nSupplier invoice: ${sourceInvoice}` : ''}\nNot this receiving workflow — that's shown by the tab.`}
                                              style={{
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                color: darkMode ? '#9ca3af' : '#6b7280',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '280px'
                                              }}
                                            >
                                              {systemName}: <span style={{ color: statusColor, fontWeight: 600, textTransform: 'capitalize' }}>{sourceStatus}</span>
                                              {extras ? <span style={{ opacity: 0.85 }}> · {extras}</span> : null}
                                            </span>
                                          );
                                        })()}
                                        {/* In the mixed ALL tab, show where the PO sits in OUR workflow so it isn't confused with the supplier status above */}
                                        {poStatusTab === 'all' && (
                                          <span
                                            title="Where this PO is in the receiving workflow"
                                            style={{
                                              ...statusPillBase,
                                              fontWeight: 700,
                                              border: `1px solid ${darkMode ? '#4f46e5' : '#c7d2fe'}`,
                                              color: darkMode ? '#c7d2fe' : '#3730a3',
                                              background: darkMode ? 'rgba(79,70,229,0.18)' : '#eef2ff'
                                            }}
                                          >
                                            {getPoTabDisplayName(effectiveTab)}
                                          </span>
                                        )}
                                      </div>
                                    </a>
                              {/* ETA + Tracking share one row: quiet uppercase micro-labels so the
                                  date and carrier chips are what the eye lands on. ETA badge
                                  behaviour (hidden date-input overlay, permission gate) and
                                  tracking add/remove are untouched — layout only. */}
                              {['draft', 'waiting_arrival', 'arrived'].includes(effectiveTab) && (
                                // flexBasis 100%: the header wrapper is a wrap-flex, and on short
                                // titles this row floated UP beside the PO number — forcing a full
                                // row keeps ETA/Tracking in the same spot on every card.
                                // Wraps (2026-10-26): with the Confirm / Confirmed chip on the ETA
                                // line there was no room left and Tracking + "+ Add" were clipped to
                                // nothing; now Tracking drops to its own line only when it won't fit.
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px', paddingTop: '1px', minWidth: 0, flexBasis: '100%' }} onClick={(e) => e.stopPropagation()}>
                              {/* Expected Arrival (ETA) - all PO viewers see the same styled badge; editing requires ETA permission */}
                              {(['draft', 'waiting_arrival'].includes(effectiveTab) && (
                                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '4px 7px', minWidth: 0, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                    <span style={microLabelStyle}>ETA</span>
                                    {(() => {
                                      const etaDate = po.localMetadata?.expectedArrivalDate;
                                      const baseEtaLabel = etaDate
                                        ? new Date(etaDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                        : null;
                                      // Flag POs whose expected arrival has already passed while still Waiting Arrival.
                                      const startOfToday = new Date();
                                      startOfToday.setHours(0, 0, 0, 0);
                                      const isOverdue = effectiveTab === 'waiting_arrival' && etaDate && new Date(etaDate) < startOfToday;
                                      const etaLabel = baseEtaLabel && isOverdue ? `${baseEtaLabel} · Overdue` : baseEtaLabel;
                                      const badgeStyle = isOverdue ? {
                                        ...pillBase, fontWeight: 700,
                                        color: darkMode ? '#fca5a5' : '#b91c1c', background: darkMode ? 'rgba(198,40,40,0.18)' : '#fef1f1',
                                        border: `1.5px solid ${darkMode ? '#c62828' : '#dc2626'}`,
                                      } : etaDate ? {
                                        ...pillBase, fontWeight: 700,
                                        color: darkMode ? '#5eead4' : '#0f766e', background: darkMode ? 'rgba(13,148,136,0.15)' : '#effcf9',
                                        border: `1px solid ${darkMode ? '#0d9488' : '#8fe0d2'}`,
                                      } : {
                                        ...pillBase, fontWeight: 500,
                                        color: 'var(--app-ink-muted)', background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9fafb',
                                        border: `1px dashed ${darkMode ? '#6b7280' : '#d1d5db'}`,
                                      };
                                      if (canManageEtaDates) {
                                        return (
                                          <div
                                            role="button"
                                            tabIndex={0}
                                            title="Click to change arrival date"
                                            onClick={(e) => {
                                              const input = e.currentTarget.querySelector('input[type="date"]');
                                              if (input) { input.focus(); try { input.showPicker?.(); } catch (_) { input.click(); } }
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                const input = e.currentTarget.querySelector('input[type="date"]');
                                                if (input) { input.focus(); try { input.showPicker?.(); } catch (_) { input.click(); } }
                                              }
                                            }}
                                            style={{ position: 'relative', cursor: 'pointer', ...badgeStyle }}
                                            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.75'; }}
                                            onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
                                          >
                                            <input
                                              type="date"
                                              // UNCONTROLLED + commit-on-blur, not commit-on-change (2026-08-25).
                                              // Chrome's picker popup pushes a value into the page for EVERY
                                              // month ↑/↓ click while the popup is still open (the selection
                                              // follows the visible month — calendar_picker.js onBodyClick →
                                              // pagePopupController.setValue). Saving on change committed those
                                              // interim dates AND re-sorted the Waiting Arrival list by ETA,
                                              // moving this card's DOM node and killing the open popup mid-pick.
                                              // So: change only stages the value in the input + live pill label;
                                              // the save happens once, on blur (or Enter). Esc in the popup
                                              // restores the original value, so blur then finds no diff.
                                              // The key remounts the input whenever the canonical date changes
                                              // (commit, error revert, background refresh) — while the user is
                                              // mid-edit nothing re-renders, so React never touches the node.
                                              key={`eta-${etaDate || 'none'}`}
                                              defaultValue={etaDate || ''}
                                              onChange={(e) => {
                                                const label = e.target.parentElement?.querySelector('[data-eta-live-label]');
                                                if (label) {
                                                  label.textContent = e.target.value
                                                    ? new Date(e.target.value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                                    : 'Set arrival date';
                                                }
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  // Commit via the blur handler; stop the pill wrapper's own
                                                  // Enter handler from immediately reopening the picker.
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  e.currentTarget.blur();
                                                }
                                              }}
                                              onBlur={async (e) => {
                                                const newDate = e.target.value || '';
                                                const prevDate = po.localMetadata?.expectedArrivalDate || '';
                                                if (newDate === prevDate) return;
                                                applyPoMetadataEdit(po.id, { expectedArrivalDate: newDate || null }, { storeKey: po.storeKey, reason: 'expected date' });
                                                try {
                                                  await axios.post(`${API_URL}/api/purchase-orders/${po.id}/expected-date`, {
                                                    expectedArrivalDate: newDate || null
                                                  });
                                                  clearPoResponseCache('expected date saved');
                                                  setSuccessMessage(newDate ? 'Expected arrival date updated' : 'Expected arrival date cleared');
                                                  setTimeout(() => setSuccessMessage(''), 2000);
                                                } catch (err) {
                                                  console.error('Failed to update expected date:', err);
                                                  setError('Failed to update expected arrival date');
                                                  applyPoMetadataEdit(po.id, { expectedArrivalDate: prevDate || null }, { storeKey: po.storeKey, reason: 'expected date reverted' });
                                                }
                                              }}
                                              style={{
                                                position: 'absolute', inset: 0,
                                                width: '100%', height: '100%',
                                                margin: 0, padding: 0,
                                                opacity: 0, cursor: 'pointer', zIndex: 1
                                              }}
                                            />
                                            <svg style={{ pointerEvents: 'none', flexShrink: 0 }} width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="14" height="14" rx="2"/><line x1="3" y1="8" x2="17" y2="8"/><line x1="7" y1="2" x2="7" y2="6"/><line x1="13" y1="2" x2="13" y2="6"/></svg>
                                            <span data-eta-live-label style={{ pointerEvents: 'none' }}>{etaLabel || 'Set arrival date'}</span>
                                          </div>
                                        );
                                      }
                                      return (
                                        <span style={badgeStyle}>
                                          {etaDate && <svg style={{ flexShrink: 0 }} width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="14" height="14" rx="2"/><line x1="3" y1="8" x2="17" y2="8"/><line x1="7" y1="2" x2="7" y2="6"/><line x1="13" y1="2" x2="13" y2="6"/></svg>}
                                          {etaLabel || 'No arrival date'}
                                        </span>
                                      );
                                    })()}
                                    {/* Confirmed for today (CEO round 2, 2026-10-23): Waiting Arrival POs
                                        due today or overdue. A tick from an earlier day is ignored. */}
                                    {effectiveTab === 'waiting_arrival' && (() => {
                                      const todayKey = getTodayDateString();
                                      const etaKey = String(po.localMetadata?.expectedArrivalDate || '').slice(0, 10);
                                      const confirmation = po.localMetadata?.arrivalConfirmed;
                                      const confirmedToday = confirmation?.forDay === todayKey;
                                      if (confirmedToday) {
                                        const who = confirmation.byName || String(confirmation.by || '').split('@')[0];
                                        const at = confirmation.at
                                          ? new Date(confirmation.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Australia/Sydney' })
                                          : '';
                                        const canUndo = hasModulePermission('po');
                                        return (
                                          <span
                                            style={{
                                              ...pillBase, fontWeight: 700,
                                              color: darkMode ? '#86efac' : '#15803d', background: darkMode ? 'rgba(22,163,74,0.18)' : '#ecfdf3',
                                              border: `1px solid ${darkMode ? '#16a34a' : '#86d9a5'}`
                                            }}
                                            title={`Confirmed for today by ${confirmation.byName || confirmation.by || 'someone'}${at ? ` at ${at}` : ''}`}
                                          >
                                            Confirmed · {who}{at ? ` ${at}` : ''}
                                            {canUndo && (
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setPoArrivalConfirmed(po, false); }}
                                                title="Clear the confirmation"
                                                style={{ background: 'none', border: 'none', padding: 0, marginLeft: '2px', color: 'inherit', fontSize: '11px', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                                              >
                                                undo
                                              </button>
                                            )}
                                          </span>
                                        );
                                      }
                                      if (!etaKey || etaKey > todayKey || !hasModulePermission('po')) return null;
                                      return (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); setPoArrivalConfirmed(po, true); }}
                                          title="Mark this PO as definitely arriving today"
                                          style={{
                                            ...pillBase, cursor: 'pointer',
                                            color: darkMode ? '#86efac' : '#15803d', background: 'transparent',
                                            border: `1px dashed ${darkMode ? '#16a34a' : '#86d9a5'}`
                                          }}
                                        >
                                          Confirm for today
                                        </button>
                                      );
                                    })()}
                                  </div>
                                ))}

                              {/* Tracking links (2026-08-22) — paste carrier URLs on the card; agents POST the same route */}
                                <div
                                  style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '6px', minWidth: 0, overflow: 'hidden' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* Long pasted labels clip, never wrap (Rhys 2026-10-23); the group itself moves under ETA when the line is full. */}
                                  {/* Label only while empty — a carrier chip IS the tracking (Rhys 2026-08-24) */}
                                  {(po.localMetadata?.trackingLinks || []).length === 0 && (
                                    <span style={microLabelStyle}>Tracking</span>
                                  )}
                                  {(() => {
                                    // Two parcels with the same carrier looked like duplicate
                                    // chips — number them ("① ②") when a carrier repeats
                                    // (Rhys 2026-08-24). Numbers follow the order added.
                                    const trackingLinks = po.localMetadata?.trackingLinks || [];
                                    const carrierTotals = {};
                                    trackingLinks.forEach((l) => {
                                      const k = carrierForLink(l) || l.label || 'link';
                                      carrierTotals[k] = (carrierTotals[k] || 0) + 1;
                                    });
                                    const carrierSeen = {};
                                    return trackingLinks.map((link) => {
                                    const linkCarrier = carrierForLink(link);
                                    const carrierKey = linkCarrier || link.label || 'link';
                                    carrierSeen[carrierKey] = (carrierSeen[carrierKey] || 0) + 1;
                                    const parcelNo = carrierTotals[carrierKey] > 1 ? carrierSeen[carrierKey] : null;
                                    let display = linkCarrier || link.label;
                                    if (!display) {
                                      try { display = new URL(link.url).hostname.replace(/^www\./, ''); } catch (_) { display = 'link'; }
                                    }
                                    // Known carrier: the brand mark alone, larger — name text was redundant.
                                    const markOnly = hasCarrierMark(linkCarrier);
                                    return (
                                      <span
                                        key={link.url}
                                        style={{
                                          ...pillBase,
                                          padding: markOnly ? '2px 6px' : pillBase.padding,
                                          color: darkMode ? '#93c5fd' : '#1d4ed8',
                                          background: darkMode ? 'rgba(29,78,216,0.18)' : '#eff6ff',
                                          border: `1px solid ${darkMode ? '#3b82f6' : '#bfdbfe'}`
                                        }}
                                      >
                                        <a
                                          href={link.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={`${(markOnly && linkCarrier === 'TNT') ? 'TNT (FedEx)' : display}${parcelNo ? ` parcel ${parcelNo}` : ''} — ${link.url}`}
                                          style={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                        >
                                          {markOnly ? (
                                            <CarrierMark carrier={linkCarrier} size="lg" />
                                          ) : (
                                            <>
                                              <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 12l8-8M11 3h6v6"/><path d="M16 12v5a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1h5"/></svg>
                                              <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
                                            </>
                                          )}
                                          {parcelNo && (
                                            <span style={{ fontSize: '11px', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                              {parcelNo}
                                            </span>
                                          )}
                                        </a>
                                        <span
                                          role="button"
                                          tabIndex={0}
                                          title="Remove tracking link"
                                          onClick={() => removeTrackingLinkFromPo(po, link.url)}
                                          onKeyDown={(e) => { if (e.key === 'Enter') removeTrackingLinkFromPo(po, link.url); }}
                                          style={{ cursor: 'pointer', opacity: 0.6, fontWeight: 700, lineHeight: 1 }}
                                        >×</span>
                                      </span>
                                    );
                                  });
                                  })()}
                                  {trackingInputPoId === po.id ? (
                                    <input
                                      type="text"
                                      autoFocus
                                      placeholder="Paste tracking URL…"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveTrackingLink(po, e.currentTarget.value);
                                        if (e.key === 'Escape') setTrackingInputPoId(null);
                                      }}
                                      onBlur={() => setTrackingInputPoId(null)}
                                      onPaste={(e) => {
                                        // A paste is almost always the whole action — save it straight away.
                                        const pasted = (e.clipboardData?.getData('text') || '').trim();
                                        if (pasted) {
                                          e.preventDefault();
                                          saveTrackingLink(po, pasted);
                                        }
                                      }}
                                      style={{
                                        fontSize: '12px', padding: '2px 10px', borderRadius: '999px', lineHeight: '18px',
                                        border: `1px solid ${darkMode ? '#6b7280' : '#d1d5db'}`,
                                        background: darkMode ? '#1f2937' : '#fff',
                                        color: darkMode ? '#e5e7eb' : '#111',
                                        width: '210px', outline: 'none'
                                      }}
                                    />
                                  ) : (() => {
                                    // Multi-parcel POs carry several tracking numbers (SI-7916,
                                    // 2026-08-24) — adding stays available after the first link:
                                    // the full "+ Add" chip while empty, a small round + beside
                                    // the chips once links exist.
                                    const hasLinks = (po.localMetadata?.trackingLinks || []).length > 0;
                                    const openAdd = async () => {
                                      // One-click paste: if the clipboard already holds a link,
                                      // save it straight away; otherwise open the manual input.
                                      // (Clipboard read may prompt once per origin, and throws
                                      // when denied — both fall back to the input.)
                                      try {
                                        const clip = String((await navigator.clipboard.readText()) || '').trim();
                                        if (clip && !/\s/.test(clip) && clip.includes('.') && clip.length > 8) {
                                          saveTrackingLink(po, clip);
                                          return;
                                        }
                                      } catch (_) { /* permission denied / unsupported */ }
                                      setTrackingInputPoId(po.id);
                                    };
                                    return (
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        title="Add a tracking link (paste-aware)"
                                        onClick={openAdd}
                                        onKeyDown={(e) => { if (e.key === 'Enter') setTrackingInputPoId(po.id); }}
                                        style={hasLinks ? {
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          width: '20px', height: '20px', borderRadius: '50%',
                                          fontSize: '13px', fontWeight: 600, lineHeight: 1,
                                          cursor: 'pointer', flexShrink: 0,
                                          color: 'var(--app-ink-muted)',
                                          background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9fafb',
                                          border: `1px dashed ${darkMode ? '#6b7280' : '#d1d5db'}`
                                        } : {
                                          ...pillBase,
                                          gap: '4px', fontWeight: 500, cursor: 'pointer',
                                          color: 'var(--app-ink-muted)',
                                          background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9fafb',
                                          border: `1px dashed ${darkMode ? '#6b7280' : '#d1d5db'}`
                                        }}
                                      >{hasLinks ? '+' : '+ Add'}</span>
                                    );
                                  })()}
                                </div>
                                </div>
                              )}
                                  </div>
                                </div>

                                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                                  {/* Brand store pill — same drawn pill as the Products card
                                      (StorePill in ProductCard.js): storefront colours, so the
                                      linked modules read as one system. */}
                                  {effectiveTab !== 'archived' && (
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center',
                                      padding: '3px 9px', borderRadius: '7px',
                                      fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em',
                                      textTransform: 'uppercase', whiteSpace: 'nowrap', lineHeight: 1.2,
                                      background: po.storeKey === 'phaser' ? 'var(--brand-phaser, #e8192e)' : (po.storeKey === 'ccc' ? '#b45309' : 'var(--app-surface-3)'),
                                      color: '#ffffff'
                                    }}>
                                      {po.storeKey === 'phaser' ? 'Phaser' : (po.storeKey === 'ccc' ? 'CCC' : 'Store')}
                                    </span>
                                  )}
                                  {/* Always-visible preorder summary so low-qty preorder SKUs below
                                      the top-5 fold can't be missed. Amber, not red — preorder is
                                      a handling note, not an error (Rhys 2026-08-29). */}
                                  {(() => {
                                    const preorderLineCount = (po.items || []).filter(i => preorderSkusSet.has(preorderBadgeKey(po, i?.sku))).length;
                                    if (preorderLineCount === 0) return null;
                                    return (
                                      <span
                                        title="Items on this PO with active customer preorder demand"
                                        style={{
                                          ...statusPillBase,
                                          fontWeight: 700,
                                          color: darkMode ? '#fcd34d' : '#92400e',
                                          background: darkMode ? 'rgba(245, 158, 11, 0.16)' : '#fef3c7',
                                          border: `1px solid ${darkMode ? '#b45309' : '#fcd34d'}`
                                        }}
                                      >
                                        {`⚑ ${preorderLineCount} preorder item${preorderLineCount === 1 ? '' : 's'}`}
                                      </span>
                                    );
                                  })()}
                                  {showArrivedAge && (
                                    <span
                                      title={`${arrivalStamp.estimated ? 'Estimated from the ETA (no arrival stamp yet): ' : 'Moved to Arrived '}${new Date(arrivedStampIso).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })} — green <24h, orange 24–48h, red >48h unprocessed`}
                                      style={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        padding: '1px 9px',
                                        borderRadius: '999px',
                                        whiteSpace: 'nowrap',
                                        color: '#fff',
                                        background: arrivedAgeColor
                                      }}
                                    >
                                      {arrivedAgeLabel}
                                    </span>
                                  )}
                                  {/* One subdued meta line, same position on every card: the icons
                                      carry the meaning so the values can stand unlabelled. */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', fontWeight: 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }} title="Date of order/invoice">
                                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.8 }}>
                                        <rect x="3" y="4" width="14" height="14" rx="2" />
                                        <line x1="3" y1="8" x2="17" y2="8" />
                                        <line x1="7" y1="2" x2="7" y2="5" />
                                        <line x1="13" y1="2" x2="13" y2="5" />
                                      </svg>
                                      {new Date(po.created_date).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }} title="Lines on this PO">
                                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.8 }}>
                                        <path d="M3 6l7-4 7 4v8l-7 4-7-4V6z" />
                                        <path d="M3 6l7 4 7-4" />
                                        <line x1="10" y1="10" x2="10" y2="18" />
                                      </svg>
                                      {(() => {
                                        // Count PO lines and extras separately — an extra was never ordered,
                                        // so folding it into "N Items" would misreport the PO.
                                        const orderedCount = po.items.filter(item => !item.isExtraItem).length;
                                        const extraCount = po.items.length - orderedCount;
                                        return (
                                          <>
                                            {orderedCount} {orderedCount === 1 ? 'Item' : 'Items'}
                                            {extraCount > 0 && ` + ${extraCount} extra`}
                                          </>
                                        );
                                      })()}
                                    </span>
                                  </div>
                                </div>
                              </div>



                              {/* Item List. flex:1 is deliberate: it pins the Receiving
                                  Workflow block to a consistent position near the card
                                  bottom, aligned across the row — Rhys prefers the
                                  workflow in the same spot on every card over reclaiming
                                  the slack (2026-08-29). */}
                              <div style={{ flex: 1, marginBottom: '6px', borderTop: `1px solid ${cardHairline}`, paddingTop: '6px' }}>
                                {expandedPOs.has(po.id) ? (
                                  // Expanded View: ALL items at full height. No inner scroll —
                                  // the card grows instead, so staff see every line at once
                                  // (Rhys 2026-10-22).
                                  <div className="po-item-list">
                                    {[...po.items]
                                      // Extras (not on the PO) always sit at the bottom of the list.
                                      .sort((a, b) => (
                                        (a.isExtraItem === true) === (b.isExtraItem === true)
                                          ? getPoLineArrivedQuantity(b) - getPoLineArrivedQuantity(a)
                                          : (a.isExtraItem ? 1 : -1)
                                      ))
                                      .map((item, idx) => (
                                        <div
                                          key={idx}
                                          className="po-item-row"
                                        >
                                          <PoLineQuantityCell item={item} />
                                          <div className="po-item-sku">
                                            <Badge tone={item.isExtraItem ? 'attention' : 'info'} size="small">{item.sku}</Badge>
                                            {item.isExtraItem && (
                                              <span
                                                title="Arrived in this shipment but was not on the PO"
                                                style={{
                                                  fontSize: '10px',
                                                  fontWeight: 600,
                                                  letterSpacing: '0.3px',
                                                  padding: '1px 7px',
                                                  borderRadius: '999px',
                                                  color: darkMode ? '#c5cae9' : '#3949ab',
                                                  background: darkMode ? 'rgba(57, 73, 171, 0.22)' : '#e8eaf6',
                                                  border: `1px solid ${darkMode ? '#3949ab' : '#c5cae9'}`
                                                }}
                                              >
                                                EXTRA
                                              </span>
                                            )}
                                          </div>
                                          <div className="po-item-content">
                                            {/* No truncate: a cut-off product name is useless to
                                                receiving staff — long names wrap to a second line
                                                instead (Rhys 2026-08-29). */}
                                            <Text variant="bodySm">
                                              {resolvePoItemDisplayTitle(item)}
                                            </Text>
                                            {preorderSkusSet.has(preorderBadgeKey(po, item.sku)) && (
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const poId = po.id != null ? String(po.id) : null;
                                                  setPreorderFocusSku(null);
                                                  setPreorderSourcePoId(poId);
                                                  setModuleView('preorders');
                                                  window.history.pushState({}, '', `/preorders?relatedPo=${encodeURIComponent(poId)}`);
                                                }}
                                                title="View customer preorders matching items on this PO"
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  padding: '0 7px',
                                                  fontSize: '10px',
                                                  fontWeight: 700,
                                                  letterSpacing: '0.03em',
                                                  lineHeight: '15px',
                                                  color: darkMode ? '#fcd34d' : '#92400e',
                                                  background: darkMode ? 'rgba(245, 158, 11, 0.16)' : '#fef3c7',
                                                  border: `1px solid ${darkMode ? '#b45309' : '#fcd34d'}`,
                                                  borderRadius: '999px',
                                                  cursor: 'pointer',
                                                  flexShrink: 0,
                                                  whiteSpace: 'nowrap'
                                                }}
                                              >
                                                Preorder
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    <div
                                      style={{ textAlign: 'center', padding: '8px 0', fontSize: '11px', color: '#5C6AC4', cursor: 'pointer', fontWeight: '500' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        togglePOExpansion(po.id);
                                      }}
                                    >
                                      Show less
                                    </div>
                                  </div>
                                ) : (
                                  (() => {
                                    // Collapsed preview budgeted by estimated LINES, not a flat
                                    // item cap (Rhys 2026-08-29): short titles fit more items in
                                    // the same height, long wrapped ones fewer, so card heights
                                    // stay level and another row of cards fits on screen. The
                                    // chars-per-line guess is deliberately rough — it bounds
                                    // height, it does not measure it. Extras stay OUTSIDE the
                                    // budget: staff must always see them to label them.
                                    const PREVIEW_LINE_BUDGET = 6;
                                    const PREVIEW_CHARS_PER_LINE = 60;
                                    const orderedLines = po.items
                                      .filter(item => !item.isExtraItem)
                                      // Lines the supplier got wrong sort to the top with the
                                      // big ones — a missing line must not hide behind "+N more".
                                      .sort((a, b) => (
                                        (poLineArrivalDiffersFromOrder(b) ? 1 : 0) - (poLineArrivalDiffersFromOrder(a) ? 1 : 0)
                                        || getPoLineArrivedQuantity(b) - getPoLineArrivedQuantity(a)
                                      ));
                                    const preview = [];
                                    let linesUsed = 0;
                                    for (const line of orderedLines) {
                                      const estLines = Math.max(1, Math.ceil(resolvePoItemDisplayTitle(line).length / PREVIEW_CHARS_PER_LINE));
                                      // Always show at least two items so a card never reads empty.
                                      if (preview.length >= 2 && linesUsed + estLines > PREVIEW_LINE_BUDGET) break;
                                      preview.push(line);
                                      linesUsed += estLines;
                                    }
                                    const hiddenCount = orderedLines.length - preview.length;
                                    return (<div className="po-item-list">
                                    {[
                                      ...preview,
                                      ...po.items.filter(item => item.isExtraItem)
                                    ]
                                      .map((item, idx) => (
                                        <div
                                          key={idx}
                                          className="po-item-row"
                                        >
                                          <PoLineQuantityCell item={item} />
                                          <div className="po-item-sku">
                                            <Badge tone={item.isExtraItem ? 'attention' : 'info'} size="small">{item.sku}</Badge>
                                            {item.isExtraItem && (
                                              <span
                                                title="Arrived in this shipment but was not on the PO"
                                                style={{
                                                  fontSize: '10px',
                                                  fontWeight: 600,
                                                  letterSpacing: '0.3px',
                                                  padding: '1px 7px',
                                                  borderRadius: '999px',
                                                  color: darkMode ? '#c5cae9' : '#3949ab',
                                                  background: darkMode ? 'rgba(57, 73, 171, 0.22)' : '#e8eaf6',
                                                  border: `1px solid ${darkMode ? '#3949ab' : '#c5cae9'}`
                                                }}
                                              >
                                                EXTRA
                                              </span>
                                            )}
                                          </div>
                                          <div className="po-item-content">
                                            {/* No truncate: a cut-off product name is useless to
                                                receiving staff — long names wrap to a second line
                                                instead (Rhys 2026-08-29). */}
                                            <Text variant="bodySm">
                                              {resolvePoItemDisplayTitle(item)}
                                            </Text>
                                            {preorderSkusSet.has(preorderBadgeKey(po, item.sku)) && (
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const poId = po.id != null ? String(po.id) : null;
                                                  setPreorderFocusSku(null);
                                                  setPreorderSourcePoId(poId);
                                                  setModuleView('preorders');
                                                  window.history.pushState({}, '', `/preorders?relatedPo=${encodeURIComponent(poId)}`);
                                                }}
                                                title="View customer preorders matching items on this PO"
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  padding: '0 7px',
                                                  fontSize: '10px',
                                                  fontWeight: 700,
                                                  letterSpacing: '0.03em',
                                                  lineHeight: '15px',
                                                  color: darkMode ? '#fcd34d' : '#92400e',
                                                  background: darkMode ? 'rgba(245, 158, 11, 0.16)' : '#fef3c7',
                                                  border: `1px solid ${darkMode ? '#b45309' : '#fcd34d'}`,
                                                  borderRadius: '999px',
                                                  cursor: 'pointer',
                                                  flexShrink: 0,
                                                  whiteSpace: 'nowrap'
                                                }}
                                              >
                                                Preorder
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    {hiddenCount > 0 && (
                                      <div
                                        style={{ textAlign: 'center', padding: '4px 0', fontSize: '11px', color: '#5C6AC4', cursor: 'pointer', fontWeight: '500' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          togglePOExpansion(po.id);
                                        }}
                                      >
                                        + {hiddenCount} more item{hiddenCount === 1 ? '' : 's'}...
                                      </div>
                                    )}
                                    </div>);
                                  })()
                                )}
                              </div>

                              {/* Task Checklist - shown in Arrived and Website Team tabs */}
                              {(effectiveTab === 'arrived' || effectiveTab === 'website_team') && (
                                <div style={{
                                  marginTop: '6px',
                                  marginBottom: '4px',
                                  padding: '8px 10px',
                                  background: darkMode ? '#1e1e1e' : '#fafafa',
                                  borderRadius: '8px',
                                  border: `1px solid ${cardHairline}`
                                }} onClick={(e) => e.stopPropagation()}>
                                  <POTaskChecklist
                                    po={po}
                                    tasks={po.localMetadata?.tasks || {}}
                                    onTaskUpdate={handleTaskUpdate}
                                    onTaskAction={handlePOTaskAction}
                                    onOpenReceiptModal={(selectedPo) => setSelectedPOForReceipt(selectedPo)}
                                    currentUser={currentUser}
                                    compact={true}
                                    darkMode={darkMode}
                                    taskDefinitions={effectiveTab === 'website_team' ? WEBSITE_TEAM_TASK_DEFINITIONS : undefined}
                                    taskDefinitionOverrides={effectiveTab === 'arrived' ? buildHighQuantityPoLabelTaskOverride(po) : undefined}
                                    workflowTitle={effectiveTab === 'website_team' ? 'Website Team Workflow' : 'Receiving Workflow'}
                                    fullModeTitle={effectiveTab === 'website_team' ? 'Website Team Checklist' : 'Receiving Checklist'}
                                    completionMessage={effectiveTab === 'website_team' ? 'Website team tasks complete! This PO is ready to archive.' : 'All tasks complete! This PO is ready for the next step.'}
                                  />
                                </div>
                              )}

                              {/* Actions Footer — one row, two groups: quiet utilities on the
                                  left (reverse a stage, Split), the working actions on the
                                  right (Load in Grid beside the primary forward button).
                                  Every control sits on the same 28px height. */}
                              <div style={{
                                marginTop: 'auto',
                                paddingTop: '8px',
                                borderTop: `1px solid ${cardHairline}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px'
                              }}>

                                {/* Left: low-priority utilities */}
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                  {effectiveTab === 'arrived' && (
                                    <button
                                      style={poReverseButtonStyle}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        confirmAndUpdatePOStatus(po, 'waiting_arrival', `Move ${po.reference || po.id} back to Waiting Arrival? This reverses its current workflow stage.`);
                                      }}
                                    >
                                      ‹ Waiting
                                    </button>
                                  )}
                                  {effectiveTab === 'website_team' && (
                                    <button
                                      style={poReverseButtonStyle}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        confirmAndUpdatePOStatus(po, 'arrived', `Move ${po.reference || po.id} back to Arrived? This returns it to the receiving workflow.`);
                                      }}
                                    >
                                      ‹ Arrived
                                    </button>
                                  )}
                                  {effectiveTab === 'waiting_arrival' && (
                                    <button
                                      style={poReverseButtonStyle}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        confirmAndUpdatePOStatus(po, 'draft', `Move ${po.reference || po.id} back to Drafts? This reverses its current workflow stage.`);
                                      }}
                                    >
                                      ‹ Drafts
                                    </button>
                                  )}
                                  {effectiveTab === 'archived' && (
                                    <button
                                      style={poReverseButtonStyle}
                                      onClick={(e) => { e.stopPropagation(); confirmAndUpdatePOStatus(po, 'arrived', `Restore ${po.reference || po.id} from Archived back to Arrived?`); }}
                                    >
                                      ‹ Restore
                                    </button>
                                  )}
                                  {/* Pre-arrival split: supplier back-ordered some lines — move them
                                      to a new -PARTx part with its own ETA before receiving */}
                                  {/* Arrived too (2026-09-29): split off the lines that did NOT
                                      land — the new part goes back to Waiting Arrival, this card
                                      keeps its arrival stamp + steps. Hidden once a receipt
                                      record exists (the backend refuses those). */}
                                  {(effectiveTab === 'waiting_arrival'
                                    || (effectiveTab === 'arrived'
                                      && !po.localMetadata?.receivedAt
                                      && !(po.localMetadata?.receivedItems || []).length)) && canSplitPos && (
                                    <Button size="slim" onClick={(e) => { e.stopPropagation(); setSelectedPOForPreArrivalSplit(po); }}>Split</Button>
                                  )}
                                </div>

                                {/* Right: Load in Grid + the primary forward action */}
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0 }}>
                                  {effectiveTab !== 'draft' && (
                                    <Button size="slim" onClick={(e) => { e.stopPropagation(); handlePOGridLoad(po); }}>Load in Grid</Button>
                                  )}
                                  {effectiveTab === 'draft' && (() => {
                                    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                                    const isAuthorized = isDev || (currentUser && ['rhys100@gmail.com', 'rhys@phaserfpv.com.au'].includes(currentUser.email));
                                    const buttonBlock = (
                                      <button
                                        style={poForwardButtonStyle(isAuthorized)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isAuthorized) handleUpdatePOStatus(po, 'waiting_arrival');
                                        }}
                                      >
                                        Waiting Arrival ›
                                      </button>
                                    );

                                    if (isAuthorized) return buttonBlock;

                                    return (
                                      <Tooltip content="Only managers can move POs from Drafts">
                                        <div style={{ display: 'inline-block' }}>{buttonBlock}</div>
                                      </Tooltip>
                                    );
                                  })()}
                                  {effectiveTab === 'waiting_arrival' && (
                                    <button
                                      style={poForwardButtonStyle(true)}
                                      onClick={(e) => { e.stopPropagation(); handleUpdatePOStatus(po, 'arrived'); }}
                                    >
                                      Arrived ›
                                    </button>
                                  )}
                                  {effectiveTab === 'arrived' && (() => {
                                    const tasks = po.localMetadata?.tasks || {};
                                    const allTasksComplete = ARRIVED_WORKFLOW_TASK_KEYS.every(key => tasks[key]?.completed === true);
                                    const stepsLeft = ARRIVED_WORKFLOW_TASK_KEYS.filter(key => tasks[key]?.completed !== true).length;

                                    return (
                                      // Inline, not a stacked column: the hint under the button
                                      // pushed it off the Load in Grid baseline (Rhys 2026-08-29).
                                      <>
                                      {!allTasksComplete && (
                                        <span style={{ fontSize: '11px', color: darkMode ? '#9ca3af' : '#6b7280', whiteSpace: 'nowrap' }}>
                                          🔒 {stepsLeft} step{stepsLeft === 1 ? '' : 's'} left
                                        </span>
                                      )}
                                      <button
                                        style={{
                                          ...poForwardButtonStyle(allTasksComplete),
                                          border: allTasksComplete ? '2px solid #14b8a6' : 'none',
                                          boxShadow: allTasksComplete ? '0 0 8px rgba(13, 148, 136, 0.55)' : 'none',
                                          animation: allTasksComplete ? 'po-complete-pulse 2s infinite' : 'none'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (allTasksComplete) handleUpdatePOStatus(po, 'website_team');
                                        }}
                                        title={allTasksComplete ? 'Warehouse checks complete! Send this PO to Website Team' : `Complete all 7 warehouse tasks first — ${stepsLeft} receiving step${stepsLeft === 1 ? '' : 's'} left`}
                                      >
                                        Website Team ›
                                      </button>
                                      </>
                                    );
                                  })()}
                                  {effectiveTab === 'website_team' && (() => {
                                    const tasks = po.localMetadata?.tasks || {};
                                    const allKeys = [...ARRIVED_WORKFLOW_TASK_KEYS, ...WEBSITE_TEAM_TASK_KEYS];
                                    const allTasksComplete = allKeys.every(key => tasks[key]?.completed === true);
                                    const stepsLeft = allKeys.filter(key => tasks[key]?.completed !== true).length;

                                    return (
                                      // Inline hint + button on one baseline, same as the Arrived tab.
                                      <>
                                      {!allTasksComplete && (
                                        <span style={{ fontSize: '11px', color: darkMode ? '#9ca3af' : '#6b7280', whiteSpace: 'nowrap' }}>
                                          🔒 {stepsLeft} step{stepsLeft === 1 ? '' : 's'} left
                                        </span>
                                      )}
                                      <button
                                        style={{
                                          ...poForwardButtonStyle(allTasksComplete),
                                          border: allTasksComplete ? '2px solid #14b8a6' : 'none',
                                          boxShadow: allTasksComplete ? '0 0 8px rgba(13, 148, 136, 0.55)' : 'none',
                                          animation: allTasksComplete ? 'po-complete-pulse 2s infinite' : 'none'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (allTasksComplete) confirmAndUpdatePOStatus(po, 'archived', `Archive ${po.reference || po.id}? It will move to the Archived tab.`);
                                        }}
                                        title={allTasksComplete ? '✅ All tasks complete! Click to archive this PO' : 'Complete the Website Team checklist first'}
                                      >
                                        {allTasksComplete ? '✅ ' : ''}Archive
                                      </button>
                                      </>
                                    );
                                  })()}
                                </div>

                              </div>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                </div>
              </BlockStack>
            )}
            </div>
          </div>
        )}



        {showProductTagsModal && (
          <Suspense fallback={null}>
            <ProductTagsModal
              open={showProductTagsModal}
              darkMode={darkMode}
            />
          </Suspense>
        )}

        {(showPOPlanningModal || everOpenedPOPlanningRef.current) && (
          <Suspense fallback={
            showPOPlanningModal ? <div style={{ position: 'fixed', inset: 0, background: 'var(--bg, #f6f6f7)', zIndex: 1000 }} /> : null
          }>
            <POPlanningModal
              open={showPOPlanningModal}
              onClose={navigateToHome}
              darkMode={darkMode}
              currentUser={currentUser}
              initialPlannerChannel={plannerChannel}
            />
          </Suspense>
        )}

        {(showPriceMoleModal || everOpenedPriceMoleRef.current) && (
          <Suspense fallback={
            showPriceMoleModal ? <div style={{ position: 'fixed', inset: 0, background: 'var(--bg, #f6f6f7)', zIndex: 1000 }} /> : null
          }>
            <PriceMoleModal
              open={showPriceMoleModal}
              onClose={navigateToHome}
              darkMode={darkMode}
              currentUser={currentUser}
              channel={pricemoleChannel}
              focusSku={pricemoleFocusSku}
              onChannelChange={handlePricemoleChannelChange}
              onReviewCountChange={fetchPricemoleReviewCount}
            />
          </Suspense>
        )}

        {showCoffeeStocktakeModal && (
          <Suspense fallback={
            <div style={{ position: 'fixed', inset: 0, background: 'var(--bg, #f6f6f7)', zIndex: 1000 }} />
          }>
            <CoffeeStocktakeModal
              open={showCoffeeStocktakeModal}
              onClose={navigateToHome}
              darkMode={darkMode}
              currentUser={currentUser}
            />
          </Suspense>
        )}

        {(showPreordersModal || everOpenedPreordersRef.current) && (
          <Suspense fallback={
            showPreordersModal ? <div style={{ position: 'fixed', inset: 0, background: 'var(--bg, #f6f6f7)', zIndex: 1000 }} /> : null
          }>
            <PreordersModal
              key={preordersNavigationKey}
              open={showPreordersModal}
              onClose={() => { setPreorderFocusSku(null); setPreorderSourcePoId(null); navigateToHome(); }}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              currentUser={currentUser}
              canManageEtaDates={canManageEtaDates}
              initialFocusSku={preorderFocusSku}
              sourcePoId={preorderSourcePoId}
              onClearFocusSku={() => setPreorderFocusSku(null)}
              onEtaSyncedToPO={() => fetchPurchaseOrders(true)}
              onOpenPlannerForSupplier={hasModulePermission('poPlanning') ? ({ supplierName, store }) => {
                const channelKey = store === 'central-coast-coffee' ? 'centralcoastcoffee' : 'phaserfpv';
                setPlannerChannel(channelKey);
                setModuleView('po-planning');
                const params = new URLSearchParams();
                params.set('tab', 'drafts');
                if (supplierName) params.set('supplier', supplierName);
                window.history.pushState({}, '', `${plannerChannelPath(channelKey)}?${params.toString()}`);
              } : null}
              onNavigateToPO={(poId, status, poReference) => {
                const mappedStatus = status === 'awaiting arrival' ? 'waiting_arrival' : (status === 'print_labels' ? 'arrived' : status);
                const statusTab = PURCHASE_ORDER_TABS.includes(mappedStatus) ? mappedStatus : 'all';
                setPoStatusTab(statusTab);
                setPoPage(1);
                setPoNavigateTarget({ poId, status: statusTab });
                setPoStoreFilter('all');
                setPoFilter(poReference || String(poId));
                setPoFilterQuery(poReference || String(poId));
                setModuleView('po');
                window.history.pushState({}, '', incomingStockPath(statusTab));
              }}
              onNavigateToJobs={navigateToJobs}
              onNavigateToPOs={navigateToPOs}
              onNavigateToPreorders={navigateToPreorders}
              onNavigateToRAs={navigateToRAs}
              onNavigateToNewProducts={navigateToNewProducts}
              preorderUnreadCount={preorderUnreadCount}
              onPreorderUnreadCountRefetch={() => fetchPreorderUnreadCount(true)}
              jobsUnreadCount={jobsUnreadCount}
            />
          </Suspense>
        )}

        {(showJobsHub || everOpenedJobsRef.current) && (
          <Suspense fallback={
            showJobsHub ? <div style={{ position: 'fixed', top: 'var(--app-header-offset, 56px)', left: 0, right: 0, bottom: 0, background: 'var(--bg, #f6f6f7)', zIndex: 1000 }} /> : null
          }>
            <JobsHubModal
              open={showJobsHub}
              onClose={() => {
                setJobsHubOpenToTab(null);
                navigateToHome();
              }}
              initialTab={showJobsHub ? jobsHubOpenToTab : null}
              onActiveTabChange={setJobsHubActiveTab}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              currentUser={currentUser}
              navigateToJobs={navigateToJobs}
              navigateToPOs={navigateToPOs}
              navigateToPreorders={navigateToPreorders}
              navigateToPicking={navigateToPicking}
              navigateToRAs={navigateToRAs}
              navigateToNewProducts={navigateToNewProducts}
              activeModule={moduleView === 'ra' ? 'ra' : 'jobs'}
              preorderUnreadCount={preorderUnreadCount}
              onAssignedCountsChanged={() => fetchJobsAssignedCounts(true)}
            />
          </Suspense>
        )}

        {/*
          The boundary sits OUTSIDE Suspense: a lazy chunk that 404s mid-deploy
          rejects the import and throws through Suspense, and without this the
          whole staff app drops to the generic "Something went wrong" page with
          a manual Refresh button — on a tablet held by a picker mid-batch.
          The fallback says something, too: it used to be an empty full-screen
          rectangle, which is indistinguishable from the app having died when
          the chunk request hangs on warehouse Wi-Fi.
        */}
        {(showPickingModal || everOpenedPickingRef.current) && !showMobileNoPickingAccess && (
          <PickingErrorBoundary>
          <Suspense fallback={
            showPickingModal ? (
              <div style={{
                position: 'fixed', inset: 0, background: 'var(--bg, #f6f6f7)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: 'var(--app-ink-secondary, #6d7175)'
              }}
              >
                Loading picking…
              </div>
            ) : null
          }>
            <PickingModal
              open={showPickingModal}
              onClose={isPickingTouch ? undefined : navigateToHome}
              mobilePickingOnly={isPickingTouch}
              tabletPicking={isTabletPicking}
              darkMode={darkMode}
              showInternalWarnings={showPickingInternalWarnings}
              onOpenStocktake={hasModulePermission('stocktake') ? navigateToStocktake : undefined}
            />
          </Suspense>
          </PickingErrorBoundary>
        )}

        {showStocktake && (
          <PickingErrorBoundary>
          <Suspense fallback={
            <div style={{
              position: 'fixed', inset: 0, background: 'var(--app-bg, #f6f6f7)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, color: 'var(--app-ink-secondary, #6d7175)'
            }}
            >
              Loading stocktake…
            </div>
          }>
            <StocktakeView
              open={showStocktake}
              onClose={navigateToPicking}
              initialLocation={stocktakeLocation}
              darkMode={darkMode}
            />
          </Suspense>
          </PickingErrorBoundary>
        )}

        {/* PWA install prompt — mobile/tablet picking in browser tab only (hidden when standalone) */}
        {isPickingTouch && showPickingModal && !showMobileNoPickingAccess && (
          <PickingInstallBanner darkMode={darkMode} />
        )}

        {(showNewProductsModal || everOpenedNewProductsRef.current) && (
          <Suspense fallback={
            showNewProductsModal ? <div style={{ position: 'fixed', inset: 0, background: 'var(--bg, #f6f6f7)', zIndex: 1000 }} /> : null
          }>
            <NewProductsModal
              open={showNewProductsModal}
              onClose={navigateToHome}
              darkMode={darkMode}
            />
          </Suspense>
        )}

        {/* Settings Modal */}
        <MountAfterFirstOpen open={showSettingsModal}>
          <SettingsModal
            open={showSettingsModal}
            onClose={() => setShowSettingsModal(false)}
            darkMode={darkMode}
            canManageSettings={canManageSettings}
            canViewLogs={hasModulePermission('logs')}
            onOpenLogs={() => {
              setShowSettingsModal(false);
              openLogViewer();
            }}
          />
        </MountAfterFirstOpen>

        <MountAfterFirstOpen open={showMyAccessModal}>
          <UserPermissionsModal
            open={showMyAccessModal}
            onClose={() => setShowMyAccessModal(false)}
            permissions={permissionSettings}
            loading={!permissionSettingsLoaded}
            onRetry={fetchPermissionSettings}
          />
        </MountAfterFirstOpen>

        <MountAfterFirstOpen open={showAdminApiModal}>
          <AdminApiEndpointsModal
            open={showAdminApiModal}
            onClose={() => setShowAdminApiModal(false)}
          />
        </MountAfterFirstOpen>

        <MountAfterFirstOpen open={showSecurityIncidentsModal}>
          <SecurityIncidentsModal
            open={showSecurityIncidentsModal}
            onClose={() => setShowSecurityIncidentsModal(false)}
            onAcknowledged={refreshSecurityIncidentSummary}
          />
        </MountAfterFirstOpen>

        {/* Log Viewer Modal */}
        < Modal
          open={showLogViewer}
          onClose={() => setShowLogViewer(false)
          }
          title="Activity Log"
          primaryAction={{
            content: 'Refresh',
            onAction: fetchLogs,
            loading: isLoadingLogs,
          }}
          secondaryActions={
            [
              {
                content: 'Clear Logs',
                destructive: true,
                onAction: clearLogs,
              },
            ]}
        >
          <style>{`
          /* Force full screen modal - same styling as PO modals */
          [data-polaris-layer] .Polaris-Modal-Dialog__Container {
            max-width: 100vw !important;
            width: 100vw !important;
            height: 100vh !important;
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            justify-content: flex-start !important;
            align-items: flex-start !important;
          }
          [data-polaris-layer] .Polaris-Modal-Dialog {
            max-width: 100vw !important;
            width: 100vw !important;
            height: 100vh !important;
            margin: 0 !important;
            border-radius: 0 !important;
            ${darkMode ? 'background-color: #1a1a1a !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Modal-Dialog__Modal {
            height: 100vh !important;
            max-height: 100vh !important;
            width: 100vw !important;
            max-width: 100vw !important;
            border-radius: 0 !important;
            ${darkMode ? 'background-color: #1a1a1a !important; color: #e5e5e5 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Modal-Dialog__Body {
            max-height: calc(100vh - 80px) !important;
            overflow-y: auto !important;
            ${darkMode ? 'background-color: #1a1a1a !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Modal-Dialog__Header {
            padding: 1rem 1.5rem !important;
            ${darkMode ? 'background-color: #1a1a1a !important; border-bottom: 1px solid #333 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Modal-Header__Title {
            ${darkMode ? 'color: #e5e5e5 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Modal-CloseButton {
            ${darkMode ? 'color: #e5e5e5 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Button {
            ${darkMode ? 'background-color: #2a2a2a !important; color: #e5e5e5 !important; border-color: #444 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Button--primary {
            ${darkMode ? 'background-color: #5C6AC4 !important; color: white !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Card {
            ${darkMode ? 'background-color: #2a2a2a !important; border-color: #333 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Text--root {
            ${darkMode ? 'color: #e5e5e5 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-Text--subdued {
            ${darkMode ? 'color: #999 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-DataTable {
            ${darkMode ? 'background-color: #2a2a2a !important; color: #e5e5e5 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-DataTable__Cell {
            ${darkMode ? 'border-color: #333 !important; color: #e5e5e5 !important;' : ''}
          }
          [data-polaris-layer] .Polaris-DataTable__Heading {
            ${darkMode ? 'background-color: #222 !important; color: #e5e5e5 !important; border-color: #333 !important;' : ''}
          }
          /* Also target by class in case data attribute isn't present */
          .Polaris-Backdrop {
            background: rgba(0, 0, 0, 0.8) !important;
          }
          .Polaris-DataTable__ScrollContainer {
            overflow-x: auto !important;
          }
        `}</style>
          <Modal.Section>
            {isLoadingLogs ? (
              <Box padding="400">
                <Text alignment="center" tone="subdued">
                  Loading logs...
                </Text>
              </Box>
            ) : logs.length === 0 ? (
              <Box padding="400">
                <Text alignment="center" tone="subdued">
                  No activity logged yet
                </Text>
              </Box>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={['Time', 'Type', 'Message', 'Details']}
                rows={logs.map(log => [
                  new Date(log.timestamp).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' }),
                  <Badge tone={
                    log.type === 'search' ? 'info' :
                      log.type === 'update' ? 'success' :
                        log.type === 'error' ? 'critical' : 'default'
                  }>
                    {log.type}
                  </Badge>,
                  log.message,
                  log.details ? (
                    <details>
                      <summary style={{ cursor: 'pointer' }}>View</summary>
                      <pre style={{ fontSize: '11px', marginTop: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </details>
                  ) : '-'
                ])}
              />
            )}
          </Modal.Section>
        </Modal >

        {/* Refresh Database Modal */}
        < Modal
          open={showRefreshModal}
          onClose={() => setShowRefreshModal(false)}
          title="Refresh database caches"
          primaryAction={{
            content: isRefreshingCache ? 'Refresh running…' : 'Refresh Selected',
            onAction: handleRefreshStores,
            disabled: isRefreshingCache || !Object.values(selectedStores).some((v) => v),
          }}
          secondaryActions={
            [
              {
                content: 'Select All',
                onAction: () => setSelectedStores({ ...ALL_REFRESH_STORE_SELECTION }),
              },
              {
                content: 'Cancel',
                onAction: () => setShowRefreshModal(false),
              },
            ]}
        >
          <Modal.Section>
            <div className="RefreshDatabaseModal-root">
              <RefreshDatabasePicker
                darkMode={darkMode}
                refreshRun={refreshRun}
                selectedStores={selectedStores}
                setSelectedStores={setSelectedStores}
                hasModulePermission={hasModulePermission}
                cacheStatus={cacheStatus}
                refreshTargetStatuses={refreshTargetStatuses}
                isRefreshTargetInCurrentRun={isRefreshTargetInCurrentRun}
                plannerSyncJobs={plannerSyncJobs}
                plannerCostBackfillStatus={plannerCostBackfillStatus}
                plannerSupplierSkuSyncStatus={plannerSupplierSkuSyncStatus}
                activeRunStatusRows={activeRunStatusRows}
                getTargetRunStatusTone={getTargetRunStatusTone}
                getTargetRunStatusLabel={getTargetRunStatusLabel}
                moduleGroups={REFRESH_MODULE_GROUPS}
              />
            </div>
          </Modal.Section>
        </Modal >

        {/* Save Queue Status Bar */}
        < SaveQueueStatus saveQueue={saveQueue} darkMode={darkMode} />

        {/* AI Copilot — bottom-right chat, capability decided server-side.
            Hidden on the delivery run board, the pickup board, AND on
            touch-device digital picking: the launcher overlaps the controls
            staff tap all shift (Rhys 2026-08-27, pickup 2026-08-31).
            SHOWN on Ship (2026-09-25): packers teach it boxes and file
            bugs/ideas from the bench; the packaging step publishes its order
            as chat context (utils/aiChatContext.js).
            Hidden on Roast Stocktake (2026-10-02): it covered "Review changes". */}
        {!showLocalDeliveries && !showPickFromStore && !showStocktake && !showCoffeeStocktakeModal && !(showPickingModal && isPickingTouch) && (
          <Suspense fallback={null}>
          <AiChatWidget
            darkMode={darkMode}
            moduleView={moduleView}
            onNavigate={handleAiNavigate}
            ready={permissionSettingsLoaded}
          />
          </Suspense>
        )}



        <MountAfterFirstOpen open={showCacheModal}>
          <CacheStatusModal
            open={showCacheModal}
            onClose={() => setShowCacheModal(false)}
            darkMode={darkMode}
            cacheStatus={cacheStatus}
            isRefreshingCache={isRefreshingCache}
            activeRefreshTargetLabels={activeRefreshTargetLabels}
            onRefreshDatabase={refreshDatabase}
            hasModulePermission={hasModulePermission}
            refreshTargetStatuses={refreshTargetStatuses}
            isRefreshTargetInCurrentRun={isRefreshTargetInCurrentRun}
            plannerSyncJobs={plannerSyncJobs}
            plannerCostBackfillStatus={plannerCostBackfillStatus}
            plannerSupplierSkuSyncStatus={plannerSupplierSkuSyncStatus}
          />
        </MountAfterFirstOpen>

        {expiryPanel && (
          <Suspense fallback={null}>
            <ExpiryBatchesPanel
              open
              store={expiryPanel.store}
              sku={expiryPanel.sku}
              title={expiryPanel.title}
              canEdit={canTrackExpiry}
              onChanged={handleExpiryPanelChanged}
              onClose={() => setExpiryPanel(null)}
            />
          </Suspense>
        )}
        {kitSplitEditor && (
          <Suspense fallback={null}>
            <KitSplitModal
              product={kitSplitEditor.options?.mode === 'add-to-kit' ? null : kitSplitEditor.product}
              kit={kitSplitEditor.kit}
              mode={kitSplitEditor.options?.mode || 'edit'}
              addPart={kitSplitEditor.options?.mode === 'add-to-kit' ? kitSplitEditor.product : null}
              initialKind={kitSplitEditor.options?.kind || null}
              findKit={findKitForParent}
              capabilities={kitSplitCapabilities}
              canEdit={canEditKitSplit}
              onClose={() => setKitSplitEditor(null)}
              onSaved={handleKitSplitSaved}
              onDeleted={handleKitSplitDeleted}
            />
          </Suspense>
        )}
        {productLinkEditor && (
          <Suspense fallback={null}>
            <ProductLinksModal
              product={productLinkEditor.product}
              link={productLinkEditor.productLink?.link || null}
              complementedBy={productLinkEditor.productLink?.complementedBy || []}
              canEdit={canEditProductLinks}
              onClose={() => setProductLinkEditor(null)}
              onSaved={handleProductLinksSaved}
            />
          </Suspense>
        )}

        <MountAfterFirstOpen open={!!selectedPOForReceipt}>
          <POReceiptModal
            po={selectedPOForReceipt}
            open={!!selectedPOForReceipt}
            onClose={() => setSelectedPOForReceipt(null)}
            onConfirm={handleConfirmReceipt}
            onSplitCreated={(splitPO) => {
              console.log('[SPLIT] New split PO created:', splitPO);
              // Add the split PO to the local state so it shows up in the UI immediately
              setPurchaseOrders(prev => {
                // Check if it already exists (in case of race condition)
                if (prev.some(po => po.id === splitPO.id)) {
                  return prev;
                }
                // Create a local PO entry for the split
                const newSplitPO = {
                  id: splitPO.id,
                  reference: splitPO.reference,
                  vendor_display_name: splitPO.vendor,
                  supplier_name: splitPO.vendor,
                  storeKey: splitPO.storeKey,
                  items: splitPO.splitItems?.map(item => ({
                    sku: item.sku,
                    quantity: item.quantity,
                    title: item.title
                  })) || [],
                  line_items: splitPO.splitItems?.map(item => ({
                    sku: item.sku,
                    quantity: item.quantity,
                    title: item.title
                  })) || [],
                  localMetadata: {
                    status: splitPO.status || 'waiting_arrival',
                    isSplitPO: true,
                    originalPoId: splitPO.originalPoId,
                    partNumber: splitPO.partNumber,
                    tasks: splitPO.tasks || {
                      printLabels: { completed: false },
                      verifyProductData: { completed: false },
                      notifyArrival: { completed: false },
                      checkPreorders: { completed: false },
                      awaitingStockin: { completed: false },
                      putAway: { completed: false }
                    }
                  }
                };
                return [...prev, newSplitPO];
              });
              // Show success notification
              setSuccessMessage(`Split PO ${splitPO.reference} created for unreceived items`);
              setTimeout(() => setSuccessMessage(''), 4000);
              // A plain cached fetch re-applied the pre-split page over the
              // optimistic update (new PO invisible until a hard refresh), so
              // drop the client pages and refetch uncached, without ?refresh
              // (which forced a full supplier rebuild on the server). Split
              // POs come from po_metadata, not the supplier cache.
              clearPoResponseCache('split created');
              fetchPurchaseOrders(false, { skipCache: true, backgroundRefresh: true, suppressSuccess: true, retryEmptyDraft: false });
            }}
            darkMode={darkMode}
          />
        </MountAfterFirstOpen>

        <MountAfterFirstOpen open={!!selectedPOForPreArrivalSplit}>
          <POPreArrivalSplitModal
            po={selectedPOForPreArrivalSplit}
            open={!!selectedPOForPreArrivalSplit}
            onClose={() => setSelectedPOForPreArrivalSplit(null)}
            darkMode={darkMode}
            onCreated={(splitPO, movedItems) => {
              const parentId = selectedPOForPreArrivalSplit?.id;
              // Optimistic update: reduce the parent's lines and add the new part
              // so both cards are right immediately; the refetch replaces this.
              setPurchaseOrders(prev => {
                const withReducedParent = prev.map(p =>
                  String(p.id) === String(parentId)
                    ? {
                        ...p,
                        items: subtractMovedItemsFromPoItems(p.items, movedItems),
                        line_items: subtractMovedItemsFromPoItems(p.line_items || p.items, movedItems)
                      }
                    : p
                );
                if (withReducedParent.some(p => String(p.id) === String(splitPO.id))) {
                  return withReducedParent;
                }
                const childItems = (splitPO.splitItems || []).map(item => ({
                  sku: item.sku,
                  quantity: item.quantity,
                  title: item.title
                }));
                return [...withReducedParent, {
                  id: splitPO.id,
                  reference: splitPO.reference,
                  vendor_display_name: splitPO.vendor,
                  supplier_name: splitPO.vendor,
                  storeKey: splitPO.storeKey,
                  created_date: splitPO.splitCreatedAt,
                  items: childItems,
                  line_items: childItems,
                  localMetadata: splitPO,
                  isSplitPO: true,
                  originalPoId: splitPO.originalPoId,
                  partNumber: splitPO.partNumber
                }];
              });
              setSuccessMessage(`${splitPO.reference} created — split-off items now carry their own ETA`);
              setTimeout(() => setSuccessMessage(''), 4000);
              // A cached fetchPurchaseOrders(false) re-applied the pre-split
              // page over the optimistic update above, so both new POs stayed
              // invisible until a hard refresh (Rhys 2026-08-12). Drop the
              // client pages and refetch uncached, without ?refresh (a full
              // supplier rebuild the split never needed).
              clearPoResponseCache('pre-arrival split created');
              fetchPurchaseOrders(false, { skipCache: true, backgroundRefresh: true, suppressSuccess: true, retryEmptyDraft: false });
            }}
          />
        </MountAfterFirstOpen>

        <MountAfterFirstOpen open={!!selectedPOForWorksheet}>
          <POWorksheet
            po={selectedPOForWorksheet}
            open={!!selectedPOForWorksheet}
            onClose={closePOWorksheet}
            onPrinted={handlePOWorksheetPrinted}
            darkMode={darkMode}
            directPrint
          />
        </MountAfterFirstOpen>



      </ErrorBoundary>
    </div>
  );
}

function AppWithAuth() {
  const {
    updateReady,
    remoteVersion,
    targetKey,
    refreshLoopBlocked,
    refreshApp,
  } = useAppUpdateMonitor();

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return JSON.parse(saved);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark-mode');
      document.body.classList.add('dark-mode');
      document.body.style.backgroundColor = '#1a1a1a';
      document.body.style.color = '#e5e5e5';
    } else {
      document.documentElement.classList.remove('dark-mode');
      document.body.classList.remove('dark-mode');
      document.body.style.backgroundColor = '#f6f6f7';
      document.body.style.color = '#202223';
    }
    return () => {
      document.body.classList.remove('dark-mode');
      document.documentElement.classList.remove('dark-mode');
    };
  }, [darkMode]);

  const darkModeContextValue = useMemo(
    () => ({ darkMode, setDarkMode }),
    [darkMode]
  );

  return (
    <DarkModeContext.Provider value={darkModeContextValue}>
    <AppProvider theme={darkMode ? 'dark-experimental' : 'light'} i18n={enTranslations}>
      <AppUpdateToast
        updateReady={updateReady}
        remoteVersion={remoteVersion}
        targetKey={targetKey}
        refreshLoopBlocked={refreshLoopBlocked}
        onRefresh={refreshApp}
      />
      <style>{`
        /* FORCE PO WORKFLOW BUTTON COLORS */
        .Polaris-Button.btn-custom-green,
        .btn-custom-green .Polaris-Button,
        .btn-custom-green {
          background-color: #008060 !important;
          color: white !important;
          border: none !important;
          font-weight: 600 !important;
          min-width: 150px !important;
          white-space: nowrap !important;
        }
        .Polaris-Button.btn-custom-green:hover,
        .btn-custom-green:hover .Polaris-Button,
        .btn-custom-green:hover {
          background-color: #006048 !important;
        }

        .Polaris-Button.btn-custom-orange,
        .btn-custom-orange .Polaris-Button,
        .btn-custom-orange {
          background-color: #F49342 !important;
          color: white !important;
          border: none !important;
          font-weight: 600 !important;
          min-width: 150px !important;
          white-space: nowrap !important;
        }
        .Polaris-Button.btn-custom-orange:hover,
        .btn-custom-orange:hover .Polaris-Button,
        .btn-custom-orange:hover {
          background-color: #D6660F !important;
        }

        /* Enforce Icon Color */
        .btn-custom-green .Polaris-Icon svg,
        .btn-custom-orange .Polaris-Icon svg {
          fill: white !important;
        }
      `}</style>
      <AuthWrapper>
        <App />
      </AuthWrapper>
    </AppProvider>
    </DarkModeContext.Provider>
  );
}

export default AppWithAuth;
